<template>
  <div v-if="agentSetupChatHasOpened === true && agentSetupScreenMode === 'chat'" class="ai-agent-setup agent-setup-chat-header px-4 pt-4" :dir="isRtl === true ? 'rtl' : 'ltr'">
    <div class="flex items-center justify-end agent-setup-chat-toolbar">
      <button type="button" class="btn btn-sm agent-setup-new-chat-btn" @click="startAgentSetupNewChat">
        <Icon icon="tabler:plus" class="agent-setup-new-chat-icon" />
        <span>{{ t('chat.newChatButton', 'New Chat') }}</span>
      </button>
      <div class="agent-setup-mode-switch" role="tablist" :aria-label="t('aiChatAgentSetup.modeSwitch.label', 'Mode')">
        <button type="button" role="tab" class="agent-setup-mode-button" :class="agentSetupScreenMode === 'ui' ? 'agent-setup-mode-button-active' : ''" :aria-selected="agentSetupScreenMode === 'ui'" @click="setAgentSetupScreenMode('ui')">
          <Icon icon="tabler:layout-dashboard" class="agent-setup-mode-icon" />
          <span>{{ t('aiChatAgentSetup.modeSwitch.uiMode', 'UI Mode') }}</span>
        </button>
        <button type="button" role="tab" class="agent-setup-mode-button" :class="agentSetupScreenMode === 'chat' ? 'agent-setup-mode-button-active' : ''" :aria-selected="agentSetupScreenMode === 'chat'" @click="setAgentSetupScreenMode('chat')">
          <Icon icon="tabler:sparkles" class="agent-setup-mode-icon" />
          <span>{{ t('aiChatAgentSetup.modeSwitch.chatMode', 'Chat Mode') }}</span>
        </button>
      </div>
    </div>
  </div>
  <div v-if="agentSetupChatHasOpened === true" v-show="agentSetupScreenMode === 'chat'" class="ai-agent-setup-chat-mode agent-setup-adi-surface">
    <AdiWidget ref="agentSetupAdiWidgetRef" layout="page" :hide-header="true" :setup-surface="true" :agent-setup-mode="true" />
  </div>
  <div v-show="agentSetupScreenMode === 'ui'">
    <ThemeOnly theme="modern">
      <ModernAIChatAgentSetup />
    </ThemeOnly>
    <ThemeOnly theme="modern" :invert="true">
    <div class="relative ai-agent-setup" :dir="isRtl === true ? 'rtl' : 'ltr'">
      <div v-if="initializing === true" class="fixed inset-0 bg-base-100 bg-opacity-75 z-50 flex items-center justify-center">
        <span class="loading loading-spinner loading-lg"></span>
      </div>
      
      
      
      <div class="mt-6 ml-4 flex justify-between space-x-4 m-2 mr-4 flex-end relative title-row" :class="{ 'rtl-row': isRtl === true }">
        <div>
          <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
            {{ isEditMode === true ? t('aiChatAgentSetup.pageTitle.edit', 'Edit AI Agent') : t('aiChatAgentSetup.pageTitle.new', 'New Agent') }}
          </h1>
          <h1 class="text text-gray-900 dark:text-gray-200" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
            {{ isEditMode === true ? t('aiChatAgentSetup.pageSubtitle.edit', 'Edit your custom AI agent.') : t('aiChatAgentSetup.pageSubtitle.new', 'Create a new custom AI agent for your use.') }}
          </h1>
        </div>
        <div class="flex items-center space-x-4" :class="isRtl === true ? 'space-x-reverse' : ''">
          <div v-if="isEditMode === true" class="flex items-center space-x-4 bg-base-100 p-2 rounded-lg shadow-md pl-3 pr-3" :class="isRtl === true ? 'space-x-reverse' : ''">
            <span class="text-lg font-semibold">{{ agentName }}</span>
            <span class="badge badge-outline" v-if="agentType === 'PublicAgent'">{{ t('aiChatAgentSetup.agentType.publicAgent', 'Public Agent') }}</span>
            <span class="badge badge-outline" v-if="agentType === 'PrivateAgent'">{{ t('aiChatAgentSetup.agentType.privateAgent', 'Private Agent') }}</span>
            <button @click="openChat" class="btn btn-sm">{{ openAgentButtonLabel }}</button>
          </div>
          <div class="agent-setup-mode-switch agent-setup-title-mode-switch" :class="isRtl === true ? 'order-first' : 'order-last'" role="tablist" :aria-label="t('aiChatAgentSetup.modeSwitch.label', 'Mode')">
            <button type="button" role="tab" class="agent-setup-mode-button" :class="agentSetupScreenMode === 'ui' ? 'agent-setup-mode-button-active' : ''" :aria-selected="agentSetupScreenMode === 'ui'" @click="setAgentSetupScreenMode('ui')">
              <Icon icon="tabler:layout-dashboard" class="agent-setup-mode-icon" />
              <span>{{ t('aiChatAgentSetup.modeSwitch.uiMode', 'UI Mode') }}</span>
            </button>
            <button type="button" role="tab" class="agent-setup-mode-button" :class="agentSetupScreenMode === 'chat' ? 'agent-setup-mode-button-active' : ''" :aria-selected="agentSetupScreenMode === 'chat'" @click="setAgentSetupScreenMode('chat')">
              <Icon icon="tabler:sparkles" class="agent-setup-mode-icon" />
              <span>{{ t('aiChatAgentSetup.modeSwitch.chatMode', 'Chat Mode') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div role="tablist" v-show="initializing === false" class="tabs tabs-lifted ai-agent-setup" :dir="isRtl === true ? 'rtl' : 'ltr'" style="margin:1%; margin-top: 2%;" @change="syncCurrentPageFromMainTabs">
      <input type="radio" data-currentPage="1" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.agentInformation', 'Agent Information')" checked="checked" />
      <div role="tabpanel" data-currentPage="1" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <div class="flex justify-between" id="tabTitle">
          <h1 class="text-2xl" style="margin-bottom: 0%;" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.title', 'Agent Information') }}</h1>
          <div v-if="isVoiceOnlyChannelSelection !== true || showAllFields === true" class="form-control ml-4" id="agent-active-toggle-div">
            <label class="label cursor-pointer">
              <span class="label-text mr-2">{{ t('aiChatAgentSetup.agentInformation.activeLabel', 'Active') }}</span>
              <input type="checkbox" v-model="agentActive" class="toggle toggle-accent" />
            </label>
          </div>
        </div>
      <div class="flex">
        <div v-if="route.query.preview === 'left'" class="flex-shrink-0" style="width: 35%; margin-right: 2%;">
          <div class="card bg-base-200 p-3 rounded-md">
            <div class="tabs tabs-lifted">
              <input type="radio" name="setup_preview_tabs" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.preview.live','Live Agent')" checked="checked" />
              <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-2" style="min-height: 460px;">
                <div id="setup-live-preview-container" style="min-height: 440px; position: relative;"></div>
              </div>
              <input type="radio" name="setup_preview_tabs" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.preview.flow','Flow Preview')" />
              <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-2" style="min-height: 460px;">
                <FlowPreviewChat v-if="isEditMode === true && route.query.edit !== undefined" :agent-id="route.query.edit" :key="flowPreviewKey" />
              </div>
            </div>
          </div>
        </div>
        <div id="section-1" :style="route.query.preview === 'left' ? 'width:63%' : 'width:60%'">
            <div class="mb-4">
              <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentNameLabel', 'Agent Name') }}:<span class="text-error">*</span></p>
              <label class="input input-bordered flex items-center space-x-2 w-90" :class="{ 'input-error': agentNameError === true }">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" class="h-4 w-4 opacity-70">
                  <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12.735 14c.618 0 1.093-.561.872-1.139a6.002 6.002 0 0 0-11.215 0c-.22.578.254 1.139.872 1.139h9.47Z" />
                </svg>
                <input type="text" v-model="agentName" maxlength="100" class="grow" :placeholder="t('aiChatAgentSetup.agentInformation.agentNamePlaceholder', 'Agent name')" :style="getDirStyle('agentName')" @keydown="handleDirKeydown($event, 'agentName')" @input="agentNameError = false" />
              </label>
              <div class="text-xs text-right pr-1 mt-1 text-gray-500">{{ t('aiChatAgentSetup.agentInformation.charCount', { current: agentName.length, max: 100 }, agentName.length + '/100') }}</div>
              <p v-if="agentNameError === true" class="text-error text-xs mt-1">{{ agentNameErrorMessage }}</p>
            </div>
            <div class="mb-4 w-90">
              <div class="flex items-center justify-between mb-2">
                <p class="label cursor-pointer mb-0" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
                  {{ t('aiChatAgentSetup.agentInformation.agentInstructionsLabel', 'Agent Instructions and Description') }}:<span class="text-error">*</span>
                </p>
                <div class="flex items-center">
                  <AgentToneSelector
                    :selected-tone="selectedAgentTone"
                    @update:selected-tone="selectedAgentTone = $event"
                  />
                  <AgentPersonalitySelector
                    :selected-personality="selectedAgentPersonality"
                    @personality-change-requested="handlePersonalityChange"
                    style="margin-inline-start: 16px;"
                  />
                </div>
              </div>
              <div class="relative mt-2">
                <button type="button" class="btn btn-ghost btn-xs absolute z-10" :style="isRtl === true ? { top: '8px', left: '12px' } : { top: '8px', right: '12px' }" @click="openMaxPromptModal" :title="t('aiChatAgentSetup.agentInformation.agentInstructionsLabel', 'Agent Instructions and Description')">
                  <Icon icon="lucide:maximize-2" class="w-4 h-4" />
                </button>
                <button v-show="false" type="button" class="btn btn-outline btn-secondary btn-xs absolute z-10 shadow-sm" style="top: 8px; left: 40px;" @click="enhanceDescriptionWithAI(agentDescriptionRef)" :disabled="isEnhancingDescription === true">
                  <span v-if="isEnhancingDescription === true" class="loading loading-spinner loading-xs"></span>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:4px;"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>
                  <span v-if="isEnhancingDescription === false">{{ t('aiChatAgentSetup.agentInformation.enhanceDescription', 'Enhance with AI') }}</span>
                </button>
                <textarea
                  ref="agentDescriptionRef"
                  v-model="agentDescription"
                  class="textarea textarea-bordered w-100"
                  :class="{ 'textarea-error': agentDescriptionError === true }"
                  style="width: 100%;height: 160px;"
                  :style="getDirStyle('agentDescription')"
                  :placeholder="t('aiChatAgentSetup.agentInformation.agentInstructionsPlaceholder', 'Description Prompt')"
                  @keydown="handleDirKeydown($event, 'agentDescription')" @input="handleAgentDescriptionInput()"
                  :maxlength="maxAgentDescriptionCharacters"
                ></textarea>
              </div>
              <div class="text-xs text-right pr-1 mt-1 text-gray-500">{{ agentDescription.length }}/{{ maxAgentDescriptionCharacters }}</div>
              <div v-if="showVoiceAgentPromptWarning === true" class="alert alert-warning mt-2">
                <span>{{ t('aiChatAgentSetup.agentInformation.voiceAgentPromptWarning', 'More than 3000 chars can cause the voice agent to respond slowly, around 2-3 seconds per response. We recommend keeping the prompt lower than that.') }}</span>
              </div>
              <p v-if="agentDescriptionError === true" class="text-error text-xs mt-1">{{ agentDescriptionErrorMessage }}</p>
            </div>
            <div class="mb-4 w-90">
              <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentShortDescriptionLabel', 'Agent Short Description') }}:<span class="text-error">*</span></p>
              <textarea
                v-model="agentShortDescription"
                class="textarea textarea-bordered w-100"
                :class="{ 'textarea-error': agentShortDescriptionError === true }"
                style="width: 100%;height: 80px;" :style="getDirStyle('agentShortDescription')"
                :placeholder="t('aiChatAgentSetup.agentInformation.agentShortDescriptionPlaceholder', 'A brief summary (for agent selection lists)')"
                @keydown="handleDirKeydown($event, 'agentShortDescription')" @input="agentShortDescriptionError = false"
                maxlength="250"
              ></textarea>
              <div class="text-xs text-right pr-1 mt-1 text-gray-500">{{ t('aiChatAgentSetup.agentInformation.charCount', { current: agentShortDescription.length, max: 250 }, agentShortDescription.length + '/250') }}</div>
              <p v-if="agentShortDescriptionError === true" class="text-error text-xs mt-1">{{ agentShortDescriptionErrorMessage }}</p>
            </div>
            <div v-if="agentMode !== 'Flow' && agentMode !== 'FlowBased'" class="mb-4 w-90">
              <div class="flex justify-between items-center mb-2">
                <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.startingMessageLabel', 'Starting Message') }}:<span class="text-error">*</span></p>
              <CustomLanguageSelector
                v-model="selectedStartingMessageLang"
                :options="languages"
                :placeholder="t('aiChatAgentSetup.agentInformation.startingMessageLanguageSelectPlaceholder', 'Select Language')"
                widthClass="w-max min-w-[150px]"
              />
              </div>
              <textarea
                v-model="currentStartingMessage"
                class="textarea textarea-bordered w-100"
                :class="{ 'textarea-error': startingMessageError === true }"
                style="width: 100%;height: 100px;" :style="getDirStyle('startingMessage')"
                :placeholder="t('aiChatAgentSetup.agentInformation.startingMessagePlaceholder', 'Initial message when chat starts')"
                @keydown="handleDirKeydown($event, 'startingMessage')" @input="startingMessageError = false"
                maxlength="2000"
              ></textarea>
              <div class="text-xs text-right pr-1 mt-1 text-gray-500">{{ t('aiChatAgentSetup.agentInformation.charCount', { current: currentStartingMessage.length, max: 2000 }, currentStartingMessage.length + '/2000') }}</div>
              <p v-if="startingMessageError === true" class="text-error text-xs mt-1">{{ startingMessageErrorMessage }}</p>
            </div>
  
            <div class="form-control w-90 mb-4" id="divisions-selector-div">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
                  {{ t('aiChatAgentSetup.agentInformation.divisionsLabel', 'Divisions') }}:
                </span>
                <span class="text-error">*</span>
              </label>
              <DivisionSelect
                v-model="selectedDivisionIds"
                :options="divisions"
                :error="divisionError"
                :errorMessage="divisionErrorMessage"
              />
            </div>
            <div :class="showAllFields === true ? '' : 'hidden'">
              <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentLanguageLabel', 'Agent Language') }}</p>
              <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">Agent Language:</p>
              <CustomLanguageSelector
                v-model="agentLanguage"
                :options="[
                  { code: 'auto', name: t('aiChatAgentSetup.agentInformation.languageOptions.auto', 'Auto') },
                  { code: 'en', name: t('aiChatAgentSetup.agentInformation.languageOptions.en', 'English') },
                  { code: 'he', name: t('aiChatAgentSetup.agentInformation.languageOptions.he', 'עברית') },
                  { code: 'es', name: t('aiChatAgentSetup.agentInformation.languageOptions.es', 'Spanish') },
                  { code: 'fr', name: t('aiChatAgentSetup.agentInformation.languageOptions.fr', 'French') },
                  { code: 'de', name: t('aiChatAgentSetup.agentInformation.languageOptions.de', 'German') },
                  { code: 'zh', name: t('aiChatAgentSetup.agentInformation.languageOptions.zh', 'Chinese') },
                  { code: 'ja', name: t('aiChatAgentSetup.agentInformation.languageOptions.ja', 'Japanese') },
                  { code: 'ar', name: t('aiChatAgentSetup.agentInformation.languageOptions.ar', 'Arabic') },
                  { code: 'ru', name: t('aiChatAgentSetup.agentInformation.languageOptions.ru', 'Russian') },
                  { code: 'pt', name: t('aiChatAgentSetup.agentInformation.languageOptions.pt', 'Portuguese') },
                  { code: 'it', name: t('aiChatAgentSetup.agentInformation.languageOptions.it', 'Italian') },
                  { code: 'ko', name: t('aiChatAgentSetup.agentInformation.languageOptions.ko', 'Korean') },
                  { code: 'hi', name: t('aiChatAgentSetup.agentInformation.languageOptions.hi', 'Hindi') },
                  { code: 'sv', name: t('aiChatAgentSetup.agentInformation.languageOptions.sv', 'Swedish') },
                  { code: 'nl', name: t('aiChatAgentSetup.agentInformation.languageOptions.nl', 'Dutch') },
                  { code: 'da', name: t('aiChatAgentSetup.agentInformation.languageOptions.da', 'Danish') },
                  { code: 'fi', name: t('aiChatAgentSetup.agentInformation.languageOptions.fi', 'Finnish') },
                  { code: 'no', name: t('aiChatAgentSetup.agentInformation.languageOptions.no', 'Norwegian') },
                  { code: 'tr', name: t('aiChatAgentSetup.agentInformation.languageOptions.tr', 'Turkish') },
                  { code: 'pl', name: t('aiChatAgentSetup.agentInformation.languageOptions.pl', 'Polish') }
                ]"
                :placeholder="t('aiChatAgentSetup.agentInformation.agentLanguagePlaceholder', 'Select Agent Language')"
              />
            </div>
            
          </div>
          <div style="width:2px; background-color: dimgray; border-radius: 10px;margin-right: 2%;margin-left: 2%;"></div>
          <div id="section-2" class="flex flex-end flex-col w-90" style="width: 40%;">
            <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.configurationsTitle', 'Configurations') }}:</p>
            
            <div class="form-control w-90 mb-4" id="agent-type-selector-div">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentTypeLabel', 'Agent Type') }}:</span>
              </label>
              <AgentTypeSelector 
                :selected-agent-type="agentType" 
                @update:selected-agent-type="agentType = $event"
              />
            </div>
  
            <div class="form-control w-90 mb-4" id="agent-mode-selector-div">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentModeLabel', 'Agent Mode') }}:</span>
              </label>
              <AgentModeSelector 
                :selected-agent-mode="agentMode" 
                :allowed-modes="voiceOnlyAgentModeOptions"
                @mode-change-requested="handleAgentModeSelection"
              />
            </div>
  
            <div class="form-control w-90 mt-2" id="llm-selector-div" v-if="isFieldHidden('llm-selector-div') !== true && (isVoiceOnlyChannelSelection !== true || showAllFields === true)">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.llmModelLabel', 'LLM Model') }}:</span>
              </label>
              <LLMSelector 
                :availableLLMModels="availableLLMModels" 
                :selectedLLMModel="selectedLLMModel" 
                :locked="llmLockedId !== null"
                @update:selectedLLMModel="selectedLLMModel = $event"
              />
            </div>
  
            <div class="form-control w-90 mt-2" id="verify-llm-selector-div" v-if="(showVerificationSelector === true || showAllFields === true) && (isVoiceOnlyChannelSelection !== true || showAllFields === true)">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.verifyLlmModelLabel', 'Verification LLM (verify twice)') }}:</span>
              </label>
              <LLMSelector 
                :availableLLMModels="availableLLMModels" 
                :selectedLLMModel="selectedVerificationLLMModel" 
                @update:selectedLLMModel="selectedVerificationLLMModel = $event"
              />
            </div>
            <div class="form-control w-90 mt-2" id="groq-first-answer-reasoning-selector-div" v-if="showGroqGptOssFirstAnswerReasoningSelector === true && isVoiceOnlyChannelSelection !== true">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.groqFirstAnswerReasoningLabel', 'Groq GPT OSS First Answer Reasoning') }}:</span>
              </label>
              <CustomDropdownSelector
                id="groqFirstAnswerReasoningSelect"
                v-model="groqGptOssFirstAnswerReasoningEffort"
                class="w-full"
                :options="groqGptOssReasoningOptions"
              />
            </div>
            <div class="form-control w-90 mt-2" id="groq-verify-reasoning-selector-div" v-if="showGroqGptOssVerifyReasoningSelector === true && isVoiceOnlyChannelSelection !== true">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.groqVerifyReasoningLabel', 'Groq GPT OSS Verify Twice Reasoning') }}:</span>
              </label>
              <CustomDropdownSelector
                id="groqVerifyReasoningSelect"
                v-model="groqGptOssVerifyReasoningEffort"
                class="w-full"
                :options="groqGptOssReasoningOptions"
              />
            </div>
            
  
            <div v-if="isVoiceOnlyChannelSelection !== true || showAllFields === true" class="form-control w-90 mt-4 mb-4" id="agent-style-selector-div">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentStyleLabel', 'Agent Style') }}:</span>
              </label>
              <AgentStyleSelect
                v-model="selectedStyleGuid"
                :options="agentStyles"
                @create-new-style="showCreateStyleModal = true"
              />
            </div>
            
            
  
            <div class="form-control hidden" id="usage-model-external-knowledge-checkbox-div">
              <label class="label cursor-pointer">
                <input type="checkbox" checked="checked" class="checkbox mr-4" />
                <span class="label-text float-left" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.usageModelExternalKnowledgeLabel', 'Usage Model External Knowledge') }}</span>
              </label>
            </div>
            
       
            <div class="form-control w-90 mt-1 mb-4" id="deployment-channels-selector-div">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.deploymentChannelsLabel', 'Deployment Channels') }}:</span>
              </label>
              <DeploymentChannelsOptionsSelector
                v-model:selectedChannels="selectedDeploymentChannels"
                v-model:whatsappSourceId="selectedWhatsappSourceId"
                :agentId="route.query.edit"
                :whatsappNumber="whatsappDisplayNumber"
                v-model:phoneSourceId="selectedPhoneSourceId"
                :phoneNumber="phoneDisplayNumber"
                :enabledChannels="enabledDeploymentChannels"
              />
            </div>
            <div class="form-control w-90 mt-2" id="phone-voice-selector-div" v-if="isPhoneChannelSelected === true || isWebVoiceAgentChannelSelected === true || isWhatsappVoiceChannelSelected === true || isTelegramVoiceChannelSelected === true">
              <label class="label">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ isPhoneChannelSelected === true ? t('aiChatAgentSetup.agentInformation.phoneVoiceLabel', 'Phone Voice') : (isWhatsappVoiceChannelSelected === true ? t('aiChatAgentSetup.agentInformation.whatsappVoiceLabel', 'WhatsApp Voice') : (isTelegramVoiceChannelSelected === true ? t('aiChatAgentSetup.agentInformation.telegramVoiceLabel', 'Telegram Voice') : t('aiChatAgentSetup.agentInformation.webVoiceAgentVoiceLabel', 'Web Voice Agent Voice'))) }}:</span>
              </label>
              <PhoneVoiceSelector
                v-model:selectedVoiceId="voiceSettingsVoiceId"
                :accent="voiceSettingsAccent"
                :accentEnabled="true"
                :voiceType="voiceSettingsVoiceType"
                :voiceFilterMode="voiceSettingsDefaultEngineVersion === 'v3Flash' ? 'v3Flash' : (voiceSettingsDefaultEngineVersion === 'v3Duplex' ? 'v3Duplex' : '')"
                :agentLanguage="voiceSettingsLanguage"
                @update:accent="voiceSettingsAccent = $event"
              />
              <div class="mt-3">
                <label class="label">
                  <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.voiceSettings.language', 'Language') }}:</span>
                </label>
                <CustomLanguageSelector v-model="voiceSettingsLanguage" :options="voiceLanguageOptions" :showFlagOnRight="true" />
              </div>
            </div>
  
            <div v-if="isVoiceOnlyChannelSelection !== true || showAllFields === true" class="form-control mt-4 mb-4" id="enable-feedback-buttons-toggle-div">
              <label class="label cursor-pointer">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.enableFeedbackButtonsLabel', 'Enable Feedback Buttons (Like/Dislike)') }}:</span>
                <input type="checkbox" v-model="enableFeedbackButtons" class="toggle toggle-primary" />
              </label>
            </div>
            <div v-if="isVoiceOnlyChannelSelection !== true || showAllFields === true" class="form-control mt-1 mb-8" id="enable-suggested-responses-toggle-div">
              <label class="label cursor-pointer">
                <span class="label-text" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.enableSuggestedResponsesLabel', 'Enable Suggested Responses') }}:</span>
                <input type="checkbox" v-model="enableSuggestedResponses" class="toggle toggle-primary" />
              </label>
            </div>
            
          </div>
        </div>
        <div class="flex justify-end" style="position: relative;">
          <div class="flex space-x-2">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
            </button>
            <button @click="goNextStep(1, 1)" class="btn">
              <span v-if="isNavigating === 2" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
            </button>
          </div>
        </div>
      </div>
    <template v-if="showVoiceSettingsTab === true">
      <input type="radio" data-currentPage="12" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.voiceSettings','Voice Bot Settings')" />
      <div role="tabpanel" data-currentPage="12" class="tab-content bg-base-100 border border-base-300 rounded-box p-6 voice-settings-tab" style="min-height: 40vh;">
        <div id="tabTitle" class="voice-settings-header">
          <h1 class="text-2xl font-medium voice-settings-title">{{ t('aiChatAgentSetup.voiceSettings.title','Voice Bot Settings') }}</h1>
          <p class="mt-2 text-sm text-base-content/60 voice-settings-subtitle">{{ t('aiChatAgentSetup.voiceSettings.pageDescription', 'Choose how your voice agent sounds and handles speech recognition.') }}</p>
        </div>

        <div class="voice-settings-grid">
          <section class="voice-settings-card">
            <div class="voice-settings-card-head">
              <div class="voice-settings-card-icon">
                <Icon icon="tabler:microphone" class="h-5 w-5" />
              </div>
              <div class="voice-settings-card-copy">
                <h2 class="text-base font-semibold">{{ t('aiChatAgentSetup.voiceSettings.voiceSectionTitle', 'Agent voice') }}</h2>
                <p class="mt-1 text-xs text-base-content/60">{{ t('aiChatAgentSetup.voiceSettings.voiceSectionDescription', 'Select the voice callers will hear.') }}</p>
              </div>
            </div>
            <div class="mt-4">
              <PhoneVoiceSelector
                v-model:selectedVoiceId="voiceSettingsVoiceId"
                :accent="voiceSettingsAccent"
                :accentEnabled="true"
                :voiceType="voiceSettingsVoiceType"
                :voiceFilterMode="voiceSettingsDefaultEngineVersion === 'v3Flash' ? 'v3Flash' : (voiceSettingsDefaultEngineVersion === 'v3Duplex' ? 'v3Duplex' : '')"
                :agentLanguage="voiceSettingsLanguage"
                @update:accent="voiceSettingsAccent = $event"
              />
              <div class="mt-3">
                <label class="label">
                  <span class="label-text">{{ t('aiChatAgentSetup.voiceSettings.language', 'Language') }}:</span>
                </label>
                <CustomLanguageSelector v-model="voiceSettingsLanguage" :options="voiceLanguageOptions" :showFlagOnRight="true" />
              </div>
              <div class="voice-settings-call-tools mt-4">
                <div class="voice-settings-call-tools-title">
                  <Icon icon="tabler:tool" class="h-5 w-5" />
                  <span class="font-semibold">{{ t('aiChatAgentSetup.voiceSettings.callToolsTitle', 'Call tools') }}</span>
                </div>
                <label class="voice-settings-call-tool cursor-pointer">
                  <div class="voice-settings-call-tool-copy">
                    <span class="text-sm font-medium">{{ t('aiChatAgentSetup.voiceSettings.allowAgentHangup', 'Allow agent to hang up') }}</span>
                    <span class="mt-1 text-xs text-base-content/60">{{ t('aiChatAgentSetup.voiceSettings.allowAgentHangupDescription', 'Gives the voice agent its own hang-up tool for completed calls and explicit caller requests.') }}</span>
                  </div>
                  <input type="checkbox" v-model="voiceAllowAgentHangup" class="toggle toggle-accent" />
                </label>
                <label class="voice-settings-call-tool cursor-pointer">
                  <div class="voice-settings-call-tool-copy">
                    <span class="text-sm font-medium">{{ t('aiChatAgentSetup.voiceSettings.allowCallTransfer', 'Allow transfer to phone numbers') }}</span>
                    <span class="mt-1 text-xs text-base-content/60">{{ t('aiChatAgentSetup.voiceSettings.allowCallTransferDescription', 'Gives the voice agent a transfer tool that accepts a destination phone number or SIP address.') }}</span>
                  </div>
                  <input type="checkbox" v-model="voiceAllowCallTransfer" class="toggle toggle-accent" />
                </label>
              </div>
            </div>
          </section>

          <section class="voice-settings-card">
            <div class="voice-settings-card-head">
              <div class="voice-settings-card-icon">
                <Icon icon="tabler:adjustments-horizontal" class="h-5 w-5" />
              </div>
              <div class="voice-settings-card-copy">
                <h2 class="text-base font-semibold">{{ t('aiChatAgentSetup.voiceSettings.engineTitle', 'Voice engine') }}</h2>
                <p class="mt-1 text-xs text-base-content/60">{{ t('aiChatAgentSetup.voiceSettings.engineDescription', 'Choose the balance between transcription quality and response speed.') }}</p>
              </div>
            </div>
            <div class="mt-4">
              <div class="label py-0">
                <span class="label-text text-xs font-medium">{{ t('aiChatAgentSetup.voiceSettings.defaultEngineVersion', 'Voice engine version') }}</span>
              </div>
              <AgentTypeSelector
                class="w-full mt-2"
                :selected-agent-type="voiceSettingsDefaultEngineVersion"
                :options="voiceSettingsEngineVersionSelectorOptions"
                :placeholder-text="t('aiChatAgentSetup.voiceSettings.defaultEngineVersion', 'Select voice engine version')"
                @update:selected-agent-type="selectVoiceSettingsEngineVersion"
              />
            </div>
            <div v-if="voiceSettingsDefaultEngineVersion === 'v3Flash'" class="form-control mt-4">
              <label class="label px-0">
                <span class="label-text text-sm font-medium">{{ t('aiChatAgentSetup.voiceSettings.speed', 'Speech speed') }}</span>
                <span class="badge badge-neutral">{{ Number(voiceSettingsSpeed).toFixed(1) }}x</span>
              </label>
              <input v-model.number="voiceSettingsSpeed" type="range" min="0.5" max="2" step="0.1" class="range range-primary w-full" />
              <p class="mt-2 text-xs text-base-content/60">{{ t('aiChatAgentSetup.voiceSettings.inworldSpeedDescription', 'Controls the Inworld voice playback speed.') }}</p>
            </div>
            <div v-if="voiceSettingsDefaultEngineVersion === 'v1' || voiceSettingsDefaultEngineVersion === 'v2'" class="voice-settings-reasoning mt-4">
              <div class="voice-settings-reasoning-head">
                <span class="text-sm font-medium">{{ t('aiChatAgentSetup.voiceSettings.reasoningLevel', 'Voice reasoning level') }}</span>
              </div>
              <AgentTypeSelector
                class="w-full mt-2"
                :selected-agent-type="voiceSettingsReasoningLevel"
                :options="voiceReasoningLevelSelectorOptions"
                :placeholder-text="t('aiChatAgentSetup.voiceSettings.reasoningLevelPlaceholder', 'Select voice reasoning level')"
                @update:selected-agent-type="voiceSettingsReasoningLevel = $event"
              />
              <p class="mt-2 text-xs text-base-content/60">{{ t('aiChatAgentSetup.voiceSettings.reasoningLevelDescription', 'Controls how much reasoning the default voice engine uses before responding. This engine supports None or High only. High may take longer.') }}</p>
            </div>
          </section>

          <section class="voice-settings-card voice-settings-card--wide">
            <div class="voice-settings-card-head">
              <div class="voice-settings-card-icon">
                <Icon icon="app:termsPackage" class="h-5 w-5" />
              </div>
              <div class="voice-settings-card-copy">
                <h2 class="text-base font-semibold">{{ t('termsPackages.title', 'Terms Packages') }}</h2>
                <p class="mt-1 text-xs text-base-content/60">{{ t('aiChatAgentSetup.voiceSettings.termsPackageDescription', 'Improve recognition of specialized names and terminology.') }}</p>
              </div>
            </div>
            <div class="mt-4">
              <TermsPackageSelector
                :modelValue="voiceSettingsTermsPackages"
                :placeholder="t('termsPackages.selectPlaceholder', 'Select terms packages')"
                @update:modelValue="voiceSettingsTermsPackages = $event"
                @update:selectedTerms="voiceSettingsSonioxTerms = $event"
              />
            </div>
          </section>

          <TranscriptionOptimizerSettings
            class="voice-settings-card--wide"
            :enabled="voiceSettingsTranscriptionOptimizerEnabled === true"
            :terms="voiceSettingsTranscriptionOptimizerTerms"
            :sourcePrompt="transcriptionOptimizerSourcePrompt"
            :language="voiceSettingsLanguage"
            :providerLabel="voiceSettingsDefaultEngineVersion === 'v3Flash' ? 'Inworld' : ''"
            :isRtl="isRtl === true"
            @update:enabled="voiceSettingsTranscriptionOptimizerEnabled = $event"
            @update:terms="voiceSettingsTranscriptionOptimizerTerms = $event"
          />

          <section
            :id="'voice-settings-quick-response-filler-' + voiceSettingsDefaultEngineVersion"
            class="voice-settings-card voice-settings-card--wide voice-settings-quick-response-filler"
            :data-voice-engine="voiceSettingsDefaultEngineVersion"
          >
            <div class="voice-settings-card-head">
              <div class="voice-settings-card-icon">
                <Icon icon="tabler:bolt" class="h-5 w-5" />
              </div>
              <div class="voice-settings-card-copy">
                <h2 class="text-base font-semibold">{{ t('aiChatAgentSetup.voiceSettings.quickResponseFillerTitle', 'Instant response filler') }}</h2>
                <p class="mt-1 text-xs text-base-content/60">{{ t('aiChatAgentSetup.voiceSettings.quickResponseFillerDescription', 'Play a short word immediately after the caller stops speaking while the answer is prepared.') }}</p>
              </div>
            </div>
            <label class="voice-settings-idle-toggle mt-4 cursor-pointer">
              <span class="text-sm font-medium">{{ t('aiChatAgentSetup.voiceSettings.quickResponseFillerEnabled', 'Enabled') }}</span>
              <input v-model="voiceSettingsQuickResponseFillerEnabled" type="checkbox" class="toggle toggle-accent" />
            </label>
            <div v-if="voiceSettingsQuickResponseFillerEnabled === true" class="form-control mt-4">
              <label class="label px-0">
                <span class="label-text">{{ t('aiChatAgentSetup.voiceSettings.quickResponseFillerText', 'Filler word') }}</span>
              </label>
              <input v-model="voiceSettingsQuickResponseFillerText" type="text" maxlength="80" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.voiceSettings.quickResponseFillerPlaceholder', 'היי')" />
            </div>
          </section>

          <div class="voice-settings-bottom-row">
            <section class="voice-settings-card">
              <div class="voice-settings-card-head">
                <div class="voice-settings-card-icon">
                  <Icon icon="tabler:message-dots" class="h-5 w-5" />
                </div>
                <div class="voice-settings-card-copy">
                  <h2 class="text-base font-semibold">{{ t('aiChatAgentSetup.voiceSettings.knowledgeFillersTitle', 'Knowledge base filler words') }}</h2>
                  <p class="mt-1 text-xs text-base-content/60">{{ t('aiChatAgentSetup.voiceSettings.knowledgeFillersDescription', 'Short phrases the agent can say while looking for information in the knowledge base.') }}</p>
                </div>
              </div>
              <div class="voice-settings-fillers-toolbar">
                <div class="form-control voice-settings-fillers-language">
                  <label class="label py-1">
                    <span class="label-text">{{ t('aiChatAgentSetup.advanced.fillerLanguage', 'Language') }}</span>
                  </label>
                  <CustomLanguageSelector
                    v-model="selectedVoiceSettingsFillerLanguage"
                    :options="voiceSettingsFillerLanguageOptions"
                    widthClass="w-full"
                  />
                </div>
                <button type="button" class="btn voice-settings-fillers-add" @click="addVoiceSettingsToolFiller">
                  <Icon icon="tabler:plus" class="h-4 w-4 voice-settings-fillers-add-icon" />
                  {{ t('aiChatAgentSetup.advanced.addFillerSentence', 'Add sentence') }}
                </button>
              </div>
              <div class="voice-settings-fillers-list">
                <div v-for="(sentence, index) in currentVoiceSettingsToolFillers" :key="selectedVoiceSettingsFillerLanguage + '-knowledge-' + index" class="voice-settings-filler-row">
                  <input
                    type="text"
                    class="input input-bordered voice-settings-filler-input"
                    :dir="voiceSettingsFillerInputDirection"
                    :value="sentence"
                    :placeholder="t('aiChatAgentSetup.advanced.fillerSentencePlaceholder', 'Enter a filler sentence')"
                    @input="updateVoiceSettingsToolFiller(index, $event.target.value)"
                  />
                  <button type="button" class="btn btn-error btn-sm voice-settings-filler-delete" @click="deleteVoiceSettingsToolFiller(index)">
                    {{ t('aiChatAgentSetup.buttons.delete', 'Delete') }}
                  </button>
                </div>
                <p v-if="currentVoiceSettingsToolFillers.length === 0" class="text-sm text-base-content/60">{{ t('aiChatAgentSetup.advanced.noFillerSentences', 'No filler sentences for this language and type.') }}</p>
              </div>
            </section>

            <section class="voice-settings-card">
              <div class="voice-settings-card-head">
                <div class="voice-settings-card-icon">
                  <Icon icon="tabler:clock" class="h-5 w-5" />
                </div>
                <div class="voice-settings-card-copy">
                  <h2 class="text-base font-semibold">{{ t('aiChatAgentSetup.voiceSettings.idleAutoResponse', 'Respond when both sides are idle') }}</h2>
                  <p class="mt-1 text-xs text-base-content/60">{{ t('aiChatAgentSetup.voiceSettings.idleAutoResponseDescription', 'Force the agent to answer after the caller and agent are silent for the selected time.') }}</p>
                </div>
              </div>
              <div class="mt-4">
                <label class="voice-settings-idle-toggle cursor-pointer">
                  <span class="text-sm font-medium">{{ t('aiChatAgentSetup.voiceSettings.idleAutoResponseEnabled', 'Enabled') }}</span>
                  <input type="checkbox" v-model="voiceSettingsIdleAutoResponseEnabled" class="toggle toggle-accent" />
                </label>
                <div v-if="voiceSettingsIdleAutoResponseEnabled === true" class="voice-settings-idle-control">
                  <label class="label">
                    <span class="label-text">{{ t('aiChatAgentSetup.voiceSettings.startingMessageIdleAutoResponseSeconds', 'Idle after starting message in seconds') }}:</span>
                  </label>
                  <input type="number" min="1" max="300" step="1" v-model.number="voiceSettingsStartingMessageIdleAutoResponseSeconds" class="input input-bordered w-full" />
                </div>
                <div v-if="voiceSettingsIdleAutoResponseEnabled === true" class="voice-settings-idle-control">
                  <label class="label">
                    <span class="label-text">{{ t('aiChatAgentSetup.voiceSettings.idleAutoResponseSeconds', 'Idle during conversation in seconds') }}:</span>
                  </label>
                  <input type="number" min="1" max="300" step="1" v-model.number="voiceSettingsIdleAutoResponseSeconds" class="input input-bordered w-full" />
                </div>
                <div class="voice-settings-idle-control">
                  <label class="label">
                    <span class="label-text">{{ t('aiChatAgentSetup.voiceSettings.toolUseAutoResponseSeconds', 'Forced answer after tool use in seconds (0 = off)') }}:</span>
                  </label>
                  <input type="number" min="0" max="300" step="1" v-model.number="voiceSettingsToolUseAutoResponseSeconds" class="input input-bordered w-full" />
                </div>
              </div>
            </section>
          </div>
          <VoiceCallLimitsFields
            :maxCallMinutes="voiceSettingsMaxCallMinutes"
            :maxCallerMinutes="voiceSettingsMaxCallerMinutes"
            :callerCooldownMinutes="voiceSettingsCallerCooldownMinutes"
            @update:maxCallMinutes="voiceSettingsMaxCallMinutes = $event"
            @update:maxCallerMinutes="voiceSettingsMaxCallerMinutes = $event"
            @update:callerCooldownMinutes="voiceSettingsCallerCooldownMinutes = $event"
          />
          <section v-if="showWebVoiceSettingsTab === true" class="voice-settings-card voice-settings-card--wide">
            <button type="button" class="btn w-full justify-between" @click="isWebVoiceVisualSettingsOpen = isWebVoiceVisualSettingsOpen !== true">
              <span class="flex items-center">
                <Icon icon="tabler:device-desktop" class="h-5 w-5 mr-2" />
                {{ t('aiChatAgentSetup.webVoiceSettings.visualMode', 'Visual mode') }}
              </span>
              <Icon :icon="isWebVoiceVisualSettingsOpen === true ? 'heroicons:chevron-up' : 'heroicons:chevron-down'" class="h-5 w-5" />
            </button>
            <WebVoiceVisualSettings v-if="isWebVoiceVisualSettingsOpen === true" v-model="webVoiceVisualSettings" class="mt-4" />
          </section>
        </div>

        <div class="voice-settings-footer flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(12, -1)" class="btn">
            <span v-if="isNavigating === 10" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button @click="createAgent(true)" class="btn">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
            </button>
          </div>
        </div>
      </div>
    </template>
      <template v-if="agentMode === 'Flow'">
        <input type="radio" data-currentPage="2" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.agentFlow', 'Agent Flow')" />
        <div role="tabpanel" data-currentPage="2" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 70vh;"> 
          <div class="flex justify-between items-center mb-4" id="tabTitle">
            <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentFlow.title', 'Agent Flow') }}</h1>
          </div>
          <div class="flex"> 
            <div class="flex-grow" style="width: 100%;">
              <FlowManager
                v-if="currentPage === 2"
                ref="llmFlowRef"
                v-model:starting-message="startingMessage"
                :agent-id="route.query.edit !== undefined ? route.query.edit : ''"
                :selected-channels="selectedDeploymentChannels"
                @builder-closed="onFlowBuilderClosed"
              />
            </div>
          </div>
  
          <div class="flex justify-between mt-4" style="position: relative;">
            <button @click="goNextStep(2, -1)" class="btn">
              <span v-if="isNavigating === 1" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
            </button>
            <div class="flex space-x-2">
              <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
                  <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
               </button>
              <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
                  <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
               </button>
              <button @click="goNextStep(2, 1)" class="btn">
                <span v-if="isNavigating === 3" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
              </button>
            </div>
          </div>
        </div>
      </template>
      <template v-if="agentMode === 'FlowBased'">
        <input type="radio" data-currentPage="2" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.flowBased', 'Flow Based Agent')" />
        <div role="tabpanel" data-currentPage="2" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 70vh;">
          <div class="flex justify-between items-center mb-4" id="tabTitle">
            <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.flowBased.title', 'Flow Based Agent') }}</h1>
          </div>
          <div class="flex justify-between mt-4" style="position: relative;">
            <button @click="goNextStep(2, -1)" class="btn">
              <span v-if="isNavigating === 1" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
            </button>
            <div class="flex space-x-2">
              <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
                  <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
               </button>
              <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
                  <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
               </button>
              <button @click="goNextStep(2, 1)" class="btn">
                <span v-if="isNavigating === 3" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
              </button>
            </div>
          </div>
        </div>
      </template>
      <input type="radio" data-currentPage="3" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.knowledgeBase', 'Knowledge Base')" />
      <div role="tabpanel" data-currentPage="3" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <div class="flex justify-between items-center" id="tabTitle">
          <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.knowledgeBase.title', 'Knowledge Base') }}</h1>
          <div class="flex items-center justify-evenly">
            <button @click="refreshAgentData" class="btn btn-ghost btn-square" :disabled="isRefreshingKB === true" :title="t('aiChatAgentSetup.knowledgeBase.refreshButtonTooltip', 'Refresh Statuses')">
               <Icon icon="heroicons:arrow-path" class="h-5 w-5" :class="{'animate-spin': isRefreshingKB !== undefined ? isRefreshingKB : false}" />
            </button>
            <button @click="openCreateFolderModal" class="btn">
               <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"><path fill="none" stroke="#000" stroke-linecap="round" stroke-width="1.5" d="M18 10h-5m9 1.798c0-2.632 0-3.949-.77-4.804a3 3 0 0 0-.224-.225C20.151 6 18.834 6 16.202 6h-.374c-1.153 0-1.73 0-2.268-.153a4 4 0 0 1-.848-.352C12.224 5.224 11.816 4.815 11 4l-.55-.55c-.274-.274-.41-.41-.554-.53a4 4 0 0 0-2.18-.903C7.53 2 7.336 2 6.95 2c-.883 0-1.324 0-1.692.07A4 4 0 0 0 2.07 5.257C2 5.626 2 6.068 2 6.95M21.991 16c-.036 2.48-.22 3.885-1.163 4.828C19.657 22 17.771 22 14 22h-4c-3.771 0-5.657 0-6.828-1.172S2 17.771 2 14v-3"/></svg>
               {{ t('aiChatAgentSetup.knowledgeBase.createFolderButton', 'Create Folder +') }}
            </button>
            <div class="form-control" id="upload-knowledge-dropdown-div">
              <div ref="uploadKnowledgeDropdownRef" class="dropdown dropdown-end" :class="{ 'dropdown-top': shouldOpenUploadKnowledgeDropdownTop === true }">
                <div tabindex="0" role="button" class="btn" @mousedown="prepareDropdownPosition('uploadKnowledge')" @focus="prepareDropdownPosition('uploadKnowledge')">{{ t('aiChatAgentSetup.knowledgeBase.uploadKnowledgeButton', 'Upload Knowledge +') }}</div>
                <ul tabindex="0" class="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-52">
                  <li>
                    <label class="btn btn-ghost justify-start">
                      <input type="file" @change="handleFileUpload" multiple class="hidden" accept=".docx,.pdf,.txt,.xlsx" />
                      {{ t('aiChatAgentSetup.knowledgeBase.uploadOptions.uploadFiles', 'Upload Files') }}
                    </label>
                  </li>
                  <li><button @click="openTextInputModal" class="btn btn-ghost justify-start">{{ t('aiChatAgentSetup.knowledgeBase.uploadOptions.addText', 'Add Text') }}</button></li>
                  <li><button @click="showWebsiteLinkModal = true" class="btn btn-ghost justify-start">{{ t('aiChatAgentSetup.knowledgeBase.uploadOptions.addWebsiteLink', 'Add Website Link') }}</button></li>
                   <li><button @click="showWebsiteBatchModal = true" class="btn btn-ghost justify-start">{{ t('aiChatAgentSetup.knowledgeBase.uploadOptions.addWebsiteBatch', 'Add Website Batch URLs') }}</button></li>
                  <li><button @click="showWebsiteScrapeModal = true" class="btn btn-ghost justify-start">{{ t('aiChatAgentSetup.knowledgeBase.uploadOptions.scrapeWebsite', 'Scrape Entire Website') }}</button></li>
                  <li><button @click="showSharedKbPicker = true" class="btn btn-ghost justify-start" :class="isRtl === true ? 'text-right' : 'text-left'">{{ t('aiChatAgentSetup.knowledgeBase.linkSharedKnowledgeBases', 'Link Shared Knowledge Bases') }}</button></li>
                  <li><button @click="showAgentKbPicker = true" class="btn btn-ghost justify-start" :class="isRtl === true ? 'text-right' : 'text-left'">{{ t('aiChatAgentSetup.knowledgeBase.linkAgentKnowledgeBases', 'Agents Knowledge Base') }}</button></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div class="text-sm breadcrumbs my-4">
           <ul>
             <li v-for="(part, index) in currentKbPath" :key="part.id != undefined ? part.id : 'root'">
               <a @click="navigateToBreadcrumb(index)" class="cursor-pointer hover:underline">
                 <svg v-if="index === 0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mr-1 inline-block">
                   <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h7.5" />
                 </svg>
                 <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4 mr-1 inline-block">
                   <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
                 </svg>
                 {{ part.name }}
              </a>
             </li>
           </ul>
         </div>
        <div class="flex flex-wrap items-center mb-4 mt-4">
         <div class="form-control flex-1 min-w-0" id="kb-search-input-div">
            <div class="flex items-center">
             <label class="input input-bordered flex items-center flex-1 min-w-0">
               <Icon icon="tabler:search" class="w-4 h-4 text-base-content/60 flex-shrink-0" :class="isRtl === true ? 'ml-2' : 'mr-2'" />
               <input type="text" v-model="searchQuery" :placeholder="t('aiChatAgentSetup.knowledgeBase.searchPlaceholder', 'Search knowledge in current folder...')" class="grow min-w-0 bg-transparent outline-none" :style="getDirStyle('searchQuery')" @keydown="handleDirKeydown($event, 'searchQuery')"/>
             </label>
             <button class="btn flex-shrink-0" :class="isRtl === true ? 'mr-5' : 'ml-5'" @click="openAiSearch" :title="t('aiChatAgentSetup.knowledgeBase.searchWithAi', 'Search with AI')">
               <img :src="aiSearchIcon" alt="" class="w-5 h-5" />
               <span class="hidden sm:inline">{{ t('aiChatAgentSetup.knowledgeBase.searchWithAi', 'Search with AI') }}</span>
             </button>
            </div>
          </div>
          <div ref="kbViewDropdownRef" class="ml-4 dropdown dropdown-end" :class="{ 'dropdown-top': shouldOpenKbViewDropdownTop === true }">
              <label tabindex="0" class="btn m-1" @mousedown="prepareDropdownPosition('kbView')" @focus="prepareDropdownPosition('kbView')">
                  {{ t('aiChatAgentSetup.knowledgeBase.viewMode.label', 'View') }}: {{ kbViewMode === 'rows' ? t('aiChatAgentSetup.knowledgeBase.viewMode.rows', 'Rows') : t('aiChatAgentSetup.knowledgeBase.viewMode.grid', 'Grid') }}
                  <Icon icon="heroicons:chevron-down" class="w-4 h-4 ml-1"/>
              </label>
              <ul tabindex="0" class="dropdown-content z-[1] menu p-2 shadow bg-base-100 rounded-box w-32">
                  <li><a @click="kbViewMode = 'rows'" :class="{'font-bold text-primary': kbViewMode === 'rows'}">{{ t('aiChatAgentSetup.knowledgeBase.viewMode.rows', 'Rows') }}</a></li>
                  <li><a @click="kbViewMode = 'grid'" :class="{'font-bold text-primary': kbViewMode === 'grid'}">{{ t('aiChatAgentSetup.knowledgeBase.viewMode.grid', 'Grid') }}</a></li>
              </ul>
           </div>
          <div class="ml-4 dropdown dropdown-end" :class="{ 'dropdown-open': isAdvancedOpen === true, 'dropdown-top': shouldOpenAdvancedDropdownTop === true }" ref="advancedDropdownRef">
              <label tabindex="0" class="btn m-1" @click="toggleAdvancedDropdown">
                  {{ t('aiChatAgentSetup.knowledgeBase.advanced.label', 'Advanced') }}
                  <Icon icon="heroicons:chevron-down" class="w-4 h-4 ml-1"/>
              </label>
              <div tabindex="0" class="dropdown-content z-[2] p-4 shadow bg-base-100 rounded-box w-80">
                  <div class="flex items-center mb-3">
                      <span class="mr-2">{{ t('aiChatAgentSetup.knowledgeBase.advanced.rowsPerPage', 'Rows per page') }}:</span>
                      <CustomDropdownSelector id="kbItemsPerPageSelect" v-model.number="itemsPerPageKb" class="w-28" buttonClass="btn-sm" :options="[...itemsPerPageKbOptions.map(opt => ({ value: opt, label: String(opt) })), { value: 0, label: t('aiChatAgentSetup.knowledgeBase.advanced.all', 'All') }]" />
                  </div>
                  <div class="flex items-center mb-2">
                      <button class="btn btn-sm mr-2" @click="runAdvancedAction('selectAllFiltered')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.selectAll', 'Select All') }}</button>
                      <button class="btn btn-sm mr-2" @click="runAdvancedAction('clearSelection')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.clearSelection', 'Clear Selection') }}</button>
                      <button class="btn btn-sm btn-error" @click="runAdvancedAction('deleteSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.deleteSelected', 'Delete Selected') }}</button>
                  </div>
                  <button class="btn btn-sm w-full mb-2" @click="runAdvancedAction('downloadKnowledgeBaseCsv')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.downloadCsv', 'Download Knowledge CSV') }}</button>
                  <button class="btn btn-sm w-full mb-2" @click="runAdvancedAction('downloadKnowledgeBaseXlsx')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.downloadXlsx', 'Export Knowledge to XLSX') }}</button>
                  <div class="divider my-2"></div>
                  <div class="mb-2 text-sm font-semibold">{{ t('aiChatAgentSetup.knowledgeBase.advanced.bulkScheduler', 'Bulk Rescrape Scheduler') }}</div>
                  <div class="flex items-center mb-2">
                      <span class="mr-2">{{ t('aiChatAgentSetup.knowledgeBase.advanced.interval', 'Interval') }}:</span>
                      <CustomDropdownSelector id="bulkRescrapeIntervalSelect" v-model.number="bulkRescrapeIntervalDays" class="w-28" buttonClass="btn-sm" :options="[{ value: 1, label: t('aiChatAgentSetup.knowledgeBase.advanced.oneDay', '1 day') }, { value: 7, label: t('aiChatAgentSetup.knowledgeBase.advanced.sevenDays', '7 days') }, { value: 14, label: t('aiChatAgentSetup.knowledgeBase.advanced.fourteenDays', '14 days') }, { value: 30, label: t('aiChatAgentSetup.knowledgeBase.advanced.thirtyDays', '30 days') }]" />
                  </div>
                  <div class="flex items-center mb-3">
                      <span class="mr-2">{{ t('aiChatAgentSetup.knowledgeBase.advanced.time', 'Time (UTC)') }}:</span>
                      <input type="time" v-model="bulkRescrapeAtTime" step="1800" class="input input-bordered input-xs w-28" />
                  </div>
                  <button class="btn btn-sm w-full mb-2" @click="runAdvancedAction('enableRescrapeSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.enableRescrapeSelected', 'Update Rescrape Info (Selected)') }}</button>
                  <button class="btn btn-sm w-full mb-2" @click="runAdvancedAction('disableRescrapeSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.disableRescrapeSelected', 'Disable Rescrape (Selected)') }}</button>
                  <div class="divider my-2">{{ t('aiChatAgentSetup.knowledgeBase.advanced.immediate', 'Immediate') }}</div>
                  <button class="btn btn-sm w-full mb-2" @click="runAdvancedAction('rescanSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.rescanSelected', 'Rescrape Selected') }}</button>
                  <button class="btn btn-sm w-full mb-2" @click="runAdvancedAction('changeSelectedWebsiteDomain')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.changeSelectedWebsiteDomain', 'Change Selected Domains and Rescrape') }}</button>
                  <button class="btn btn-sm w-full mb-2" @click="runAdvancedAction('retryProcessingSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.retryProcessingSelected', 'Rescrape Selected Processing') }}</button>
                  <button class="btn btn-sm w-full" @click="runAdvancedAction('rescanFailedSelected')">{{ t('aiChatAgentSetup.knowledgeBase.advanced.rescanFailedSelected', 'Rescrape Selected Failed') }}</button>
              </div>
          </div>
          
        </div>
  
      
        <div v-if="kbViewMode === 'rows'">
          <div class="overflow-x-auto relative">
            <div v-if="isRefreshingKB === true" class="absolute inset-0 bg-base-100 bg-opacity-75 flex items-center justify-center z-10 rounded-box">
               <span class="loading loading-spinner loading-lg"></span>
            </div>
            <table class="table w-full">
              <thead>
                <tr>
                  <th class="w-1/12">
                    <input type="checkbox" :checked="isAllSelectedOnPage === true" @change="toggleSelectAllOnPage" />
                  </th>
                  <th class="w-2/5">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.name', 'Name') }}</th>
                  <th class="w-1/5">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.type', 'Type') }}</th>
                  <th class="w-1/5">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.status', 'Status') }}</th>
                  <th class="w-1/5 text-center">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.actions', 'Actions') }}</th>
                  <th class="w-1/5 text-center">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.rescrape', 'Rescrape') }}</th>
                  <th class="w-1/5">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.cache', 'Cache') }}</th>
                  <th class="w-1/5 text-center">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.cachedKnowledgeData', 'Cached Data') }}</th>
                  <th class="w-1/5 text-center">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.hideSource', 'Hide Source') }}</th>
                </tr>
              </thead>
              <tbody>
                   <tr v-if="currentFolderId === null" v-for="kb in linkedSharedKbItems" :key="'sharedkb-' + kb.kbId" class="hover">
                     <td></td>
                     <td class="cursor-default hover:text-primary flex items-center">
                       <Icon icon="mdi:library" class="w-5 h-5 mr-2 text-purple-500" />
                       {{ kb.name }}
                     </td>
                     <td>{{ t('aiChatAgentSetup.knowledgeBase.fileType.sharedKb', 'Shared KB') }}</td>
                     <td>
                       <span class="badge badge-success">Linked</span>
                     </td>
                     <td class="kb-actions-cell text-center">
                       <div class="inline-flex items-center justify-center whitespace-nowrap">
                        <button @click="unlinkSharedKb(kb.kbId)" class="btn btn-error btn-xs mx-1">{{ t('aiChatAgentSetup.knowledgeBase.unlinkButton','Unlink') }}</button>
                        <router-link to="/sharedKnowledgeBases" class="btn btn-xs mx-1">{{ t('aiChatAgentSetup.knowledgeBase.openButton','Open') }}</router-link>
                       </div>
                     </td>
                     <td></td>
                     <td></td>
                     <td class="text-center">-</td>
                     <td class="text-center">-</td>
                   </tr>
                   <tr v-if="currentFolderId === null" v-for="agentKb in linkedAgentKbItems" :key="'agentkb-' + agentKb.agentId" class="hover">
                     <td></td>
                     <td class="cursor-default hover:text-primary flex items-center">
                       <Icon icon="mdi:robot-outline" class="w-5 h-5 mr-2 text-info" />
                       {{ agentKb.agentName }}
                     </td>
                     <td>{{ t('aiChatAgentSetup.knowledgeBase.agentKbType', 'Agent KB') }}</td>
                     <td>
                       <span class="badge badge-success">{{ t('aiChatAgentSetup.knowledgeBase.linkedStatus', 'Linked') }}</span>
                     </td>
                     <td class="kb-actions-cell text-center">
                       <div class="inline-flex items-center justify-center whitespace-nowrap">
                        <button @click="unlinkLinkedAgentKb(agentKb.agentId)" class="btn btn-error btn-xs mx-1">{{ t('aiChatAgentSetup.knowledgeBase.unlinkButton','Unlink') }}</button>
                        <router-link :to="'/aiChatAgentSetup?edit=' + agentKb.agentId" class="btn btn-xs mx-1">{{ t('aiChatAgentSetup.knowledgeBase.openButton','Open') }}</router-link>
                       </div>
                     </td>
                     <td></td>
                     <td></td>
                     <td class="text-center">-</td>
                     <td class="text-center">-</td>
                   </tr>
                   <tr v-for="folder in paginatedFoldersInCurrentView"
                       :key="folder.id"
                       class="hover"
                       :class="{ 'bg-blue-100 dark:bg-blue-800 border-blue-300 dark:border-blue-600': isDragOverFolder === folder.id }"
                       @dragover.prevent="handleFolderDragOver($event, folder.id)"
                       @dragleave.prevent="handleFolderDragLeave($event, folder.id)"
                       @drop.prevent="handleFileDropOnFolder(folder.id)">
                    <td>-</td>
                    <td @click="navigateToFolder(folder)" class="cursor-pointer hover:text-primary flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5 mr-2 text-warning">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
                      </svg>
        
                      <span v-if="folderBeingRenamedId !== folder.id">{{ folder.name }}</span>
                      <input v-else
                             type="text"
                             v-model="newFolderNameInput"
                             class="input input-sm input-bordered w-full max-w-xs"
                             :data-folder-rename-id="folder.id"
                             @blur="saveFolderName(folder)"
                             @keyup.enter="saveFolderName(folder)"
                             @keyup.esc="cancelRename"/>
                    </td>
                    <td>{{ t('aiChatAgentSetup.knowledgeBase.fileType.folder', 'Folder') }}</td>
                    <td></td>
                    <td class="kb-actions-cell text-center">
                      <div class="inline-flex items-center justify-center whitespace-nowrap">
                      <button v-if="folderBeingRenamedId !== folder.id"
                              @click.stop="startRenamingFolder(folder)"
                              class="btn btn-xs mx-1">{{ t('aiChatAgentSetup.knowledgeBase.actions.rename', 'Rename') }}</button>
                      <button @click.stop="deleteFolder(folder.id)" class="btn btn-error btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.delete','Delete') }}</button>
                      </div>
                    </td>
                    <td></td>
                    <td>
                        <input type="checkbox" 
                               v-if="folder.fileType === 'text'" 
                               :checked="isCached(folder.name) !=false" 
                               @change.stop="toggleCacheFile(folder)" 
                               class="checkbox checkbox-primary checkbox-xs" />
                    </td>
                    <td class="text-center">-</td>
                    <td class="text-center">
                        <input type="checkbox"
                               v-model="folder.hiddenFromSources"
                               class="checkbox checkbox-secondary checkbox-xs"
                               @change.stop
                               :title="t('aiChatAgentSetup.knowledgeBase.hideFolderSourceTitle', 'Hide this folder from being cited as a source in chat responses')" />
                    </td>
                  </tr>
                   <tr v-for="file in paginatedFilteredExistingFiles"
                       :key="'existing-' + file.fileName"
                       class="hover"
                       draggable="true"
                       @dragstart="handleFileDragStart(file, 'existing', $event)"
                       @dragend="handleDragEnd">
                    <td>
                      <input type="checkbox" :checked="isRowSelected(file.fileName) === true" @change="toggleSelectRow(file)" />
                    </td>
                     <td @click="openInspectOrEditTextFile(file)" class="cursor-pointer hover:text-primary flex items-center" :title="decodeURIComponentSafe(file.fileName)">
                        <Icon icon="heroicons:document" class="w-5 h-5 mr-2 text-info" />
                       {{ truncateDisplayFileName(file.fileName) }}
                     </td>
                     <td>{{ getKnowledgeFileTypeLabel(file.fileType) }}</td>
                     <td>
                       <span v-if="file.status === 'deleted'" class="badge badge-error">To Delete</span>
                       <template v-else>
                         <span v-if="file.processingStatus === 'Available' || file.processingStatus === 'Completed'" class="badge badge-success">Available</span>
                         <span v-else-if="file.processingStatus === 'Processing'" class="badge badge-warning">Processing</span>
                         <span v-else-if="file.processingStatus === 'Failed' || file.processingStatus === 'Error'" class="badge badge-error">{{ file.processingStatus }}</span>
                         <span v-else class="badge">{{ file.processingStatus }}</span>
                       </template>
                     </td>
                     <td class="kb-actions-cell text-center">
                       <div class="inline-flex items-center justify-center whitespace-nowrap">
                       <button v-if="file.status !== 'deleted' && (file.processingStatus === 'Available' || file.processingStatus === 'Completed' || file.processingStatus === 'Failed' || file.processingStatus === 'Error')" @click="removeFileByName(file, 'existing')" class="btn btn-error btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.delete','Delete') }}</button>
                       <button v-else-if="file.status === 'deleted'" @click="existingFiles[existingFiles.findIndex(ef => ef.fileName === file.fileName)].status = 'existing'" class="btn btn-success btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.restore','Restore') }}</button>
                       <button @click="openMoveFileModal(file, 'existing')" class="btn btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.move','Move') }}</button>
                        <button v-if="['website','websiteurl','websitepage'].includes(file.fileType) === true" @click="openScrapeReport(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.reportButton','Report') }}</button>
                        <button v-if="shouldShowRescanWebsiteUrl(file) === true" @click="rescanWebsiteUrlForSetup(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.rescanButton','Rescan') }}</button>
                        <button v-if="shouldShowContinue(file) === true" @click="continueWebsiteScanForSetup(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.continueButton','Continue') }}</button>
                        <button v-if="shouldShowRetryFailed(file) === true" @click="retryFailedWebsiteScanForSetup(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.retryFailedButton','Retry Failed') }}</button>
                       </div>
                     </td>
                    <td class="min-w-48">
                      <div v-if="['website','websiteurl','websitepage'].includes(file.fileType) !== false" class="form-control w-full">
                        <div class="flex items-center justify-between">
                          <div dir="ltr" class="inline-flex items-center whitespace-nowrap">
                            <input type="number"
                                   v-model.number="file.rescrapeIntervalDays"
                                   class="input input-bordered input-xs w-14 mx-1 shrink-0"
                                   min="1"
                                   :readonly="file.rescrapeEnabled !== true"
                                   :class="file.rescrapeEnabled !== true ? 'pointer-events-none' : ''"
                                   :placeholder="t('aiChatAgentSetup.knowledgeBase.daysPlaceholder', 'Days')" />
                            <input type="time"
                                   v-model="file.rescrapeAtTime"
                                   step="1800"
                                   class="input input-bordered input-xs w-20 shrink-0"
                                   :readonly="file.rescrapeEnabled !== true"
                                   :class="file.rescrapeEnabled !== true ? 'pointer-events-none' : ''" />
                          </div>
                          <input
                                 type="checkbox"
                                 :checked="file.rescrapeEnabled === true"
                                 @click.stop
                                 @change.stop="file.rescrapeEnabled = $event.target.checked"
                                 class="toggle toggle-primary"
                                 :aria-label="t('aiChatAgentSetup.knowledgeBase.rescrapeLabel', 'Rescrape')" />
                        </div>
                      </div>
                    </td>
                    <td>
                        <input type="checkbox" 
                               v-if="file.fileType === 'text'" 
                               :checked="isCached(file.fileName) != false" 
                               @change.stop="toggleCacheFile(file)" 
                               class="checkbox checkbox-primary checkbox-xs" />
                     </td>
                     <td class="text-center">
                        <input type="checkbox"
                               v-if="isPromptKnowledgeBaseEligibleFile(file) === true"
                               :checked="isVoicePromptKnowledgeBaseFileSelected(file.fileName) === true"
                               @click.stop
                               @change.stop="toggleCachedKnowledgeDataFile(file.fileName)"
                               class="checkbox checkbox-accent checkbox-xs"
                               :title="t('aiChatAgentSetup.knowledgeBase.cachedKnowledgeDataTitle', 'Use this file as realtime cached prompt data for phone calls')" />
                     </td>
                     <td class="text-center">
                        <input type="checkbox"
                               v-model="file.hiddenFromSources"
                               class="checkbox checkbox-secondary checkbox-xs"
                               @change.stop
                               :title="t('aiChatAgentSetup.knowledgeBase.hideFileSourceTitle', 'Hide this file from being cited as a source in chat responses')" />
                     </td>
                   </tr>
                   <tr v-for="file in paginatedFilteredUploadedFiles"
                       :key="'new-' + file.fileName"
                       class="hover"
                       draggable="true"
                       @dragstart="handleFileDragStart(file, 'new', $event)"
                       @dragend="handleDragEnd">
                    <td>
                      <input type="checkbox" :checked="isRowSelected(file.fileName) === true" @change="toggleSelectRow(file)" />
                    </td>
                     <td @click="openInspectOrEditTextFile(file)" class="cursor-pointer hover:text-primary flex items-center" :title="decodeURIComponentSafe(file.fileName)">
                        <Icon icon="heroicons:document-arrow-up" class="w-5 h-5 mr-2 text-info" />
                       {{ truncateDisplayFileName(file.fileName) }}
                     </td>
                     <td>{{ getKnowledgeFileTypeLabel(file.fileType) }}</td>
                     <td>
                       <span v-if="file.isEditedVersion === true" class="badge badge-warning">Waiting To Update</span>
                       <span v-else class="badge badge-info">New</span>
                      </td>
                     <td class="kb-actions-cell text-center">
                       <div class="inline-flex items-center justify-center whitespace-nowrap">
                       <button @click="removeFileByName(file, 'new')" class="btn btn-error btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.delete','Delete') }}</button>
                       <button @click="openMoveFileModal(file, 'new')" class="btn btn-xs mx-1">{{ t('aiChatAgentSetup.buttons.move','Move') }}</button>
                       <button v-if="['website','websiteurl','websitepage'].includes(file.fileType) === true" @click="openScrapeReport(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.reportButton', 'Report') }}</button>
                       <button v-if="shouldShowRescanWebsiteUrl(file) === true" @click="rescanWebsiteUrlForSetup(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.rescanButton','Rescan') }}</button>
                       <button v-if="shouldShowContinue(file) === true" @click="continueWebsiteScanForSetup(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.continueButton','Continue') }}</button>
                       <button v-if="shouldShowRetryFailed(file) === true" @click="retryFailedWebsiteScanForSetup(file)" class="btn btn-xs mx-1">{{ t('aiChats.knowledgeStatusModal.retryFailedButton','Retry Failed') }}</button>
                       </div>
                     </td>
                    <td class="min-w-48">
                      <div v-if="['website','websiteurl','websitepage'].includes(file.fileType) !== false" class="form-control w-full">
                        <div class="flex items-center justify-between">
                          <div dir="ltr" class="inline-flex items-center whitespace-nowrap">
                            <input type="number"
                                   v-model.number="file.rescrapeIntervalDays"
                                   class="input input-bordered input-xs w-14 mx-1 shrink-0"
                                   min="1"
                                   :readonly="file.rescrapeEnabled !== true"
                                   :class="file.rescrapeEnabled !== true ? 'pointer-events-none' : ''"
                                   :placeholder="t('aiChatAgentSetup.knowledgeBase.daysPlaceholder', 'Days')" />
                            <input type="time"
                                   v-model="file.rescrapeAtTime"
                                   step="1800"
                                   class="input input-bordered input-xs w-20 shrink-0"
                                   :readonly="file.rescrapeEnabled !== true"
                                   :class="file.rescrapeEnabled !== true ? 'pointer-events-none' : ''" />
                          </div>
                          <input
                                 type="checkbox"
                                 :checked="file.rescrapeEnabled === true"
                                 @click.stop
                                 @change.stop="file.rescrapeEnabled = $event.target.checked"
                                 class="toggle toggle-primary"
                                 :aria-label="t('aiChatAgentSetup.knowledgeBase.rescrapeLabel', 'Rescrape')" />
                        </div>
                      </div>
                    </td>
                    <td>
                        <input type="checkbox" 
                               v-if="file.fileType === 'text'" 
                               :checked="isCached(file.fileName) !=false" 
                               @change.stop="toggleCacheFile(file)" 
                               class="checkbox checkbox-primary checkbox-xs" />
                     </td>
                     <td class="text-center">
                       <input type="checkbox"
                              v-if="isPromptKnowledgeBaseEligibleFile(file) === true"
                              :checked="isVoicePromptKnowledgeBaseFileSelected(file.fileName) === true"
                              @click.stop
                              @change.stop="toggleCachedKnowledgeDataFile(file.fileName)"
                              class="checkbox checkbox-accent checkbox-xs"
                              :title="t('aiChatAgentSetup.knowledgeBase.cachedKnowledgeDataTitle', 'Use this file as realtime cached prompt data for phone calls')" />
                     </td>
                     <td class="text-center">
                       <input type="checkbox"
                              v-model="file.hiddenFromSources"
                              class="checkbox checkbox-secondary checkbox-xs"
                              @change.stop
                             :title="t('aiChatAgentSetup.knowledgeBase.hideFileSourceTitle', 'Hide this file from being cited as a source in chat responses')" />
                     </td>
                   </tr>
                <tr v-if="paginatedFoldersInCurrentView.length === 0 && paginatedFilteredExistingFiles.length === 0 && paginatedFilteredUploadedFiles.length === 0 && (currentFolderId !== null || (linkedSharedKbItems.length === 0 && linkedAgentKbItems.length === 0))">
                    <td colspan="9" class="text-center text-gray-500 py-4">
                         {{ t('aiChatAgentSetup.knowledgeBase.emptyFolder', 'This folder is empty. Use search to find items, or add new knowledge.') }}
                     </td>
                 </tr>
               </tbody>
            </table>
          </div>
        </div>
  
   
         <div v-else-if="kbViewMode === 'grid'">
           <div class="relative">
              <div v-if="isRefreshingKB !== undefined ? isRefreshingKB : false" class="absolute inset-0 bg-base-100 bg-opacity-75 flex items-center justify-center z-10 rounded-box" style="min-height: 200px;">
                  <span class="loading loading-spinner loading-lg"></span>
              </div>
             <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 space-x-4 p-4">
              
              <div v-for="folder in paginatedFoldersInCurrentView"
                   :key="folder.id"
                   class="flex flex-col items-center justify-start p-3 rounded-lg border border-base-300 cursor-pointer hover:bg-base-200 dark:hover:bg-gray-700 transition-colors h-32"
                   :class="{ 'bg-blue-100 dark:bg-blue-800 border-blue-500 dark:border-blue-600 ring-2 ring-blue-400': isDragOverFolder === folder.id }"
                   @click="navigateToFolder(folder)"
                   @dragover.prevent="handleFolderDragOver($event, folder.id)"
                   @dragleave.prevent="handleFolderDragLeave($event, folder.id)"
                   @drop.prevent="handleFileDropOnFolder(folder.id)">
                   <Icon icon="heroicons:folder" class="w-12 h-12 text-warning mb-2"/>
                   <span class="text-sm text-center break-all" :title="folder.name">
                       {{ truncateDisplayFileName(folder.name, 25) }}
                   </span>
              </div>
  
               
               <div v-for="file in paginatedFilteredExistingFiles"
                    :key="'grid-existing-' + file.fileName"
                    class="flex flex-col items-center justify-start p-3 rounded-lg border border-base-300 cursor-pointer hover:bg-base-200 dark:hover:bg-gray-700 transition-colors h-32 relative"
                    draggable="true"
                    @dragstart="handleFileDragStart(file, 'existing', $event)"
                    @dragend="handleDragEnd"
                    @click="openInspectOrEditTextFile(file)"
                    :title="decodeURIComponentSafe(file.fileName) + ` (Status: ${file.status === 'deleted' ? 'To Delete' : file.processingStatus})`">
                   <Icon :icon="file.fileType === 'pdf' ? 'heroicons:document-text' : file.fileType === 'docx' ? 'simple-icons:microsoftword' : file.fileType === 'xlsx' ? 'simple-icons:microsoftexcel' : file.fileType === 'text' ? 'heroicons:document-text' : file.fileType === 'websiteurl' ? 'heroicons:link' : file.fileType === 'website' ? 'heroicons:globe-alt' : 'heroicons:document'"
                         class="w-12 h-12 mb-2"
                         :class="{
                             'text-error opacity-50': file.status === 'deleted',
                             'text-success': file.status !== 'deleted' && (file.processingStatus === 'Available' || file.processingStatus === 'Completed'),
                             'text-warning': file.status !== 'deleted' && file.processingStatus === 'Processing',
                             'text-error': file.status !== 'deleted' && (file.processingStatus === 'Failed' || file.processingStatus === 'Error'),
                             'text-secondary': file.status !== 'deleted' && (['Available', 'Completed', 'Processing', 'Failed', 'Error'].includes(file.processingStatus) === true)
                         }"/>
                   <span class="text-sm text-center break-all">
                       {{ truncateDisplayFileName(file.fileName, 25) }}
                   </span>
                   <span v-if="file.status === 'deleted'" class="badge badge-xs badge-error mt-1">To Delete</span>
                    <span v-else-if="file.processingStatus && (['Available', 'Completed'].includes(file.processingStatus) === true)"
                         class="badge badge-xs mt-1"
                         :class="{
                           'badge-warning': file.processingStatus === 'Processing',
                           'badge-error': file.processingStatus === 'Failed' || file.processingStatus === 'Error'
                         }">
                      {{ file.processingStatus }}
                   </span>
                    <input type="checkbox" 
                           v-if="file.fileType === 'text'" 
                           :checked="isCached(file.fileName)!=false" 
                           @click.stop
                           @change.stop="toggleCacheFile(file)" 
                           class="checkbox checkbox-primary checkbox-xs absolute top-1 right-1" 
                           :title="t('aiChatAgentSetup.knowledgeBase.cacheCheckboxTitle', 'Cache this file')"/>
                    <input type="checkbox"
                           v-if="isPromptKnowledgeBaseEligibleFile(file) === true"
                           :checked="isVoicePromptKnowledgeBaseFileSelected(file.fileName) === true"
                           @click.stop
                           @change.stop="toggleCachedKnowledgeDataFile(file.fileName)"
                           class="checkbox checkbox-accent checkbox-xs absolute bottom-1 right-1"
                           :title="t('aiChatAgentSetup.knowledgeBase.cachedKnowledgeDataTitle', 'Use this file as realtime cached prompt data for phone calls')"/>
                    <input type="checkbox"
                           v-model="file.hiddenFromSources"
                           @click.stop
                           class="checkbox checkbox-secondary checkbox-xs absolute top-1 left-1"
                           :title="t('aiChatAgentSetup.knowledgeBase.hideFileSourceTitle', 'Hide this file from being cited as a source in chat responses')"/>
              </div>
  
            
               <div v-for="file in paginatedFilteredUploadedFiles"
                    :key="'grid-new-' + file.fileName"
                    class="flex flex-col items-center justify-start p-3 rounded-lg border border-base-300 cursor-pointer hover:bg-base-200 dark:hover:bg-gray-700 transition-colors h-32 relative"
                    draggable="true"
                    @dragstart="handleFileDragStart(file, 'new', $event)"
                    @dragend="handleDragEnd"
                    @click="openInspectOrEditTextFile(file)"
                    :title="decodeURIComponentSafe(file.fileName) + ' (Status: New)'">
                   <Icon :icon="file.fileType === 'pdf' ? 'heroicons:document-text' : file.fileType === 'docx' ? 'simple-icons:microsoftword' : file.fileType === 'xlsx' ? 'simple-icons:microsoftexcel' : file.fileType === 'text' ? 'heroicons:document-text' : file.fileType === 'websiteurl' ? 'heroicons:link' : file.fileType === 'website' ? 'heroicons:globe-alt' : 'heroicons:document-arrow-up'"
                         class="w-12 h-12 text-info mb-2"/>
                   <span class="text-sm text-center break-all">
                       {{ truncateDisplayFileName(file.fileName, 25) }}
                   </span>
                   <span v-if="file.isEditedVersion === true" class="badge badge-xs badge-warning mt-1">Waiting To Update</span>
                   <span v-else class="badge badge-xs badge-info mt-1">New</span>
                    <input type="checkbox" 
                           v-if="file.fileType === 'text'" 
                           :checked="isCached(file.fileName)!=false" 
                           @click.stop
                           @change.stop="toggleCacheFile(file)" 
                           class="checkbox checkbox-primary checkbox-xs absolute top-1 right-1" 
                           :title="t('aiChatAgentSetup.knowledgeBase.cacheCheckboxTitle', 'Cache this file')"/>
                    <input type="checkbox"
                           v-if="isPromptKnowledgeBaseEligibleFile(file) === true"
                           :checked="isVoicePromptKnowledgeBaseFileSelected(file.fileName) === true"
                           @click.stop
                           @change.stop="toggleCachedKnowledgeDataFile(file.fileName)"
                           class="checkbox checkbox-accent checkbox-xs absolute bottom-1 right-1"
                           :title="t('aiChatAgentSetup.knowledgeBase.cachedKnowledgeDataTitle', 'Use this file as realtime cached prompt data for phone calls')"/>
                    <input type="checkbox"
                           v-model="file.hiddenFromSources"
                           @click.stop
                           class="checkbox checkbox-secondary checkbox-xs absolute top-1 left-1"
                           :title="t('aiChatAgentSetup.knowledgeBase.hideFileSourceTitle', 'Hide this file from being cited as a source in chat responses')"/>
              </div>
  
             <div v-if="paginatedFoldersInCurrentView.length === 0 && paginatedFilteredExistingFiles.length === 0 && paginatedFilteredUploadedFiles.length === 0"
                  class="col-span-full text-center text-gray-500 py-8">
                {{ t('aiChatAgentSetup.knowledgeBase.emptyFolderGrid', 'This folder is empty. Use search to find items, or add new knowledge.') }}
             </div>
           </div>
          </div>
        </div>
  
  
        <div class="flex justify-between items-center mt-4">
          <button class="btn btn-sm" :disabled="currentKbPage <= 1" @click="currentKbPage = currentKbPage - 1"><Icon :icon="isRtl === true ? 'tabler:chevron-right' : 'tabler:chevron-left'" class="w-4 h-4" /></button>
          <span :style="getDirStyle('kbPagination')">{{ currentKbPage }}/{{ totalKbPages }}</span>
          <button class="btn btn-sm" :disabled="currentKbPage >= totalKbPages" @click="currentKbPage = currentKbPage + 1"><Icon :icon="isRtl === true ? 'tabler:chevron-left' : 'tabler:chevron-right'" class="w-4 h-4" /></button>
        </div>
        <div class="space-y-2" style="min-height: 100px;"></div>
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(3, -1)" class="btn">
            <span v-if="isNavigating === 2" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
            </button>
            <button @click="goNextStep(3, 1)" class="btn">
              <span v-if="isNavigating === 4" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
            </button>
          </div>
        </div>
      </div>
      <template v-if="isEditMode === true">
         <input type="radio" data-currentPage="4" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.fineTuneQA', 'Fine Tune Q&A')" />
        <div role="tabpanel" data-currentPage="4" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
          <div class="flex justify-between" id="tabTitle">
            <h1 class="text-2xl" style="margin-bottom: 0%;" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.fineTuneQA.title', 'Fine Tune Q&A') }}</h1>
          </div>
          <FineTuneAgent
            :selected-method="selectedFineTuneMethod"
            :show-content="false"
            :setup-type="viewMode"
            @update:selected-method="selectedFineTuneMethod = $event"
          />
          <div v-if="selectedFineTuneMethod === 'answers'" role="tablist" class="tabs tabs-lifted mt-4">
            <input 
              type="radio" 
              name="fine_tune_tabs" 
              role="tab" 
              class="tab" 
              :aria-label="t('aiChatAgentSetup.fineTuneQA.fineTuneTab', 'Fine Tune')" 
              :checked="activeFineTuneTab === 'fineTune'" 
              @click="activeFineTuneTab = 'fineTune'"
            />
            <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
              <div class="mt-4">
                <FineTuneAgent
                  :agentID="route.query.edit"
                  :selected-method="selectedFineTuneMethod"
                  :show-method-selector="false"
                  :setup-type="viewMode"
                  @save-fine-tune="handleFineTuneSave"
                  @learn-question-answers="handleLearnQuestionAnswers"
                  @approve-generated-rules="approveGeneratedRulesFromAdvanced"
                />
              </div>
            </div>
            <input 
              type="radio" 
              name="fine_tune_tabs" 
              role="tab" 
              class="tab" 
              :aria-label="t('aiChatAgentSetup.fineTuneQA.fineTunedAnswersTab', 'Fine Tuned Answers')" 
              :checked="activeFineTuneTab === 'fineTunedAnswers'" 
              @click="activeFineTuneTab = 'fineTunedAnswers'"
            />
            <div role="tabpanel" class="tab-content bg-base-100 border-base-300 rounded-box p-6">
              <h2 class="text-xl mb-4">{{ t('aiChatAgentSetup.fineTuneQA.existingAnswersTitle', 'Existing Fine-Tuned Answers (Assigned to Folders)') }}</h2>
              <p class="text-sm text-gray-500 mb-2">
                {{ t('aiChatAgentSetup.fineTuneQA.existingAnswersDescription', 'Fine-tuned answers are treated as files and can be organized into folders. They are primarily managed here but their folder assignment happens in the Knowledge Base tab.') }}
              </p>
              <div class="overflow-x-auto">
                <table class="table w-full">
                  <thead>
                    <tr>
                      <th>{{ t('aiChatAgentSetup.fineTuneQA.tableHeaders.row', 'Row #') }}</th>
                      <th>{{ t('aiChatAgentSetup.fineTuneQA.tableHeaders.originalQuestion', 'Original Question (Preview)') }}</th>
                      <th>{{ t('aiChatAgentSetup.fineTuneQA.tableHeaders.fineTunedAnswer', 'Fine-Tuned Answer (Preview)') }}</th>
                      <th>{{ t('aiChatAgentSetup.fineTuneQA.tableHeaders.folder', 'Folder') }}</th>
                      <th>{{ t('aiChatAgentSetup.fineTuneQA.tableHeaders.actions', 'Actions') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(answerFile, index) in fineTunedAnswers" :key="'finedtuned-' + index">
                      <td>{{ index + 1 }}</td>
                      <td>{{ answerFile.originalQuery }}</td>
                      <td>{{ answerFile.fineTunedAnswer }}</td>
                      <td>
                        {{ knowledgeBaseFileAssignments[answerFile.fileName] ? (knowledgeBaseFolders.find(f => f.id === knowledgeBaseFileAssignments[answerFile.fileName])?.name || t('aiChatAgentSetup.fineTuneQA.unknownFolder', 'Unknown Folder')) : t('aiChatAgentSetup.fineTuneQA.rootFolder', 'Knowledge Base Root') }}
                      </td>
                      <td>
                        <button @click="removeFineTunedAnswer(index)" class="btn btn-error btn-sm">{{ t('aiChatAgentSetup.fineTuneQA.actions.remove', 'Remove') }}</button>
                      </td>
                    </tr>
                    <tr v-if="fineTunedAnswers.length === 0">
                      <td colspan="5" class="text-center">{{ t('aiChatAgentSetup.fineTuneQA.noAnswers', 'No fine-tuned answers yet.') }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div v-else class="mt-4">
            <FineTuneAgent
              :agentID="route.query.edit"
              :selected-method="selectedFineTuneMethod"
              :show-method-selector="false"
              :setup-type="viewMode"
              @save-fine-tune="handleFineTuneSave"
              @learn-question-answers="handleLearnQuestionAnswers"
              @approve-generated-rules="approveGeneratedRulesFromAdvanced"
            />
          </div>
          <div class="flex justify-between mt-4" style="position: relative;">
            <button @click="goNextStep(4, -1)" class="btn">
              <span v-if="isNavigating === 3" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
            </button>
            <div class="flex items-center justify-evenly">
              <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
                  <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
               </button>
              <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
                  <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
                  {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
               </button>
              <button @click="goNextStep(4, 1)" class="btn">
                <span v-if="isNavigating === 5" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
              </button>
            </div>
          </div>
        </div>
      </template>
       <input type="radio" data-currentPage="5" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.rules', 'Rules')" />
      <div role="tabpanel" data-currentPage="5" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <div class="flex justify-between" id="tabTitle">
          <h1 class="text-2xl mb-0" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.rules.title', 'Agent Rules') }}</h1>
        </div>
        <div class="mt-4">
          <div class="mb-4">
            <RuleLibraryPanel :is-rtl="isRtl" :existing-rules="rules" :selected-language="agentLanguage" :agent-description="agentDescription" @add-rule="addRuleFromLibrary" />
          </div>
          <div class="flex space-x-2 mb-4">
            <textarea v-model="newRule" maxlength="3500" rows="1" class="textarea textarea-bordered flex-1 resize-none overflow-hidden" :placeholder="t('aiChatAgentSetup.rules.newRulePlaceholder','Enter a new rule')" :style="getDirStyle('newRule')" @keydown="handleDirKeydown($event, 'newRule')" @input="($event.target.style.height='auto',$event.target.style.height=$event.target.scrollHeight+'px')" />
            <div class="text-xs text-right pr-1 mt-1 self-center text-gray-500">{{ newRule.length }}/3500</div>
            <button @click="addRule" class="btn">{{ t('aiChatAgentSetup.rules.addRuleButton','Add Rule') }}</button>
          </div>
          <div class="space-y-2" style="min-height: 300px;">
            <div v-for="(rule, index) in rules" :key="index" class="flex items-center space-x-2 p-2 bg-base-200 rounded-lg">
              <span v-if="editingRuleIndex !== index" class="flex-1" @click="startEditingRule(index)">{{ rule }}</span>
              <textarea v-else ref="editingRuleInput" v-model="rules[index]" maxlength="3500" rows="1" class="textarea textarea-bordered flex-1 resize-none overflow-hidden" :style="getDirStyle('editingRule')" @keydown="handleDirKeydown($event, 'editingRule')" @input="($event.target.style.height='auto',$event.target.style.height=$event.target.scrollHeight+'px')" @blur="stopEditingRule" @keyup.enter="stopEditingRule" />
              <button @click="removeRule(index)" class="btn btn-sm btn-error">{{ t('aiChatAgentSetup.rules.removeRuleButton','Remove') }}</button>
            </div>
          </div>
        </div>
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(5, -1)" class="btn">
            <span v-if="isNavigating === 4" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
            </button>
            <button @click="goNextStep(5, 1)" class="btn">
              <span v-if="isNavigating === 6" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
            </button>
          </div>
        </div>
      </div>
      <template v-if="agentMode !== 'Flow' && agentMode !== 'FlowBased'">
        <input type="radio" data-currentPage="7" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.toolsApi', 'Tools/API')" />
        <div role="tabpanel" data-currentPage="7" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
          <div class="flex justify-between" id="tabTitle">
            <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }" style="margin-bottom: 0%;">{{t('aiChatAgentSetup.toolsApi.title', 'Tools & API Configuration')}}</h1>
          </div>
          <div class="mt-4">
            <div class="mb-4">
              <div class="flex justify-between items-center mb-2">
                <p class="label cursor-pointer" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.toolsApi.availableToolsLabel', 'Available Tools:') }}</p>
                <div class="flex space-x-2">
                  <button @click="refreshTools" class="btn btn-sm btn-info">{{ t('aiChatAgentSetup.toolsApi.refreshButton', 'Refresh') }}</button>
                  <router-link to="/aiAgentTools" class="btn btn-sm">{{ t('aiChatAgentSetup.toolsApi.manageToolsButton', 'Manage Tools') }}</router-link>
                </div>
              </div>
              <div class="flex flex-wrap items-center gap-2 mb-3">
                <label class="input input-bordered flex items-center gap-2 flex-1 min-w-[220px]">
                  <Icon icon="tabler:search" class="w-4 h-4 opacity-70" />
                  <input type="text" v-model="toolsSearchFilter" class="grow" :placeholder="t('common.search', 'Search')" />
                </label>
                <div class="min-w-[240px] flex-1">
                  <CustomDropdownSelector
                    id="toolsCollectionSelect"
                    v-model="toolsCollectionFilter"
                    class="w-full"
                    buttonClass="!h-[3rem] !min-h-[3rem]"
                    :placeholder="'All collections'"
                    :options="toolsCollectionOptions"
                    :toneLike="true"
                  />
                </div>
              </div>
              <div class="mb-4">
                <div class="flex space-x-2">
                  <CustomDropdownSelector id="availableToolsSelect" v-model="selectedTool" class="w-full" buttonClass="!h-[3rem] !min-h-[3rem]" :placeholder="t('aiChatAgentSetup.toolsApi.selectToolPlaceholder', 'Select a tool to add')" :options="availableToolOptions" :toneLike="true" />
                  <button @click="addSelectedTool" class="btn">{{ t('aiChatAgentSetup.toolsApi.addToolButton','Add Tool') }}</button>
                </div>
              </div>
              <div class="mb-2">
                <span class="text-sm text-gray-500" :style="{ direction: 'ltr', textAlign: '' }">{{ t('aiChatAgentSetup.toolsApi.selectedToolsCount', { count: selectedToolIds.length }, selectedToolIds.length + ' Selected Tools') }}</span>
              </div>
              <div class="space-y-4">
                <div v-for="(tool, index) in tools" :key="isDefinedAndNotEmpty(tool.toolid) != false ? tool.toolid : (isDefinedAndNotEmpty(tool.toolId) != false ? tool.toolId : (isDefinedAndNotEmpty(tool.id) != false ? tool.id : index))" class="card bg-base-200">
                  <div class="card-body">
                    <div class="flex justify-between items-start">
                      <div class="flex-1">
                        <h3 class="card-title font-bold" :style="{ direction: 'rtl', textAlign: 'right' }">
                          <span class="inline-flex items-center justify-center w-6 h-6 rounded-md bg-base-300" :class="isRtl === true ? 'ml-2' : 'mr-2'">
                            <img :src="getToolDisplayLogo(tool)" alt="" class="w-4 h-4 object-contain" />
                          </span>
                          {{ t('aiChatAgentSetup.toolsApi.functionNameLabel', 'Function Name:') }} {{ getToolDisplayLabel(tool) }}
                        </h3>
                        <div class="mt-2">
                          <div class="space-y-2">
                            <div class="text-sm">
                              <span class="font-semibold" :style="{ direction: 'rtl', textAlign: 'right' }">{{ t('aiChatAgentSetup.toolsApi.descriptionLabel', 'Description:') }}</span>
                              <p class="text-gray-600" :style="{ direction: 'rtl', textAlign: 'right' }">{{ getToolDisplayDescription(tool) }}</p>
                            </div>
                            <div class="mt-3 rounded-xl border border-base-300 bg-base-100 p-4">
                              <label class="label cursor-pointer justify-start">
                                <input
                                  type="checkbox"
                                  class="checkbox"
                                  :checked="getToolSettingsEntry(tool).waitForResponse !== false"
                                  @change="updateToolWaitForResponse(tool, $event.target.checked)"
                                />
                                <span class="label-text ml-3">{{ t('aiChatAgentSetup.toolsApi.waitForToolResponseLabel', 'Wait for tool response before continuing') }}</span>
                              </label>
                              <p class="text-xs text-base-content/60">{{ t('aiChatAgentSetup.toolsApi.waitForToolResponseHint', 'Turn this off for actions such as sending WhatsApp or SMS when the agent should continue immediately without waiting for the provider response.') }}</p>
                            </div>
                            <div v-if="(tool.tooldata !== undefined && tool.tooldata !== null && tool.tooldata.requiredParams !== undefined && tool.tooldata.requiredParams !== null && tool.tooldata.requiredParams.length > 0) || (tool.toolData !== undefined && tool.toolData !== null && tool.toolData.requiredParams !== undefined && tool.toolData.requiredParams !== null && tool.toolData.requiredParams.length > 0)" class="text-sm">
                              <span class="font-semibold" :style="{ direction: 'rtl', textAlign: 'right' }">{{ t('aiChatAgentSetup.toolsApi.parametersLabel', 'Parameters:') }}</span>
                              <ul class="list-disc list-inside text-gray-600" :style="{ direction: 'rtl', textAlign: 'right' }">
                                <li v-for="(param, paramIndex) in (isDefinedAndNotEmpty(tool.tooldata) != false && Array.isArray(tool.tooldata.requiredParams) === true ? tool.tooldata.requiredParams : (isDefinedAndNotEmpty(tool.toolData) != false && Array.isArray(tool.toolData.requiredParams) === true ? tool.toolData.requiredParams : []))" :key="paramIndex" :style="{ direction: 'rtl', textAlign: 'right' }">
                                  {{ param }}
                                </li>
                              </ul>
                            </div>
                            <div v-if="isSmsTool(tool) === true" class="mt-4 rounded-xl border border-base-300 bg-base-100 p-4">
                              <div class="flex items-center justify-between">
                                <span class="font-semibold">{{ t('aiChatAgentSetup.toolsApi.smsSettingsTitle', 'SMS Settings') }}</span>
                                <router-link to="/communicationSources" class="btn btn-xs">{{ t('aiChatAgentSetup.toolsApi.manageSmsSources', 'Manage SMS Sources') }}</router-link>
                              </div>
                              <div class="mt-3">
                                <label class="label"><span class="label-text">{{ t('aiChatAgentSetup.toolsApi.smsSourceLabel', 'SMS Source Number') }}</span></label>
                                <select
                                  class="select select-bordered w-full"
                                  :value="getToolSettingsEntry(tool).sourceId"
                                  :disabled="loadingSmsSources === true"
                                  @change="updateSmsToolSource(tool, $event.target.value)"
                                >
                                  <option value="">{{ t('aiChatAgentSetup.toolsApi.smsSourcePlaceholder', 'Select an SMS source') }}</option>
                                  <option v-for="sourceOption in smsSources" :key="sourceOption.sourceId" :value="String(sourceOption.sourceId)">
                                    {{ sourceOption.display }}{{ sourceOption.phoneNumber !== '' ? ` (${sourceOption.phoneNumber})` : '' }}
                                  </option>
                                </select>
                                <div v-if="loadingSmsSources === true" class="text-xs text-gray-500 mt-2">{{ t('aiChatAgentSetup.toolsApi.smsLoadingSources', 'Loading SMS sources...') }}</div>
                                <div v-else-if="smsSources.length === 0" class="text-xs text-gray-500 mt-2">{{ t('aiChatAgentSetup.toolsApi.smsNoSources', 'No SMS sources found.') }}</div>
                              </div>
                            </div>
                            <div v-if="isEmailTool(tool) === true" class="mt-4 rounded-xl border border-base-300 bg-base-100 p-4">
                              <div class="flex items-center justify-between">
                                <span class="font-semibold">{{ t('aiChatAgentSetup.toolsApi.emailSettingsTitle', 'Email Settings') }}</span>
                                <router-link to="/communicationSources" class="btn btn-xs">{{ t('aiChatAgentSetup.toolsApi.manageEmailSources', 'Manage Email Sources') }}</router-link>
                              </div>
                              <div class="mt-3">
                                <label class="label"><span class="label-text">{{ t('aiChatAgentSetup.toolsApi.emailSourceLabel', 'Email Source') }}</span></label>
                                <select
                                  class="select select-bordered w-full"
                                  :value="getToolSettingsEntry(tool).sourceId"
                                  :disabled="loadingEmailSources === true"
                                  @change="updateEmailToolSource(tool, $event.target.value)"
                                >
                                  <option value="">{{ t('aiChatAgentSetup.toolsApi.emailSourcePlaceholder', 'Select an email source') }}</option>
                                  <option v-for="sourceOption in emailSources" :key="sourceOption.sourceId" :value="String(sourceOption.sourceId)">
                                    {{ sourceOption.display }}{{ sourceOption.address !== '' ? ` (${sourceOption.address})` : '' }}
                                  </option>
                                </select>
                                <div v-if="loadingEmailSources === true" class="text-xs text-gray-500 mt-2">{{ t('aiChatAgentSetup.toolsApi.emailLoadingSources', 'Loading email sources...') }}</div>
                                <div v-else-if="emailSources.length === 0" class="text-xs text-gray-500 mt-2">{{ t('aiChatAgentSetup.toolsApi.emailNoSources', 'No email sources found.') }}</div>
                              </div>
                            </div>
                            <div v-if="isWhatsappTemplateTool(tool) === true" class="mt-4 rounded-xl border border-base-300 bg-base-100 p-4">
                              <div class="flex items-center justify-between">
                                <span class="font-semibold">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateSettingsTitle', 'WhatsApp Template Settings') }}</span>
                                <router-link to="/whatsappTemplates" class="btn btn-xs">{{ t('aiChatAgentSetup.toolsApi.manageWhatsappTemplates', 'Manage Templates') }}</router-link>
                              </div>
                              <div class="mt-3">
                                <label class="label"><span class="label-text">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateSourceLabel', 'Meta WhatsApp Number') }}</span></label>
                                <select
                                  class="select select-bordered w-full"
                                  :value="getToolSettingsEntry(tool).sourceId"
                                  :disabled="loadingWhatsappTemplateSources === true"
                                  @change="updateWhatsappTemplateToolSource(tool, $event.target.value)"
                                >
                                  <option value="">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateSourcePlaceholder', 'Select a Meta WhatsApp number') }}</option>
                                  <option v-for="sourceOption in whatsappTemplateSources" :key="sourceOption.sourceId" :value="String(sourceOption.sourceId)">
                                    {{ sourceOption.display }}{{ sourceOption.phoneNumber !== '' ? ` (${sourceOption.phoneNumber})` : '' }}
                                  </option>
                                </select>
                                <div v-if="loadingWhatsappTemplateSources === true" class="text-xs text-gray-500 mt-2">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateLoadingSources', 'Loading Meta WhatsApp numbers...') }}</div>
                                <div v-else-if="whatsappTemplateSources.length === 0" class="text-xs text-gray-500 mt-2">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateNoSources', 'No Meta WhatsApp sources found.') }}</div>
                              </div>
                              <div class="mt-3">
                                <label class="label"><span class="label-text">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateTemplateLabel', 'Template') }}</span></label>
                                <select
                                  class="select select-bordered w-full"
                                  :value="getToolSettingsEntry(tool).templateId"
                                  :disabled="getToolSettingsEntry(tool).sourceId === '' || isWhatsappTemplateTemplatesLoading(getToolSettingsEntry(tool).sourceId) === true"
                                  @change="updateWhatsappTemplateToolTemplate(tool, $event.target.value)"
                                >
                                  <option value="">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateTemplatePlaceholder', 'Select a template') }}</option>
                                  <option v-for="templateOption in getWhatsappTemplateOptions(tool)" :key="templateOption.templateId" :value="String(templateOption.templateId)">
                                    {{ templateOption.templateName }} [{{ templateOption.status }}]
                                  </option>
                                </select>
                                <div v-if="isWhatsappTemplateTemplatesLoading(getToolSettingsEntry(tool).sourceId) === true" class="text-xs text-gray-500 mt-2">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateLoadingTemplates', 'Loading templates...') }}</div>
                                <div v-else-if="getToolSettingsEntry(tool).sourceId !== '' && getWhatsappTemplateOptions(tool).length === 0" class="text-xs text-gray-500 mt-2">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateNoTemplates', 'No templates found for this Meta number.') }}</div>
                              </div>
                              <div v-if="getToolSettingsEntry(tool).templateId !== '' && getSelectedWhatsappTemplateStatus(tool) !== ''" class="text-xs text-gray-500 mt-2">
                                {{ t('aiChatAgentSetup.toolsApi.whatsappTemplateStatusLabel', 'Selected template status:') }} {{ getSelectedWhatsappTemplateStatus(tool) }}
                              </div>
                              <div v-if="getSelectedWhatsappTemplateRecord(tool) !== null" class="mt-4 rounded-box border border-base-300 bg-base-200 p-4">
                                <div class="text-sm font-semibold">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplatePreviewTitle', 'Selected message') }}</div>
                                <p class="mt-2 whitespace-pre-wrap text-sm text-base-content/80">{{ getSelectedWhatsappTemplateBody(tool) }}</p>
                              </div>
                              <div v-if="getSelectedWhatsappTemplateRecord(tool) !== null" class="mt-4">
                                <div class="text-sm font-semibold">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateVariablesTitle', 'Template variables') }}</div>
                                <p class="mt-1 text-xs text-base-content/60">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateVariablesHint', 'Describe what the AI should place in each variable. The variable ID and your description are added to the tool the AI receives.') }}</p>
                                <div v-if="getWhatsappTemplateVariableDefinitions(tool).length === 0" class="mt-3 text-sm text-base-content/60">
                                  {{ t('aiChatAgentSetup.toolsApi.whatsappTemplateNoVariables', 'This template has no body variables.') }}
                                </div>
                                <div v-for="variable in getWhatsappTemplateVariableDefinitions(tool)" :key="variable.id" class="mt-3 rounded-box border border-base-300 bg-base-100 p-4">
                                  <div class="flex items-center justify-between">
                                    <span class="badge">{{ variable.token }}</span>
                                    <span v-if="variable.example !== ''" class="text-xs text-base-content/60">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateVariableExample', 'Example') }}: {{ variable.example }}</span>
                                  </div>
                                  <label class="label mt-2">
                                    <span class="label-text">{{ t('aiChatAgentSetup.toolsApi.whatsappTemplateVariableDescription', 'What should the AI put in this variable?') }}</span>
                                  </label>
                                  <input
                                    type="text"
                                    class="input input-bordered w-full"
                                    :value="variable.description"
                                    :placeholder="getWhatsappTemplateVariablePlaceholder(variable)"
                                    @input="updateWhatsappTemplateVariableDescription(tool, variable.id, $event.target.value)"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <button @click="removeTool(index)" class="btn btn-sm btn-error ml-4">{{ t('aiChatAgentSetup.toolsApi.removeButton','Remove') }}</button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="space-y-2" style="min-height: 300px;"></div>
            </div>
          </div>
          <div class="flex justify-between mt-4" style="position: relative;">
            <button @click="goNextStep(7, -1)" class="btn">
              <span v-if="isNavigating === 6" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
            </button>
            <div class="flex items-center justify-evenly">
              <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
                <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
              </button>
              <button @click="createAgent(true)" class="btn btn-accent">
                <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
                {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
              </button>
            </div>
          </div>
        </div>
      </template>
  
      <input type="radio" data-currentPage="8" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.analysis', 'Analysis')" />
      <div role="tabpanel" data-currentPage="8" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <div class="flex justify-between items-center mb-6" id="tabTitle">
          <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{t('aiChatAgentSetup.analysis.title', 'Analysis Configuration')}}</h1>
        </div>
        <div>
          <p class="label cursor-pointer text-lg font-medium" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.analysis.selectTagCollectionsLabel', 'Select Tag Collections') }}</p>
          <p class="text-sm text-gray-500 mb-4" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.analysis.selectTagCollectionsDescription', 'Select relevant tag collections to be used for analysis. Only collections linked to the agent\'s selected divisions are shown.') }}</p>
          
          <div v-if="selectedDivisionIds.length === 0" class="text-center text-gray-500 p-6 border border-dashed rounded-md bg-base-200" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
            {{ t('aiChatAgentSetup.analysis.noDivisionsSelected', 'Please select at least one division in the \'Agent Information\' tab to see available tag collections.') }}
          </div>
          <div v-else-if="isLoadingCollections === true" class="text-center py-6">
            <span class="loading loading-spinner loading-lg"></span>
          </div>
           <div v-else-if="availableCollectionsForAgent.length === 0 && selectedDivisionIds.length > 0" class="text-center text-gray-500 p-6 border border-dashed rounded-md bg-base-200" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
             {{ t('aiChatAgentSetup.analysis.noCollectionsFound', 'No tag collections found linked to the selected division(s).') }}
          </div>
          <div v-else class="border rounded-md p-4 bg-base-200 dark:bg-gray-700 max-h-80 overflow-y-auto space-y-1" style="margin-bottom: 60px;">
            <label v-for="collection in availableCollectionsForAgent" :key="collection.collectionId" class="label cursor-pointer hover:bg-base-300 dark:hover:bg-gray-600 rounded-md px-3 py-2">
              <span class="label-text font-medium">{{ collection.collectionName }}</span> 
              <input type="checkbox" 
                     :value="collection.collectionId" 
                     v-model="selectedAnalysisCollectionIds" 
                     class="checkbox checkbox-primary" />
            </label>
          </div>
        </div>
  
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(8, -1)" class="btn">
              <span v-if="isNavigating === 7" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
           <div class="flex items-center justify-evenly">
              <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
                <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
                {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
              </button>
              <button @click="createAgent(true)" class="btn btn-accent">
                 <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
                {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
              </button>
            </div>
        </div>
      </div>
  
      <template v-if="isVoiceOnlyChannelSelection === true || showAllFields === true">
      <input type="radio" data-currentPage="6" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.avatars', 'Avatars')" />
      <div role="tabpanel" data-currentPage="6" class="tab-content bg-base-100 border-base-300 rounded-box p-6" style="min-height: 40vh;">
          <div class="flex justify-between" id="tabTitle">
            <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
            {{ t('aiChatAgentSetup.avatars.title', 'Avatar Configuration') }}
          </h1>
        </div>
        <div class="mt-4">
          <AvatarProvidersPanel
            :selected-provider="selectedAvatarProvider"
            @update:selected-provider="selectedAvatarProvider = $event"
            v-model="avatarProviderConfigs"
          />
        </div>
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(6, -1)" class="btn">
            <span v-if="isNavigating === 8" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button v-if="isEditMode === true" @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
            </button>
            <button @click="goNextStep(6, 1)" class="btn">
              <span v-if="isNavigating === 9" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
            </button>
          </div>
        </div>
      </div>
      </template>

      <input type="radio" data-currentPage="9" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.advanced', 'Advanced')" />
      <div role="tabpanel" data-currentPage="9" class="tab-content bg-base-100 border border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <AdvancedTab
          :agentId="typeof route.query.edit === 'string' ? route.query.edit : ''"
          :languages="languages"
          :isRtl="isRtl"
          v-model:retrieveUserLocation="retrieveUserLocation"
          v-model:enableFileUploadInChat="enableFileUploadInChat"
          v-model:enableUserFineTune="enableUserFineTune"
          v-model:inappropriateContentMessage="inappropriateContentMessage"
          v-model:dontKnowMessage="dontKnowMessage"
          v-model:networkErrorMessage="networkErrorMessage"
          v-model:makeLinksClickable="makeLinksClickable"
          v-model:clearSessionOnRefresh="clearSessionOnRefresh"
          v-model:enabledSavedSessionHistory="enabledSavedSessionHistory"
          v-model:regularOpenAiContentFilterFailoverEnabled="regularOpenAiContentFilterFailoverEnabled"
          :showRegularOpenAiContentFilterFailover="true"
          v-model:showMap="showMap"
          v-model:limitReachedMessage="limitReachedMessage"
          v-model:messageLimitPerConversation="messageLimitPerConversation"
          v-model:messageLimitPerIP="messageLimitPerIP"
          v-model:limitMessagesPerConversationEnabled="limitMessagesPerConversationEnabled"
          v-model:limitMessagesPerIPEnabled="limitMessagesPerIPEnabled"
          v-model:enablePIIRedaction="enablePIIRedaction"
          v-model:redactEmails="redactEmails"
          v-model:redactPhoneNumbers="redactPhoneNumbers"
          v-model:verifyLinks="verifyLinks"
          v-model:customBlockRegex="customBlockRegex"
          v-model:responseFilters="responseFilters"
          v-model:kbToolUseMode="kbToolUseMode"
          v-model:retrieverKValue="retrieverKValue"
          v-model:vectorDbQueriesLimit="vectorDbQueriesLimit"
          v-model:vectorQueryAdditionalText="vectorQueryAdditionalText"
          v-model:prependHistoryToShortVectorQueries="prependHistoryToShortVectorQueries"
          v-model:retrievalStrategy="retrievalStrategy"
          v-model:maxOutputTokens="maxOutputTokens"
          v-model:actionButtons="actionButtons"
          v-model:whatsappClosingMessage="whatsappClosingMessage"
          v-model:verifyAnswerEnabled="verifyAnswerEnabled"
          v-model:selectedVerificationLLMModel="selectedVerificationLLMModel"
          v-model:selectedVectorDBId="selectedVectorDBId"
          v-model:selectedEmbeddingModelId="selectedEmbeddingModelId"
          :availableLLMModels="availableLLMModels"
          :currentLlmModelId="typeof resolvedSelectedLLMModelId === 'string' ? resolvedSelectedLLMModelId : ''"
          :hideEmbeddingSelector="hideEmbeddingSelector"
          :isVoiceOnly="isVoiceOnlyChannelSelection"
          :showAllFields="showAllFields"
          :promptVersions="promptVersions"
          @restore-prompt-version="restorePromptVersion"
        />
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(9, -1)" class="btn">
            <span v-if="isNavigating === 8" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
            </button>
          </div>
        </div>
      </div>
  
      <input type="radio" data-currentPage="10" name="my_tabs_2" role="tab" class="tab" :aria-label="t('aiChatAgentSetup.tabs.guardrails', 'Guardrails')" />
      <div role="tabpanel" data-currentPage="10" class="tab-content bg-base-100 border border-base-300 rounded-box p-6" style="min-height: 40vh;">
        <SecuritySettings
          :languages="languages"
          v-model:inappropriateContentMessage="inappropriateContentMessage"
          v-model:enablePIIRedaction="enablePIIRedaction"
          v-model:redactEmails="redactEmails"
          v-model:redactPhoneNumbers="redactPhoneNumbers"
          v-model:verifyLinks="verifyLinks"
          v-model:verifyAnswerEnabled="verifyAnswerEnabled"
          v-model:customBlockRegex="customBlockRegex"
          v-model:limitReachedMessage="limitReachedMessage"
          v-model:messageLimitPerConversation="messageLimitPerConversation"
          v-model:messageLimitPerIP="messageLimitPerIP"
          v-model:messageLimitPerIPDurationMinutes="messageLimitPerIPDurationMinutes"
          v-model:messageLimitPerPhoneNumber="messageLimitPerPhoneNumber"
          v-model:messageLimitPerPhoneNumberDurationMinutes="messageLimitPerPhoneNumberDurationMinutes"
          v-model:limitMessagesPerConversationEnabled="limitMessagesPerConversationEnabled"
          v-model:limitMessagesPerIPEnabled="limitMessagesPerIPEnabled"
          v-model:authenticatorEnabled="authenticatorEnabled"
          v-model:authenticatorProvider="authenticatorProvider"
          v-model:authenticatorWelcomeMessage="authenticatorWelcomeMessage"
          v-model:authenticatorRetryMessage="authenticatorRetryMessage"
          v-model:authenticatorSuccessMessage="authenticatorSuccessMessage"
          v-model:authenticatorRunAiToolOnLogin="authenticatorRunAiToolOnLogin"
          :disable-verify-answer-toggle="isFastOrSuperFastMode === true"
        />
        <div class="flex justify-between mt-4" style="position: relative;">
          <button @click="goNextStep(10, -1)" class="btn">
            <span v-if="isNavigating === 9" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
          </button>
          <div class="flex items-center justify-evenly">
            <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
              <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
              {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
            </button>
            <button @click="createAgent(true)" class="btn btn-accent">
              <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
              {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
            </button>
          </div>
        </div>
      </div>

      <AgentSetupHumanInTheLoopTab
        v-if="isVoiceOnlyChannelSelection === true || showAllFields === true"
        :isRtl="isRtl === true"
        :t="t"
        :languages="languages"
        :enabled="humanInTheLoopEnabled"
        :startConversationWebhook="humanInTheLoopStartConversationWebhook"
        :newMessageWebhook="humanInTheLoopNewMessageWebhook"
        :inboundSecret="humanInTheLoopInboundSecret"
        :selectedTransferLang="selectedHumanInTheLoopTransferMessageLang"
        :transferMessage="currentHumanInTheLoopTransferMessage"
        :selectedAiBackLang="selectedHumanInTheLoopAiBackMessageLang"
        :aiBackMessage="currentHumanInTheLoopAiBackMessage"
        :startWebhookExample="humanInTheLoopStartWebhookExample"
        :newMessageWebhookExample="humanInTheLoopNewMessageWebhookExample"
        :sendMessageEndpoint="humanInTheLoopSendMessageEndpoint"
        :returnToAgentEndpoint="humanInTheLoopReturnToAgentEndpoint"
        :isNavigating="isNavigating"
        :isEditMode="isEditMode === true"
        :showSaveButton="isEditMode === true && uploadedFiles.length === 0"
        :isSaving="isSaving === true"
        :isSavingAndRedirecting="isSavingAndRedirecting === true"
        :copyTextToClipboard="copyTextToClipboard"
        :downloadPostman="downloadHumanInTheLoopPostman"
        :goPrevious="() => goNextStep(11, -1)"
        :saveWithoutRedirect="saveAgentWithoutRedirect"
        :saveAndClose="() => createAgent(true)"
        :getDirStyle="getDirStyle"
        :handleDirKeydown="handleDirKeydown"
        @update:enabled="humanInTheLoopEnabled = $event"
        @update:startConversationWebhook="humanInTheLoopStartConversationWebhook = $event"
        @update:newMessageWebhook="humanInTheLoopNewMessageWebhook = $event"
        @update:inboundSecret="humanInTheLoopInboundSecret = $event"
        @update:selectedTransferLang="selectedHumanInTheLoopTransferMessageLang = $event"
        @update:transferMessage="currentHumanInTheLoopTransferMessage = $event"
        @update:selectedAiBackLang="selectedHumanInTheLoopAiBackMessageLang = $event"
        @update:aiBackMessage="currentHumanInTheLoopAiBackMessage = $event"
      />
  

    <template v-if="isVoiceOnlyChannelSelection === true || showAllFields === true">
    <input type="radio" data-currentPage="14" name="my_tabs_2" role="tab" class="tab" aria-label="Agents to Agents" />
    <div role="tabpanel" data-currentPage="14" class="tab-content bg-base-100 border border-base-300 rounded-box p-6" style="min-height: 40vh;">
      <div class="flex justify-between" id="tabTitle">
        <h1 class="text-2xl" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">Agents to Agents</h1>
      </div>
      <div class="mt-4">
        <AgentsToAgentsFlow
          :currentAgentId="route.query.edit || 'new-agent'"
          :currentAgentName="agentName || 'Current Agent'"
          :availableLLMModels="availableLLMModels"
          :selectedRouterLlmModel="agentsToAgentsRouterLlmModelId"
          :showSubAgentConversationsInReports="showSubAgentConversationsInReports"
          @update:selectedRouterLlmModel="agentsToAgentsRouterLlmModelId = $event"
          @update:showSubAgentConversationsInReports="showSubAgentConversationsInReports = $event"
          ref="agentsToAgentsFlowRef"
        />
      </div>
      <div class="flex justify-between mt-4" style="position: relative;">
        <button @click="goNextStep(14, -1)" class="btn">
          {{ t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
        </button>
        <div class="flex items-center justify-evenly">
          <button v-if="isEditMode === true && uploadedFiles.length === 0" @click="saveAgentWithoutRedirect" class="btn btn-neutral">
            <span v-if="isSaving === true && isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
            {{ t('aiChatAgentSetup.buttons.save', 'Save') }}
          </button>
          <button @click="createAgent(true)" class="btn btn-accent">
            <span v-if="isSaving === true && isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
            {{ isEditMode === true ? t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') : t('aiChatAgentSetup.buttons.createNewAgent', 'Create New Agent +') }}
          </button>
        </div>
      </div>
    </div>
    </template>
  
    </div>
   
    <OpenAgentChannelsModal
      :visible="showOpenAgentModal === true"
      :isRtl="isRtl === true"
      :channels="availableOpenAgentChannels"
      @close="showOpenAgentModal = false"
      @open="openAgentChannel"
    />
    <VoiceSettingsOverlayModal
      :visible="showVoiceSettingsModal === true"
      :isRtl="isRtl === true"
      :voiceType="voiceSettingsVoiceType"
      :defaultEngineVersion="voiceSettingsDefaultEngineVersion"
      :defaultEngineVersionOptions="voiceSettingsDefaultEngineModelOptions"
      :vadEngine="voiceSettingsVadEngine"
      :selectedVoiceId="voiceSettingsVoiceId"
      :accent="voiceSettingsAccent"
      :language="voiceSettingsLanguage"
      :languageOptions="voiceLanguageOptions"
      :selectedTermsPackages="voiceSettingsTermsPackages"
      :speed="voiceSettingsSpeed"
      :customPacingEnabled="voiceSettingsCustomPacingEnabled"
      :pacing="voiceSettingsPacing"
      :durationStretch="voiceSettingsDurationStretch"
      :useDashPauses="voiceSettingsUseDashPauses"
      :pauseEveryWords="voiceSettingsPauseEveryWords"
      :dashPauseMs="voiceSettingsDashPauseMs"
      :commaPauseMs="voiceSettingsCommaPauseMs"
      :periodPauseMs="voiceSettingsPeriodPauseMs"
      :idleAutoResponseEnabled="voiceSettingsIdleAutoResponseEnabled"
      :startingMessageIdleAutoResponseSeconds="voiceSettingsStartingMessageIdleAutoResponseSeconds"
      :idleAutoResponseSeconds="voiceSettingsIdleAutoResponseSeconds"
      :toolUseAutoResponseSeconds="voiceSettingsToolUseAutoResponseSeconds"
      :promptToVoiceEnabled="voiceSettingsPromptToVoiceEnabled"
      :promptToVoicePrompt="voiceSettingsPromptToVoicePrompt"
      :tone="voiceSettingsTone"
      :toneOptions="voiceToneOptions"
      :mode="voiceSettingsMode"
      :modeOptions="voiceModeOptions"
      :cancellingMode="voiceSettingsCancellingMode"
      :cancellingModeOptions="voiceCancellingModeOptions"
      :audioProcessorEngine="voiceSettingsAudioProcessorEngine"
      :audioProcessorEngineOptions="voiceAudioProcessorEngineOptions"
      :fillerSentences="voiceSettingsFillerSentences"
      :quickResponseFillerEnabled="voiceSettingsQuickResponseFillerEnabled"
      :quickResponseFillerText="voiceSettingsQuickResponseFillerText"
      :transcriptionOptimizerEnabled="voiceSettingsTranscriptionOptimizerEnabled"
      :transcriptionOptimizerTerms="voiceSettingsTranscriptionOptimizerTerms"
      :transcriptionOptimizerSourcePrompt="transcriptionOptimizerSourcePrompt"
      @update:voiceType="voiceSettingsVoiceType = $event"
      @update:defaultEngineVersion="selectVoiceSettingsEngineVersion"
      @update:vadEngine="voiceSettingsVadEngine = $event"
      @update:selectedVoiceId="voiceSettingsVoiceId = $event"
      @update:accent="voiceSettingsAccent = $event"
      @update:language="voiceSettingsLanguage = $event"
      @update:selectedTermsPackages="voiceSettingsTermsPackages = $event"
      @update:selectedTerms="voiceSettingsSonioxTerms = $event"
      @update:speed="voiceSettingsSpeed = $event"
      @update:customPacingEnabled="voiceSettingsCustomPacingEnabled = $event"
      @update:pacing="voiceSettingsPacing = $event"
      @update:durationStretch="voiceSettingsDurationStretch = $event"
      @update:useDashPauses="voiceSettingsUseDashPauses = $event"
      @update:pauseEveryWords="voiceSettingsPauseEveryWords = $event"
      @update:dashPauseMs="voiceSettingsDashPauseMs = $event"
      @update:commaPauseMs="voiceSettingsCommaPauseMs = $event"
      @update:periodPauseMs="voiceSettingsPeriodPauseMs = $event"
      @update:idleAutoResponseEnabled="voiceSettingsIdleAutoResponseEnabled = $event"
      @update:startingMessageIdleAutoResponseSeconds="voiceSettingsStartingMessageIdleAutoResponseSeconds = $event"
      @update:idleAutoResponseSeconds="voiceSettingsIdleAutoResponseSeconds = $event"
      @update:toolUseAutoResponseSeconds="voiceSettingsToolUseAutoResponseSeconds = $event"
      @update:promptToVoiceEnabled="voiceSettingsPromptToVoiceEnabled = $event"
      @update:promptToVoicePrompt="voiceSettingsPromptToVoicePrompt = $event"
      @update:tone="voiceSettingsTone = $event"
      @update:mode="voiceSettingsMode = $event"
      @update:cancellingMode="voiceSettingsCancellingMode = $event"
      @update:audioProcessorEngine="voiceSettingsAudioProcessorEngine = $event"
      @update:fillerSentences="voiceSettingsFillerSentences = normalizeWaitingFillerSentences($event)"
      @update:quickResponseFillerEnabled="voiceSettingsQuickResponseFillerEnabled = $event"
      @update:quickResponseFillerText="voiceSettingsQuickResponseFillerText = $event"
      @update:transcriptionOptimizerEnabled="voiceSettingsTranscriptionOptimizerEnabled = $event"
      @update:transcriptionOptimizerTerms="voiceSettingsTranscriptionOptimizerTerms = $event"
      @close="closeVoiceSettingsModal"
      @confirm="confirmVoiceSettings"
    />
   
    <AppModal
      :visible="showCreateFolderModal === true"
      :title="t('aiChatAgentSetup.modals.createFolder.title', 'Create New Folder')"
      boxClass="max-w-md w-11/12"
      @close="showCreateFolderModal = false"
    >
      <template #left>
        <div class="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
          <Icon icon="heroicons:folder-plus" class="w-5 h-5" />
        </div>
      </template>
      <div class="px-4 pb-2" :dir="isRtl === true ? 'rtl' : 'ltr'">
        <p class="text-sm text-base-content/70 mb-4 leading-relaxed">
          {{ t('aiChatAgentSetup.modals.createFolder.promptPrefix', 'Enter a name for the new folder inside') }}
          <span class="font-semibold text-base-content">{{ createFolderTargetName }}</span>
        </p>
        <div class="form-control w-full">
          <label class="label py-1">
            <span class="label-text font-medium">{{ t('aiChatAgentSetup.modals.createFolder.folderNamePlaceholder', 'Folder name') }}</span>
          </label>
          <input
            type="text"
            v-model="newFolderName"
            class="input input-bordered w-full"
            :placeholder="t('aiChatAgentSetup.modals.createFolder.folderNamePlaceholder', 'Folder name')"
            :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }"
            @keydown.enter="handleCreateFolder"
          />
        </div>
      </div>
      <template #actions>
        <button type="button" class="btn" @click="showCreateFolderModal = false">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
        <button type="button" class="btn" @click="handleCreateFolder">{{ t('aiChatAgentSetup.modals.createFolder.createButton', 'Create') }}</button>
      </template>
    </AppModal>

    <AppModal
      :visible="showMoveFileModal === true && fileToMove !== null"
      :title="t('aiChatAgentSetup.modals.moveFile.title', 'Move File')"
      boxClass="max-w-lg w-11/12"
      @close="closeMoveFileModal"
    >
      <template #left>
        <div class="w-10 h-10 rounded-xl bg-info/10 text-info flex items-center justify-center flex-shrink-0">
          <Icon icon="heroicons:arrow-right-circle" class="w-5 h-5" />
        </div>
      </template>
      <div class="px-4 pb-2" :dir="isRtl === true ? 'rtl' : 'ltr'">
        <div class="rounded-xl border border-base-300 bg-base-200/50 px-4 py-3 mb-4">
          <div class="text-xs uppercase tracking-wide text-base-content/50 mb-1">{{ t('aiChatAgentSetup.knowledgeBase.tableHeaders.name', 'Name') }}</div>
          <div class="text-sm font-medium break-all">{{ decodeURIComponentSafe(fileToMove !== null ? fileToMove.fileName : '') }}</div>
        </div>
        <div class="form-control w-full">
          <MultiSelect
            v-model="selectedMoveFolderArray"
            :options="moveFolderOptions"
            idField="folderId"
            labelField="folderName"
            :label="t('aiChatAgentSetup.modals.moveFile.destinationLabel', 'Select destination folder')"
            :placeholder="t('aiChatAgentSetup.modals.moveFile.rootDestination', 'Knowledge Base (Root)')"
            :maxSelections="1"
            buttonClass="min-h-14"
            :forceDirection="isRtl === true ? 'rtl' : 'ltr'"
          />
        </div>
      </div>
      <template #actions>
        <button type="button" class="btn" @click="closeMoveFileModal">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
        <button type="button" class="btn" @click="handleMoveFile">{{ t('aiChatAgentSetup.modals.moveFile.moveButton', 'Move File') }}</button>
      </template>
    </AppModal>
   
    <dialog :open="showWebsiteLinkModal === true" class="modal">
      <div class="modal-box">
        <h3 class="font-bold text-lg" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.addWebsiteLink.title', 'Add Website Link') }}</h3>
        <div class="py-4">
          <input type="url" v-model="websiteLink" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.modals.addWebsiteLink.urlPlaceholder', 'Enter website URL')" :style="getDirStyle('websiteLink')" @keydown="handleDirKeydown($event, 'websiteLink')" />
        </div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="showWebsiteLinkModal = false">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="addWebsiteLink">{{ t('aiChatAgentSetup.modals.addWebsiteLink.addButton', 'Add') }}</button>
        </div>
      </div>
    </dialog>
    <dialog :open="showWebsiteBatchModal === true" class="modal">
      <div class="modal-box">
        <h3 class="font-bold text-lg" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.addWebsiteBatch.title', 'Add Website Batch URLs') }}</h3>
        <div class="py-4">
          <textarea v-model="websiteBatchText" class="textarea textarea-bordered w-full" :placeholder="t('aiChatAgentSetup.modals.addWebsiteBatch.placeholder', 'Enter one URL per line')" style="min-height: 200px;" :style="getDirStyle('websiteBatchText')" @keydown="handleDirKeydown($event, 'websiteBatchText')"></textarea>
          <div class="mt-4 md:flex">
            <label class="label cursor-pointer flex items-center md:me-3">
              <input type="checkbox" class="toggle toggle-accent" v-model="rescrapeEnabled" />
              <span class="label-text ms-2">Enable Rescrape</span>
            </label>
            <div class="md:me-3 mt-3 md:mt-0">
              <label class="label"><span class="label-text">Interval (days)</span></label>
              <input type="number" class="input input-bordered w-full" v-model="rescrapeIntervalDays" min="1" :disabled="rescrapeEnabled !== true" />
            </div>
            <div class="mt-3 md:mt-0">
              <label class="label"><span class="label-text">Hour (0-23, UTC)</span></label>
              <input type="number" class="input input-bordered w-full" v-model="rescrapeAtHour" min="0" max="23" :disabled="rescrapeEnabled !== true" />
            </div>
          </div>
        </div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="showWebsiteBatchModal = false">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="addWebsiteBatchUrls">{{ t('aiChatAgentSetup.modals.addWebsiteBatch.addButton', 'Add URLs') }}</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button @click="showWebsiteBatchModal = false">close</button>
      </form>
    </dialog>
    <dialog :open="showWebsiteScrapeModal === true" class="modal">
      <div class="modal-box text-center">
        <h3 class="font-bold text-lg">{{ t('aiChatAgentSetup.modals.scrapeWebsite.title', 'Scrape Entire Website') }}</h3>
        <div class="py-4">
          <input type="url" v-model="websiteToScrape" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.modals.scrapeWebsite.urlPlaceholder', 'Enter website URL to scrape')" :style="getDirStyle('websiteToScrape')" @keydown="handleDirKeydown($event, 'websiteToScrape')" />
          <div class="mt-4">
            <label class="label" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
              <span class="label-text">{{ t('aiChatAgentSetup.modals.scrapeWebsite.maxPagesLabel', 'Max Pages (optional)') }}</span>
            </label>
            <input type="number" v-model="maxPages" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.modals.scrapeWebsite.maxPagesPlaceholder', 'Leave empty for unlimited')" :style="getDirStyle('maxPages')" @keydown="handleDirKeydown($event, 'maxPages')" />
          </div>
        </div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="showWebsiteScrapeModal = false">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="scrapeWebsite">{{ t('aiChatAgentSetup.modals.scrapeWebsite.scrapeButton', 'Start Scraping') }}</button>
        </div>
      </div>
    </dialog>
    <dialog :open="showChangeSelectedWebsiteDomainModal === true" class="modal">
      <div class="modal-box">
        <h3 class="font-bold text-lg" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.changeSelectedWebsiteDomain.title', 'Change Selected Website Domains') }}</h3>
        <p class="text-sm text-base-content/70 mt-2" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.changeSelectedWebsiteDomain.help', 'Selected website URLs keep their paths, queries, and hashes while the domain is replaced. The agent is saved before rescraping the new URLs.') }}</p>
        <div class="py-4">
          <label class="label" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">
            <span class="label-text">{{ t('aiChatAgentSetup.modals.changeSelectedWebsiteDomain.domainLabel', 'New domain') }}</span>
          </label>
          <input type="text" v-model="selectedWebsiteNewDomain" class="input input-bordered w-full" :placeholder="t('aiChatAgentSetup.modals.changeSelectedWebsiteDomain.domainPlaceholder', 'example.com or https://example.com')" :style="getDirStyle('selectedWebsiteNewDomain')" @keydown="handleDirKeydown($event, 'selectedWebsiteNewDomain')" />
        </div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="closeChangeSelectedWebsiteDomainModal">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="changeSelectedWebsiteDomainsAndRescan">{{ t('aiChatAgentSetup.modals.changeSelectedWebsiteDomain.confirmButton', 'Change Domain and Rescrape') }}</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button @click="closeChangeSelectedWebsiteDomainModal">close</button>
      </form>
    </dialog>
    <dialog :open="showTextInputModal === true" class="modal">
      <div class="modal-box">
        <h3 class="font-bold text-lg" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.addTextContent.title', 'Add Text Content') }}</h3>
        <div class="py-4">
          <input type="text" v-model="textFileName" maxlength="255" class="input input-bordered w-full mb-4" :placeholder="t('aiChatAgentSetup.modals.addTextContent.fileNamePlaceholder', 'Enter file name')" :style="getDirStyle('textFileName')" @keydown="handleDirKeydown($event, 'textFileName')" />
          <div class="text-xs text-right pr-1 mb-2 text-gray-500" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ textFileName.length }}/255</div>
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm text-gray-500" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.addTextContent.transcribeLabel', 'Live transcription') }}</span>
            <button type="button" class="btn btn-sm btn-circle" :class="isTextTranscribing === true ? 'btn-error' : 'btn-ghost'" @click="toggleTextTranscription" :title="isTextTranscribing === true ? t('aiChatAgentSetup.modals.addTextContent.stopTranscription', 'Stop transcription') : t('aiChatAgentSetup.modals.addTextContent.startTranscription', 'Start transcription')">
              <span v-if="isTextTranscribing === true" class="loading loading-spinner loading-xs"></span>
              <Icon v-else icon="tabler:microphone" class="w-5 h-5" />
            </button>
          </div>
          <textarea v-model="textContent" maxlength="10000" class="textarea textarea-bordered w-full" style="min-height: 200px;" :style="getDirStyle('textContent')" @keydown="handleDirKeydown($event, 'textContent')" :placeholder="t('aiChatAgentSetup.modals.addTextContent.contentPlaceholder', 'Enter your text content here')"></textarea>
          <div v-if="textTranscriptionInterim.length > 0" class="text-sm text-gray-500 mt-2 italic" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ textTranscriptionInterim }}</div>
          <div class="text-xs text-right pr-1 mt-1 text-gray-500" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ textContent.length }}/10000</div>
          <div class="mt-4">
            <button type="button" class="btn btn-sm" @click="showTextAdvancedOptions = showTextAdvancedOptions !== true">{{ t('aiChatAgentSetup.modals.addTextContent.advancedButton', 'Advanced') }}</button>
            <div v-if="showTextAdvancedOptions === true" class="mt-3 rounded-lg border border-base-300 bg-base-200 p-3">
              <p class="text-sm font-semibold mb-2" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.addTextContent.regexSchemasTitle', 'Regex schemas for voice script injection') }}</p>
              <p class="text-xs text-base-content/70 mb-3" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.modals.addTextContent.regexSchemasHelp', 'When a caller turn matches one of these regex schemas, this text is injected into the voice agent as temporary script context.') }}</p>
              <div class="flex items-center mb-2">
                <input type="text" v-model="textRegexSchemaInput" maxlength="500" class="input input-bordered input-sm flex-1" :placeholder="t('aiChatAgentSetup.modals.addTextContent.regexSchemaPlaceholder', 'Example: /pricing/i')" :style="getDirStyle('textRegexSchemaInput')" @keydown="handleDirKeydown($event, 'textRegexSchemaInput')" @keyup.enter="addTextRegexSchema" />
                <button type="button" class="btn btn-sm ml-2" @click="addTextRegexSchema">{{ t('aiChatAgentSetup.modals.addTextContent.addRegexSchema', 'Add') }}</button>
              </div>
              <div class="text-xs text-right pr-1 text-gray-500" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ textRegexSchemaInput.length }}/500</div>
              <div v-if="textRegexSchemas.length > 0" class="mt-3">
                <div v-for="(schema, index) in textRegexSchemas" :key="'text-regex-schema-' + index" class="flex items-center justify-between rounded-md bg-base-100 p-2 mb-2">
                  <code class="text-xs break-all">{{ schema }}</code>
                  <button type="button" class="btn btn-sm ml-3" @click="removeTextRegexSchema(index)">{{ t('aiChatAgentSetup.rules.removeRuleButton','Remove') }}</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="closeTextInputModal">{{ t('aiChatAgentSetup.buttons.cancel', 'Cancel') }}</button>
          <button class="btn" @click="addTextContent">{{ t('aiChatAgentSetup.modals.addTextContent.addButton', 'Add') }}</button>
        </div>
      </div>
    </dialog>
    <AgentLoadingModal
      :open="showLoadingModal !== false"
      :modalState="modalState"
      :currentTip="currentTip"
      :close-on-backdrop="false"
      @close="closeModal"
    />
    <Transition name="prompt-editor-modal">
      <dialog v-if="showMaxPromptModal === true" open class="modal modal-open prompt-editor-modal">
        <div class="prompt-editor-shell" :class="isRtl === true ? 'prompt-editor-shell--rtl' : ''" :dir="isRtl === true ? 'rtl' : 'ltr'">
      <div class="modal-box max-w-4xl shadow-2xl prompt-editor-main" style="animation: scaleIn 200ms ease-out;">
        <div class="flex items-center justify-between pb-3 border-b border-base-300 prompt-editor-header">
          <h3 class="font-bold text-xl text-base-content" :style="isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ t('aiChatAgentSetup.agentInformation.agentInstructionsLabel') }}</h3>
          <button class="btn btn-sm btn-circle btn-ghost" type="button" @click="closeMaxPromptModal" :title="t('aiChatAgentSetup.buttons.close','Close')" :aria-label="t('aiChatAgentSetup.buttons.close','Close')">
            <Icon icon="lucide:x" class="w-5 h-5" />
          </button>
        </div>
          <div class="relative prompt-editor-workspace">
            <button v-show="false" type="button" class="btn btn-outline btn-secondary btn-xs absolute z-10 shadow-sm" style="top: 8px; left: 12px;" @click="enhanceDescriptionWithAI(maxAgentDescriptionRef)" :disabled="isEnhancingDescription === true">
              <span v-if="isEnhancingDescription === true" class="loading loading-spinner loading-xs"></span>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:4px;"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>
              <span v-if="isEnhancingDescription === false">{{ t('aiChatAgentSetup.agentInformation.enhanceDescription', 'Enhance with AI') }}</span>
            </button>
            <textarea
              ref="maxAgentDescriptionRef"
            v-model="agentDescription"
            class="textarea textarea-bordered w-full text-base prompt-editor-textarea"
            :class="{ 'textarea-error': agentDescriptionError === true }"
            style="min-height: 500px; resize: vertical;"
            :style="getDirStyle('agentDescription')"
            :placeholder="t('aiChatAgentSetup.agentInformation.agentInstructionsPlaceholder', 'Description Prompt')"
            @keydown="handleDirKeydown($event, 'agentDescription')" @input="handleAgentDescriptionInput()"
            :maxlength="isPhoneChannelSelected === true || isWebVoiceAgentChannelSelected === true || isWhatsappVoiceChannelSelected === true || isTelegramVoiceChannelSelected === true ? 70000 : 200000"
          ></textarea>
          <div class="text-xs text-right pr-1 mt-1 text-gray-500">{{ agentDescription.length }}/{{ isPhoneChannelSelected === true || isWebVoiceAgentChannelSelected === true || isWhatsappVoiceChannelSelected === true || isTelegramVoiceChannelSelected === true ? '70000' : '200000' }}</div>
        </div>
        <div class="modal-action pt-3 border-t border-base-300 prompt-editor-footer">
          <button class="btn btn-sm" type="button" @click="enhanceDescriptionWithAI(maxAgentDescriptionRef)" :disabled="isEnhancingDescription === true">
            <span v-if="isEnhancingDescription === true" class="loading loading-spinner loading-xs"></span>
            <Icon v-else icon="tabler:sparkles" class="w-4 h-4" />
            <span>{{ t('aiChatAgentSetup.agentInformation.enhanceDescription', 'Enhance with AI') }}</span>
          </button>
          <button class="btn" type="button" @click="closeMaxPromptModal">{{ t('aiChatAgentSetup.buttons.close','Close') }}</button>
        </div>
      </div>
          <aside class="prompt-editor-assistant" :class="isPromptAssistantOpen === true ? 'prompt-editor-assistant--open' : 'prompt-editor-assistant--closed'">
            <header class="prompt-editor-assistant-header">
              <div class="prompt-editor-assistant-heading">
                <Icon icon="mingcute:ai-fill" class="w-5 h-5" />
                <div>
                  <div class="font-semibold">{{ t('adi.agentName', 'Adi') }}</div>
                  <div class="text-xs text-base-content/60">{{ t('aiChatAgentSetup.promptAssistant.helpText', 'Ask for help improving this agent prompt') }}</div>
                </div>
              </div>
              <button class="btn btn-sm btn-circle btn-ghost" type="button" @click="isPromptAssistantOpen = false" :title="t('aiChatAgentSetup.promptAssistant.close', 'Close assistant')" :aria-label="t('aiChatAgentSetup.promptAssistant.close', 'Close assistant')">
                <Icon icon="tabler:chevron-right" class="w-5 h-5" :class="isRtl === true ? 'rotate-180' : ''" />
              </button>
            </header>
            <AdiWidget layout="page" :hide-header="true" :setup-surface="true" :agent-setup-draft="promptAssistantDraftContext" class="prompt-editor-assistant-widget" />
          </aside>
          <button v-if="isPromptAssistantOpen !== true" class="btn btn-sm prompt-editor-assistant-open" type="button" @click="isPromptAssistantOpen = true" :title="t('aiChatAgentSetup.promptAssistant.open', 'Open prompt assistant')" :aria-label="t('aiChatAgentSetup.promptAssistant.open', 'Open prompt assistant')">
            <Icon icon="mingcute:ai-fill" class="w-5 h-5" />
            <span>{{ t('aiChatAgentSetup.promptAssistant.open', 'Open prompt assistant') }}</span>
          </button>
        </div>
        <form method="dialog" class="modal-backdrop prompt-editor-backdrop" @click="closeMaxPromptModal">
          <button type="button">{{ t('aiChatAgentSetup.buttons.close', 'Close') }}</button>
        </form>
      </dialog>
    </Transition>
    <AppModal
      :visible="showPersonalityReplaceModal === true"
      :title="t('agentPersonalitySelector.replaceTitle', 'Replace Description')"
      boxClass="max-w-md"
      @close="cancelPersonalityReplace"
    >
      <template #actions>
        <button class="btn" @click="cancelPersonalityReplace">{{ t('agentPersonalitySelector.cancel', 'Cancel') }}</button>
        <button class="btn" @click="confirmPersonalityReplace">{{ t('agentPersonalitySelector.confirm', 'Replace') }}</button>
      </template>
    </AppModal>
    <dialog :open="isSwitchConfirmOpen === true" class="modal">
      <div class="modal-box text-center">
        <h3 class="font-bold text-lg">{{ t('aiChatAgentSetup.modals.confirmModeSwitch.title', 'Confirm Mode Switch') }}</h3>
        <p class="py-4">{{ t('aiChatAgentSetup.modals.confirmModeSwitch.prompt', 'Switching away from Flow will discard the current flow configuration. Are you sure you want to continue?') }}</p>
        <div class="modal-action space-x-2">
          <button class="btn" @click="cancelSwitchMode">{{ t('aiChatAgentSetup.modals.confirmModeSwitch.cancelButton', 'Cancel') }}</button>
          <button class="btn btn-warning" @click="confirmSwitchMode">{{ t('aiChatAgentSetup.modals.confirmModeSwitch.confirmButton', 'Discard Flow & Switch') }}</button>
        </div>
        <form method="dialog" class="modal-backdrop">
          <button @click="cancelSwitchMode">close</button>
        </form>
      </div>
    </dialog>
    <InspectFile
      :is-open="isInspectModalOpen === true"
      :file="selectedFileToInspect"
      @close="closeInspectModal"
    />
    
    <CreateAgentStyleModal
      :visible="showCreateStyleModal"
      @close="showCreateStyleModal = false"
      @created="onStyleCreated"
    />
    <it-modal v-model="showScrapeReportModal" width="min(96vw, 1440px)">
      <template v-slot:body>
        <div v-if="scrapeReportSource !== null" class="max-h-[calc(100vh-3rem)] overflow-y-auto p-2 sm:p-4">
          <WebsiteScrapeReport :agent-id="route.query.edit" :source="scrapeReportSource" :initial-open="true" :hide-toggle="true" />
          <div class="flex justify-end mt-4 pb-1">
            <button class="btn" @click="closeScrapeReportModal()">{{ t('aiChats.knowledgeStatusModal.closeButton','Close') }}</button>
          </div>
        </div>
      </template>
    </it-modal>
    <dialog :open="showSharedKbPicker === true" class="modal">
      <div class="modal-box max-w-lg">
        <h3 class="font-bold text-lg mb-4">{{ t('aiChatAgentSetup.modals.linkSharedKb.title','Link Shared Knowledge Bases') }}</h3>
        <MultiSelect
          v-model="selectedSharedKbIds"
          :options="sharedKnowledgeBases"
          idField="kbId"
          labelField="name"
          :placeholder="t('aiChatAgentSetup.modals.linkSharedKb.placeholder', 'Select knowledge bases')"
          buttonClass="min-h-14"
          :forceDirection="isRtl === true ? 'rtl' : 'ltr'"
        />
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="confirmAddSharedKbs">{{ t('aiChatAgentSetup.modals.linkSharedKb.addButton','Add') }}</button>
          <button class="btn" @click="showSharedKbPicker = false">{{ t('aiChatAgentSetup.modals.linkSharedKb.closeButton','Close') }}</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button @click="showSharedKbPicker = false">close</button>
      </form>
    </dialog>
    <dialog :open="showAgentKbPicker === true" class="modal">
      <div class="modal-box max-w-lg">
        <h3 class="font-bold text-lg mb-4">{{ t('aiChatAgentSetup.modals.linkAgentKb.title','Link Agent Knowledge Bases') }}</h3>
        <MultiSelect
          v-model="selectedLinkedAgentKbIds"
          :options="availableAgentKnowledgeBaseOptions"
          idField="value"
          labelField="label"
          :placeholder="t('aiChatAgentSetup.modals.linkAgentKb.placeholder', 'Select agents')"
          buttonClass="min-h-14"
          :forceDirection="isRtl === true ? 'rtl' : 'ltr'"
        />
        <div class="modal-action flex items-center justify-evenly">
          <button class="btn" @click="confirmAddLinkedAgentKbs">{{ t('aiChatAgentSetup.modals.linkAgentKb.addButton','Add') }}</button>
          <button class="btn" @click="showAgentKbPicker = false">{{ t('aiChatAgentSetup.modals.linkAgentKb.closeButton','Close') }}</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button @click="showAgentKbPicker = false">close</button>
      </form>
    </dialog>
    <it-modal v-model="isAiSearchModalOpen" width="min(800px, calc(100vw - 24px))">
      <template v-slot:body>
        <div class="bg-base-100 p-5 sm:p-6 w-full overflow-hidden max-h-[calc(100vh-96px)] flex flex-col">
          <div class="flex items-start justify-between mb-5 flex-shrink-0">
            <div class="flex items-start min-w-0">
              <div class="w-10 h-10 border border-base-300 bg-base-100 text-base-content flex items-center justify-center flex-shrink-0">
                <Icon icon="tabler:search" class="w-5 h-5" />
              </div>
              <div class="min-w-0" :class="isRtl === true ? 'mr-4 text-right' : 'ml-4 text-left'">
                <h3 class="text-xl font-semibold">{{ t('aiChatAgentSetup.modals.aiSearch.title','Search with AI') }}</h3>
                <p class="text-sm text-base-content/70 mt-1">{{ aiSearchContextMessage }}</p>
              </div>
            </div>
            <button class="btn btn-ghost btn-sm btn-circle flex-shrink-0" type="button" :aria-label="t('aiChatAgentSetup.buttons.close','Close')" @click="closeAiSearch">
              <Icon icon="mdi:close" class="w-5 h-5" />
            </button>
          </div>

          <form class="border border-base-300 bg-base-100 p-4 flex-shrink-0" @submit.prevent="runAiSearch">
            <div class="mb-4">
              <label class="block text-sm font-medium mb-2" :class="isRtl === true ? 'text-right' : 'text-left'">
                {{ t('aiChatAgentSetup.modals.aiSearch.queryLabel', 'Ask a question') }}
              </label>
              <textarea
                v-model="aiSearchQuery"
                class="textarea textarea-bordered w-full bg-base-100 text-base leading-7"
                style="min-height: 110px;"
                :placeholder="t('aiChatAgentSetup.modals.aiSearch.queryPlaceholder', 'Ask a question about your knowledge base...')"
                :style="getDirStyle('aiSearchQuery')"
                @keydown="handleDirKeydown($event, 'aiSearchQuery')"
              ></textarea>
            </div>
            <div class="flex flex-wrap items-center justify-between">
              <div class="text-xs text-base-content/60" :class="isRtl === true ? 'text-right' : 'text-left'">
                {{ aiSearchHasSearched === true ? aiSearchResults.length + ' ' + t('aiChatAgentSetup.modals.aiSearch.resultsLabel', 'results') : t('aiChatAgentSetup.modals.aiSearch.searchHelper', 'Semantic search across your knowledge base and local pending files') }}
              </div>
              <button class="btn mt-3 sm:mt-0" type="submit" :disabled="aiSearchLoading === true || aiSearchQuery.trim() === ''">
                <span v-if="aiSearchLoading === true" class="loading loading-spinner loading-xs"></span>
                <Icon v-else icon="tabler:search" class="w-4 h-4" />
                <span>{{ t('aiChatAgentSetup.modals.aiSearch.searchButton', 'Search') }}</span>
              </button>
            </div>
          </form>

          <div class="border border-base-300 bg-base-100 px-4 py-3 text-xs text-base-content/70 mt-4 flex items-start flex-shrink-0" :class="isRtl === true ? 'text-right' : 'text-left'">
            <Icon icon="tabler:info-circle" class="w-5 h-5 flex-shrink-0" />
            <span>{{ t('aiChatAgentSetup.modals.aiSearch.chunkExplanation', 'AI Search shows the most relevant chunk from a document because the knowledge base is indexed in smaller parts. Use View document to see the full text already loaded in this page, without fetching saved documents again.') }}</span>
          </div>

          <div class="border border-base-300 bg-base-100 mt-4 overflow-hidden min-h-0 flex flex-col flex-1">
            <div class="flex flex-wrap items-center justify-between border-b border-base-300 px-4 py-3 flex-shrink-0">
              <div class="text-sm font-semibold">
                {{ t('aiChatAgentSetup.modals.aiSearch.matchesTitle', 'Top matches') }}
              </div>
              <div class="badge badge-ghost badge-sm mt-2 sm:mt-0">
                {{ aiSearchHasSearched === true ? aiSearchResults.length + ' ' + t('aiChatAgentSetup.modals.aiSearch.resultsLabel', 'results') : t('aiChatAgentSetup.modals.aiSearch.waitingLabel', 'Waiting for search') }}
              </div>
            </div>

            <div v-if="aiSearchLoading === true" class="py-14 px-6 overflow-y-auto">
              <div class="flex flex-col items-center justify-center">
                <span class="loading loading-spinner loading-lg text-primary"></span>
                <div class="text-sm text-base-content/70 mt-4">{{ t('aiChatAgentSetup.modals.aiSearch.searching', 'Searching your knowledge base...') }}</div>
              </div>
            </div>

            <div v-else-if="aiSearchError !== ''" class="p-4 overflow-y-auto">
              <div class="alert alert-error">
                <span>{{ aiSearchError }}</span>
              </div>
            </div>

            <div v-else-if="aiSearchHasSearched === true && aiSearchResults.length === 0" class="py-14 px-6 text-center overflow-y-auto">
              <div class="w-14 h-14 rounded-full bg-base-200 mx-auto flex items-center justify-center">
                <Icon icon="tabler:search-off" class="w-7 h-7 text-base-content/50" />
              </div>
              <div class="text-sm font-medium text-base-content mt-4">{{ t('aiChatAgentSetup.modals.aiSearch.noResults', 'No matching knowledge was found for this question.') }}</div>
              <div class="text-xs text-base-content/60 mt-2">{{ t('aiChatAgentSetup.modals.aiSearch.noResultsHint', 'Try a shorter question, another phrasing, or keywords from the source content.') }}</div>
            </div>

            <div v-else-if="aiSearchResults.length > 0" class="min-h-0 overflow-y-auto p-3">
              <div v-for="(result, index) in aiSearchResults" :key="result.source + '-' + result.snippet + '-' + index" class="border border-base-300 bg-base-100 p-4 mb-3">
                <div class="flex items-start justify-between">
                  <div class="min-w-0">
                    <div class="font-semibold break-all text-base-content">{{ result.sourceLabel !== undefined && result.sourceLabel !== null && result.sourceLabel !== '' ? result.sourceLabel : result.source }}</div>
                    <div class="mt-2">
                      <span class="badge badge-outline badge-sm">{{ result.scope === 'shared' ? t('aiChatAgentSetup.modals.aiSearch.sharedScope', 'Linked shared knowledge base') : result.scope === 'local' ? t('aiChatAgentSetup.modals.aiSearch.localScope', 'Pending changes in this page') : t('aiChatAgentSetup.modals.aiSearch.agentScope', 'Agent knowledge base') }}</span>
                    </div>
                  </div>
                  <div class="flex flex-wrap items-start justify-end flex-shrink-0" :class="isRtl === true ? 'mr-3' : 'ml-3'">
                    <button class="btn btn-sm flex-shrink-0" type="button" @click="openAiSearchDocument(result)">
                      <Icon icon="tabler:file-text" class="w-4 h-4" />
                      <span>{{ t('aiChatAgentSetup.modals.aiSearch.viewDocument', 'View document') }}</span>
                    </button>
                    <button v-if="canOpenAiSearchResult(result) === true" class="btn btn-sm flex-shrink-0" :class="isRtl === true ? 'mr-2' : 'ml-2'" type="button" @click="openAiSearchResult(result)">
                      <Icon icon="tabler:external-link" class="w-4 h-4" />
                      <span>{{ t('aiChatAgentSetup.modals.aiSearch.openSource', 'Open source') }}</span>
                    </button>
                  </div>
                </div>
                <div class="flex flex-wrap items-center justify-between mt-4">
                  <div class="text-xs text-base-content/60">
                    {{ t('aiChatAgentSetup.modals.aiSearch.fullChunkLabel', 'Full chunk') }}
                  </div>
                  <div class="join mt-2 sm:mt-0">
                    <button class="btn btn-xs join-item" :class="result.viewMode === 'markdown' ? 'btn-active' : ''" type="button" @click="setAiSearchResultViewMode(index, 'markdown')">
                      {{ t('aiChatAgentSetup.modals.aiSearch.markdownView', 'Markdown') }}
                    </button>
                    <button class="btn btn-xs join-item" :class="result.viewMode === 'plain' ? 'btn-active' : ''" type="button" @click="setAiSearchResultViewMode(index, 'plain')">
                      {{ t('aiChatAgentSetup.modals.aiSearch.plainView', 'Plain') }}
                    </button>
                  </div>
                </div>
                <div v-if="result.viewMode === 'markdown'" class="rounded-xl bg-base-200/60 px-4 py-3 mt-3 text-sm leading-6 overflow-x-auto markdown-content" :dir="isRtl === true ? 'rtl' : 'ltr'" v-html="renderAiSearchMarkdown(getAiSearchResultChunk(result))"></div>
                <div v-else class="rounded-xl bg-base-200/60 px-4 py-3 mt-3 text-sm leading-6 whitespace-pre-wrap overflow-x-auto">{{ getAiSearchResultChunk(result) }}</div>
              </div>
            </div>

            <div v-else class="py-14 px-6 text-center overflow-y-auto">
              <div class="w-12 h-12 border border-base-300 bg-base-100 mx-auto flex items-center justify-center">
                <Icon icon="tabler:search" class="w-6 h-6 text-base-content/60" />
              </div>
              <div class="text-sm font-medium text-base-content mt-4">{{ t('aiChatAgentSetup.modals.aiSearch.emptyStateTitle', 'Ask your knowledge base a question') }}</div>
              <div class="text-xs text-base-content/60 mt-2">{{ t('aiChatAgentSetup.modals.aiSearch.emptyStateDescription', 'Results will appear here after you run a search.') }}</div>
            </div>
          </div>

          <div class="flex justify-end mt-4 flex-shrink-0">
            <button class="btn" type="button" @click="closeAiSearch">{{ t('aiChatAgentSetup.buttons.close','Close') }}</button>
          </div>
        </div>
      </template>
    </it-modal>
    <dialog :open="isAiSearchDocumentModalOpen === true" class="modal">
      <div class="modal-box max-w-5xl w-11/12 max-h-[90vh] flex flex-col" :dir="isRtl === true ? 'rtl' : 'ltr'">
        <div class="flex items-start justify-between flex-shrink-0">
          <div class="min-w-0" :class="isRtl === true ? 'text-right' : 'text-left'">
            <h3 class="font-bold text-lg break-all">{{ aiSearchDocumentTitle }}</h3>
            <p class="text-xs text-base-content/60 mt-1 break-all">{{ aiSearchDocumentSource }}</p>
          </div>
          <button class="btn btn-ghost btn-sm btn-circle flex-shrink-0" type="button" :aria-label="t('aiChatAgentSetup.buttons.close','Close')" @click="closeAiSearchDocument">
            <Icon icon="mdi:close" class="w-5 h-5" />
          </button>
        </div>

        <div class="alert mt-4 flex-shrink-0" :class="aiSearchDocumentIsFull === true ? 'alert-info' : 'alert-warning'">
          <Icon icon="tabler:info-circle" class="w-5 h-5 flex-shrink-0" />
          <span>{{ aiSearchDocumentIsFull === true ? t('aiChatAgentSetup.modals.aiSearch.documentFullExplanation', 'This is the full document content already loaded in this page. No saved document was fetched again.') : t('aiChatAgentSetup.modals.aiSearch.documentChunkOnlyExplanation', 'This saved source is not loaded in this page. Without fetching the whole document, this popup can show only the indexed chunk returned by AI Search.') }}</span>
        </div>

        <div class="flex flex-wrap items-center justify-between mt-4 flex-shrink-0">
          <div class="text-xs text-base-content/60">
            {{ aiSearchDocumentIsFull === true ? t('aiChatAgentSetup.modals.aiSearch.fullDocumentLabel', 'Full document') : t('aiChatAgentSetup.modals.aiSearch.availableChunkLabel', 'Available chunk') }}
          </div>
          <div class="badge badge-ghost badge-sm mt-2 sm:mt-0">
            {{ t('aiChatAgentSetup.modals.aiSearch.plainContentLabel', 'Plain content') }}
          </div>
        </div>

        <div class="min-h-0 overflow-y-auto mt-3 rounded-xl bg-base-200/60 px-4 py-3 text-sm leading-6 flex-1">
          <div v-if="aiSearchDocumentContent !== ''" class="whitespace-pre-wrap overflow-x-auto">{{ aiSearchDocumentContent }}</div>
          <div v-else class="text-base-content/60">{{ t('aiChatAgentSetup.modals.aiSearch.noDocumentContent', 'No document content is available in this page.') }}</div>
        </div>

        <div class="modal-action flex-shrink-0">
          <button class="btn" type="button" @click="closeAiSearchDocument">{{ t('aiChatAgentSetup.buttons.close','Close') }}</button>
        </div>
      </div>
      <form method="dialog" class="modal-backdrop">
        <button @click="closeAiSearchDocument">close</button>
      </form>
    </dialog>
    </ThemeOnly>
  </div>
  </template>
  
  <script setup>
  import { ref, onMounted, onBeforeUnmount, reactive, computed, nextTick, watch } from 'vue';
  import { marked } from 'marked';
  import * as XLSX from 'xlsx';
  import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';
  import axios from '../api/axiosInstance';
  import router from '@/router';
  import { useRoute } from 'vue-router';
  import { globalState } from '@/globalState';
  import ThemeOnly from '@/components/ThemeOnly.vue';
  import ModernAIChatAgentSetup from '@/Themes/modern/views/AIChatAgentSetup.vue';
  import AdiWidget from '@/components/AdiWidget.vue';
  import { fetchUserData } from '@/api/accountsService';
  import FineTuneAgent from '../components/FineTuneAgent.vue';
  import FlowManager from '../components/FlowManager/FlowManager.vue';
  import InspectFile from '@/components/InspectFile.vue';
  import { useToast } from 'vue-toastification';
  import { v4 as uuidv4 } from 'uuid';
  import { Icon } from "@iconify/vue/dist/offline";
  import aiSearchIcon from '@/assets/icons/ai-search.svg';
  import FlowPreviewChat from '../components/FlowPreviewChat.vue'; 
  import AgentsToAgentsFlow from '../components/AgentsToAgentsFlow.vue';
  import LLMSelector from '../components/LLMSelector.vue';
  import AgentModeSelector from '../components/AgentModeSelector.vue';
  import AgentTypeSelector from '../components/AgentTypeSelector.vue';
  import AgentToneSelector from '../components/AgentToneSelector.vue';
  import AgentPersonalitySelector from '../components/AgentPersonalitySelector.vue';
  import ToolTip from "../components/Tooltip.vue";
  import { useI18n, applyTranslationsToDOM } from '@/i18n';
  import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
  import AgentLoadingModal from '@/components/AgentLoadingModal.vue';
  import DivisionSelect from '../components/DivisionSelect.vue';
  import AgentStyleSelect from '../components/AgentStyleSelect.vue';
  import WebsiteScrapeReport from '@/components/WebsiteScrapeReport.vue';
  import CreateAgentStyleModal from '../components/CreateAgentStyleModal.vue';
  import AdvancedTab from '../components/AdvancedTab.vue';
  import DeploymentChannelsOptionsSelector from '../components/DeploymentChannelsOptionsSelector.vue';
  import SecuritySettings from '../components/SecuritySettings.vue';
  import PhoneVoiceSelector from '../components/PhoneVoiceSelector.vue';
import TermsPackageSelector from '../components/TermsPackageSelector.vue';
import AgentSetupHumanInTheLoopTab from '../components/AgentSetupHumanInTheLoopTab.vue';
import CustomLanguageSelector from '../components/CustomLanguageSelector.vue';
import CustomToneSelector from '../components/CustomToneSelector.vue';
import VoiceSettingsOverlayModal from '../components/VoiceSettingsOverlayModal.vue';
import TranscriptionOptimizerSettings from '../components/TranscriptionOptimizerSettings.vue';
import VoiceCallLimitsFields from '../components/VoiceCallLimitsFields.vue';
import WebVoiceVisualSettings from '../components/WebVoiceVisualSettings.vue';
import OpenAgentChannelsModal from '@/shared/components/OpenAgentChannelsModal.vue';
import { defaultVoiceId, getVoiceOptionById, fetchVoices } from '../utils/voiceCatalog.js';
import { createDefaultWaitingFillerSentences, normalizeWaitingFillerSentences } from '../utils/waitingFillerDefaults.js';
import { geminiLanguageNames } from '../utils/geminiLanguages.js';
  import AvatarProvidersPanel from '../components/AvatarProvidersPanel.vue';
  import RuleLibraryPanel from '../components/RuleLibraryPanel.vue';
  import CustomDropdownSelector from '../components/CustomDropdownSelector.vue';
  import AppModal from '@/components/AppModal.vue';
  import MultiSelect from '@/components/MultiSelect.vue';
  import { buildOpenAgentChannels, getOpenAgentButtonLabel, normalizeOpenAgentPhoneDigits, normalizeOpenAgentTelValue, shouldOpenSingleAgentChannelDirectly } from '@/shared/utils/openAgentChannels.js';
  import { buildCollectionFilterOptions, getToolLogoUrl, inferToolCollectionId } from '@/shared/tools/toolCollections.js';
  
  
  const toast = useToast();
  const route = useRoute();
  const { t, currentLanguage } = useI18n();
  const aiAgentSetupRoot = ref(null);
  const agentSetupScreenMode = ref('ui');
  const agentSetupChatHasOpened = ref(false);
  const agentSetupAdiWidgetRef = ref(null);
  function setAgentSetupScreenMode(mode) {
    const nextMode = mode === 'chat' ? 'chat' : 'ui';
    agentSetupScreenMode.value = nextMode;
    if (nextMode === 'chat') {
      agentSetupChatHasOpened.value = true;
    }
  }
  function startAgentSetupNewChat() {
    if (agentSetupAdiWidgetRef.value !== null && typeof agentSetupAdiWidgetRef.value.startNewChat === 'function') {
      agentSetupAdiWidgetRef.value.startNewChat();
    }
  }
  function setAgentSetupMainScrollLocked(locked) {
    if (typeof document === 'undefined') {
      return;
    }
    const lockClassName = 'agent-setup-main-scroll-locked';
    const overflowValue = locked === true ? 'hidden' : '';
    document.body.style.overflow = overflowValue;
    if (locked === true) {
      document.body.classList.add(lockClassName);
    } else {
      document.body.classList.remove(lockClassName);
    }
    if (document.documentElement !== null) {
      document.documentElement.style.overflow = overflowValue;
      if (locked === true) {
        document.documentElement.classList.add(lockClassName);
      } else {
        document.documentElement.classList.remove(lockClassName);
      }
    }
    const bodyScrollElement = document.getElementById('body-scroll');
    if (bodyScrollElement !== null) {
      bodyScrollElement.style.overflow = overflowValue;
      bodyScrollElement.style.overflowY = overflowValue;
      bodyScrollElement.style.overflowX = locked === true ? 'hidden' : '';
      if (locked === true) {
        bodyScrollElement.classList.add(lockClassName);
      } else {
        bodyScrollElement.classList.remove(lockClassName);
      }
      if (locked === true) {
        bodyScrollElement.scrollTop = 0;
      }
    }
  }
  watch(agentSetupScreenMode, async mode => {
    await nextTick();
    setAgentSetupMainScrollLocked(mode === 'chat');
  }, { immediate: true });
  let isActive = true;
  onBeforeUnmount(() => {
    isActive = false;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
      document.body.style.overscrollBehavior = '';
      document.body.classList.remove('agent-setup-main-scroll-locked');
      if (document.documentElement !== null) {
        document.documentElement.style.overflow = '';
        document.documentElement.style.overscrollBehavior = '';
        document.documentElement.classList.remove('agent-setup-main-scroll-locked');
      }
      const bodyScrollElement = document.getElementById('body-scroll');
      if (bodyScrollElement !== null) {
        bodyScrollElement.style.overflow = '';
        bodyScrollElement.style.overflowY = '';
        bodyScrollElement.style.overflowX = '';
        bodyScrollElement.classList.remove('agent-setup-main-scroll-locked');
        bodyScrollElement.classList.remove('modal-page-scroll-locked');
      }
    }
  });
  const agentActive = ref(true);
  const agentName = ref('');
  const agentDescription = ref('');
  const maxAgentDescriptionTokens = 300000;
  const maxAgentDescriptionCharacters = maxAgentDescriptionTokens * 4;
  const selectedAgentTone = ref('default');
  const selectedAgentPersonality = ref('');
  const pendingPersonalitySelection = ref(null);
  const showPersonalityReplaceModal = ref(false);
  const agentShortDescription = ref('');
  const startingMessage = ref({});
  const agentLanguage = ref('auto');
  const organizationID = ref(null);
  const knowledgeBaseRef = ref(null);
  const currentPage = ref(1);
  const apiEndpoint = reactive(globalState.apiEndpoint);
  const isSuccessModalOpen = ref(false);
  const successMessage = ref('');
  const isErrorModalOpen = ref(false);
  const errorMessage = ref('');
  const rules = ref([]);
  const newRule = ref('');
  const tools = ref([]);
  const isEditMode = ref(false);
  const uploadedFiles = ref([]);
  const existingFiles = ref([]);
  const autoLlmModelId = 'auto-llm';
  const autoFastModeModelId = ref(null);
  const autoSuperFastModeModelId = ref(null);
  const autoVerificationModelId = ref(null);
  const selectedLLMModel = ref(null);
  const selectedVerificationLLMModel = ref('');
  const agentsToAgentsRouterLlmModelId = ref('');
  const showSubAgentConversationsInReports = ref(false);
  const availableLLMModels = ref([]);
  const orgPolicies = ref({});
  const subscriptionPolicyConfig = computed(() => {
    const rawUserInfo = globalState.userInfo;
    if (rawUserInfo === null || rawUserInfo === undefined) {
      return {};
    }
    const rawConfig = rawUserInfo.subscriptionConfig;
    if (typeof rawConfig !== 'object' || rawConfig === null) {
      return {};
    }
    if (typeof rawConfig.agentsSetup === 'object' && rawConfig.agentsSetup !== null) {
      return rawConfig.agentsSetup;
    }
    return rawConfig;
  });
  const fieldPolicyAliases = {
    'llm-selector-div': ['llm-selector-div', 'llmModelId'],
    'embedding-selector-div': ['embedding-selector-div', 'embeddingSelector']
  };
  function mergePolicyValue(baseValue, overrideValue) {
    if (Array.isArray(overrideValue) === true) {
      return [...overrideValue];
    }
    if (typeof overrideValue !== 'object' || overrideValue === null) {
      if (overrideValue !== undefined) {
        return overrideValue;
      }
      if (Array.isArray(baseValue) === true) {
        return [...baseValue];
      }
      return baseValue;
    }
    const output = typeof baseValue === 'object' && baseValue !== null && Array.isArray(baseValue) === false ? { ...baseValue } : {};
    const keys = Object.keys(overrideValue);
    for (const key of keys) {
      output[key] = mergePolicyValue(output[key], overrideValue[key]);
    }
    return output;
  }
  const effectiveOrgPolicies = computed(() => {
    const subscriptionPolicies = typeof subscriptionPolicyConfig.value === 'object' && subscriptionPolicyConfig.value !== null ? subscriptionPolicyConfig.value : {};
    const organizationPolicies = typeof orgPolicies.value === 'object' && orgPolicies.value !== null ? orgPolicies.value : {};
    return mergePolicyValue(subscriptionPolicies, organizationPolicies);
  });
  function getFieldPolicyValue(policyType, fieldId) {
    const policyGroup = effectiveOrgPolicies.value?.features?.[policyType];
    if (typeof policyGroup !== 'object' || policyGroup === null) {
      return null;
    }
    const aliases = Array.isArray(fieldPolicyAliases[fieldId]) === true ? fieldPolicyAliases[fieldId] : [fieldId];
    for (const alias of aliases) {
      if (Object.prototype.hasOwnProperty.call(policyGroup, alias) === true) {
        return policyGroup[alias];
      }
    }
    return null;
  }
  const showAllFields = computed(() => {
    const queryValue = Array.isArray(route.query.showallfields) === true ? route.query.showallfields[0] : route.query.showallfields;
    return queryValue === 'true';
  });
  function isFieldHidden(fieldId) {
    return showAllFields.value !== true && getFieldPolicyValue('hide', fieldId) === true;
  }
  function getLockedFieldValue(fieldId) {
    if (showAllFields.value === true) {
      return null;
    }
    const value = getFieldPolicyValue('lock', fieldId);
    return typeof value === 'string' && value !== '' ? value : null;
  }
  const llmLockedId = computed(() => {
    return getLockedFieldValue('llm-selector-div');
  });
  const hideEmbeddingSelector = computed(() => isFieldHidden('embedding-selector-div'));
  const enableFeedbackButtons = ref(true);
  const enableSuggestedResponses = ref(false);
  const enableStreamingText = ref(true);
  const enableUserFineTune = ref(false);
    const whatsappClosingMessage = ref('');
  const selectedDeploymentChannels = ref([]);
  const selectedWhatsappSourceId = ref(null);
  const whatsappDisplayNumber = ref('');
  const selectedPhoneSourceId = ref(null);
  const phoneDisplayNumber = ref('');
  const showOpenAgentModal = ref(false);
  const availableOpenAgentChannels = computed(() => buildOpenAgentChannels({
    selectedChannels: selectedDeploymentChannels.value,
    whatsappNumber: whatsappDisplayNumber.value,
    phoneNumber: phoneDisplayNumber.value,
    t
  }));
  const openAgentButtonLabel = computed(() => getOpenAgentButtonLabel(availableOpenAgentChannels.value, t));
  function getApprovedDeploymentChannels(featuresValue) {
    const features = typeof featuresValue === 'object' && featuresValue !== null ? featuresValue : {};
    const approved = [];
    if (features.voiceWeb === true) {
      approved.push('Web Voice Agent');
    }
    if (features.whatsapp === true) {
      approved.push('Whatsapp');
    }
    if (features.whatsappVoice === true) {
      approved.push('Whatsapp Voice');
    }
    if (features.telegram === true) {
      approved.push('Telegram');
    }
    if (features.telegramVoice === true) {
      approved.push('Telegram Voice');
    }
    if (features.phoneAgent === true) {
      approved.push('Phone');
    }
    if (features.email === true) {
      approved.push('Email');
    }
    return approved;
  }
  const allDeploymentChannels = ['Web Voice Agent', 'Whatsapp', 'Whatsapp Voice', 'Telegram', 'Telegram Voice', 'Phone', 'Email'];
  const enabledDeploymentChannels = computed(() => {
    const subscription = typeof globalState.userInfo?.subscription === 'string' ? globalState.userInfo.subscription.trim().toLowerCase() : '';
    if (subscription === 'enterprise') {
      return allDeploymentChannels;
    }
    return Array.isArray(globalState.orgSettings?.allowedSources) === true ? globalState.orgSettings.allowedSources : getApprovedDeploymentChannels(effectiveOrgPolicies.value?.features);
  });
  function sanitizeDeploymentChannels(channelsValue) {
  const current = Array.isArray(channelsValue) === true ? channelsValue.filter(channel => typeof channel === 'string' && channel.length > 0 && channel !== 'Web') : [];
    if (current.length > 0) {
    return Array.from(new Set(current));
    }
    return [];
  }
  function applyDeploymentChannelApprovals() {
    selectedDeploymentChannels.value = sanitizeDeploymentChannels(selectedDeploymentChannels.value);
  const hasWhatsappSelected = Array.isArray(selectedDeploymentChannels.value) === true && (selectedDeploymentChannels.value.includes('Whatsapp') === true || selectedDeploymentChannels.value.includes('Whatsapp Voice') === true);
  const whatsappChannelEnabled = enabledDeploymentChannels.value.includes('Whatsapp') === true || enabledDeploymentChannels.value.includes('Whatsapp Voice') === true;
  if (whatsappChannelEnabled !== true && hasWhatsappSelected !== true) {
      selectedWhatsappSourceId.value = null;
      whatsappDisplayNumber.value = '';
    }
  const hasPhoneSelected = Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.includes('Phone') === true;
  if (enabledDeploymentChannels.value.includes('Phone') !== true && hasPhoneSelected !== true) {
      selectedPhoneSourceId.value = null;
      phoneDisplayNumber.value = '';
    }
  }
  const agentType = ref('PublicAgent');
  const retrieveUserLocation = ref(false);
  const enableFileUploadInChat = ref(false); 
  const makeLinksClickable = ref(true);
  const clearSessionOnRefresh = ref(false);
  const enabledSavedSessionHistory = ref(false);
  const regularOpenAiContentFilterFailoverEnabled = ref(false);
  const defaultLimitReachedMessageTemplate = 'You have reached the maximum messages for this conversation. Start a new chat to continue.';
  const showMap = ref(true);
  const limitReachedMessage = ref(defaultLimitReachedMessageTemplate);
  const messageLimitPerConversation = ref(0);
  const messageLimitPerIP = ref(0);
  const messageLimitPerIPDurationMinutes = ref(3);
  const messageLimitPerPhoneNumber = ref(0);
  const messageLimitPerPhoneNumberDurationMinutes = ref(3);
  const limitMessagesPerConversationEnabled = ref(false);
  const limitMessagesPerIPEnabled = ref(false);
  const enablePIIRedaction = ref(false);
  const redactEmails = ref(true);
  const redactPhoneNumbers = ref(false);
  const customBlockRegex = ref([]);
  const responseFilters = ref([]);
  const kbToolUseMode = ref('auto');
  const retrieverKValue = ref(10);
  const vectorDbQueriesLimit = ref(3);
  const vectorQueryAdditionalText = ref('');
  const prependHistoryToShortVectorQueries = ref(false);
  const retrievalStrategy = ref('standard');
  const maxOutputTokens = ref(2048);
  const actionButtons = ref([]);
  const verifyAnswerEnabled = ref(false);
  const verifyLinks = ref(true);
  const groqGptOssFirstAnswerReasoningEffort = ref('medium');
  const groqGptOssVerifyReasoningEffort = ref('medium');
  const selectedVectorDBId = ref('');
  const selectedEmbeddingModelId = ref('');
  const defaultAuthenticatorWelcomeTemplate = 'Welcome! Please sign in at {{authURL}} to continue.';
  const defaultAuthenticatorRetryTemplate = 'You must login via {{authURL}} to continue.';
  const defaultAuthenticatorSuccessTemplate = 'You are signed in. Lets talk.';
  const authenticatorEnabled = ref(false);
  const authenticatorProvider = ref('google');
  const authenticatorWelcomeMessage = ref(defaultAuthenticatorWelcomeTemplate);
  const authenticatorRetryMessage = ref(defaultAuthenticatorRetryTemplate);
  const authenticatorSuccessMessage = ref(defaultAuthenticatorSuccessTemplate);
  const authenticatorRunAiToolOnLogin = ref(false);
  const voiceAgentExtraPrompt = ref('');
  const voiceAllowAgentHangup = ref(false);
  const voiceAllowCallTransfer = ref(false);
  const defaultVoicePromptKnowledgeBaseLoadStatement = 'Load these files only when the caller asks about facts, policies, prices, scripts, requirements, or flow details that are not in the main prompt. Do not load them for greetings, small talk, unclear audio, or information already available in the main prompt.';
  const voicePromptKnowledgeBaseEnabled = ref(false);
  const voicePromptKnowledgeBaseFileNames = ref([]);
  const voicePromptKnowledgeBaseLoadStatement = ref(defaultVoicePromptKnowledgeBaseLoadStatement);
  const voiceAgentProvider = ref('openai-realtime');
  const voiceAgentVoice = ref('alloy');
  const voiceProviders = [
    { id: 'openai-realtime', name: 'OpenAI GPT Real Time' },
    { id: 'openai-mini', name: 'OpenAI GPT Mini' },
    { id: 'gemini-live-flash', name: 'Gemini Live Flash' },
    { id: 'gemini-live-pro', name: 'Gemini Live Pro' }
  ];
  const availableVoicesByProvider = {
    'openai-realtime': ['alloy','verse','aria','sage'],
    'openai-mini': ['alloy','verse','aria','sage'],
    'gemini-live-flash': ['classic','bright','warm'],
    'gemini-live-pro': ['classic','bright','warm']
  };
  const defaultVoicePacing = 1.05;
  const defaultVoiceDurationStretch = 1;
  const defaultVoiceUseDashPauses = true;
  const defaultVoicePauseEveryWords = 0;
  const defaultVoiceDashPauseMs = 180;
  const defaultVoiceCommaPauseMs = 120;
  const defaultVoicePeriodPauseMs = 260;
  const defaultVoiceSettingsDefaultEngineVersion = 'v3';
  const voiceSettingsDefaultEngineModelOptions = [
    {
      value: 'v1',
      label: 'V1'
    },
    {
      value: 'v2',
      label: 'V2'
    },
    {
      value: 'v3',
      label: 'V3'
    },
    {
      value: 'v3Flash',
      label: 'V3 Flash'
    },
    {
      value: 'v3Duplex',
      label: 'V3 Duplex'
    },
    {
      value: 'v5',
      label: 'V5'
    }
  ];
  const voiceSettingsEngineVersionSelectorOptions = computed(() => [
    {
      id: 'v1',
      displayName: t('aiChatAgentSetup.voiceSettings.engineVersionV1', 'V1'),
      description: t('aiChatAgentSetup.voiceSettings.engineVersionV1Description', 'A proven voice engine with standard response speed.'),
      icon: 'tabler:rocket'
    },
    {
      id: 'v2',
      displayName: t('aiChatAgentSetup.voiceSettings.engineVersionV2', 'V2'),
      description: t('aiChatAgentSetup.voiceSettings.engineVersionV2Description', 'Improved speech recognition with configurable reasoning.'),
      icon: 'tabler:sparkles'
    },
    {
      id: 'v3',
      displayName: t('aiChatAgentSetup.voiceSettings.engineVersionV3', 'V3 (Recommended)'),
      description: t('aiChatAgentSetup.voiceSettings.engineVersionV3Description', 'Realtime voice engine with the best balance of speed, quality, and reliability.'),
      icon: 'tabler:bolt'
    },
    {
      id: 'v3Flash',
      displayName: t('aiChatAgentSetup.voiceSettings.engineVersionV3Flash', 'V3 Flash'),
      description: t('aiChatAgentSetup.voiceSettings.engineVersionV3FlashDescription', 'Realtime voice engine optimized for fast natural conversations.'),
      icon: 'tabler:bolt'
    },
    {
      id: 'v3Duplex',
      displayName: t('aiChatAgentSetup.voiceSettings.engineVersionV3Duplex', 'V3 Duplex'),
      description: t('aiChatAgentSetup.voiceSettings.engineVersionV3DuplexDescription', 'Full-duplex GPT-Live voice that listens and speaks at the same time.'),
      icon: 'tabler:arrows-exchange'
    },
    {
      id: 'v5',
      displayName: t('aiChatAgentSetup.voiceSettings.engineVersionV5', 'V5'),
      description: t('aiChatAgentSetup.voiceSettings.engineVersionV5Description', 'Next-generation live voice with fast, natural full-duplex conversations.'),
      icon: 'tabler:wave-sine'
    }
  ]);
  function selectVoiceSettingsEngineVersion(version) {
    voiceSettingsDefaultEngineVersion.value = normalizeVoiceSettingsDefaultEngineVersion(version);
    voiceSettingsVoiceType.value = voiceSettingsDefaultEngineVersion.value === 'v5' ? 'geminiLive' : 'vtv';
    if (voiceSettingsDefaultEngineVersion.value === 'v3' || voiceSettingsDefaultEngineVersion.value === 'v3Flash' || voiceSettingsDefaultEngineVersion.value === 'v3Duplex' || voiceSettingsDefaultEngineVersion.value === 'v5') {
      voiceSettingsReasoningLevel.value = 'none';
    }
  }
  const defaultPromptToVoicePrompt = "# Audio Profile\nA helpful and professional personal assistant.\n\n# Director's note\nStyle: Clear, warm, natural, and phone-friendly. Pace: Natural. Accent: Match the selected language.\n\n## Scene:\nA quiet, professional remote workspace.\n\n## Sample Context:\nSteady, efficient, and unhurried. Tone is empathetic, crisp, and reassuring.";
  const selectedPhoneVoiceId = ref(defaultVoiceId);
  const geminiLiveVoiceIds = ['Achird', 'Charon', 'Schedar', 'Kore', 'Aoede', 'Callirrhoe'];
  const voiceSettingsVoiceType = ref('vtv');
  const voiceSettingsDefaultEngineVersion = ref(defaultVoiceSettingsDefaultEngineVersion);
  const voiceSettingsVadEngine = ref('tiger');
  const voiceSettingsReasoningLevel = ref('none');
  const voiceSettingsVoiceId = ref(defaultVoiceId);
  const voiceSettingsAccent = ref('Israeli');
  const voiceSettingsLanguage = ref('English');
  const voiceSettingsSpeed = ref(1);
  const voiceSettingsCustomPacingEnabled = ref(false);
  const voiceSettingsPacing = ref(defaultVoicePacing);
  const voiceSettingsDurationStretch = ref(defaultVoiceDurationStretch);
  const voiceSettingsUseDashPauses = ref(defaultVoiceUseDashPauses);
  const voiceSettingsPauseEveryWords = ref(defaultVoicePauseEveryWords);
  const voiceSettingsDashPauseMs = ref(defaultVoiceDashPauseMs);
  const voiceSettingsCommaPauseMs = ref(defaultVoiceCommaPauseMs);
  const voiceSettingsPeriodPauseMs = ref(defaultVoicePeriodPauseMs);
  const voiceSettingsIdleAutoResponseEnabled = ref(true);
  const voiceSettingsStartingMessageIdleAutoResponseSeconds = ref(6);
  const voiceSettingsIdleAutoResponseSeconds = ref(3);
  const voiceSettingsToolUseAutoResponseSeconds = ref(2);
  const voiceSettingsMaxCallMinutes = ref(19);
  const voiceSettingsMaxCallerMinutes = ref(19);
  const voiceSettingsCallerCooldownMinutes = ref(60);
  const webVoiceVisualSettings = ref({ enabled: false, items: [] });
  const isWebVoiceVisualSettingsOpen = ref(false);
  const voiceSettingsPromptToVoiceEnabled = ref(false);
  const voiceSettingsPromptToVoicePrompt = ref(defaultPromptToVoicePrompt);
  const voiceSettingsTone = ref('Neutral');
  const voiceSettingsMode = ref('interruptible');
  const voiceSettingsCancellingMode = ref('fast');
  const voiceSettingsAudioProcessorEngine = ref('quail');
  const voiceSettingsTermsPackages = ref([]);
  const voiceSettingsSonioxTerms = ref([]);
  const voiceSettingsFillerSentences = ref(createDefaultWaitingFillerSentences());
  const voiceSettingsQuickResponseFillerEnabled = ref(false);
  const voiceSettingsQuickResponseFillerText = ref('');
  const voiceSettingsTranscriptionOptimizerEnabled = ref(false);
  const voiceSettingsTranscriptionOptimizerTerms = ref([]);
  const transcriptionOptimizerSourcePrompt = computed(() => {
    const localizedStartingMessages = startingMessage.value !== null && typeof startingMessage.value === 'object'
      ? Object.values(startingMessage.value).filter((value) => typeof value === 'string' && value.trim() !== '')
      : [];
    return [
      agentDescription.value,
      voiceAgentExtraPrompt.value,
      ...localizedStartingMessages
    ].filter((value) => typeof value === 'string' && value.trim() !== '').join('\n\n');
  });
  const selectedVoiceSettingsFillerLanguage = ref('he');
  const voiceSettingsFillerLanguageOptions = computed(() => [
    { code: 'he', name: t('aiChatAgentSetup.agentInformation.languageOptions.he', 'עברית') },
    { code: 'en', name: t('aiChatAgentSetup.agentInformation.languageOptions.en', 'English') },
    { code: 'es', name: t('aiChatAgentSetup.agentInformation.languageOptions.es', 'Spanish') },
    { code: 'it', name: t('aiChatAgentSetup.agentInformation.languageOptions.it', 'Italian') },
    { code: 'ar', name: t('aiChatAgentSetup.agentInformation.languageOptions.ar', 'Arabic') }
  ]);
  const currentVoiceSettingsToolFillers = computed(() => {
    const normalized = normalizeWaitingFillerSentences(voiceSettingsFillerSentences.value);
    const language = normalized[selectedVoiceSettingsFillerLanguage.value];
    if (language === undefined || language === null || typeof language !== 'object') {
      return [];
    }
    return Array.isArray(language.toolUse) === true ? language.toolUse : [];
  });
  const voiceSettingsFillerInputDirection = computed(() => selectedVoiceSettingsFillerLanguage.value === 'he' || selectedVoiceSettingsFillerLanguage.value === 'ar' ? 'rtl' : 'ltr');
  function ensureVoiceSettingsFillerLanguage() {
    const normalized = normalizeWaitingFillerSentences(voiceSettingsFillerSentences.value);
    const language = selectedVoiceSettingsFillerLanguage.value;
    if (normalized[language] === undefined || normalized[language] === null || typeof normalized[language] !== 'object') {
      normalized[language] = { standard: [], toolUse: [] };
    }
    if (Array.isArray(normalized[language].toolUse) !== true) {
      normalized[language].toolUse = [];
    }
    return normalized;
  }
  function updateVoiceSettingsToolFiller(index, value) {
    const normalized = ensureVoiceSettingsFillerLanguage();
    const language = selectedVoiceSettingsFillerLanguage.value;
    const sentences = normalized[language].toolUse.slice();
    if (index < 0 || index >= sentences.length) {
      return;
    }
    sentences[index] = typeof value === 'string' ? value : '';
    normalized[language].toolUse = sentences;
    voiceSettingsFillerSentences.value = normalized;
  }
  function deleteVoiceSettingsToolFiller(index) {
    const normalized = ensureVoiceSettingsFillerLanguage();
    const language = selectedVoiceSettingsFillerLanguage.value;
    const sentences = normalized[language].toolUse.slice();
    if (index < 0 || index >= sentences.length) {
      return;
    }
    sentences.splice(index, 1);
    normalized[language].toolUse = sentences;
    voiceSettingsFillerSentences.value = normalized;
  }
  function addVoiceSettingsToolFiller() {
    const normalized = ensureVoiceSettingsFillerLanguage();
    const language = selectedVoiceSettingsFillerLanguage.value;
    normalized[language].toolUse = normalized[language].toolUse.concat('');
    voiceSettingsFillerSentences.value = normalized;
  }
  const humanInTheLoopEnabled = ref(false);
  const humanInTheLoopStartConversationWebhook = ref('');
  const humanInTheLoopNewMessageWebhook = ref('');
  const humanInTheLoopInboundSecret = ref('');
  const humanInTheLoopHumanTransferMessage = ref({});
  const humanInTheLoopAiBackMessage = ref({});
  const voiceLanguageOptions = geminiLanguageNames;

  function normalizeVoicePromptKnowledgeBaseFileNames(value) {
    const rawItems = Array.isArray(value) === true ? value : [];
    const names = [];
    for (let i = 0; i < rawItems.length; i = i + 1) {
      const name = typeof rawItems[i] === 'string' ? rawItems[i].trim() : '';
      if (name === '') {
        continue;
      }
      if (names.includes(name) === true) {
        continue;
      }
      names.push(name);
    }
    return names;
  }

  function isPromptKnowledgeBaseEligibleFile(file) {
    if (file === undefined) { return false; }
    if (file === null) { return false; }
    if (typeof file !== 'object') { return false; }
    const status = typeof file.status === 'string' ? file.status.trim().toLowerCase() : '';
    if (status === 'deleted') { return false; }
    const fileName = typeof file.fileName === 'string' ? file.fileName.trim() : '';
    if (fileName === '') { return false; }
    const fileType = typeof file.fileType === 'string' ? file.fileType.trim().toLowerCase() : '';
    if (fileType === 'finetuneanswer') { return false; }
    return true;
  }

  function getKnowledgeFileTypeLabel(fileType) {
    const value = typeof fileType === 'string' ? fileType.trim().toLowerCase() : '';
    if (value === 'websiteurl') { return t('aiChatAgentSetup.knowledgeBase.fileType.websiteurl', 'Website URL'); }
    if (value === 'website') { return t('aiChatAgentSetup.knowledgeBase.fileType.website', 'Website'); }
    if (value === 'websitepage') { return t('aiChatAgentSetup.knowledgeBase.fileType.websitepage', 'Website Page'); }
    if (value === 'text') { return t('aiChatAgentSetup.knowledgeBase.fileType.text', 'Text'); }
    if (value === '') { return t('aiChatAgentSetup.knowledgeBase.fileType.file', 'File'); }
    const directKey = `aiChatAgentSetup.knowledgeBase.fileType.${value}`;
    const fallback = value.toUpperCase();
    return t(directKey, fallback);
  }

  const promptKnowledgeBaseFileOptions = computed(() => {
    const options = [];
    const seen = new Set();
    const sources = [
      ...(Array.isArray(existingFiles.value) === true ? existingFiles.value : []),
      ...(Array.isArray(uploadedFiles.value) === true ? uploadedFiles.value : [])
    ];
    for (let i = 0; i < sources.length; i = i + 1) {
      const file = sources[i];
      if (isPromptKnowledgeBaseEligibleFile(file) !== true) {
        continue;
      }
      const fileName = typeof file.fileName === 'string' ? file.fileName.trim() : '';
      if (seen.has(fileName) === true) {
        continue;
      }
      seen.add(fileName);
      options.push(file);
    }
    return options;
  });

  function isVoicePromptKnowledgeBaseFileSelected(fileName) {
    const name = typeof fileName === 'string' ? fileName.trim() : '';
    if (name === '') { return false; }
    return voicePromptKnowledgeBaseFileNames.value.includes(name) === true;
  }

  function toggleVoicePromptKnowledgeBaseFile(fileName) {
    const name = typeof fileName === 'string' ? fileName.trim() : '';
    if (name === '') { return; }
    const next = normalizeVoicePromptKnowledgeBaseFileNames(voicePromptKnowledgeBaseFileNames.value);
    const index = next.indexOf(name);
    if (index >= 0) {
      next.splice(index, 1);
    } else {
      next.push(name);
    }
    voicePromptKnowledgeBaseFileNames.value = next;
  }

  function toggleCachedKnowledgeDataFile(fileName) {
    const name = typeof fileName === 'string' ? fileName.trim() : '';
    if (name === '') { return; }
    const next = normalizeVoicePromptKnowledgeBaseFileNames(voicePromptKnowledgeBaseFileNames.value);
    const index = next.indexOf(name);
    if (index >= 0) {
      next.splice(index, 1);
    } else {
      next.push(name);
    }
    voicePromptKnowledgeBaseFileNames.value = next;
    voicePromptKnowledgeBaseEnabled.value = next.length > 0;
  }

  function pruneVoicePromptKnowledgeBaseFileNames() {
    const allowed = promptKnowledgeBaseFileOptions.value.map(file => file.fileName);
    const current = normalizeVoicePromptKnowledgeBaseFileNames(voicePromptKnowledgeBaseFileNames.value);
    const next = [];
    for (let i = 0; i < current.length; i = i + 1) {
      if (allowed.includes(current[i]) === true) {
        next.push(current[i]);
      }
    }
    voicePromptKnowledgeBaseFileNames.value = next;
  }
  const voiceToneOptions = ['Neutral','Friendly','Formal','Enthusiastic','Calm'];
  const voiceModeOptions = [
    { id: 'interruptible', label: 'Talk and interrupt' },
    { id: 'wait full', label: 'Wait for full answer' }
  ];
  const voiceCancellingModeOptions = [
    { id: 'fast', label: 'Fast Mode' },
    { id: 'quality', label: 'Quality Mode' }
  ];
  const voiceAudioProcessorEngineOptions = [
    { id: 'quail', label: 'Quail VF' },
    { id: 'internal', label: 'Genovise' },
    { id: 'external', label: 'External engine' }
  ];
  const voiceReasoningLevelSelectorOptions = computed(() => [
    {
      id: 'none',
      displayName: t('aiChatAgentSetup.voiceSettings.reasoningOptions.none', 'None'),
      description: t('aiChatAgentSetup.voiceSettings.reasoningOptions.noneDescription', 'Respond immediately without extended reasoning.'),
      icon: 'tabler:circle-dotted'
    },
    {
      id: 'high',
      displayName: t('aiChatAgentSetup.voiceSettings.reasoningOptions.high', 'High'),
      description: t('aiChatAgentSetup.voiceSettings.reasoningOptions.highDescription', 'Uses deeper reasoning before responding. May increase response time.'),
      icon: 'tabler:brain'
    }
  ]);
  const voiceSettingsVoiceTypeLabel = computed(() => {
    const value = normalizeVoiceSettingsVoiceType(voiceSettingsVoiceType.value);
    if (value === 'geminiLive') {
      return t('aiChatAgentSetup.voiceSettings.voiceTypeGeminiLive', 'Gemini Live');
    }
    if (value === 'layer') {
      return t('aiChatAgentSetup.voiceSettings.voiceTypeGenovise', 'Genovise');
    }
    if (value === 'hybrid') {
      return t('aiChatAgentSetup.voiceSettings.voiceTypeHybrid', 'Hybrid');
    }
    return t('aiChatAgentSetup.voiceSettings.voiceTypeVtv', 'VTV');
  });
  const showVoiceSettingsModal = ref(false);
  const forceShowVoiceTab = ref(false);
  const hasInitializedPhoneSelection = ref(false);
  function normalizeVoiceSettingsBoolean(value, fallback) {
    if (value === true) {
      return true;
    }
    if (value === false) {
      return false;
    }
    if (typeof value === 'string') {
      const lowered = value.trim().toLowerCase();
      if (lowered === '1' || lowered === 'true' || lowered === 'yes' || lowered === 'on') {
        return true;
      }
      if (lowered === '0' || lowered === 'false' || lowered === 'no' || lowered === 'off') {
        return false;
      }
    }
    return fallback === true;
  }
  function normalizeVoiceSettingsNumber(value, fallback) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) === true) {
      return parsed;
    }
    return fallback;
  }
  function normalizeVoiceSettingsCancellingMode(value) {
    if (typeof value !== 'string') {
      return 'fast';
    }
    const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (normalized === 'quality') {
      return 'quality';
    }
    if (normalized === 'qualitymode') {
      return 'quality';
    }
    return 'fast';
  }
  function normalizeVoiceSettingsAudioProcessorEngine(value) {
    if (typeof value !== 'string') {
      return 'quail';
    }
    const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (normalized === 'quail' || normalized === 'quailvf' || normalized === 'aic' || normalized === 'aicoustics') {
      return 'quail';
    }
    if (normalized === 'internal' || normalized === 'internalengine' || normalized === 'genovise' || normalized === 'genoviseengine') {
      return 'internal';
    }
    if (normalized === 'external' || normalized === 'externalengine' || normalized === 'krisp' || normalized === 'krispvoicefocus') {
      return 'external';
    }
    return 'quail';
  }
  function normalizeVoiceSettingsVoiceType(value) {
    if (typeof value !== 'string') {
      return 'vtv';
    }
    const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (['geminilive', 'geminirealtime', 'lite'].includes(normalized) === true) {
      return 'geminiLive';
    }
    if (normalized === 'hybrid') {
      return 'hybrid';
    }
    if (normalized === 'vtvwithtts') {
      return 'hybrid';
    }
    if (normalized === 'realtimewithtts') {
      return 'hybrid';
    }
    if (normalized === 'genovise') {
      return 'layer';
    }
    if (normalized === 'layer') {
      return 'layer';
    }
    if (normalized === 'layers') {
      return 'layer';
    }
    if (normalized === 'vllm') {
      return 'layer';
    }
    if (normalized === 'sttllmtts') {
      return 'layer';
    }
    if (normalized === 'realtimewithcustomsttopenai') {
      return 'layer';
    }
    if (normalized === 'openairealtimecustomstt') {
      return 'layer';
    }
    if (normalized === 'realtimewithcustomsttdefault') {
      return 'layer';
    }
    return 'vtv';
  }
  function normalizeVoiceSettingsVadEngine(value) {
    if (typeof value !== 'string') {
      return 'tiger';
    }
    const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (normalized === 'flyingbird') {
      return 'flyingBird';
    }
    return 'tiger';
  }
  function normalizeVoiceReasoningLevel(value) {
    if (typeof value !== 'string') {
      return 'none';
    }
    const normalized = value.trim().toLowerCase();
    if (normalized === 'high') {
      return 'high';
    }
    return 'none';
  }
  function normalizeVoiceSettingsDefaultEngineVersion(value) {
    if (typeof value !== 'string') {
      return defaultVoiceSettingsDefaultEngineVersion;
    }
    const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
    if (normalized === 'v1' || normalized === '1') {
      return 'v1';
    }
    if (normalized === 'v2' || normalized === '2') {
      return 'v2';
    }
    if (normalized === 'v3' || normalized === '3') {
      return 'v3';
    }
    if (normalized === 'v3flash' || normalized === '3flash') {
      return 'v3Flash';
    }
    if (normalized === 'v3duplex' || normalized === '3duplex' || normalized === 'duplex') {
      return 'v3Duplex';
    }
    if (normalized === 'v5' || normalized === '5') {
      return 'v5';
    }
    return defaultVoiceSettingsDefaultEngineVersion;
  }
  function resolveVoiceSettingsDefaultEngineVersionFromSettings(settings) {
    const source = settings !== undefined && settings !== null && typeof settings === 'object' ? settings : {};
    const candidates = [
      source.defaultEngineVersion,
      source.defaultModeVersion
    ];
    for (let index = 0; index < candidates.length; index = index + 1) {
      const candidate = candidates[index];
      if (typeof candidate !== 'string') {
        continue;
      }
      const trimmed = candidate.trim().toLowerCase();
      if (trimmed.length === 0) {
        continue;
      }
      if (trimmed === 'v1') {
        return 'v1';
      }
      if (trimmed === 'v2') {
        return 'v2';
      }
      if (trimmed === 'v3') {
        return 'v3';
      }
      if (trimmed === 'v3flash' || trimmed === 'v3 flash') {
        return 'v3Flash';
      }
      if (trimmed === 'v3duplex' || trimmed === 'v3 duplex' || trimmed === 'duplex') {
        return 'v3Duplex';
      }
      if (trimmed === 'v5' || trimmed === '5') {
        return 'v5';
      }
    }
    return defaultVoiceSettingsDefaultEngineVersion;
  }
  function defaultAvatarProviderConfig(providerId) {
    if (providerId === 'did') {
      return {
        presenterId: '',
        presenterName: '',
        previewUrl: ''
      };
    }
    return {};
  }
  const selectedAvatarProvider = ref('did');
  const avatarProviderConfigs = ref({
    did: defaultAvatarProviderConfig('did')
  });
  const showWebsiteLinkModal = ref(false);
  const showWebsiteScrapeModal = ref(false);
  const showScrapeReportModal = ref(false);
  const scrapeReportSource = ref(null);
  const websiteLink = ref('');
  const websiteToScrape = ref('');
  const maxPages = ref('');
  const showWebsiteBatchModal = ref(false);
  const websiteBatchText = ref('');
  const showChangeSelectedWebsiteDomainModal = ref(false);
  const selectedWebsiteNewDomain = ref('');
  const rescrapeEnabled = ref(false);
  const rescrapeIntervalDays = ref('');
  const rescrapeAtHour = ref('');
  const editingRuleIndex = ref(null);
  const editingRuleInput = ref(null);
  const showTextInputModal = ref(false);
  const textFileName = ref('');
  const textContent = ref('');
  const showTextAdvancedOptions = ref(false);
  const textRegexSchemaInput = ref('');
  const textRegexSchemas = ref([]);
  const isTextTranscribing = ref(false);
  const textTranscriptionInterim = ref('');
  let textTranscriptionRecognition = null;
  let textTranscriptionShouldRestart = false;
  const searchQuery = ref('');
  const isAiSearchModalOpen = ref(false);
  const aiSearchQuery = ref('');
  const aiSearchResults = ref([]);
  const aiSearchLoading = ref(false);
  const aiSearchError = ref('');
  const aiSearchHasSearched = ref(false);
  const isAiSearchDocumentModalOpen = ref(false);
  const aiSearchDocumentTitle = ref('');
  const aiSearchDocumentSource = ref('');
  const aiSearchDocumentContent = ref('');
  const aiSearchDocumentIsFull = ref(false);

  marked.setOptions({ gfm: true, breaks: true });

  const aiSearchContextMessage = computed(() => {
    const hasSavedAgent = typeof route.query.edit === 'string' && route.query.edit.trim().length > 0;
    if (hasSavedAgent === true) {
      return t('aiChatAgentSetup.modals.aiSearch.description', 'Searches saved knowledge semantically and includes pending local text when available.');
    }
    return t('aiChatAgentSetup.modals.aiSearch.unsavedDescription', 'Searches the files currently loaded in this page. Save the agent to search indexed knowledge.');
  });

  function openAiSearch() {
    aiSearchQuery.value = searchQuery.value.trim();
    aiSearchResults.value = [];
    aiSearchError.value = '';
    aiSearchHasSearched.value = false;
    isAiSearchModalOpen.value = true;
  }

  function getTextTranscriptionConstructor() {
    if (typeof window === 'undefined') {
      return null;
    }
    if (window.SpeechRecognition !== undefined && window.SpeechRecognition !== null) {
      return window.SpeechRecognition;
    }
    if (window.webkitSpeechRecognition !== undefined && window.webkitSpeechRecognition !== null) {
      return window.webkitSpeechRecognition;
    }
    return null;
  }

  function getTextTranscriptionLanguage() {
    const selected = typeof agentLanguage.value === 'string' ? agentLanguage.value.trim() : '';
    const fallback = typeof currentLanguage.value === 'string' ? currentLanguage.value.trim() : '';
    const language = selected !== '' && selected !== 'auto' && selected !== 'default' ? selected : fallback;
    if (language === 'he') { return 'he-IL'; }
    if (language === 'ar') { return 'ar-SA'; }
    if (language === 'es') { return 'es-ES'; }
    if (language === 'es-AR') { return 'es-AR'; }
    if (language === 'fr') { return 'fr-FR'; }
    if (language === 'de') { return 'de-DE'; }
    if (language === 'zh') { return 'zh-CN'; }
    if (language === 'ja') { return 'ja-JP'; }
    if (language === 'ru') { return 'ru-RU'; }
    if (language === 'pt') { return 'pt-PT'; }
    if (language === 'it') { return 'it-IT'; }
    if (language === 'ko') { return 'ko-KR'; }
    if (language === 'hi') { return 'hi-IN'; }
    if (language === 'sv') { return 'sv-SE'; }
    if (language === 'nl') { return 'nl-NL'; }
    if (language === 'da') { return 'da-DK'; }
    if (language === 'fi') { return 'fi-FI'; }
    if (language === 'no') { return 'nb-NO'; }
    if (language === 'tr') { return 'tr-TR'; }
    if (language === 'pl') { return 'pl-PL'; }
    return 'en-US';
  }

  function appendTextTranscription(value) {
    const transcript = typeof value === 'string' ? value.trim() : '';
    if (transcript === '') {
      return;
    }
    const current = typeof textContent.value === 'string' ? textContent.value : '';
    const separator = current.trim().length > 0 ? ' ' : '';
    const nextText = current + separator + transcript;
    textContent.value = nextText.length > 10000 ? nextText.slice(0, 10000) : nextText;
  }

  function stopTextTranscription() {
    textTranscriptionShouldRestart = false;
    isTextTranscribing.value = false;
    textTranscriptionInterim.value = '';
    if (textTranscriptionRecognition !== null) {
      try {
        textTranscriptionRecognition.stop();
      } catch (error) {}
    }
  }

  function startTextTranscription() {
    const RecognitionConstructor = getTextTranscriptionConstructor();
    if (RecognitionConstructor === null) {
      toast.error(t('aiChatAgentSetup.modals.addTextContent.transcriptionUnsupported', 'Live transcription is not supported in this browser.'));
      return;
    }
    if (textTranscriptionRecognition === null) {
      textTranscriptionRecognition = new RecognitionConstructor();
      textTranscriptionRecognition.continuous = true;
      textTranscriptionRecognition.interimResults = true;
      textTranscriptionRecognition.onresult = event => {
        let finalTranscript = '';
        let interimTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; i = i + 1) {
          const result = event.results[i];
          const resultText = result.length > 0 && result[0] !== undefined && result[0] !== null ? result[0].transcript : '';
          if (result.isFinal === true) {
            finalTranscript = finalTranscript + resultText;
          } else {
            interimTranscript = interimTranscript + resultText;
          }
        }
        appendTextTranscription(finalTranscript);
        textTranscriptionInterim.value = interimTranscript.trim();
      };
      textTranscriptionRecognition.onerror = event => {
        const errorName = event !== undefined && event !== null && typeof event.error === 'string' ? event.error : '';
        if (['not-allowed', 'service-not-allowed', 'audio-capture'].includes(errorName) === true) {
          textTranscriptionShouldRestart = false;
          isTextTranscribing.value = false;
          toast.error(t('aiChatAgentSetup.modals.addTextContent.transcriptionPermissionError', 'Microphone access is required for live transcription.'));
        }
      };
      textTranscriptionRecognition.onend = () => {
        if (textTranscriptionShouldRestart === true && showTextInputModal.value === true) {
          try {
            textTranscriptionRecognition.start();
          } catch (error) {
            isTextTranscribing.value = false;
          }
        } else {
          isTextTranscribing.value = false;
          textTranscriptionInterim.value = '';
        }
      };
    }
    textTranscriptionRecognition.lang = getTextTranscriptionLanguage();
    textTranscriptionShouldRestart = true;
    isTextTranscribing.value = true;
    try {
      textTranscriptionRecognition.start();
    } catch (error) {}
  }

  function toggleTextTranscription() {
    if (isTextTranscribing.value === true) {
      stopTextTranscription();
      return;
    }
    startTextTranscription();
  }

  function closeTextInputModal() {
    stopTextTranscription();
    showTextInputModal.value = false;
    resetTextInputModalFields();
  }

  function resetTextInputModalFields() {
    textFileName.value = '';
    textContent.value = '';
    textRegexSchemaInput.value = '';
    textRegexSchemas.value = [];
    showTextAdvancedOptions.value = false;
    isEditingTextFile.value = false;
    originalTextFileToEdit.value = null;
  }

  function openTextInputModal() {
    resetTextInputModalFields();
    showTextInputModal.value = true;
  }

  function normalizeTextRegexSchemas(value) {
    const rawItems = Array.isArray(value) === true ? value : [];
    const schemas = [];
    for (let i = 0; i < rawItems.length; i = i + 1) {
      const item = rawItems[i];
      let schema = '';
      if (typeof item === 'string') {
        schema = item.trim();
      } else if (item !== undefined && item !== null && typeof item === 'object') {
        if (typeof item.pattern === 'string') {
          schema = item.pattern.trim();
        } else if (typeof item.regex === 'string') {
          schema = item.regex.trim();
        } else if (typeof item.schema === 'string') {
          schema = item.schema.trim();
        }
      }
      if (schema === '') {
        continue;
      }
      if (schema.length > 500) {
        continue;
      }
      if (schemas.includes(schema) === true) {
        continue;
      }
      schemas.push(schema);
    }
    return schemas;
  }

  function getFileVoiceScriptInjectionRegexSchemas(file) {
    if (file === undefined) { return []; }
    if (file === null) { return []; }
    if (typeof file !== 'object') { return []; }
    if (Array.isArray(file.voiceScriptInjectionRegexSchemas) === true) { return normalizeTextRegexSchemas(file.voiceScriptInjectionRegexSchemas); }
    if (Array.isArray(file.scriptInjectionRegexSchemas) === true) { return normalizeTextRegexSchemas(file.scriptInjectionRegexSchemas); }
    if (Array.isArray(file.regexSchemas) === true) { return normalizeTextRegexSchemas(file.regexSchemas); }
    return [];
  }

  function compileTextRegexSchema(schema) {
    const value = typeof schema === 'string' ? schema.trim() : '';
    if (value === '') { return null; }
    let pattern = value;
    let flags = 'i';
    if (value.charAt(0) === '/') {
      const lastSlash = value.lastIndexOf('/');
      if (lastSlash > 0) {
        pattern = value.slice(1, lastSlash);
        const rawFlags = value.slice(lastSlash + 1);
        flags = rawFlags !== '' ? rawFlags : 'i';
      }
    }
    try { return new RegExp(pattern, flags); } catch (error) { return null; }
  }

  function addTextRegexSchema() {
    const schema = typeof textRegexSchemaInput.value === 'string' ? textRegexSchemaInput.value.trim() : '';
    if (schema === '') {
      toast.error(t('aiChatAgentSetup.modals.addTextContent.regexSchemaRequired', 'Enter a regex schema first.'));
      return;
    }
    if (schema.length > 500) {
      toast.error(t('aiChatAgentSetup.modals.addTextContent.regexSchemaTooLong', 'Regex schema exceeds 500 characters.'));
      return;
    }
    if (compileTextRegexSchema(schema) === null) {
      toast.error(t('aiChatAgentSetup.modals.addTextContent.regexSchemaInvalid', 'Regex schema is invalid.'));
      return;
    }
    const next = normalizeTextRegexSchemas(textRegexSchemas.value);
    if (next.includes(schema) === true) {
      toast.info(t('aiChatAgentSetup.modals.addTextContent.regexSchemaDuplicate', 'Regex schema already exists.'));
      return;
    }
    next.push(schema);
    textRegexSchemas.value = next;
    textRegexSchemaInput.value = '';
  }

  function removeTextRegexSchema(index) {
    const numericIndex = Number.isFinite(Number(index)) === true ? Math.floor(Number(index)) : -1;
    if (numericIndex < 0) { return; }
    const next = normalizeTextRegexSchemas(textRegexSchemas.value);
    if (numericIndex >= next.length) { return; }
    next.splice(numericIndex, 1);
    textRegexSchemas.value = next;
  }

  function closeAiSearch() {
    isAiSearchModalOpen.value = false;
    closeAiSearchDocument();
    aiSearchLoading.value = false;
  }

  function normalizeAiSearchText(value) {
    if (typeof value !== 'string') {
      return '';
    }
    return value.toLowerCase().replace(/\s+/g, ' ').trim();
  }

  function getAiSearchTokens(value) {
    const normalized = normalizeAiSearchText(value);
    if (normalized === '') {
      return [];
    }
    return Array.from(new Set(normalized.split(/[^\p{L}\p{N}]+/u).filter(token => token.length > 1)));
  }

  function buildAiSearchSnippet(rawText, queryText) {
    if (typeof rawText !== 'string') {
      return '';
    }
    const compactText = rawText.replace(/\s+/g, ' ').trim();
    if (compactText === '') {
      return '';
    }
    const normalizedQuery = normalizeAiSearchText(queryText);
    if (normalizedQuery === '') {
      return compactText.length <= 360 ? compactText : compactText.slice(0, 360) + '...';
    }
    const lowerText = compactText.toLowerCase();
    const index = lowerText.indexOf(normalizedQuery);
    if (index < 0) {
      return compactText.length <= 360 ? compactText : compactText.slice(0, 360) + '...';
    }
    const start = index - 140 > 0 ? index - 140 : 0;
    const end = index + normalizedQuery.length + 220 < compactText.length ? index + normalizedQuery.length + 220 : compactText.length;
    const prefix = start > 0 ? '...' : '';
    const suffix = end < compactText.length ? '...' : '';
    return prefix + compactText.slice(start, end) + suffix;
  }

  function renderAiSearchMarkdown(text) {
    if (typeof text !== 'string' || text.trim() === '') {
      return '';
    }
    return sanitizeHtml(marked.parse(text));
  }

  function getAiSearchLocalContent(file) {
    if (isDefinedAndNotEmpty(file?.content) != false) {
      return String(file.content);
    }
    if ((file?.fileType === 'text' || file?.fileType === 'txt') && isDefinedAndNotEmpty(file?.fileData) != false) {
      return decodeBase64UTF8(file.fileData);
    }
    if ((file?.fileType === 'website' || file?.fileType === 'websiteurl') && isDefinedAndNotEmpty(file?.url) != false) {
      return String(file.url);
    }
    return '';
  }

  function scoreAiSearchLocalFile(file, normalizedQuery, tokens) {
    const sourceText = normalizeAiSearchText(isDefinedAndNotEmpty(file?.fileName) != false ? String(file.fileName) : '');
    const contentText = normalizeAiSearchText(getAiSearchLocalContent(file));
    let score = 0;
    if (normalizedQuery !== '') {
      if (sourceText.includes(normalizedQuery) === true) {
        score = score + 80;
      }
      if (contentText.includes(normalizedQuery) === true) {
        score = score + 35;
      }
    }
    for (let i = 0; i < tokens.length; i = i + 1) {
      const token = tokens[i];
      if (sourceText.includes(token) === true) {
        score = score + 15;
      }
      if (contentText.includes(token) === true) {
        score = score + 6;
      }
    }
    return score;
  }

  function collectAiSearchLocalResults(queryText) {
    const normalizedQuery = normalizeAiSearchText(queryText);
    if (normalizedQuery === '') {
      return [];
    }
    const tokens = getAiSearchTokens(queryText);
    const localFiles = [];
    const existing = Array.isArray(existingFiles.value) === true ? existingFiles.value : [];
    const uploaded = Array.isArray(uploadedFiles.value) === true ? uploadedFiles.value : [];
    for (let i = 0; i < uploaded.length; i = i + 1) {
      localFiles.push(uploaded[i]);
    }
    for (let i = 0; i < existing.length; i = i + 1) {
      const file = existing[i];
      if (file?.status === 'deleted') {
        continue;
      }
      localFiles.push(file);
    }
    const ranked = [];
    const seen = new Set();
    for (let i = 0; i < localFiles.length; i = i + 1) {
      const file = localFiles[i];
      const source = isDefinedAndNotEmpty(file?.fileName) != false ? String(file.fileName).trim() : '';
      if (source === '') {
        continue;
      }
      const localContent = getAiSearchLocalContent(file);
      const score = scoreAiSearchLocalFile(file, normalizedQuery, tokens);
      if (score <= 0) {
        continue;
      }
      const snippet = buildAiSearchSnippet(localContent !== '' ? localContent : source, queryText);
      if (snippet === '') {
        continue;
      }
      const dedupeKey = source + '|' + snippet;
      if (seen.has(dedupeKey) === true) {
        continue;
      }
      seen.add(dedupeKey);
      ranked.push({
        source,
        sourceLabel: source,
        page: '',
        snippet,
        fullChunk: localContent !== '' ? localContent : source,
        scope: 'local',
        score
      });
    }
    ranked.sort((a, b) => b.score - a.score);
    return ranked.slice(0, 8).map(item => ({
      source: item.source,
      sourceLabel: item.sourceLabel,
      page: item.page,
      snippet: item.snippet,
      fullChunk: item.fullChunk,
      scope: item.scope,
      viewMode: 'markdown'
    }));
  }

  function mergeAiSearchResults(localResults, remoteResults) {
    const merged = [];
    const seen = new Set();
    const sourceLists = [Array.isArray(localResults) === true ? localResults : [], Array.isArray(remoteResults) === true ? remoteResults : []];
    for (let listIndex = 0; listIndex < sourceLists.length; listIndex = listIndex + 1) {
      const list = sourceLists[listIndex];
      for (let i = 0; i < list.length; i = i + 1) {
        const item = list[i];
        const source = isDefinedAndNotEmpty(item?.source) != false ? String(item.source).trim() : '';
        const snippet = isDefinedAndNotEmpty(item?.snippet) != false ? String(item.snippet).trim() : '';
        const fullChunk = isDefinedAndNotEmpty(item?.fullChunk) != false ? String(item.fullChunk) : snippet;
        if (source === '' || snippet === '') {
          continue;
        }
        const dedupeKey = source + '|' + snippet;
        if (seen.has(dedupeKey) === true) {
          continue;
        }
        seen.add(dedupeKey);
        merged.push({
          source,
          sourceLabel: isDefinedAndNotEmpty(item?.sourceLabel) != false ? String(item.sourceLabel) : source,
          page: isDefinedAndNotEmpty(item?.page) != false ? String(item.page) : '',
          snippet,
          fullChunk,
          scope: isDefinedAndNotEmpty(item?.scope) != false ? String(item.scope) : 'agent',
          viewMode: 'markdown'
        });
      }
    }
    return merged.slice(0, 12);
  }

  function normalizeAiSearchSourceName(value) {
    const raw = isDefinedAndNotEmpty(value) != false ? String(value).trim() : '';
    if (raw === '') {
      return '';
    }
    return raw.replace(/\s+\(Page [^)]+\)$/i, '').trim();
  }

  function getAiSearchComparableFileName(value) {
    const raw = isDefinedAndNotEmpty(value) != false ? String(value).trim() : '';
    if (raw === '') {
      return '';
    }
    return decodeURIComponentSafe(raw).trim().toLowerCase();
  }

  function aiSearchFileNameMatchesSource(file, source) {
    if (file === undefined) {
      return false;
    }
    if (file === null) {
      return false;
    }
    if (isDefinedAndNotEmpty(file.fileName) == false) {
      return false;
    }
    const fileName = getAiSearchComparableFileName(file.fileName);
    const sourceName = getAiSearchComparableFileName(source);
    if (fileName === '') {
      return false;
    }
    if (sourceName === '') {
      return false;
    }
    return fileName === sourceName;
  }

  function getAiSearchResultFile(result) {
    const source = normalizeAiSearchSourceName(isDefinedAndNotEmpty(result?.source) != false ? result.source : result?.sourceLabel);
    if (source === '') {
      return null;
    }
    const uploaded = Array.isArray(uploadedFiles.value) === true ? uploadedFiles.value : [];
    for (let i = 0; i < uploaded.length; i = i + 1) {
      const file = uploaded[i];
      if (aiSearchFileNameMatchesSource(file, source) === true) {
        return file;
      }
    }
    const existing = Array.isArray(existingFiles.value) === true ? existingFiles.value : [];
    for (let i = 0; i < existing.length; i = i + 1) {
      const file = existing[i];
      if (file.status === 'deleted') {
        continue;
      }
      if (aiSearchFileNameMatchesSource(file, source) === true) {
        return file;
      }
    }
    return null;
  }

  function canOpenAiSearchResult(result) {
    return getAiSearchResultFile(result) !== null;
  }

  function openAiSearchResult(result) {
    const matchedFile = getAiSearchResultFile(result);
    if (matchedFile === null) {
      return;
    }
    openInspectOrEditTextFile(matchedFile);
  }

  function getAiSearchLoadedDocumentData(result) {
    const sourceLabel = isDefinedAndNotEmpty(result?.sourceLabel) != false ? String(result.sourceLabel).trim() : '';
    const source = isDefinedAndNotEmpty(result?.source) != false ? String(result.source).trim() : sourceLabel;
    const matchedFile = getAiSearchResultFile(result);
    if (matchedFile !== null) {
      const content = getAiSearchLocalContent(matchedFile);
      if (content !== '') {
        const title = isDefinedAndNotEmpty(matchedFile.fileName) != false ? decodeURIComponentSafe(String(matchedFile.fileName)) : source;
        return {
          title,
          source: title,
          content,
          isFull: true
        };
      }
    }
    const chunk = getAiSearchResultChunk(result);
    const title = sourceLabel !== '' ? sourceLabel : source;
    return {
      title: title !== '' ? title : t('aiChatAgentSetup.modals.aiSearch.unknownSource', 'Unknown source'),
      source: source !== '' ? source : title,
      content: chunk,
      isFull: false
    };
  }

  function getAiSearchPureContent(value) {
    const raw = typeof value === 'string' ? value : '';
    if (raw.trim() === '') {
      return '';
    }
    if (typeof window === 'undefined') {
      return raw;
    }
    const hasHtmlTag = /<[a-z][\s\S]*>/i.test(raw);
    if (hasHtmlTag === false) {
      return raw;
    }
    try {
      const parser = new DOMParser();
      const parsed = parser.parseFromString(raw, 'text/html');
      const text = parsed.body !== null && typeof parsed.body.textContent === 'string' ? parsed.body.textContent : '';
      if (text.trim() === '') {
        return raw;
      }
      return text.replace(/\n{3,}/g, '\n\n').trim();
    } catch (error) {
      return raw;
    }
  }

  function openAiSearchDocument(result) {
    const documentData = getAiSearchLoadedDocumentData(result);
    aiSearchDocumentTitle.value = documentData.title;
    aiSearchDocumentSource.value = documentData.source;
    aiSearchDocumentContent.value = getAiSearchPureContent(documentData.content);
    aiSearchDocumentIsFull.value = documentData.isFull;
    isAiSearchDocumentModalOpen.value = true;
  }

  function closeAiSearchDocument() {
    isAiSearchDocumentModalOpen.value = false;
    aiSearchDocumentTitle.value = '';
    aiSearchDocumentSource.value = '';
    aiSearchDocumentContent.value = '';
    aiSearchDocumentIsFull.value = false;
  }

  function getAiSearchResultChunk(result) {
    if (isDefinedAndNotEmpty(result?.fullChunk) != false) {
      return String(result.fullChunk);
    }
    if (isDefinedAndNotEmpty(result?.snippet) != false) {
      return String(result.snippet);
    }
    return '';
  }

  function setAiSearchResultViewMode(index, mode) {
    if (Number.isInteger(index) !== true || index < 0 || index >= aiSearchResults.value.length) {
      return;
    }
    if (mode !== 'markdown' && mode !== 'plain') {
      return;
    }
    aiSearchResults.value[index].viewMode = mode;
  }

  async function runAiSearch() {
    const query = aiSearchQuery.value.trim();
    if (query === '') {
      toast.info(t('aiChatAgentSetup.modals.aiSearch.emptyQuery', 'Enter a question to search your knowledge base.'));
      return;
    }
    aiSearchLoading.value = true;
    aiSearchError.value = '';
    aiSearchHasSearched.value = true;
    const localResults = collectAiSearchLocalResults(query);
    let remoteResults = [];
    try {
      const agentId = typeof route.query.edit === 'string' ? route.query.edit.trim() : '';
      if (agentId !== '') {
        const cfg = getSecuredAxiosConfig(null);
        if (cfg !== null) {
          const response = await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/search/ai`, { query, topK: 8 }, cfg);
          remoteResults = Array.isArray(response?.data?.results) === true ? response.data.results : [];
        }
      }
      aiSearchResults.value = mergeAiSearchResults(localResults, remoteResults);
    } catch (error) {
      const apiMessage = typeof error?.response?.data?.message === 'string' ? error.response.data.message : '';
      const isAuthError = error?.response?.status === 401 || error?.response?.status === 403 || apiMessage.toLowerCase().includes('jwt expired') === true;
      if (isAuthError === true) {
        handleApiError(error, 'searching knowledge base');
      } else if (localResults.length > 0) {
        toast.error(t('aiChatAgentSetup.modals.aiSearch.partialError', 'Saved knowledge search failed. Showing local matches only.'));
      } else {
        aiSearchError.value = apiMessage !== '' ? apiMessage : t('aiChatAgentSetup.modals.aiSearch.searchError', 'Failed to search the knowledge base.');
      }
      aiSearchResults.value = mergeAiSearchResults(localResults, remoteResults);
    } finally {
      aiSearchLoading.value = false;
    }
  }

  const availableTools = ref([]);
  const selectedToolIds = ref([]);
  const selectedTool = ref('');
  const toolsSearchFilter = ref('');
  const toolsCollectionFilter = ref('all');
  const getToolIdentifier = (tool) => {
    if (tool === undefined || tool === null) {
      return null;
    }
    if (isDefinedAndNotEmpty(tool.toolid) != false) {
      return tool.toolid;
    }
    if (isDefinedAndNotEmpty(tool.toolId) != false) {
      return tool.toolId;
    }
    if (isDefinedAndNotEmpty(tool.toolGuid) != false) {
      return tool.toolGuid;
    }
    if (isDefinedAndNotEmpty(tool.id) != false) {
      return tool.id;
    }
    return null;
  };
  const toolIdsMatch = (leftId, rightId) => {
    if (leftId === undefined || leftId === null || rightId === undefined || rightId === null) {
      return false;
    }
    return String(leftId) === String(rightId);
  };
  const selectedToolSettings = ref({});
  const smsToolKey = 'sendSms';
  const emailToolKey = 'sendEmail';
  const whatsappTemplateToolKey = 'sendWhatsappTemplate';
  const smsSources = ref([]);
  const loadingSmsSources = ref(false);
  const emailSources = ref([]);
  const loadingEmailSources = ref(false);
  const whatsappTemplateSources = ref([]);
  const whatsappTemplateTemplatesBySourceId = ref({});
  const loadingWhatsappTemplateSources = ref(false);
  const loadingWhatsappTemplateTemplatesBySourceId = ref({});
  const getToolDataPayload = (tool) => {
    if (tool === undefined || tool === null || typeof tool !== 'object') {
      return {};
    }
    if (tool.tooldata !== undefined && tool.tooldata !== null && typeof tool.tooldata === 'object') {
      return tool.tooldata;
    }
    if (tool.toolData !== undefined && tool.toolData !== null && typeof tool.toolData === 'object') {
      return tool.toolData;
    }
    return {};
  };
  const getToolSystemKey = (tool) => {
    const toolData = getToolDataPayload(tool);
    return typeof toolData.systemToolKey === 'string' ? toolData.systemToolKey.trim() : '';
  };
  const getSystemToolUiMeta = (tool) => {
    const systemToolKey = getToolSystemKey(tool);
    if (systemToolKey === smsToolKey) {
      return {
        label: t('aiChatAgentSetup.toolsApi.systemToolLabels.sendSms', 'Send SMS'),
        description: t('aiChatAgentSetup.toolsApi.systemToolDescriptions.sendSms', 'Send an SMS using one of your organization SMS sources.'),
        icon: 'tabler:message'
      };
    }
    if (systemToolKey === emailToolKey) {
      return {
        label: t('aiChatAgentSetup.toolsApi.systemToolLabels.sendEmail', 'Send Email'),
        description: t('aiChatAgentSetup.toolsApi.systemToolDescriptions.sendEmail', 'Send an email using one of your organization email sources.'),
        icon: 'tabler:mail'
      };
    }
    if (systemToolKey === whatsappTemplateToolKey) {
      return {
        label: t('aiChatAgentSetup.toolsApi.systemToolLabels.sendWhatsappTemplate', 'Send WhatsApp Template'),
        description: t('aiChatAgentSetup.toolsApi.systemToolDescriptions.sendWhatsappTemplate', 'Send an approved Meta WhatsApp template using one of your organization numbers.'),
        icon: 'tabler:brand-whatsapp'
      };
    }
    return null;
  };
  const getToolRawLabel = (tool) => {
    if (isDefinedAndNotEmpty(tool?.toolname) != false) {
      return tool.toolname;
    }
    if (isDefinedAndNotEmpty(tool?.toolName) != false) {
      return tool.toolName;
    }
    if (isDefinedAndNotEmpty(tool?.name) != false) {
      return tool.name;
    }
    return '';
  };
  const getToolRawDescription = (tool) => {
    if (isDefinedAndNotEmpty(tool?.tooldata) != false && isDefinedAndNotEmpty(tool.tooldata.description) != false) {
      return tool.tooldata.description;
    }
    if (isDefinedAndNotEmpty(tool?.toolData) != false && isDefinedAndNotEmpty(tool.toolData.description) != false) {
      return tool.toolData.description;
    }
    if (isDefinedAndNotEmpty(tool?.description) != false) {
      return tool.description;
    }
    if (isDefinedAndNotEmpty(tool?.toolDescription) != false) {
      return tool.toolDescription;
    }
    return '';
  };
  const getToolDisplayLabel = (tool) => {
    const systemMeta = getSystemToolUiMeta(tool);
    if (systemMeta !== null && isDefinedAndNotEmpty(systemMeta.label) != false) {
      return systemMeta.label;
    }
    return getToolRawLabel(tool);
  };
  const getToolDisplayDescription = (tool) => {
    const systemMeta = getSystemToolUiMeta(tool);
    if (systemMeta !== null && isDefinedAndNotEmpty(systemMeta.description) != false) {
      return systemMeta.description;
    }
    const rawDescription = getToolRawDescription(tool);
    if (isDefinedAndNotEmpty(rawDescription) != false) {
      return rawDescription;
    }
    return t('aiChatAgentSetup.toolsApi.noDescription', 'No description available');
  };
  const getToolDisplayIcon = (tool) => {
    const systemMeta = getSystemToolUiMeta(tool);
    if (systemMeta !== null && isDefinedAndNotEmpty(systemMeta.icon) != false) {
      return systemMeta.icon;
    }
    return 'tabler:tool';
  };

  const getToolDisplayLogo = (tool) => {
    return getToolLogoUrl(tool);
  };

  const toolsCollectionOptions = computed(() => {
    return buildCollectionFilterOptions(availableTools.value, 'All collections');
  });

  const filteredAvailableTools = computed(() => {
    const query = typeof toolsSearchFilter.value === 'string' ? toolsSearchFilter.value.trim().toLowerCase() : '';
    const collectionId = typeof toolsCollectionFilter.value === 'string' ? toolsCollectionFilter.value.trim() : 'all';
    return availableTools.value.filter((tool) => {
      if (collectionId !== 'all') {
        const inferred = inferToolCollectionId(tool);
        if (inferred !== collectionId) {
          return false;
        }
      }
      if (query === '') {
        return true;
      }
      const label = getToolDisplayLabel(tool).toLowerCase();
      const desc = getToolDisplayDescription(tool).toLowerCase();
      return label.includes(query) === true || desc.includes(query) === true;
    });
  });
  const availableToolOptions = computed(() => {
    return filteredAvailableTools.value
      .map((tool) => {
        const toolId = getToolIdentifier(tool);
        return {
          value: toolId,
          label: getToolDisplayLabel(tool),
          description: getToolDisplayDescription(tool),
          icon: getToolDisplayIcon(tool),
          imageUrl: getToolDisplayLogo(tool)
        };
      })
      .filter((option) => option.value !== null);
  });
  const isSmsTool = (tool) => {
    return getToolSystemKey(tool) === smsToolKey;
  };
  const isEmailTool = (tool) => {
    return getToolSystemKey(tool) === emailToolKey;
  };
  const isWhatsappTemplateTool = (tool) => {
    return getToolSystemKey(tool) === whatsappTemplateToolKey;
  };
  const getCommunicationSourcePhoneDisplay = (config) => {
    const sourceConfig = config !== undefined && config !== null && typeof config === 'object' ? config : {};
    const candidates = [
      sourceConfig.phone,
      sourceConfig.phoneNumber,
      sourceConfig.sender,
      sourceConfig.from,
      sourceConfig.senderEmail,
      sourceConfig.emailAddress,
      sourceConfig.user
    ];
    for (let index = 0; index < candidates.length; index = index + 1) {
      const candidate = typeof candidates[index] === 'string' ? candidates[index].trim() : '';
      if (candidate.length > 0) {
        return candidate;
      }
    }
    return '';
  };
  const loadSmsSources = async () => {
    if (loadingSmsSources.value === true) {
      return;
    }
    loadingSmsSources.value = true;
    try {
      const response = await axios.get(`${apiEndpoint}/api/communicationSources/list`);
      const rows = Array.isArray(response.data) === true ? response.data : [];
      smsSources.value = rows
        .filter((row) => row !== undefined && row !== null && ['smsTwilio', 'smsMaskyoo', 'smsQuota'].includes(row.type) === true)
        .map((row) => {
          const config = row.config !== undefined && row.config !== null && typeof row.config === 'object' ? row.config : {};
          const phoneNumber = getCommunicationSourcePhoneDisplay(config);
          const display = typeof config.name === 'string' && config.name.trim() !== '' ? config.name.trim() : (phoneNumber !== '' ? phoneNumber : String(row.sourceId));
          return {
            sourceId: row.sourceId,
            type: row.type,
            phoneNumber,
            display
          };
        });
    } catch (error) {
      smsSources.value = [];
      handleApiError(error, 'loading SMS sources');
    } finally {
      loadingSmsSources.value = false;
    }
  };
  const initializeSmsToolData = async () => {
    const hasSmsTool = Array.isArray(tools.value) === true ? tools.value.some((tool) => isSmsTool(tool) === true) === true : false;
    if (hasSmsTool !== true) {
      return;
    }
    await loadSmsSources();
  };
  const updateSmsToolSource = (tool, sourceId) => {
    const settings = getToolSettingsEntry(tool);
    settings.sourceId = typeof sourceId === 'string' ? sourceId.trim() : '';
  };
  const loadEmailSources = async () => {
    if (loadingEmailSources.value === true) {
      return;
    }
    loadingEmailSources.value = true;
    try {
      const response = await axios.get(`${apiEndpoint}/api/communicationSources/list`);
      const rows = Array.isArray(response.data) === true ? response.data : [];
      emailSources.value = rows
        .filter((row) => row !== undefined && row !== null && (row.type === 'emailMicrosoft365' || row.type === 'emailGoogleGmail'))
        .map((row) => {
          const config = row.config !== undefined && row.config !== null && typeof row.config === 'object' ? row.config : {};
          const address = getCommunicationSourcePhoneDisplay(config);
          const display = typeof config.name === 'string' && config.name.trim() !== '' ? config.name.trim() : (address !== '' ? address : String(row.sourceId));
          return {
            sourceId: row.sourceId,
            type: row.type,
            address,
            display
          };
        });
    } catch (error) {
      emailSources.value = [];
      handleApiError(error, 'loading email sources');
    } finally {
      loadingEmailSources.value = false;
    }
  };
  const initializeEmailToolData = async () => {
    const hasEmailTool = Array.isArray(tools.value) === true ? tools.value.some((tool) => isEmailTool(tool) === true) === true : false;
    if (hasEmailTool !== true) {
      return;
    }
    await loadEmailSources();
  };
  const updateEmailToolSource = (tool, sourceId) => {
    const settings = getToolSettingsEntry(tool);
    settings.sourceId = typeof sourceId === 'string' ? sourceId.trim() : '';
  };
  const normalizeLoadedToolSettings = (value) => {
    if (value === undefined || value === null || typeof value !== 'object' || Array.isArray(value) === true) {
      return {};
    }
    const normalized = {};
    const keys = Object.keys(value);
    for (let index = 0; index < keys.length; index = index + 1) {
      const key = typeof keys[index] === 'string' ? keys[index].trim() : '';
      const item = value[key];
      if (key === '' || item === undefined || item === null || typeof item !== 'object' || Array.isArray(item) === true) {
        continue;
      }
      normalized[key] = {
        waitForResponse: item.waitForResponse !== false,
        sourceId: item.sourceId !== undefined && item.sourceId !== null ? String(item.sourceId).trim() : '',
        templateId: item.templateId !== undefined && item.templateId !== null ? String(item.templateId).trim() : '',
        templateName: typeof item.templateName === 'string' ? item.templateName.trim() : '',
        languageCode: typeof item.languageCode === 'string' ? item.languageCode.trim() : '',
        status: typeof item.status === 'string' ? item.status.trim() : '',
        variableDefinitions: normalizeWhatsappTemplateVariableDefinitions(item.variableDefinitions)
      };
    }
    return normalized;
  };
  const getToolSettingsKey = (tool) => {
    const identifier = getToolIdentifier(tool);
    if (identifier !== null && identifier !== undefined) {
      const normalizedIdentifier = String(identifier).trim();
      if (normalizedIdentifier !== '') {
        return normalizedIdentifier;
      }
    }
    if (typeof tool?.toolGuid === 'string' && tool.toolGuid.trim() !== '') {
      return tool.toolGuid.trim();
    }
    if (typeof tool?.toolguid === 'string' && tool.toolguid.trim() !== '') {
      return tool.toolguid.trim();
    }
    return '';
  };
  const getToolSettingsEntry = (tool) => {
    const key = getToolSettingsKey(tool);
    if (key === '') {
      return {
        waitForResponse: true,
        sourceId: '',
        templateId: '',
        templateName: '',
        languageCode: '',
        status: '',
        variableDefinitions: []
      };
    }
    if (selectedToolSettings.value[key] === undefined || selectedToolSettings.value[key] === null || typeof selectedToolSettings.value[key] !== 'object' || Array.isArray(selectedToolSettings.value[key]) === true) {
      selectedToolSettings.value[key] = {
        waitForResponse: true,
        sourceId: '',
        templateId: '',
        templateName: '',
        languageCode: '',
        status: '',
        variableDefinitions: []
      };
    }
    return selectedToolSettings.value[key];
  };
  const clearToolSettingsEntry = (tool) => {
    const key = getToolSettingsKey(tool);
    if (key !== '' && selectedToolSettings.value[key] !== undefined) {
      delete selectedToolSettings.value[key];
    }
  };
  const updateToolWaitForResponse = (tool, checked) => {
    const settings = getToolSettingsEntry(tool);
    settings.waitForResponse = checked !== false;
  };
  const isWhatsappTemplateTemplatesLoading = (sourceId) => {
    const key = typeof sourceId === 'string' ? sourceId.trim() : '';
    if (key === '') {
      return false;
    }
    return loadingWhatsappTemplateTemplatesBySourceId.value[key] === true;
  };
  const getWhatsappTemplateOptions = (tool) => {
    const sourceId = getToolSettingsEntry(tool).sourceId;
    const key = typeof sourceId === 'string' ? sourceId.trim() : '';
    if (key === '') {
      return [];
    }
    const list = whatsappTemplateTemplatesBySourceId.value[key];
    return Array.isArray(list) === true ? list : [];
  };
  const getSelectedWhatsappTemplateRecord = (tool) => {
    const settings = getToolSettingsEntry(tool);
    const templateId = typeof settings.templateId === 'string' ? settings.templateId.trim() : '';
    if (templateId === '') {
      return null;
    }
    const list = getWhatsappTemplateOptions(tool);
    const item = list.find((templateItem) => {
      const itemId = templateItem?.templateId !== undefined && templateItem?.templateId !== null ? String(templateItem.templateId).trim() : '';
      return itemId === templateId;
    });
    return item !== undefined ? item : null;
  };
  const getSelectedWhatsappTemplateStatus = (tool) => {
    const selectedTemplate = getSelectedWhatsappTemplateRecord(tool);
    if (selectedTemplate !== null && typeof selectedTemplate.status === 'string') {
      return selectedTemplate.status;
    }
    return getToolSettingsEntry(tool).status;
  };
  const normalizeWhatsappTemplateVariableDefinitions = (value) => {
    if (Array.isArray(value) !== true) {
      return [];
    }
    const normalized = [];
    for (let index = 0; index < value.length; index = index + 1) {
      const item = value[index];
      if (item === undefined || item === null || typeof item !== 'object' || Array.isArray(item) === true) {
        continue;
      }
      const rawId = item.id !== undefined && item.id !== null ? String(item.id).trim() : '';
      const token = typeof item.token === 'string' && item.token.trim() !== '' ? item.token.trim() : `{{${rawId}}}`;
      const id = toCamelCaseWhatsappVariableId(rawId).length > 0 ? toCamelCaseWhatsappVariableId(rawId) : toCamelCaseWhatsappVariableId(token);
      if (id === '' || normalized.some((entry) => entry.id === id) === true) {
        continue;
      }
      normalized.push({
        id,
        token,
        description: typeof item.description === 'string' ? item.description : '',
        example: typeof item.example === 'string' ? item.example.trim() : ''
      });
    }
    return normalized;
  };
  const toCamelCaseWhatsappVariableId = (value) => {
    const text = typeof value === 'string' ? value.trim().replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '') : '';
    if (text.length === 0) {
      return '';
    }
    const parts = text.split(/[^A-Za-z0-9]+/).filter((part) => part.length > 0);
    if (parts.length === 0) {
      return '';
    }
    let out = `${parts[0].charAt(0).toLowerCase()}${parts[0].slice(1)}`;
    for (let index = 1; index < parts.length; index = index + 1) {
      const part = parts[index];
      out = `${out}${part.charAt(0).toUpperCase()}${part.slice(1)}`;
    }
    return out;
  };
  const getWhatsappTemplateBodyComponent = (templateRecord) => {
    const record = templateRecord !== undefined && templateRecord !== null && typeof templateRecord === 'object' ? templateRecord : {};
    const components = Array.isArray(record.components) === true
      ? record.components
      : (Array.isArray(record.templateData?.components) === true ? record.templateData.components : []);
    for (let index = 0; index < components.length; index = index + 1) {
      const component = components[index];
      if (typeof component?.type === 'string' && component.type.trim().toUpperCase() === 'BODY') {
        return component;
      }
    }
    return {};
  };
  const getWhatsappTemplateVariableExamples = (templateRecord, tokens) => {
    const bodyComponent = getWhatsappTemplateBodyComponent(templateRecord);
    const example = bodyComponent.example !== undefined && bodyComponent.example !== null && typeof bodyComponent.example === 'object' ? bodyComponent.example : {};
    const examples = {};
    const namedExamples = Array.isArray(example.body_text_named_params) === true ? example.body_text_named_params : [];
    for (let index = 0; index < namedExamples.length; index = index + 1) {
      const item = namedExamples[index];
      const id = item?.param_name !== undefined && item?.param_name !== null ? String(item.param_name).trim() : '';
      const value = item?.example !== undefined && item?.example !== null ? String(item.example).trim() : '';
      if (id !== '' && value !== '') {
        examples[id] = value;
      }
    }
    const positionalRows = Array.isArray(example.body_text) === true ? example.body_text : [];
    const positionalExamples = positionalRows.length > 0 && Array.isArray(positionalRows[0]) === true ? positionalRows[0] : [];
    for (let index = 0; index < tokens.length; index = index + 1) {
      const token = tokens[index];
      if (examples[token] === undefined && positionalExamples[index] !== undefined && positionalExamples[index] !== null) {
        examples[token] = String(positionalExamples[index]).trim();
      }
    }
    return examples;
  };
  const buildWhatsappTemplateVariableDefinitions = (templateRecord, existingDefinitions) => {
    const record = templateRecord !== undefined && templateRecord !== null && typeof templateRecord === 'object' ? templateRecord : {};
    const bodyComponent = getWhatsappTemplateBodyComponent(record);
    const bodyText = typeof record.bodyText === 'string' && record.bodyText.trim() !== '' ? record.bodyText : (typeof bodyComponent.text === 'string' ? bodyComponent.text : '');
    const matches = bodyText.match(/\{\{\s*[^{}]+\s*\}\}/g);
    if (Array.isArray(matches) !== true) {
      return [];
    }
    const tokens = [];
    for (let index = 0; index < matches.length; index = index + 1) {
      const token = matches[index].replace(/^\{\{\s*/, '').replace(/\s*\}\}$/, '').trim();
      if (token !== '' && tokens.includes(token) !== true) {
        tokens.push(token);
      }
    }
    const parameterFormat = typeof record.parameterFormat === 'string' ? record.parameterFormat.trim().toLowerCase() : '';
    if (parameterFormat !== 'named') {
      tokens.sort((left, right) => Number(left) - Number(right));
    }
    const existing = normalizeWhatsappTemplateVariableDefinitions(existingDefinitions);
    const examples = getWhatsappTemplateVariableExamples(record, tokens);
    return tokens.map((token) => {
      const id = parameterFormat === 'named' ? toCamelCaseWhatsappVariableId(token) : token;
      const tokenText = `{{${token}}}`;
      const previous = existing.find((item) => [item.id === id, item.id === token, item.token === tokenText].includes(true));
      return {
        id,
        token: tokenText,
        description: previous !== undefined ? previous.description : '',
        example: examples[token] !== undefined ? examples[token] : (previous !== undefined ? previous.example : '')
      };
    });
  };
  const syncWhatsappTemplateVariableDefinitions = (tool) => {
    const settings = getToolSettingsEntry(tool);
    const selectedTemplate = getSelectedWhatsappTemplateRecord(tool);
    settings.variableDefinitions = selectedTemplate !== null
      ? buildWhatsappTemplateVariableDefinitions(selectedTemplate, settings.variableDefinitions)
      : [];
  };
  const getWhatsappTemplateVariableDefinitions = (tool) => {
    return normalizeWhatsappTemplateVariableDefinitions(getToolSettingsEntry(tool).variableDefinitions);
  };
  const getSelectedWhatsappTemplateBody = (tool) => {
    const selectedTemplate = getSelectedWhatsappTemplateRecord(tool);
    if (selectedTemplate === null) {
      return '';
    }
    if (typeof selectedTemplate.bodyText === 'string' && selectedTemplate.bodyText.trim() !== '') {
      return selectedTemplate.bodyText;
    }
    const bodyComponent = getWhatsappTemplateBodyComponent(selectedTemplate);
    return typeof bodyComponent.text === 'string' ? bodyComponent.text : '';
  };
  const getWhatsappTemplateVariablePlaceholder = (variable) => {
    if (typeof variable?.example === 'string' && variable.example !== '') {
      return t('aiChatAgentSetup.toolsApi.whatsappTemplateVariablePlaceholderWithExample', 'For example: {example}', { example: variable.example });
    }
    return t('aiChatAgentSetup.toolsApi.whatsappTemplateVariablePlaceholder', 'For example: customer full name, appointment date, or confirmation number');
  };
  const updateWhatsappTemplateVariableDescription = (tool, variableId, description) => {
    const settings = getToolSettingsEntry(tool);
    const definitions = normalizeWhatsappTemplateVariableDefinitions(settings.variableDefinitions);
    const variable = definitions.find((item) => item.id === String(variableId));
    if (variable !== undefined) {
      variable.description = typeof description === 'string' ? description : '';
    }
    settings.variableDefinitions = definitions;
  };
  const loadWhatsappTemplateSources = async () => {
    if (loadingWhatsappTemplateSources.value === true) {
      return;
    }
    loadingWhatsappTemplateSources.value = true;
    try {
      const response = await axios.get(`${apiEndpoint}/api/communicationSources/whatsappMeta/sources`);
      whatsappTemplateSources.value = Array.isArray(response.data) === true ? response.data : [];
    } catch (error) {
      whatsappTemplateSources.value = [];
      handleApiError(error, 'loading Meta WhatsApp sources');
    } finally {
      loadingWhatsappTemplateSources.value = false;
    }
  };
  const loadWhatsappTemplateOptionsForSource = async (sourceId) => {
    const normalizedSourceId = typeof sourceId === 'string' ? sourceId.trim() : '';
    if (normalizedSourceId === '') {
      return;
    }
    loadingWhatsappTemplateTemplatesBySourceId.value = {
      ...loadingWhatsappTemplateTemplatesBySourceId.value,
      [normalizedSourceId]: true
    };
    try {
      const response = await axios.get(`${apiEndpoint}/api/communicationSources/whatsappMeta/templates`, {
        params: {
          sourceId: normalizedSourceId
        }
      });
      const items = Array.isArray(response.data?.items) === true ? response.data.items : [];
      whatsappTemplateTemplatesBySourceId.value = {
        ...whatsappTemplateTemplatesBySourceId.value,
        [normalizedSourceId]: items
      };
      const syncError = typeof response.data?.syncError === 'string' ? response.data.syncError.trim() : '';
      if (syncError !== '') {
        toast.warning(syncError);
      }
    } catch (error) {
      whatsappTemplateTemplatesBySourceId.value = {
        ...whatsappTemplateTemplatesBySourceId.value,
        [normalizedSourceId]: []
      };
      handleApiError(error, 'loading WhatsApp templates');
    } finally {
      loadingWhatsappTemplateTemplatesBySourceId.value = {
        ...loadingWhatsappTemplateTemplatesBySourceId.value,
        [normalizedSourceId]: false
      };
    }
  };
  const initializeWhatsappTemplateToolData = async () => {
    const hasWhatsappTemplateTool = Array.isArray(tools.value) === true ? tools.value.some((tool) => isWhatsappTemplateTool(tool) === true) === true : false;
    if (hasWhatsappTemplateTool !== true) {
      return;
    }
    await loadWhatsappTemplateSources();
    for (let index = 0; index < tools.value.length; index = index + 1) {
      const tool = tools.value[index];
      if (isWhatsappTemplateTool(tool) !== true) {
        continue;
      }
      const sourceId = getToolSettingsEntry(tool).sourceId;
      if (sourceId !== '') {
        await loadWhatsappTemplateOptionsForSource(sourceId);
        syncWhatsappTemplateVariableDefinitions(tool);
      }
    }
  };
  const updateWhatsappTemplateToolSource = async (tool, sourceId) => {
    const settings = getToolSettingsEntry(tool);
    settings.sourceId = typeof sourceId === 'string' ? sourceId.trim() : '';
    settings.templateId = '';
    settings.templateName = '';
    settings.languageCode = '';
    settings.status = '';
    settings.variableDefinitions = [];
    if (settings.sourceId !== '') {
      await loadWhatsappTemplateOptionsForSource(settings.sourceId);
    }
  };
  const updateWhatsappTemplateToolTemplate = (tool, templateId) => {
    const settings = getToolSettingsEntry(tool);
    settings.templateId = typeof templateId === 'string' ? templateId.trim() : '';
    settings.templateName = '';
    settings.languageCode = '';
    settings.status = '';
    settings.variableDefinitions = [];
    const selectedTemplate = getSelectedWhatsappTemplateRecord(tool);
    if (selectedTemplate !== null) {
      settings.templateName = typeof selectedTemplate.templateName === 'string' ? selectedTemplate.templateName : '';
      settings.languageCode = typeof selectedTemplate.languageCode === 'string' ? selectedTemplate.languageCode : '';
      settings.status = typeof selectedTemplate.status === 'string' ? selectedTemplate.status : '';
    }
    syncWhatsappTemplateVariableDefinitions(tool);
  };
  const buildSelectedToolSettingsPayload = () => {
    const payload = {};
    for (let index = 0; index < tools.value.length; index = index + 1) {
      const tool = tools.value[index];
      const key = getToolSettingsKey(tool);
      if (key === '') {
        continue;
      }
      const settings = getToolSettingsEntry(tool);
      const sourceId = typeof settings.sourceId === 'string' ? settings.sourceId.trim() : '';
      const templateId = typeof settings.templateId === 'string' ? settings.templateId.trim() : '';
      const templateName = typeof settings.templateName === 'string' ? settings.templateName.trim() : '';
      const languageCode = typeof settings.languageCode === 'string' ? settings.languageCode.trim() : '';
      const status = typeof settings.status === 'string' ? settings.status.trim() : '';
      const waitForResponse = settings.waitForResponse !== false;
      const variableDefinitions = normalizeWhatsappTemplateVariableDefinitions(settings.variableDefinitions);
      if (sourceId === '' && templateId === '' && templateName === '' && languageCode === '' && status === '' && variableDefinitions.length === 0 && waitForResponse === true) {
        continue;
      }
      payload[key] = {
        waitForResponse,
        sourceId,
        templateId,
        templateName,
        languageCode,
        status,
        variableDefinitions
      };
    }
    return payload;
  };
  const validateSelectedToolSettings = () => {
    for (let index = 0; index < tools.value.length; index = index + 1) {
      const tool = tools.value[index];
      if (isSmsTool(tool) === true) {
        const smsSettings = getToolSettingsEntry(tool);
        if (typeof smsSettings.sourceId !== 'string' || smsSettings.sourceId.trim() === '') {
          toast.error(t('aiChatAgentSetup.toolsApi.smsSourceRequired', 'Select an SMS source for the SMS tool.'));
          return false;
        }
      }
      if (isWhatsappTemplateTool(tool) !== true) {
        continue;
      }
      const settings = getToolSettingsEntry(tool);
      if (typeof settings.sourceId !== 'string' || settings.sourceId.trim() === '') {
        toast.error(t('aiChatAgentSetup.toolsApi.whatsappTemplateSourceRequired', 'Select a Meta WhatsApp number for the WhatsApp template tool.'));
        return false;
      }
      if (typeof settings.templateId !== 'string' || settings.templateId.trim() === '') {
        toast.error(t('aiChatAgentSetup.toolsApi.whatsappTemplateTemplateRequired', 'Select a WhatsApp template for the WhatsApp template tool.'));
        return false;
      }
      syncWhatsappTemplateVariableDefinitions(tool);
      const variableDefinitions = getWhatsappTemplateVariableDefinitions(tool);
      for (let variableIndex = 0; variableIndex < variableDefinitions.length; variableIndex = variableIndex + 1) {
        const variable = variableDefinitions[variableIndex];
        if (typeof variable.description !== 'string' || variable.description.trim() === '') {
          toast.error(t('aiChatAgentSetup.toolsApi.whatsappTemplateVariableDescriptionRequired', 'Describe what the AI should place in variable {variableId}.', { variableId: variable.token }));
          return false;
        }
      }
    }
    return true;
  };
  const llmFlowRef = ref(null);
  const agentsToAgentsFlowRef = ref(null);
  const isSaving = ref(false);
  const isNavigating = ref(null);
  const showLoadingModal = ref(false);
  const modalState = reactive({
    type: 'loading', 
    title: 'Processing...',
    message: ''
  });
  const currentTip = ref('');
  const tipInterval = ref(null);
  const agentStyles = ref([]);
  const selectedStyleGuid = ref('');
  const currentAgentKnowledgeBaseId = computed(() => typeof route.query.edit === 'string' ? route.query.edit.trim() : '');
  const sharedKnowledgeBases = ref([]);
  const selectedSharedKbIds = ref([]);
  const showSharedKbPicker = ref(false);
  const availableAgentKnowledgeBases = ref([]);
  const selectedLinkedAgentKbIds = ref([]);
  const showAgentKbPicker = ref(false);
  const linkedSharedKbItems = computed(() => {
    const ids = Array.isArray(selectedSharedKbIds.value) === true ? selectedSharedKbIds.value : [];
    const list = Array.isArray(sharedKnowledgeBases.value) === true ? sharedKnowledgeBases.value : [];
    return list.filter(kb => ids.includes(kb.kbId) === true);
  });
  const linkedAgentKbItems = computed(() => {
    const ids = Array.isArray(selectedLinkedAgentKbIds.value) === true ? selectedLinkedAgentKbIds.value : [];
    const list = Array.isArray(availableAgentKnowledgeBases.value) === true ? availableAgentKnowledgeBases.value : [];
    const currentAgentId = currentAgentKnowledgeBaseId.value;
    return list.filter(agent => ids.includes(agent.agentId) === true && agent.agentId !== currentAgentId);
  });
  const availableAgentKnowledgeBaseOptions = computed(() => {
    const list = Array.isArray(availableAgentKnowledgeBases.value) === true ? availableAgentKnowledgeBases.value : [];
    const currentAgentId = currentAgentKnowledgeBaseId.value;
    return list
      .filter(agent => agent !== undefined && agent !== null && typeof agent.agentId === 'string' && agent.agentId !== currentAgentId)
      .map(agent => ({ value: agent.agentId, label: agent.agentName }));
  });
  const showVerificationSelector = computed(() => false);
  const groqGptOssReasoningAllowedValues = ['low', 'medium', 'high'];
  function normalizeGroqGptOssReasoningEffort(value) {
    if (typeof value !== 'string') {
      return 'medium';
    }
    const normalized = value.trim().toLowerCase();
    if (groqGptOssReasoningAllowedValues.includes(normalized) === true) {
      return normalized;
    }
    return 'medium';
  }
  const groqGptOssReasoningOptions = computed(() => [
    { value: 'low', label: t('aiChatAgentSetup.agentInformation.groqReasoningOptions.low', 'Low') },
    { value: 'medium', label: t('aiChatAgentSetup.agentInformation.groqReasoningOptions.medium', 'Medium') },
    { value: 'high', label: t('aiChatAgentSetup.agentInformation.groqReasoningOptions.high', 'High') }
  ]);
  const selectedLlmModelConfig = computed(() => {
    const selectedId = typeof resolvedSelectedLLMModelId.value === 'string' ? resolvedSelectedLLMModelId.value.trim() : '';
    if (selectedId.length === 0) {
      return null;
    }
    const selectedModel = availableLLMModels.value.find(model => model.id === selectedId);
    if (selectedModel !== undefined) {
      return selectedModel;
    }
    return null;
  });
  function isGroqGptOssModel(model) {
    if (model === undefined || model === null) {
      return false;
    }
    const provider = typeof model.provider === 'string' ? model.provider.toLowerCase() : '';
    if (provider !== 'groq') {
      return false;
    }
    const candidates = [
      typeof model.productVariant === 'string' ? model.productVariant : '',
      typeof model.id === 'string' ? model.id : '',
      typeof model.groqModelName === 'string' ? model.groqModelName : '',
      typeof model.deploymentName === 'string' ? model.deploymentName : ''
    ];
    for (let i = 0; i < candidates.length; i = i + 1) {
      const current = candidates[i].toLowerCase();
      if (current.includes('gpt-oss') === true) {
        return true;
      }
    }
    return false;
  }
  const isRegularAgentMode = computed(() => {
    const currentMode = typeof agentMode.value === 'string' ? agentMode.value : '';
    return currentMode === 'Agentic' || currentMode === 'Conversational';
  });
  const showGroqGptOssFirstAnswerReasoningSelector = computed(() => {
    return isGroqGptOssModel(selectedLlmModelConfig.value) === true;
  });
  const showGroqGptOssVerifyReasoningSelector = computed(() => {
    if (isRegularAgentMode.value !== true) {
      return false;
    }
    return isGroqGptOssModel(selectedLlmModelConfig.value) === true;
  });
  function confirmAddSharedKbs() {
    showSharedKbPicker.value = false;
  }
  function confirmAddLinkedAgentKbs() {
    showAgentKbPicker.value = false;
  }
  const divisions = ref([]);
  const selectedDivisionIds = ref([]);
  const inputDirection = ref('ltr');
  const agentMode = ref('Agentic');
  const flowBasedFlow = ref(null);
  const isFastOrSuperFastMode = computed(() => {
    const currentMode = typeof agentMode.value === 'string' ? agentMode.value.toLowerCase() : '';
    return currentMode === 'fast' || currentMode === 'superfast';
  });
  const agenticBehaviour = ref(true);
  const pendingAgenticBehaviour = ref(true);
  const isSwitchConfirmOpen = ref(false);
  const targetAgentMode = ref(null);
  const activeFineTuneTab = ref('fineTune');
  const selectedFineTuneMethod = ref('answers');
  const resolvedSelectedLLMModelId = computed(() => {
      if (selectedLLMModel.value === autoLlmModelId) {
      if (agentMode.value === 'SuperFast') {
        if (typeof autoSuperFastModeModelId.value === 'string' && autoSuperFastModeModelId.value.length > 0) {
          return autoSuperFastModeModelId.value;
        }
      } else if (agentMode.value === 'Fast') {
        if (typeof autoFastModeModelId.value === 'string' && autoFastModeModelId.value.length > 0) {
          return autoFastModeModelId.value;
        }
      }
    }
    return selectedLLMModel.value;
  });
  const isInspectModalOpen = ref(false);
  const selectedFileToInspect = ref(null);
  function unlinkSharedKb(kbId) {
    if (Array.isArray(selectedSharedKbIds.value) === true) {
      selectedSharedKbIds.value = selectedSharedKbIds.value.filter(id => id !== kbId);
    } else {
      selectedSharedKbIds.value = [];
    }
  }
  function unlinkLinkedAgentKb(agentId) {
    if (Array.isArray(selectedLinkedAgentKbIds.value) === true) {
      selectedLinkedAgentKbIds.value = selectedLinkedAgentKbIds.value.filter(id => id !== agentId);
    } else {
      selectedLinkedAgentKbIds.value = [];
    }
  }
  let loadedAgentData = null;
  let initialFlowLoadAttempted = false;
  function getLoadedAnalysisEngineForSave() {
    if (loadedAgentData !== null && loadedAgentData !== undefined && loadedAgentData.analysisEngine !== undefined && loadedAgentData.analysisEngine !== null && typeof loadedAgentData.analysisEngine === 'object' && Array.isArray(loadedAgentData.analysisEngine) !== true) {
      return loadedAgentData.analysisEngine;
    }
    return undefined;
  }
  function getLoadedFlowForSave() {
    if (loadedAgentData === null || loadedAgentData === undefined) {
      return null;
    }
    if (loadedAgentData.agentdata === undefined || loadedAgentData.agentdata === null) {
      return null;
    }
    if (loadedAgentData.agentdata.flow === undefined) {
      return null;
    }
    return loadedAgentData.agentdata.flow;
  }
  function getFlowDataForSave() {
    let flowData = null;
    if (agentMode.value === 'Flow') {
      const loadedFlow = getLoadedFlowForSave();
      if (isEditMode.value === true && initialFlowLoadAttempted !== true && loadedFlow !== null) {
        return loadedFlow;
      }
      if (llmFlowRef.value !== null && llmFlowRef.value !== undefined) {
        try {
          flowData = llmFlowRef.value.getFlowData();
          console.log("Retrieved Flow Data:", flowData);
        } catch (error) {
          console.error("Error getting flow data:", error);
        }
      }
      if (flowData !== null && flowData !== undefined) {
        return flowData;
      }
      if (isEditMode.value === true && loadedFlow !== null) {
        return loadedFlow;
      }
      return null;
    }
    if (agentMode.value === 'FlowBased') {
      if (flowBasedFlow.value !== null && flowBasedFlow.value !== undefined) {
        return flowBasedFlow.value;
      }
      if (isEditMode.value === true) {
        return getLoadedFlowForSave();
      }
    }
    return null;
  }
  function getLoadedSelectedLlmModelId(data) {
    const agentData = data !== undefined && data !== null && typeof data === 'object' ? data : {};
    const configurations = agentData.configurations !== undefined && agentData.configurations !== null && typeof agentData.configurations === 'object' ? agentData.configurations : {};
    const selectedChoice = typeof configurations.selectedLlmModelChoiceId === 'string' ? configurations.selectedLlmModelChoiceId.trim() : '';
    const savedModelId = typeof agentData.selectedLlmModelId === 'string' ? agentData.selectedLlmModelId.trim() : '';
    if (selectedChoice === autoLlmModelId) {
      return autoLlmModelId;
    }
    if (selectedChoice.length > 0) {
      return selectedChoice;
    }
    if (savedModelId === autoLlmModelId) {
      return autoLlmModelId;
    }
    return savedModelId;
  }
  function syncCurrentPageFromMainTabs() {
    const el = document.querySelector('input[name="my_tabs_2"]:checked');
    if (el === null || el === undefined) {
      return;
    }
    const raw = el.getAttribute('data-currentPage');
    if (raw === null || raw === undefined) {
      return;
    }
    const n = parseInt(String(raw), 10);
    if (Number.isFinite(n) === true) {
      currentPage.value = n;
    }
  }
  function openLlmFlowBuilder() {
    if (llmFlowRef.value !== null && llmFlowRef.value !== undefined) {
      if (typeof llmFlowRef.value.openFlowBuilder === 'function') {
        llmFlowRef.value.openFlowBuilder();
      }
    }
  }
  function onFlowBuilderClosed() {
    if (currentPage.value === 2 && agentMode.value === 'Flow') {
      const r = document.querySelector('input[data-currentPage="1"]');
      if (r !== null) {
        r.click();
      }
      syncCurrentPageFromMainTabs();
    }
  }
  function tryLoadInitialFlowData() {
    if (currentPage.value !== 2) {
      return;
    }
    if (agentMode.value !== 'Flow') {
      return;
    }
    if (isEditMode.value !== true) {
      return;
    }
    if (loadedAgentData === null || loadedAgentData === undefined) {
      return;
    }
    if (loadedAgentData.agentdata === undefined || loadedAgentData.agentdata === null) {
      return;
    }
    if (loadedAgentData.agentdata.flow === undefined || loadedAgentData.agentdata.flow === null) {
      return;
    }
    if (initialFlowLoadAttempted === true) {
      return;
    }
    if (llmFlowRef.value === null || llmFlowRef.value === undefined) {
      return;
    }
    try {
      llmFlowRef.value.loadFlowData(loadedAgentData.agentdata.flow);
      initialFlowLoadAttempted = true;
    } catch (error) {
      console.error('AIChatAgentSetup: Error loading flow data:', error);
    }
  }
  watch([currentPage, agentMode], () => {
    if (currentPage.value !== 2 || agentMode.value !== 'Flow') {
      initialFlowLoadAttempted = false;
    }
  });
  watch([currentPage, agentMode, () => llmFlowRef.value], () => {
    tryLoadInitialFlowData();
    if (currentPage.value === 2 && agentMode.value === 'Flow') {
      openLlmFlowBuilder();
      nextTick(() => {
        openLlmFlowBuilder();
        nextTick(() => {
          openLlmFlowBuilder();
        });
      });
    }
  }, { flush: 'post' });
  const availableCollectionsForAgent = ref([]);
  const selectedAnalysisCollectionIds = ref([]);
  const isLoadingCollections = ref(false);
  function normalizeAnalysisCollectionId(valueParam) {
    if (typeof valueParam === 'number' && Number.isFinite(valueParam) === true) {
      return valueParam;
    }
    if (typeof valueParam === 'string') {
      const trimmedValue = valueParam.trim();
      if (trimmedValue.length === 0) {
        return '';
      }
      const parsedValue = Number.parseInt(trimmedValue, 10);
      if (Number.isNaN(parsedValue) === false && String(parsedValue) === trimmedValue) {
        return parsedValue;
      }
      return trimmedValue;
    }
    return '';
  }
  function normalizeAnalysisCollectionIds(valueParam) {
    const rawItems = Array.isArray(valueParam) === true ? valueParam : [];
    const normalizedItems = [];
    for (let index = 0; index < rawItems.length; index = index + 1) {
      const normalizedId = normalizeAnalysisCollectionId(rawItems[index]);
      if (normalizedId === '') {
        continue;
      }
      if (normalizedItems.some((item) => String(item) === String(normalizedId)) === true) {
        continue;
      }
      normalizedItems.push(normalizedId);
    }
    return normalizedItems;
  }
  function normalizeAnalysisCollectionRow(itemParam) {
    const item = itemParam !== undefined && itemParam !== null && typeof itemParam === 'object' ? itemParam : {};
    const collectionId = normalizeAnalysisCollectionId(item.collectionId !== undefined ? item.collectionId : (item.collectionid !== undefined ? item.collectionid : item.id));
    if (collectionId === '') {
      return null;
    }
    const collectionNameRaw = item.collectionName !== undefined ? item.collectionName : (item.collectionname !== undefined ? item.collectionname : item.name);
    const collectionName = typeof collectionNameRaw === 'string' && collectionNameRaw.trim() !== '' ? collectionNameRaw.trim() : String(collectionId);
    return {
      ...item,
      collectionId,
      collectionName
    };
  }
  const phoneAgentDescriptionAndRulesLimit = 14571;
  watch(enabledDeploymentChannels, () => {
    applyDeploymentChannelApprovals();
  }, { immediate: true });
  const isPhoneChannelSelected = computed(() => Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.includes('Phone') === true);
  const isWhatsappChannelSelected = computed(() => Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.includes('Whatsapp') === true);
  const isWhatsappVoiceChannelSelected = computed(() => Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.includes('Whatsapp Voice') === true);
  const isWebVoiceAgentChannelSelected = computed(() => Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.includes('Web Voice Agent') === true);
  const isTelegramVoiceChannelSelected = computed(() => Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.includes('Telegram Voice') === true);
  const voiceDeploymentChannels = ['Phone', 'Web Voice Agent', 'Whatsapp Voice', 'Telegram Voice'];
  const isVoiceOnlyChannelSelection = computed(() => {
    const selectedChannels = Array.isArray(selectedDeploymentChannels.value) === true ? selectedDeploymentChannels.value : [];
    if (selectedChannels.length === 0) {
      return false;
    }
    return selectedChannels.every(channel => voiceDeploymentChannels.includes(channel) === true);
  });
  const voiceOnlyAgentModeOptions = computed(() => isVoiceOnlyChannelSelection.value === true ? ['Agentic', 'Flow'] : []);
  const showVoiceSettingsTab = computed(() => showAllFields.value === true || isPhoneChannelSelected.value === true || isWebVoiceAgentChannelSelected.value === true || isWhatsappVoiceChannelSelected.value === true || isTelegramVoiceChannelSelected.value === true);
  const showWebVoiceSettingsTab = computed(() => showAllFields.value === true || isWebVoiceAgentChannelSelected.value === true);
  const voiceAgentDescriptionEstimatedTokens = computed(() => estimateTokens(agentDescription.value));
  const showVoiceAgentPromptWarning = computed(() => {
    const hasVoiceChannelSelected = isPhoneChannelSelected.value === true || isWebVoiceAgentChannelSelected.value === true || isWhatsappVoiceChannelSelected.value === true || isTelegramVoiceChannelSelected.value === true;
    if (hasVoiceChannelSelected !== true) {
      return false;
    }
    return voiceAgentDescriptionEstimatedTokens.value > 4000;
  });
  function getPhoneAgentDescriptionAndRulesLength(descriptionValue, ruleEntries) {
    const descriptionText = typeof descriptionValue === 'string' ? descriptionValue : '';
    const normalizedRules = Array.isArray(ruleEntries) === true ? ruleEntries.filter(rule => typeof rule === 'string' && rule.length > 0) : [];
    if (normalizedRules.length === 0) {
      return descriptionText.length;
    }
    const rulesText = `\nRules you must follow:\n${normalizedRules.map(rule => `- ${rule}`).join('\n')}\n\n`;
    return descriptionText.length + rulesText.length;
  }
  const humanInTheLoopApiBase = computed(() => {
    const raw = apiEndpoint !== undefined && apiEndpoint !== null ? String(apiEndpoint).trim() : '';
    if (raw.length === 0) {
      return '';
    }
    if (raw.endsWith('/') === true) {
      return raw.slice(0, -1);
    }
    return raw;
  });
  const humanInTheLoopSendMessageEndpoint = computed(() => {
    if (humanInTheLoopApiBase.value.length === 0) {
      return '';
    }
    return `${humanInTheLoopApiBase.value}/agents/humanInTheLoop/sendMessage`;
  });
  const humanInTheLoopReturnToAgentEndpoint = computed(() => {
    if (humanInTheLoopApiBase.value.length === 0) {
      return '';
    }
    return `${humanInTheLoopApiBase.value}/agents/humanInTheLoop/returnToAgent`;
  });
  const humanInTheLoopStartWebhookExample = computed(() => 'http://localhost:5055/webhook/humanintheloop/startconversation');
  const humanInTheLoopNewMessageWebhookExample = computed(() => 'http://localhost:5055/webhook/humanintheloop/newmessage');
  const resolvedVoiceIdForCalls = computed(() => {
    const chosenVoiceId = typeof voiceSettingsVoiceId.value === 'string' && voiceSettingsVoiceId.value.length > 0 ? voiceSettingsVoiceId.value : selectedPhoneVoiceId.value;
    if (typeof chosenVoiceId === 'string' && chosenVoiceId.length > 0) {
      return chosenVoiceId;
    }
    if (typeof voiceAgentVoice.value === 'string' && voiceAgentVoice.value.length > 0) {
      return voiceAgentVoice.value;
    }
    return defaultVoiceId;
  });
  const selectedVoiceOptionForCalls = computed(() => getVoiceOptionById(resolvedVoiceIdForCalls.value));
  function getVoiceRuntimeType(voiceOptionParam) {
    const voiceOption = voiceOptionParam !== null && voiceOptionParam !== undefined && typeof voiceOptionParam === 'object' ? voiceOptionParam : {};
    return typeof voiceOption.runtimeType === 'string' ? voiceOption.runtimeType.trim().toLowerCase() : '';
  }
  function isDefaultRealtimeVoiceOptionForRuntime(voiceOptionParam) {
    if (getVoiceRuntimeType(voiceOptionParam) === 'default') {
      return true;
    }
    return false;
  }
  function normalizeVoiceSettingsVoiceTypeForVoice(voiceTypeParam, voiceOptionParam) {
    const normalizedVoiceType = normalizeVoiceSettingsVoiceType(voiceTypeParam);
    if (normalizedVoiceType === 'geminiLive') {
      return 'geminiLive';
    }
    if (isDefaultRealtimeVoiceOptionForRuntime(voiceOptionParam) === true) {
      return 'vtv';
    }
    return normalizedVoiceType;
  }
  watch(voiceSettingsVoiceType, (value) => {
    if (normalizeVoiceSettingsVoiceType(value) === 'geminiLive' && geminiLiveVoiceIds.includes(voiceSettingsVoiceId.value) !== true) {
      voiceSettingsVoiceId.value = geminiLiveVoiceIds[0];
    }
  });
  watch(selectedVoiceOptionForCalls, (value) => {
    if (isDefaultRealtimeVoiceOptionForRuntime(value) === true && voiceSettingsVoiceType.value !== 'vtv' && voiceSettingsVoiceType.value !== 'geminiLive') {
      voiceSettingsVoiceType.value = 'vtv';
    }
  });
  
  const inappropriateContentMessage = ref({});
  const dontKnowMessage = ref({});
  const networkErrorMessage = ref({});
  const promptVersions = ref([]);
  const languages = [
        { code: 'default', name: 'Default' },
        { code: 'en', name: 'English' },
        { code: 'he', name: 'עברית' },
        { code: 'es', name: 'Spanish' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'zh', name: 'Chinese' },
        { code: 'ja', name: 'Japanese' },
        { code: 'ar', name: 'Arabic' },
        { code: 'ru', name: 'Russian' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'it', name: 'Italian' },
        { code: 'ko', name: 'Korean' },
        { code: 'hi', name: 'Hindi' },
        { code: 'sv', name: 'Swedish' },
        { code: 'nl', name: 'Dutch' },
        { code: 'da', name: 'Danish' },
        { code: 'fi', name: 'Finnish' },
        { code: 'no', name: 'Norwegian' },
        { code: 'tr', name: 'Turkish' },
        { code: 'pl', name: 'Polish' }
  ];
  watch(selectedPhoneSourceId, (value, previous) => {
    const hasValue = Number.isFinite(Number(value)) === true && value !== null;
    const valuesDiffer = value !== previous;
    if (hasInitializedPhoneSelection.value === false) {
      return;
    }
    if (hasValue === true && valuesDiffer === true) {
      showVoiceSettingsModal.value = true;
    }
  });
  watch(selectedPhoneVoiceId, (value) => {
    if (typeof value === 'string' && value.length > 0) {
      if (value !== voiceSettingsVoiceId.value) {
        voiceSettingsVoiceId.value = value;
      }
      if (value !== voiceAgentVoice.value) {
        voiceAgentVoice.value = value;
      }
    }
  });
  watch(voiceSettingsVoiceId, (value) => {
    if (typeof value === 'string' && value.length > 0) {
      if (value !== selectedPhoneVoiceId.value) {
        selectedPhoneVoiceId.value = value;
      }
      if (value !== voiceAgentVoice.value) {
        voiceAgentVoice.value = value;
      }
    }
  });
  function openVoiceSettingsModal() {
    showVoiceSettingsModal.value = true;
  }
  function closeVoiceSettingsModal() {
    showVoiceSettingsModal.value = false;
  }
  function confirmVoiceSettings() {
    if (isPhoneChannelSelected.value === true) {
      forceShowVoiceTab.value = false;
    } else {
      forceShowVoiceTab.value = true;
    }
    showVoiceSettingsModal.value = false;
  }
  const flowPreviewKey = ref(uuidv4()); 
   
   function decodeBase64UTF8(base64String) {
    if (base64String === null || base64String === undefined) return '';
    try {
      const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new TextDecoder('utf-8').decode(bytes);
    } catch (error) {
      console.error("Error decoding base64 UTF-8 string:", error);
      toast.error("Failed to decode text content.");
      return ''; 
    }
  }
   
  function escapeHtml(value) {
    if (value === undefined || value === null) {
      return '';
    }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
  
  function sanitizeWindowOpenUrl(value) {
    if (value === undefined || value === null) {
      return null;
    }
    const raw = String(value).trim();
    if (raw.length === 0) {
      return null;
    }
    let urlObj;
    try {
      urlObj = new URL(raw, window.location.origin);
    } catch (error) {
      return null;
    }
    const protocol = typeof urlObj.protocol === 'string' ? urlObj.protocol.toLowerCase() : '';
    const isProd = process.env.environment === 'production';
    if (protocol === 'https:') {
      return urlObj.href;
    }
    if (protocol === 'http:' && isProd === false) {
      return urlObj.href;
    }
    return null;
  }

  function normalizeExternalBaseUrl(value) {
    const safe = sanitizeWindowOpenUrl(value);
    if (safe === null) {
      return '';
    }
    try {
      const parsed = new URL(safe);
      parsed.pathname = '';
      parsed.search = '';
      parsed.hash = '';
      return parsed.toString().replace(/\/$/, '');
    } catch (error) {
      return '';
    }
  }

  function getCustomDomainBase(customDomains, serviceName) {
    const domains = typeof customDomains === 'object' && customDomains !== null ? customDomains : {};
    if (typeof domains[serviceName] !== 'string') {
      return '';
    }
    return normalizeExternalBaseUrl(domains[serviceName]);
  }
  const controlCharPattern = /[\u0000-\u0008\u000B-\u001F\u007F]/;
  const dangerousTextPattern = /(javascript:|data:|vbscript:|on\w+\s*=|<\s*\/?\s*script)/i;
  function secureUserText(raw, options = {}) {
    const allowNewLines = options.allowNewLines === true;
    const allowEmpty = options.allowEmpty === true;
    const maxLength = typeof options.maxLength === 'number' ? options.maxLength : null;
    const fieldName = typeof options.fieldName === 'string' ? options.fieldName : 'Field';
    const normalized = typeof raw === 'string' ? raw.normalize('NFKC') : '';
    const prepared = allowNewLines === true ? normalized.replace(/\r\n/g, '\n') : normalized.replace(/\s+/g, ' ');
    const trimmed = prepared.trim();
    if (trimmed.length === 0) {
      if (allowEmpty === true) {
        return { value: '', error: null };
      }
      return { value: '', error: `${fieldName} is required.` };
    }
    if (maxLength !== null && trimmed.length > maxLength) {
      return { value: '', error: `${fieldName} exceeds ${maxLength} characters.` };
    }
    if (controlCharPattern.test(trimmed) === true) {
      return { value: '', error: `${fieldName} contains disallowed control characters.` };
    }
    if (dangerousTextPattern.test(trimmed) === true) {
      return { value: '', error: `${fieldName} contains disallowed expressions.` };
    }
    if (options.disallowAngleBrackets === true && /[<>]/.test(trimmed) === true) {
      return { value: '', error: `${fieldName} cannot contain angle brackets.` };
    }
    return { value: allowNewLines === true ? trimmed : trimmed.replace(/\s+/g, ' ') , error: null };
  }
  function sanitizeLocalizationObject(map, options = {}) {
    const sanitized = {};
    if (map !== null && typeof map === 'object') {
      Object.keys(map).forEach((key) => {
        const normalizedKey = typeof key === 'string' ? key.trim() : '';
        if (normalizedKey.length === 0) {
          return;
        }
        const result = secureUserText(map[key], {
          fieldName: `${options.fieldName !== undefined ? options.fieldName : 'Value'} (${normalizedKey})`,
          maxLength: options.maxLength,
          allowNewLines: options.allowNewLines === true,
          disallowAngleBrackets: options.disallowAngleBrackets === true,
          allowEmpty: options.allowEmpty === true
        });
        if (result.error === null) {
          if (result.value.length === 0 && options.allowEmpty === true) {
            return;
          }
          sanitized[normalizedKey] = result.value;
        } else {
          sanitized.__error = result.error;
        }
      });
    }
    if (sanitized.__error !== undefined) {
      const err = sanitized.__error;
      delete sanitized.__error;
      return { sanitized: {}, error: err };
    }
    return { sanitized, error: null };
  }
  function normalizeLocalizationInputValue(value) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return { default: trimmed };
      }
      return {};
    }
    if (value !== null && typeof value === 'object') {
      const normalized = {};
      Object.keys(value).forEach((key) => {
        const normalizedKey = typeof key === 'string' ? key.trim() : '';
        if (normalizedKey.length === 0) {
          return;
        }
        const normalizedValue = typeof value[key] === 'string' ? value[key].trim() : '';
        if (normalizedValue.length > 0) {
          normalized[normalizedKey] = normalizedValue;
        }
      });
      if ((typeof normalized.default !== 'string' || normalized.default.length === 0) && typeof normalized.en === 'string' && normalized.en.length > 0) {
        normalized.default = normalized.en;
        delete normalized.en;
      }
      return normalized;
    }
    return {};
  }

  function normalizeGeneratedRuleForInjection(rawRule) {
    let rule = typeof rawRule === 'string' ? rawRule.normalize('NFKC').trim() : '';
    if (rule.length === 0) {
      return '';
    }
    rule = rule.replace(/[<>]/g, '');
    rule = rule.replace(/https?:\/\/[^\/\s]+/gi, '');
    rule = rule.replace(/\(([\/][^)]+)\)/g, '$1');
    rule = normalizeRuleLinkTrailingSlashes(rule);
    rule = rule.replace(/\s+/g, ' ').trim();
    if (rule.length > 3500) {
      rule = rule.substring(0, 3500).trim();
    }
    return rule;
  }

  function normalizeRuleLinkToken(rawToken) {
    let token = typeof rawToken === 'string' ? rawToken : '';
    if (token.length === 0) {
      return token;
    }
    let prefix = '';
    let suffix = '';
    const prefixChars = '([{';
    const suffixChars = '.,;:!?)]}';
    while (token.length > 0 && prefixChars.includes(token[0]) === true) {
      prefix = `${prefix}${token[0]}`;
      token = token.substring(1);
    }
    while (token.length > 0 && suffixChars.includes(token[token.length - 1]) === true) {
      suffix = `${token[token.length - 1]}${suffix}`;
      token = token.substring(0, token.length - 1);
    }
    const isUrl = token.startsWith('http://') === true || token.startsWith('https://') === true;
    const isPath = token.startsWith('/') === true && token.startsWith('//') !== true && token.length > 1;
    if (isUrl === true || isPath === true) {
      while (token.length > 1 && token.endsWith('/') === true) {
        token = token.substring(0, token.length - 1);
      }
    }
    return `${prefix}${token}${suffix}`;
  }

  function normalizeRuleLinkTrailingSlashes(rawText) {
    const text = typeof rawText === 'string' ? rawText : '';
    if (text.length === 0) {
      return '';
    }
    let normalized = text.replace(/https?:\/\/[^\s<>"']+/gi, (match) => normalizeRuleLinkToken(match));
    normalized = normalized.replace(/^\/[^\s<>"']+/g, (match) => normalizeRuleLinkToken(match));
    normalized = normalized.replace(/[\s([{]\/[^\s<>"']+/g, (match) => {
      const prefixText = match.substring(0, 1);
      const token = match.substring(1);
      if (token.startsWith('//') === true) {
        return match;
      }
      return `${prefixText}${normalizeRuleLinkToken(token)}`;
    });
    return normalized;
  }

  function sanitizeRuleEntries(rawRules) {
    if (Array.isArray(rawRules) !== true) {
      return { sanitized: [], error: null };
    }
    const sanitized = [];
    for (let i = 0; i < rawRules.length; i += 1) {
      const rule = rawRules[i];
      if (typeof rule !== 'string') {
        continue;
      }
      const normalizedRule = normalizeRuleLinkTrailingSlashes(rule);
      const validation = secureUserText(normalizedRule, {
        fieldName: `Rule ${i + 1}`,
        maxLength: 3500,
        allowNewLines: true,
        disallowAngleBrackets: true
      });
      if (validation.error !== null) {
        return { sanitized: [], error: validation.error };
      }
      sanitized.push(validation.value);
    }
    return { sanitized, error: null };
  }

  function sanitizeOptionalText(raw, fieldName, options = {}) {
    const validation = secureUserText(raw, {
      fieldName,
      allowNewLines: options.allowNewLines === true,
      disallowAngleBrackets: options.disallowAngleBrackets === true,
      maxLength: options.maxLength,
      allowEmpty: true
    });
    if (validation.error !== null) {
      return { value: '', error: validation.error };
    }
    return { value: validation.value, error: null };
  }

  function sanitizeActionButtonsList(buttons) {
    if (Array.isArray(buttons) !== true) {
      return { sanitized: [], error: null };
    }
    const sanitized = [];
    for (let i = 0; i < buttons.length && sanitized.length < 4; i += 1) {
      const rawButton = buttons[i] !== undefined && buttons[i] !== null ? buttons[i] : {};
      const labelCheck = secureUserText(rawButton.label, {
        fieldName: `Button ${i + 1} label`,
        maxLength: 80,
        disallowAngleBrackets: true
      });
      if (labelCheck.error !== null) {
        return { sanitized: [], error: labelCheck.error };
      }
      const type = rawButton.type === 'URL' ? 'URL' : 'Text';
      let actionValue = '';
      if (type === 'URL') {
        const actionCheck = secureUserText(rawButton.action, {
          fieldName: `Button ${i + 1} URL`,
          maxLength: 500
        });
        if (actionCheck.error !== null) {
          return { sanitized: [], error: actionCheck.error };
        }
        const normalizedUrl = actionCheck.value.toLowerCase().startsWith('http://') === true || actionCheck.value.toLowerCase().startsWith('https://') === true
          ? actionCheck.value
          : null;
        if (normalizedUrl === null) {
          return { sanitized: [], error: `Button ${i + 1} URL must start with http:// or https://.` };
        }
        actionValue = normalizedUrl;
      }
      sanitized.push({
        label: labelCheck.value,
        type,
        action: actionValue
      });
    }
    return { sanitized, error: null };
  }

  function sanitizeRegexEntries(entries) {
    if (Array.isArray(entries) !== true) {
      return { sanitized: [], error: null };
    }
    const sanitized = [];
    for (let i = 0; i < entries.length; i += 1) {
      const rawValue = typeof entries[i] === 'string' ? entries[i].normalize('NFKC') : '';
      const trimmed = rawValue.trim();
      if (trimmed.length === 0) {
        continue;
      }
      if (controlCharPattern.test(trimmed) === true) {
        return { sanitized: [], error: `Custom block regex ${i + 1} contains disallowed control characters.` };
      }
      if (trimmed.length > 500) {
        return { sanitized: [], error: `Custom block regex ${i + 1} exceeds 500 characters.` };
      }
      sanitized.push(trimmed);
    }
    return { sanitized, error: null };
  }

  function normalizeDivisionIds(value) {
    if (Array.isArray(value) !== true) {
      return [];
    }
    const normalized = [];
    for (let i = 0; i < value.length; i = i + 1) {
      const raw = value[i];
      const num = Number(raw);
      if (Number.isFinite(num) === true && Number.isInteger(num) === true && num > 0) {
        if (normalized.includes(num) === false) {
          normalized.push(num);
        }
      }
    }
    return normalized;
  }

  function normalizeStyleGuidValue(value) {
    if (typeof value !== 'string') {
      return '';
    }
    const trimmed = value.trim();
    if (trimmed === '' || trimmed === '*') {
      return '';
    }
    return trimmed;
  }
  
  const knowledgeBaseFolders = ref([]); 
  const knowledgeBaseFileAssignments = ref({}); 
  const currentKbPath = ref([{ id: null, name: 'Knowledge Base' }]);
   const showCreateFolderModal = ref(false);
   const newFolderName = ref('');
   const showMoveFileModal = ref(false);
   const fileToMove = ref(null); 
   const targetFolderForMove = ref(null); 
  const createFolderTargetName = computed(() => {
    const pathValue = currentKbPath.value;
    let folderName = '';
    if (Array.isArray(pathValue) === true && pathValue.length > 0) {
      const lastIndex = pathValue.length - 1;
      const lastEntry = pathValue[lastIndex];
      if (lastEntry !== undefined && lastEntry !== null && typeof lastEntry.name === 'string') {
        folderName = lastEntry.name;
      }
    }
    if (folderName === 'Knowledge Base') {
      folderName = t('aiChatAgentSetup.knowledgeBase.title', 'Knowledge Base');
    }
    return folderName;
  });
  const moveFolderOptions = computed(() => {
    const rootOption = {
      folderId: null,
      folderName: t('aiChatAgentSetup.modals.moveFile.rootDestination', 'Knowledge Base (Root)')
    };
    const folderOptions = knowledgeBaseFolders.value.map((folder) => ({
      folderId: folder.id,
      folderName: folder.name
    }));
    return [rootOption, ...folderOptions];
  });
  const selectedMoveFolderArray = computed({
    get() {
      if (targetFolderForMove.value === null || targetFolderForMove.value === undefined) {
        return [null];
      }
      return [targetFolderForMove.value];
    },
    set(val) {
      if (Array.isArray(val) === true && val.length > 0) {
        targetFolderForMove.value = val[0];
        return;
      }
      targetFolderForMove.value = null;
    }
  });
   const draggedItem = ref(null); 
   const isDragOverFolder = ref(null); 
  
  
   const folderBeingRenamedId = ref(null);
   const newFolderNameInput = ref('');
  
  const cachedFileNames = ref([]);
  
  const isEditingTextFile = ref(false);
  const originalTextFileToEdit = ref(null);
  
  const kbViewMode = ref('rows');
  
  
  const agentNameError = ref(false);
  const agentNameErrorMessage = ref('');
  const agentDescriptionError = ref(false);
  const agentDescriptionErrorMessage = ref('');
  const agentShortDescriptionError = ref(false); 
  const agentShortDescriptionErrorMessage = ref(''); 
  const startingMessageError = ref(false);
  const startingMessageErrorMessage = ref('');
  const divisionError = ref(false);
  const divisionErrorMessage = ref('');
  
  const isSavingAndRedirecting = ref(false);
  const isRefreshingKB = ref(false);
  const showMaxPromptModal = ref(false);
  const isPromptAssistantOpen = ref(true);
  const isEnhancingDescription = ref(false);
  const agentDescriptionRef = ref(null);
  const maxAgentDescriptionRef = ref(null);
  const promptAssistantModelLabel = computed(() => {
    const model = selectedLlmModelConfig.value;
    if (model === null || model === undefined) {
      return t('aiChatAgentSetup.promptAssistant.modelUnavailable', 'Model will be selected automatically');
    }
    if (typeof model.displayName === 'string' && model.displayName !== '') {
      return model.displayName;
    }
    if (typeof model.name === 'string' && model.name !== '') {
      return model.name;
    }
    if (typeof model.id === 'string' && model.id !== '') {
      return model.id;
    }
    return t('aiChatAgentSetup.promptAssistant.modelUnavailable', 'Model will be selected automatically');
  });
  const promptAssistantDraftContext = computed(() => {
    return {
      agentName: agentName.value,
      agentDescription: agentDescription.value,
      agentShortDescription: agentShortDescription.value,
      agentLanguage: agentLanguage.value,
      agentMode: agentMode.value,
      agentType: agentType.value,
      agentTone: selectedAgentTone.value,
      agentPersonality: selectedAgentPersonality.value,
      llmModelId: typeof resolvedSelectedLLMModelId.value === 'string' ? resolvedSelectedLLMModelId.value : '',
      llmModelLabel: promptAssistantModelLabel.value,
      startingMessage: currentStartingMessage.value,
      rules: Array.isArray(rules.value) === true ? rules.value.slice() : []
    };
  });

  function openMaxPromptModal() {
    isPromptAssistantOpen.value = true;
    showMaxPromptModal.value = true;
  }

  function closeMaxPromptModal() {
    showMaxPromptModal.value = false;
  }

  const enhanceDescriptionWithAI = async (textAreaRef) => {
    if (isEnhancingDescription.value === true) return;
    try {
      const textarea = textAreaRef && textAreaRef.$el ? textAreaRef.$el : (textAreaRef && textAreaRef.value ? textAreaRef.value : textAreaRef);
      if (textarea === undefined || textarea === null) return;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      let textToEnhance = agentDescription.value;
      const isPartial = start !== end && start !== undefined && end !== undefined;
      if (isPartial === true) {
        textToEnhance = agentDescription.value.substring(start, end);
      }
      if (typeof textToEnhance !== 'string' || textToEnhance.trim() === '') return;
      isEnhancingDescription.value = true;
      let combinedRulesText = '';
      if (isPartial !== true && rules.value !== undefined && rules.value !== null && rules.value.length > 0) {
        combinedRulesText = '\n\nRules:\n' + rules.value.join('\n');
      }
      const response = await axios.post(`${apiEndpoint}/api/geni/enhance`, { textToEnhance: textToEnhance + combinedRulesText }, { headers: getAuthHeaders() });
      if (response.data !== undefined && response.data !== null && response.data.enhancedText !== undefined && response.data.enhancedText !== null) {
        if (isPartial === true) {
          agentDescription.value = agentDescription.value.substring(0, start) + response.data.enhancedText + agentDescription.value.substring(end);
        } else {
          agentDescription.value = response.data.enhancedText;
        }
      }
    } catch (error) {
      console.error('Enhance AI failed', error);
      globalState.addNotification('Failed to enhance description.', 'error');
    } finally {
      isEnhancingDescription.value = false;
    }
  };

  function normalizePromptVersionEntries(value) {
    const source = Array.isArray(value) === true ? value : [];
    const normalized = [];
    for (let i = 0; i < source.length; i = i + 1) {
      const item = source[i];
      if (item !== undefined && item !== null && typeof item === 'object') {
        const prompt = typeof item.prompt === 'string' ? item.prompt : '';
        const savedAt = typeof item.savedAt === 'string' && item.savedAt.length > 0 ? item.savedAt : '';
        const savedAtDate = typeof item.savedAtDate === 'string' && item.savedAtDate.length > 0 ? item.savedAtDate : '';
        const savedAtTime = typeof item.savedAtTime === 'string' && item.savedAtTime.length > 0 ? item.savedAtTime : '';
        const id = typeof item.id === 'string' && item.id.length > 0 ? item.id : `prompt-version-${i + 1}`;
        const versionNumberValue = Number(item.version);
        const promptLengthValue = Number(item.promptLength);
        normalized.push({
          id: id,
          version: Number.isFinite(versionNumberValue) === true && versionNumberValue > 0 ? versionNumberValue : i + 1,
          savedAt: savedAt,
          savedAtDate: savedAtDate,
          savedAtTime: savedAtTime,
          prompt: prompt,
          promptLength: Number.isFinite(promptLengthValue) === true && promptLengthValue >= 0 ? promptLengthValue : prompt.length
        });
      }
    }
    return normalized;
  }

  function createPromptVersionEntry(promptTextParam, versionNumber) {
    const savedAt = new Date();
    const promptText = typeof promptTextParam === 'string' ? promptTextParam : '';
    return {
      id: `prompt-${savedAt.getTime()}-${versionNumber}`,
      version: versionNumber,
      savedAt: savedAt.toISOString(),
      savedAtDate: savedAt.toLocaleDateString(),
      savedAtTime: savedAt.toLocaleTimeString(),
      prompt: promptText,
      promptLength: promptText.length
    };
  }

  function buildPromptVersionsForSave() {
    const current = normalizePromptVersionEntries(promptVersions.value);
    const nextVersionNumber = current.length + 1;
    const next = [createPromptVersionEntry(agentDescription.value, nextVersionNumber)];
    for (let i = 0; i < current.length; i = i + 1) {
      next.push(current[i]);
    }
    return next;
  }

  function restorePromptVersion(version) {
    if (version === undefined) {
      return;
    }
    if (version === null) {
      return;
    }
    const promptText = typeof version.prompt === 'string' ? version.prompt : '';
    if (promptText.length === 0) {
      toast.info('Prompt version is empty.');
      return;
    }
    agentDescription.value = promptText;
    handleAgentDescriptionInput();
    toast.success('Prompt version restored. Save the agent to keep it.');
  }

  watch(showMaxPromptModal, (value) => {
    if (typeof document !== 'undefined') {
      const scrollEl = document.getElementById('body-scroll');
    const locked = value === true;
    document.documentElement.style.overflow = locked === true ? 'hidden' : '';
    document.documentElement.style.overscrollBehavior = locked === true ? 'none' : '';
    document.body.style.overflow = locked === true ? 'hidden' : '';
    document.body.style.overscrollBehavior = locked === true ? 'none' : '';
    if (scrollEl !== null) {
      scrollEl.classList.toggle('modal-page-scroll-locked', locked);
      scrollEl.style.overflow = locked === true ? 'hidden' : '';
      scrollEl.style.overflowY = locked === true ? 'hidden' : '';
      }
    }
  });
  
  const loadingTips = [
    t('aiChatAgentSetup.loadingTips.tip1'),
    t('aiChatAgentSetup.loadingTips.tip2'),
    t('aiChatAgentSetup.loadingTips.tip3'),
    t('aiChatAgentSetup.loadingTips.tip4'),
    t('aiChatAgentSetup.loadingTips.tip5'),
    t('aiChatAgentSetup.loadingTips.tip6'),
    t('aiChatAgentSetup.loadingTips.tip7'),
    t('aiChatAgentSetup.loadingTips.tip8'),
    t('aiChatAgentSetup.loadingTips.tip9'),
    t('aiChatAgentSetup.loadingTips.tip10')
  ];
   
  const currentFolderId = computed(() => {
    if (currentKbPath.value.length === 0) return null;
    return currentKbPath.value[currentKbPath.value.length - 1].id;
  });
  
  function computeKnowledgeProcessingStatus(file, statusInfo) {
      let computedStatus = isDefinedAndNotEmpty(statusInfo) != false ? statusInfo.status : (file.fileType === 'website' || file.fileType === 'websiteurl' ? 'Processing' : 'Available');
      const ft = file.fileType;
      if (ft === 'website' || ft === 'websiteurl' || ft === 'websitepage') {
          const prog = statusInfo !== undefined && statusInfo !== null ? statusInfo.progress : null;
          if (prog !== undefined && prog !== null) {
              const totalVal = Number(prog.total);
              const currentVal = Number(prog.current);
              const totalIsNum = Number.isFinite(totalVal) === true;
              const currentIsNum = Number.isFinite(currentVal) === true;
              if (totalIsNum === true && currentIsNum === true) {
                  if (totalVal <= 0 || currentVal >= totalVal) {
                      computedStatus = 'Completed';
                  }
              }
          } else {
              const detailsLower = (statusInfo !== undefined && statusInfo !== null && statusInfo.details !== undefined && statusInfo.details !== null) ? String(statusInfo.details).toLowerCase() : '';
              if (detailsLower.includes('no pages') === true || detailsLower.includes('no urls') === true || detailsLower.includes('no links') === true) {
                  computedStatus = 'Completed';
              }
          }
      }
      return computedStatus;
  }
  
  function processLoadedAgentData(agentData) {
      loadedAgentData = agentData;
      console.log('AIChatAgentSetup: Processing agent data:', loadedAgentData);
      agentName.value = loadedAgentData.agentname;
      agentDescription.value = loadedAgentData.agentdescription;
      selectedAgentPersonality.value = '';
      agentShortDescription.value = isDefinedAndNotEmpty(loadedAgentData.agentShortDescription) != false ? loadedAgentData.agentShortDescription : '';
      agentActive.value = loadedAgentData.agentstatus.toLowerCase() === 'active';
      agentType.value = isDefinedAndNotEmpty(loadedAgentData.agenttype) != false ? loadedAgentData.agenttype : 'PublicAgent';
      agentMode.value = isDefinedAndNotEmpty(loadedAgentData.agentMode) != false ? loadedAgentData.agentMode : 'Agentic';
      agenticBehaviour.value = loadedAgentData.agentdata !== undefined && loadedAgentData.agentdata.agenticBehaviour === false ? false : true;
      if (agentMode.value === 'Flow' || agentMode.value === 'FlowBased') {
        agenticBehaviour.value = true;
      } else if (agenticBehaviour.value === false) {
        agentMode.value = 'SuperFast';
      }
      pendingAgenticBehaviour.value = agenticBehaviour.value;
      selectedDivisionIds.value = normalizeDivisionIds(isDefinedAndNotEmpty(loadedAgentData.divisionIds) != false ? loadedAgentData.divisionIds : []);
  
      if (loadedAgentData.agentdata !== undefined && loadedAgentData.agentdata !== null) {
          const data = loadedAgentData.agentdata;
          const realTimeFileStatuses = isDefinedAndNotEmpty(loadedAgentData.fileStatuses) != false ? loadedAgentData.fileStatuses : [];
          selectedToolIds.value = isDefinedAndNotEmpty(data.selectedToolIds) != false ? data.selectedToolIds : [];
          selectedToolSettings.value = normalizeLoadedToolSettings(data?.configurations?.toolSettings);
  
          if (typeof data.startingMessage === 'string') {
              startingMessage.value = { default: data.startingMessage };
          } else {
              startingMessage.value = isDefinedAndNotEmpty(data.startingMessage) != false ? data.startingMessage : {};
              if (startingMessage.value.en !== undefined && startingMessage.value.default === undefined) {
                  startingMessage.value.default = startingMessage.value.en;
                  delete startingMessage.value.en;
              }
          }
          
          agentLanguage.value = isDefinedAndNotEmpty(data.language) != false ? data.language : 'en';
          let loadedPromptVersions = [];
          if (data.configurations !== undefined && data.configurations !== null && data.configurations !== false && Array.isArray(data.configurations.promptVersions) === true) {
            loadedPromptVersions = data.configurations.promptVersions;
          } else if (Array.isArray(data.promptVersions) === true) {
            loadedPromptVersions = data.promptVersions;
          }
          promptVersions.value = normalizePromptVersionEntries(loadedPromptVersions);
          rules.value = isDefinedAndNotEmpty(data.rules) != false ? data.rules : [];
          selectedStyleGuid.value = normalizeStyleGuidValue(isDefinedAndNotEmpty(data.styleGuid) != false ? data.styleGuid : '');
          selectedSharedKbIds.value = Array.isArray(data.sharedKnowledgeBaseIds) === true ? data.sharedKnowledgeBaseIds : (isDefinedAndNotEmpty(data.sharedKnowledgeBaseId) != false ? [data.sharedKnowledgeBaseId] : []);
          selectedLinkedAgentKbIds.value = Array.isArray(data.linkedAgentKnowledgeBaseIds) === true ? data.linkedAgentKnowledgeBaseIds : [];
  
          knowledgeBaseFolders.value = isDefinedAndNotEmpty(data.knowledgeBaseFolders) != false ? data.knowledgeBaseFolders : [];
          knowledgeBaseFileAssignments.value = isDefinedAndNotEmpty(data.knowledgeBaseFileAssignments) != false ? data.knowledgeBaseFileAssignments : {};
  
          const allAgentFiles = isDefinedAndNotEmpty(data.files) != false ? data.files : [];
          const statusMap = new Map((isDefinedAndNotEmpty(realTimeFileStatuses) != false ? realTimeFileStatuses : []).map(s => [s.name, s]));
  
          existingFiles.value = allAgentFiles.map(file => {
              const statusInfo = statusMap.get(file.fileName);
              const computedStatus = computeKnowledgeProcessingStatus(file, statusInfo);
              const hasUtcHour = Number.isFinite(Number(file.rescrapeAtHour)) === true;
              const hasUtcMinute = Number.isFinite(Number(file.rescrapeAtMinute)) === true;
              const derivedLocalTime = (hasUtcHour === true || hasUtcMinute === true) ? toLocalHHMMFromUtc(Number(file.rescrapeAtHour), Number(file.rescrapeAtMinute)) : (typeof file.rescrapeAtTime === 'string' ? file.rescrapeAtTime : '');
              return {
                  ...file,
                  hiddenFromSources: file.hiddenFromSources === true,
                  content: (file.fileType === 'text' && file.fileData) ? decodeBase64UTF8(file.fileData) : (isDefinedAndNotEmpty(file.content) != false ? file.content : null),
                  status: 'existing',
                  processingStatus: computedStatus,
                processingDetails: isDefinedAndNotEmpty(statusInfo) != false ? statusInfo.details : null,
                rescrapeAtTime: derivedLocalTime
              };
          });
  
          const loadedSelectedLlmModelId = getLoadedSelectedLlmModelId(data);
          if (loadedSelectedLlmModelId !== '') {
              selectedLLMModel.value = loadedSelectedLlmModelId;
          } else if (availableLLMModels.value.length > 0) {
              selectedLLMModel.value = availableLLMModels.value[0].id;
          }
          const routerLlmCandidates = [
            data?.configurations?.agentsToAgentsRouterLlmModelId,
            data?.configurations?.agentToAgentRouterLlmModelId,
            data?.agentsToAgentsRouterLlmModelId,
            data?.agentToAgentRouterLlmModelId
          ];
          agentsToAgentsRouterLlmModelId.value = '';
          for (let routerLlmIndex = 0; routerLlmIndex < routerLlmCandidates.length; routerLlmIndex = routerLlmIndex + 1) {
            const routerLlmCandidate = typeof routerLlmCandidates[routerLlmIndex] === 'string' ? routerLlmCandidates[routerLlmIndex].trim() : '';
            if (routerLlmCandidate.length > 0) {
              agentsToAgentsRouterLlmModelId.value = routerLlmCandidate;
              break;
            }
          }
          showSubAgentConversationsInReports.value = false;
          if (data?.configurations?.showSubAgentConversationsInReports === true) {
            showSubAgentConversationsInReports.value = true;
          }
          if (data?.showSubAgentConversationsInReports === true) {
            showSubAgentConversationsInReports.value = true;
          }
  
          if (data.configurations!=false) {
             enableFeedbackButtons.value = isDefinedAndNotEmpty(data.configurations.enableFeedbackButtons) != false ? data.configurations.enableFeedbackButtons : true;
             enableSuggestedResponses.value = isDefinedAndNotEmpty(data.configurations.enableSuggestedResponses) != false ? data.configurations.enableSuggestedResponses : false;
             enableStreamingText.value = data.configurations.enableStreamingText !== false;
             enableUserFineTune.value = data.configurations.enableUserFineTune === true;
             retrieveUserLocation.value = isDefinedAndNotEmpty(data.configurations.retrieveUserLocation) != false ? data.configurations.retrieveUserLocation : false;
             makeLinksClickable.value = isDefinedAndNotEmpty(data.configurations.makeLinksClickable) != false ? data.configurations.makeLinksClickable : true;
            clearSessionOnRefresh.value = isDefinedAndNotEmpty(data.configurations.clearSessionOnRefresh) != false ? data.configurations.clearSessionOnRefresh : false;
            enabledSavedSessionHistory.value = data.configurations.enabledSavedSessionHistory === true;
            regularOpenAiContentFilterFailoverEnabled.value = data.configurations.regularOpenAiContentFilterFailoverEnabled === true;
             showMap.value = data.configurations.showMap !== false;
             enableFileUploadInChat.value = isDefinedAndNotEmpty(data.configurations.enableFileUploadInChat) != false ? data.configurations.enableFileUploadInChat : (agentType.value === 'PrivateAgent');
             inappropriateContentMessage.value = isDefinedAndNotEmpty(data.configurations.inappropriateContentMessage) != false ? data.configurations.inappropriateContentMessage : {};
             dontKnowMessage.value = isDefinedAndNotEmpty(data.configurations.dontKnowMessage) != false ? data.configurations.dontKnowMessage : {};
             networkErrorMessage.value = isDefinedAndNotEmpty(data.configurations.networkErrorMessage) != false ? data.configurations.networkErrorMessage : {};
              selectedDeploymentChannels.value = sanitizeDeploymentChannels(isDefinedAndNotEmpty(data.configurations.deploymentChannels) != false ? data.configurations.deploymentChannels : []);
             const legacyAllowCallControl = data.configurations.allowCallControl === true;
             voiceAllowAgentHangup.value = data.configurations.allowAgentHangup !== undefined ? data.configurations.allowAgentHangup === true : legacyAllowCallControl;
             voiceAllowCallTransfer.value = data.configurations.allowCallTransfer !== undefined ? data.configurations.allowCallTransfer === true : legacyAllowCallControl;
             limitReachedMessage.value = isDefinedAndNotEmpty(data.configurations.limitReachedMessage) != false ? data.configurations.limitReachedMessage : defaultLimitReachedMessageTemplate;
             messageLimitPerConversation.value = Number.isFinite(Number(data.configurations.messageLimitPerConversation)) != false && Number(data.configurations.messageLimitPerConversation) >= 0 ? Number(data.configurations.messageLimitPerConversation) : 0;
             messageLimitPerIP.value = Number.isFinite(Number(data.configurations.messageLimitPerIP)) != false && Number(data.configurations.messageLimitPerIP) >= 0 ? Number(data.configurations.messageLimitPerIP) : 0;
             messageLimitPerIPDurationMinutes.value = Number.isFinite(Number(data.configurations.messageLimitPerIPDurationMinutes)) != false && Number(data.configurations.messageLimitPerIPDurationMinutes) >= 1 ? Number(data.configurations.messageLimitPerIPDurationMinutes) : 3;
             messageLimitPerPhoneNumber.value = Number.isFinite(Number(data.configurations.messageLimitPerPhoneNumber)) != false && Number(data.configurations.messageLimitPerPhoneNumber) >= 0 ? Number(data.configurations.messageLimitPerPhoneNumber) : 0;
             messageLimitPerPhoneNumberDurationMinutes.value = Number.isFinite(Number(data.configurations.messageLimitPerPhoneNumberDurationMinutes)) != false && Number(data.configurations.messageLimitPerPhoneNumberDurationMinutes) >= 1 ? Number(data.configurations.messageLimitPerPhoneNumberDurationMinutes) : 3;
             if (data.configurations.limitMessagesPerConversationEnabled !== undefined && data.configurations.limitMessagesPerConversationEnabled !== null) {
               limitMessagesPerConversationEnabled.value = data.configurations.limitMessagesPerConversationEnabled === true;
             } else {
               limitMessagesPerConversationEnabled.value = false;
             }
             if (data.configurations.limitMessagesPerIPEnabled !== undefined && data.configurations.limitMessagesPerIPEnabled !== null) {
               limitMessagesPerIPEnabled.value = data.configurations.limitMessagesPerIPEnabled === true;
             } else {
               limitMessagesPerIPEnabled.value = false;
             }
             enablePIIRedaction.value = data.configurations.enablePIIRedaction === true;
             redactEmails.value = data.configurations.redactEmails !== false;
             redactPhoneNumbers.value = data.configurations.redactPhoneNumbers === true;
             customBlockRegex.value = Array.isArray(data.configurations.customBlockRegex) != false ? data.configurations.customBlockRegex : [];
             if (Array.isArray(data.configurations?.responseFilters) === true) {
               responseFilters.value = data.configurations.responseFilters;
             } else if (Array.isArray(data.responseFilters) === true) {
               responseFilters.value = data.responseFilters;
             } else {
               responseFilters.value = [];
             }
             if (isDefinedAndNotEmpty(data.configurations.kbToolUseMode) != false) {
               kbToolUseMode.value = data.configurations.kbToolUseMode;
             } else {
               kbToolUseMode.value = 'auto';
             }
             retrievalStrategy.value = ['standard', 'auto', 'multiEntity'].includes(data.configurations.retrievalStrategy) === true ? data.configurations.retrievalStrategy : 'standard';
             if (isDefinedAndNotEmpty(data.configurations.agentToneId) != false) {
               selectedAgentTone.value = data.configurations.agentToneId;
             }
              whatsappClosingMessage.value = isDefinedAndNotEmpty(data.configurations.whatsappClosingMessage) != false ? data.configurations.whatsappClosingMessage : '';
              if (Number.isFinite(Number(data.configurations.retrieverKValue)) === true && Number(data.configurations.retrieverKValue) >= 1) {
                retrieverKValue.value = Number(data.configurations.retrieverKValue);
              } else {
                retrieverKValue.value = 10;
              }
              if (Number.isFinite(Number(data.configurations.vectorDbQueriesLimit)) === true && Number(data.configurations.vectorDbQueriesLimit) >= 1) {
                vectorDbQueriesLimit.value = Number(data.configurations.vectorDbQueriesLimit);
              } else if (Number.isFinite(Number(data.configurations.vectorDbQueryCount)) === true && Number(data.configurations.vectorDbQueryCount) >= 1) {
                vectorDbQueriesLimit.value = Number(data.configurations.vectorDbQueryCount);
              } else if (Number.isFinite(Number(data.configurations.vectorDbGeneratedQueriesCount)) === true && Number(data.configurations.vectorDbGeneratedQueriesCount) >= 1) {
                vectorDbQueriesLimit.value = Number(data.configurations.vectorDbGeneratedQueriesCount);
              } else if (Number.isFinite(Number(data.configurations.generatedQueriesCount)) === true && Number(data.configurations.generatedQueriesCount) >= 1) {
                vectorDbQueriesLimit.value = Number(data.configurations.generatedQueriesCount);
              } else if (Number.isFinite(Number(data.configurations.knowledgeQueryCount)) === true && Number(data.configurations.knowledgeQueryCount) >= 1) {
                vectorDbQueriesLimit.value = Number(data.configurations.knowledgeQueryCount);
              } else {
                vectorDbQueriesLimit.value = 3;
              }
              vectorQueryAdditionalText.value = typeof data.configurations.vectorQueryAdditionalText === 'string' ? data.configurations.vectorQueryAdditionalText : '';
              prependHistoryToShortVectorQueries.value = data.configurations.prependHistoryToShortVectorQueries === true;
              if (Number.isFinite(Number(data.configurations.maxOutputTokens)) === true && Number(data.configurations.maxOutputTokens) >= 1) {
                maxOutputTokens.value = Number(data.configurations.maxOutputTokens);
              } else {
                maxOutputTokens.value = 2048;
              }
              if (Array.isArray(data.actionButtons) === true) {
                actionButtons.value = data.actionButtons;
              } else if (Array.isArray(data.configurations?.actionButtons) === true) {
                actionButtons.value = data.configurations.actionButtons;
              } else {
                actionButtons.value = [];
              }
              if (data.configurations?.verifyAnswerEnabled !== undefined && data.configurations?.verifyAnswerEnabled !== null) {
                verifyAnswerEnabled.value = isDefinedAndNotEmpty(data.configurations.verifyAnswerEnabled) != false ? data.configurations.verifyAnswerEnabled : false;
              } else {
                verifyAnswerEnabled.value = false;
              }
              if (data.configurations?.verifyLinks !== undefined && data.configurations?.verifyLinks !== null) {
                verifyLinks.value = data.configurations.verifyLinks === true;
              } else {
                verifyLinks.value = true;
              }
              const authCfg = typeof data.configurations?.authenticator === 'object' && data.configurations.authenticator !== null ? data.configurations.authenticator : {};
              const providerValue = typeof authCfg.provider === 'string' && authCfg.provider.trim().length > 0 ? authCfg.provider : 'google';
              const welcomeValue = typeof authCfg.welcomeMessageTemplate === 'string' && authCfg.welcomeMessageTemplate.trim().length > 0 ? authCfg.welcomeMessageTemplate : defaultAuthenticatorWelcomeTemplate;
              const retryValue = typeof authCfg.retryMessageTemplate === 'string' && authCfg.retryMessageTemplate.trim().length > 0 ? authCfg.retryMessageTemplate : defaultAuthenticatorRetryTemplate;
              const successValue = typeof authCfg.successMessageTemplate === 'string' && authCfg.successMessageTemplate.trim().length > 0 ? authCfg.successMessageTemplate : defaultAuthenticatorSuccessTemplate;
              authenticatorEnabled.value = authCfg.enabled === true;
              authenticatorProvider.value = providerValue;
              authenticatorWelcomeMessage.value = welcomeValue;
              authenticatorRetryMessage.value = retryValue;
              authenticatorSuccessMessage.value = successValue;
              authenticatorRunAiToolOnLogin.value = authCfg.runAiToolOnLogin === true;
              if (isDefinedAndNotEmpty(data.configurations?.verificationModelId) != false) {
                selectedVerificationLLMModel.value = data.configurations.verificationModelId;
              } else {
                selectedVerificationLLMModel.value = '';
              }
              if (isDefinedAndNotEmpty(data.configurations?.groqGptOssFirstAnswerReasoningEffort) != false) {
                groqGptOssFirstAnswerReasoningEffort.value = normalizeGroqGptOssReasoningEffort(data.configurations.groqGptOssFirstAnswerReasoningEffort);
              } else {
                groqGptOssFirstAnswerReasoningEffort.value = 'medium';
              }
              if (isDefinedAndNotEmpty(data.configurations?.groqGptOssVerifyReasoningEffort) != false) {
                groqGptOssVerifyReasoningEffort.value = normalizeGroqGptOssReasoningEffort(data.configurations.groqGptOssVerifyReasoningEffort);
              } else {
                groqGptOssVerifyReasoningEffort.value = 'medium';
              }
              if (isDefinedAndNotEmpty(data.configurations?.voiceAgent) != false) {
                const v = data.configurations.voiceAgent;
                voiceAgentExtraPrompt.value = isDefinedAndNotEmpty(v.extraPrompt) != false ? v.extraPrompt : '';
                const promptKb = v.promptKnowledgeBase !== undefined && v.promptKnowledgeBase !== null && typeof v.promptKnowledgeBase === 'object' ? v.promptKnowledgeBase : {};
                voicePromptKnowledgeBaseEnabled.value = promptKb.enabled === true;
                voicePromptKnowledgeBaseFileNames.value = normalizeVoicePromptKnowledgeBaseFileNames(promptKb.fileNames);
                voicePromptKnowledgeBaseLoadStatement.value = typeof promptKb.loadStatement === 'string' && promptKb.loadStatement.trim() !== '' ? promptKb.loadStatement.trim() : defaultVoicePromptKnowledgeBaseLoadStatement;
                voiceAgentProvider.value = isDefinedAndNotEmpty(v.provider) != false ? v.provider : 'openai-realtime';
                voiceAgentVoice.value = isDefinedAndNotEmpty(v.voice) != false ? v.voice : (availableVoicesByProvider[voiceAgentProvider.value]?.[0] || 'alloy');
              }
              const humanInTheLoopConfig = typeof data.configurations?.humanInTheLoop === 'object' && data.configurations.humanInTheLoop !== null ? data.configurations.humanInTheLoop : {};
              humanInTheLoopEnabled.value = humanInTheLoopConfig.enabled === true;
              humanInTheLoopStartConversationWebhook.value = typeof humanInTheLoopConfig.startConversationWebhook === 'string' ? humanInTheLoopConfig.startConversationWebhook : '';
              humanInTheLoopNewMessageWebhook.value = typeof humanInTheLoopConfig.newMessageWebhook === 'string' ? humanInTheLoopConfig.newMessageWebhook : '';
              humanInTheLoopInboundSecret.value = typeof humanInTheLoopConfig.inboundSecret === 'string' ? humanInTheLoopConfig.inboundSecret : '';
              humanInTheLoopHumanTransferMessage.value = normalizeLocalizationInputValue(humanInTheLoopConfig.humanTransferMessage !== undefined ? humanInTheLoopConfig.humanTransferMessage : humanInTheLoopConfig.statusMessage);
              humanInTheLoopAiBackMessage.value = normalizeLocalizationInputValue(humanInTheLoopConfig.aiBackMessage);
              if (isDefinedAndNotEmpty(data.configurations?.phoneVoiceId) != false) {
                selectedPhoneVoiceId.value = data.configurations.phoneVoiceId;
              } else if (isDefinedAndNotEmpty(data.configurations?.voiceAgent?.voice) != false) {
                selectedPhoneVoiceId.value = data.configurations.voiceAgent.voice;
              }
              if (Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.includes('Phone') === true) {
                forceShowVoiceTab.value = false;
              }
              if (isDefinedAndNotEmpty(data.voiceSettings) != false) {
                const vs = data.voiceSettings;
                voiceSettingsVadEngine.value = normalizeVoiceSettingsVadEngine(vs.vadEngine !== undefined ? vs.vadEngine : vs.vadType);
                voiceSettingsDefaultEngineVersion.value = resolveVoiceSettingsDefaultEngineVersionFromSettings(vs);
                voiceSettingsVoiceType.value = voiceSettingsDefaultEngineVersion.value === 'v5' ? 'geminiLive' : 'vtv';
                voiceSettingsReasoningLevel.value = voiceSettingsDefaultEngineVersion.value === 'v3' || voiceSettingsDefaultEngineVersion.value === 'v3Flash' || voiceSettingsDefaultEngineVersion.value === 'v3Duplex' || voiceSettingsDefaultEngineVersion.value === 'v5' ? 'none' : normalizeVoiceReasoningLevel(vs.reasoningLevel !== undefined ? vs.reasoningLevel : vs.reasoningEffort);
                if (vs.allowAgentHangup !== undefined) {
                  voiceAllowAgentHangup.value = vs.allowAgentHangup === true;
                }
                if (vs.allowCallTransfer !== undefined) {
                  voiceAllowCallTransfer.value = vs.allowCallTransfer === true;
                }
                const incomingVoiceId = isDefinedAndNotEmpty(vs.voiceId) != false ? vs.voiceId : selectedPhoneVoiceId.value;
                voiceSettingsVoiceId.value = typeof incomingVoiceId === 'string' && incomingVoiceId.length > 0 ? incomingVoiceId : selectedPhoneVoiceId.value;
                voiceSettingsAccent.value = isDefinedAndNotEmpty(vs.accent) != false ? vs.accent : voiceSettingsAccent.value;
                voiceSettingsLanguage.value = isDefinedAndNotEmpty(vs.language) != false ? vs.language : voiceSettingsLanguage.value;
                voiceSettingsSpeed.value = normalizeVoiceSettingsNumber(vs.speed, voiceSettingsSpeed.value);
                const incomingCustomPacingEnabled = vs.customPacingEnabled !== undefined ? vs.customPacingEnabled : vs.customPacing;
                voiceSettingsCustomPacingEnabled.value = normalizeVoiceSettingsBoolean(incomingCustomPacingEnabled, voiceSettingsCustomPacingEnabled.value);
                voiceSettingsPacing.value = normalizeVoiceSettingsNumber(vs.pacing !== undefined ? vs.pacing : vs.dubPacing, voiceSettingsPacing.value);
                voiceSettingsDurationStretch.value = normalizeVoiceSettingsNumber(vs.durationStretch !== undefined ? vs.durationStretch : vs.dubDurationStretch, voiceSettingsDurationStretch.value);
                voiceSettingsUseDashPauses.value = normalizeVoiceSettingsBoolean(vs.useDashPauses !== undefined ? vs.useDashPauses : vs.dubUseDashPauses, voiceSettingsUseDashPauses.value);
                voiceSettingsPauseEveryWords.value = normalizeVoiceSettingsNumber(vs.pauseEveryWords !== undefined ? vs.pauseEveryWords : vs.dubPauseEveryWords, voiceSettingsPauseEveryWords.value);
                voiceSettingsDashPauseMs.value = normalizeVoiceSettingsNumber(vs.dashPauseMs !== undefined ? vs.dashPauseMs : vs.dubDashPauseMs, voiceSettingsDashPauseMs.value);
                voiceSettingsCommaPauseMs.value = normalizeVoiceSettingsNumber(vs.commaPauseMs !== undefined ? vs.commaPauseMs : vs.dubCommaPauseMs, voiceSettingsCommaPauseMs.value);
                voiceSettingsPeriodPauseMs.value = normalizeVoiceSettingsNumber(vs.periodPauseMs !== undefined ? vs.periodPauseMs : vs.dubPeriodPauseMs, voiceSettingsPeriodPauseMs.value);
                voiceSettingsIdleAutoResponseEnabled.value = normalizeVoiceSettingsBoolean(vs.idleAutoResponseEnabled, true);
                voiceSettingsStartingMessageIdleAutoResponseSeconds.value = Number.isFinite(Number(vs.startingMessageIdleAutoResponseSeconds)) === true && Number(vs.startingMessageIdleAutoResponseSeconds) >= 1 ? Math.min(300, Number(vs.startingMessageIdleAutoResponseSeconds)) : 6;
                voiceSettingsIdleAutoResponseSeconds.value = Number.isFinite(Number(vs.idleAutoResponseSeconds)) === true && Number(vs.idleAutoResponseSeconds) >= 1 ? Math.min(300, Number(vs.idleAutoResponseSeconds)) : 3;
                voiceSettingsToolUseAutoResponseSeconds.value = Number.isFinite(Number(vs.toolUseAutoResponseSeconds)) === true && Number(vs.toolUseAutoResponseSeconds) >= 0 ? Math.min(300, Number(vs.toolUseAutoResponseSeconds)) : 2;
                voiceSettingsMaxCallMinutes.value = Number.isFinite(Number(vs.maxCallMinutes)) === true && Number(vs.maxCallMinutes) >= 0.25 ? Math.min(1440, Number(vs.maxCallMinutes)) : 19;
                voiceSettingsMaxCallerMinutes.value = Number.isFinite(Number(vs.maxCallerMinutes)) === true && Number(vs.maxCallerMinutes) >= 0.25 ? Math.min(1440, Number(vs.maxCallerMinutes)) : 19;
                voiceSettingsCallerCooldownMinutes.value = Number.isFinite(Number(vs.callerCooldownMinutes)) === true && Number(vs.callerCooldownMinutes) >= 1 ? Math.min(43200, Number(vs.callerCooldownMinutes)) : 60;
                voiceSettingsPromptToVoiceEnabled.value = normalizeVoiceSettingsBoolean(vs.promptToVoiceEnabled, voiceSettingsPromptToVoiceEnabled.value);
                voiceSettingsPromptToVoicePrompt.value = typeof vs.promptToVoicePrompt === 'string' && vs.promptToVoicePrompt.trim() !== '' ? vs.promptToVoicePrompt : voiceSettingsPromptToVoicePrompt.value;
                voiceSettingsTone.value = isDefinedAndNotEmpty(vs.tone) != false ? vs.tone : voiceSettingsTone.value;
                voiceSettingsMode.value = isDefinedAndNotEmpty(vs.mode) != false ? ((vs.mode === 'wait full' || vs.mode === 'preventuserinterruptions') ? 'wait full' : ((vs.mode === 'interruptible' || vs.mode === 'allowuserinterruptions') ? 'interruptible' : 'interruptible')) : 'interruptible';
                voiceSettingsCancellingMode.value = normalizeVoiceSettingsCancellingMode(vs.cancellingMode !== undefined ? vs.cancellingMode : vs.cancelingMode);
                voiceSettingsAudioProcessorEngine.value = normalizeVoiceSettingsAudioProcessorEngine(vs.audioProcessorEngine !== undefined ? vs.audioProcessorEngine : vs.audioProcessor);
                voiceSettingsTermsPackages.value = Array.isArray(vs.termsPackages) === true ? vs.termsPackages.filter((item) => typeof item === 'string' && item.trim() !== '') : (Array.isArray(vs.termsPackageIds) === true ? vs.termsPackageIds.filter((item) => typeof item === 'string' && item.trim() !== '') : []);
                voiceSettingsSonioxTerms.value = Array.isArray(vs.sonioxContext?.terms) === true ? vs.sonioxContext.terms.filter((item) => typeof item === 'string' && item.trim() !== '') : [];
                voiceSettingsFillerSentences.value = normalizeWaitingFillerSentences(vs.fillerSentences);
                voiceSettingsQuickResponseFillerEnabled.value = vs.quickResponseFillerEnabled === true;
                voiceSettingsQuickResponseFillerText.value = typeof vs.quickResponseFillerText === 'string' ? vs.quickResponseFillerText : '';
                voiceSettingsTranscriptionOptimizerEnabled.value = vs.transcriptionOptimizer?.enabled === true;
                voiceSettingsTranscriptionOptimizerTerms.value = Array.isArray(vs.transcriptionOptimizer?.terms) === true ? vs.transcriptionOptimizer.terms.filter((item) => typeof item === 'string' && item.trim() !== '') : [];
                if (Array.isArray(selectedDeploymentChannels.value) === true && selectedDeploymentChannels.value.includes('Phone') !== true) {
                  forceShowVoiceTab.value = true;
                }
                if (typeof voiceSettingsVoiceId.value === 'string' && voiceSettingsVoiceId.value.length > 0 && voiceSettingsVoiceId.value !== selectedPhoneVoiceId.value) {
                  selectedPhoneVoiceId.value = voiceSettingsVoiceId.value;
                }
              }
              const loadedWebVoiceVisualSettings = data.webVoiceVisualSettings !== undefined && data.webVoiceVisualSettings !== null && typeof data.webVoiceVisualSettings === 'object' ? data.webVoiceVisualSettings : {};
              webVoiceVisualSettings.value = {
                enabled: loadedWebVoiceVisualSettings.enabled === true,
                items: Array.isArray(loadedWebVoiceVisualSettings.items) === true ? loadedWebVoiceVisualSettings.items : []
              };
              if (isDefinedAndNotEmpty(data.selectedVectorDBId) != false) {
                selectedVectorDBId.value = data.selectedVectorDBId;
              }
              if (isDefinedAndNotEmpty(data.selectedEmbeddingModelId) != false) {
                selectedEmbeddingModelId.value = data.selectedEmbeddingModelId;
              }
          }
          if (data.configurations == false) {
            humanInTheLoopEnabled.value = false;
            humanInTheLoopStartConversationWebhook.value = '';
            humanInTheLoopNewMessageWebhook.value = '';
            humanInTheLoopInboundSecret.value = '';
            humanInTheLoopHumanTransferMessage.value = {};
            humanInTheLoopAiBackMessage.value = {};
            groqGptOssFirstAnswerReasoningEffort.value = 'medium';
            groqGptOssVerifyReasoningEffort.value = 'medium';
          }
  
          cachedFileNames.value = isDefinedAndNotEmpty(data.cachedFileNames) != false ? data.cachedFileNames : [];
  
          tools.value = [];
          if (Array.isArray(selectedToolIds.value) !=false && Array.isArray(availableTools.value) !=false) {
              for (let i = 0; i < selectedToolIds.value.length; i = i + 1) {
                  const toolId = selectedToolIds.value[i];
                  const foundTool = availableTools.value.find(t => {
                    const currentToolId = getToolIdentifier(t);
                    return currentToolId !== null && toolIdsMatch(currentToolId, toolId) === true;
                  });
                  if (isDefinedAndNotEmpty(foundTool) != false) {
                      tools.value.push(foundTool);
                  }
              }
          }
          initializeSmsToolData();
          initializeEmailToolData();
          initializeWhatsappTemplateToolData();
  
          selectedAnalysisCollectionIds.value = normalizeAnalysisCollectionIds(isDefinedAndNotEmpty(data.selectedAnalysisCollectionIds) != false ? data.selectedAnalysisCollectionIds : []);
          if (isDefinedAndNotEmpty(data.avatarProvider) != false) {
            selectedAvatarProvider.value = data.avatarProvider;
          } else {
            selectedAvatarProvider.value = 'did';
          }
          if (isDefinedAndNotEmpty(data.avatarProviderConfigs) != false) {
            avatarProviderConfigs.value = {
              ...avatarProviderConfigs.value,
              ...data.avatarProviderConfigs
            };
          } else {
            avatarProviderConfigs.value = {
              did: defaultAvatarProviderConfig('did')
            };
          }
          if (avatarProviderConfigs.value[selectedAvatarProvider.value] === undefined) {
            avatarProviderConfigs.value = {
              ...avatarProviderConfigs.value,
              [selectedAvatarProvider.value]: defaultAvatarProviderConfig(selectedAvatarProvider.value)
            };
          }
  
          if (agentMode.value === 'FlowBased' && data.flow) {
              flowBasedFlow.value = data.flow;
          }
        if (agentMode.value === 'Flow' && data.flow) {
            nextTick(() => {
              tryLoadInitialFlowData();
            });
        }
        if (data.agentsToAgentsFlow) {
            nextTick(() => {
                if (isDefinedAndNotEmpty(agentsToAgentsFlowRef.value) != false) {
                    agentsToAgentsFlowRef.value.loadFlowData(data.agentsToAgentsFlow);
                }
            });
        }
        syncKnowledgeStatusStreamForCurrentAgent();
      }
  }
  
  async function refreshAgentData() {
      if (isEditMode.value === false) return;
      isRefreshingKB.value = true;
      try {
          const agentIdRaw = route.query.edit;
          const agentId = agentIdRaw !== undefined && agentIdRaw !== null ? String(agentIdRaw) : '';
          if (agentId.length === 0) {
            toast.error("Failed to load agent data.");
            return;
          }
          const cfg = getSecuredAxiosConfig(null);
          if (cfg === null) {
            return;
          }
          const response = await axios.get(`${apiEndpoint}/getAIChat/${encodePathSegment(agentId)}`, cfg);
          processLoadedAgentData(response.data);
          if (selectedDivisionIds.value.length > 0) {
              await fetchFilteredCollections(selectedDivisionIds.value);
          }
      } catch (error) {
          handleApiError(error, 'loading agent data');
      } finally {
          isRefreshingKB.value = false;
      }
  }
  
  function normalizeExistingKnowledgeFile(file) {
      const hasUtcHour = Number.isFinite(Number(file.rescrapeAtHour)) === true;
      const hasUtcMinute = Number.isFinite(Number(file.rescrapeAtMinute)) === true;
      const derivedLocalTime = (hasUtcHour === true || hasUtcMinute === true) ? toLocalHHMMFromUtc(Number(file.rescrapeAtHour), Number(file.rescrapeAtMinute)) : (typeof file.rescrapeAtTime === 'string' ? file.rescrapeAtTime : '');
      let processingStatus = typeof file.processingStatus === 'string' && file.processingStatus.length > 0 ? file.processingStatus : 'Available';
      if (file.fileType === 'website' || file.fileType === 'websiteurl' || file.fileType === 'websitepage') {
          processingStatus = typeof file.processingStatus === 'string' && file.processingStatus.length > 0 ? file.processingStatus : 'Processing';
      } else if (file.fileType === 'file' || file.fileType === 'text' || file.fileType === 'pdf' || file.fileType === 'docx' || file.fileType === 'xlsx' || file.fileType === 'fineTuneAnswer') {
          processingStatus = typeof file.processingStatus === 'string' && file.processingStatus.length > 0 ? file.processingStatus : 'Processing';
      }
      if (file.fileType === 'questionAnswer') {
          processingStatus = 'Available';
      }
      return {
          ...file,
          hiddenFromSources: file.hiddenFromSources === true,
          content: (file.fileType === 'text' && file.fileData) ? decodeBase64UTF8(file.fileData) : (isDefinedAndNotEmpty(file.content) != false ? file.content : null),
          status: 'existing',
          processingStatus: processingStatus,
          processingDetails: typeof file.processingDetails === 'string' ? file.processingDetails : null,
          rescrapeAtTime: derivedLocalTime
      };
  }
  
  function applyKnowledgeStatusSnapshot(realTimeFileStatuses) {
      const statusArray = Array.isArray(realTimeFileStatuses) === true ? realTimeFileStatuses : [];
      const statusMap = new Map(statusArray.map(entry => [entry.name, entry]));
      existingFiles.value = existingFiles.value.map(file => {
          const statusInfo = statusMap.get(file.fileName);
          let nextStatus = typeof file.processingStatus === 'string' && file.processingStatus.length > 0 ? file.processingStatus : 'Available';
          if (statusInfo !== undefined && statusInfo !== null) {
              nextStatus = computeKnowledgeProcessingStatus(file, statusInfo);
          } else if ((file.fileType === 'website' || file.fileType === 'websiteurl' || file.fileType === 'websitepage') && nextStatus.length === 0) {
              nextStatus = 'Processing';
          }
          return {
              ...file,
              processingStatus: nextStatus,
              processingDetails: isDefinedAndNotEmpty(statusInfo) != false ? statusInfo.details : (typeof file.processingDetails === 'string' ? file.processingDetails : null)
          };
      });
  }
  
  function mergeSavedFilesIntoExistingFiles(savedFiles) {
      const filesToMerge = Array.isArray(savedFiles) === true ? savedFiles : [];
      const retainedFiles = existingFiles.value.filter(file => file.status !== 'deleted');
      const mergedFiles = [...retainedFiles];
      for (let index = 0; index < filesToMerge.length; index = index + 1) {
          const savedFile = filesToMerge[index];
          if (savedFile === undefined || savedFile === null || typeof savedFile !== 'object') {
              continue;
          }
          const normalizedFile = normalizeExistingKnowledgeFile(savedFile);
          const existingIndex = mergedFiles.findIndex(file => file.fileName === normalizedFile.fileName);
          if (existingIndex >= 0) {
              mergedFiles[existingIndex] = {
                  ...mergedFiles[existingIndex],
                  ...normalizedFile
              };
          } else {
              mergedFiles.push(normalizedFile);
          }
      }
      existingFiles.value = mergedFiles;
  }
  
  function hasKnowledgeProcessingFiles() {
      return existingFiles.value.some(file => file !== undefined && file !== null && file.status !== 'deleted' && typeof file.processingStatus === 'string' && file.processingStatus === 'Processing');
  }
  
  async function refreshKnowledgeStatusesOnce(agentId, showErrorToast = false) {
      const normalizedAgentId = agentId !== undefined && agentId !== null ? String(agentId) : '';
      if (normalizedAgentId.length === 0) {
          return;
      }
      try {
          const cfg = getSecuredAxiosConfig(null);
          if (cfg === null) {
              return;
          }
          const response = await axios.get(`${apiEndpoint}/api/agents/${encodePathSegment(normalizedAgentId)}/knowledgeStatus`, cfg);
          const statuses = Array.isArray(response.data) === true ? response.data : (Array.isArray(response?.data?.statuses) === true ? response.data.statuses : []);
          applyKnowledgeStatusSnapshot(statuses);
      } catch (error) {
          if (showErrorToast === true) {
              const errorData = isDefinedAndNotEmpty(error.response?.data) != false ? error.response.data : {};
              const errorMessageText = isDefinedAndNotEmpty(errorData?.message) != false ? errorData.message : (isDefinedAndNotEmpty(errorData?.error) != false ? errorData.error : 'Failed to refresh knowledge base statuses.');
              toast.error(errorMessageText);
          }
      }
  }
  
  const knowledgeStatusStream = ref(null);
  const knowledgeStatusStreamAgentId = ref('');
  const knowledgeStatusStreamHadMessage = ref(false);
  
  function closeKnowledgeStatusStream() {
      if (knowledgeStatusStream.value !== null) {
          knowledgeStatusStream.value.close();
          knowledgeStatusStream.value = null;
      }
      knowledgeStatusStreamAgentId.value = '';
      knowledgeStatusStreamHadMessage.value = false;
  }
  
  function startKnowledgeStatusStream(agentId) {
      const normalizedAgentId = agentId !== undefined && agentId !== null ? String(agentId) : '';
      if (normalizedAgentId.length === 0) {
          return;
      }
      if (knowledgeStatusStream.value !== null && knowledgeStatusStreamAgentId.value === normalizedAgentId) {
          return;
      }
      closeKnowledgeStatusStream();
      const streamUrl = `${apiEndpoint}/api/agents/${encodePathSegment(normalizedAgentId)}/knowledgeStatus/stream`;
      const stream = openAuthenticatedEventStream(streamUrl, {
        status: (payload) => {
          if (payload === null || payload === undefined) {
              return;
          }
          const statuses = Array.isArray(payload) === true ? payload : (Array.isArray(payload.statuses) === true ? payload.statuses : []);
          knowledgeStatusStreamHadMessage.value = true;
          applyKnowledgeStatusSnapshot(statuses);
          if (hasKnowledgeProcessingFiles() === false) {
              closeKnowledgeStatusStream();
          }
        },
        failed: (payload) => {
          const errorMessageText = typeof payload?.message === 'string' && payload.message.length > 0 ? payload.message : 'Knowledge base status stream stopped.';
          if (isActive === true) {
              toast.error(errorMessageText);
          }
          closeKnowledgeStatusStream();
        },
        error: () => {
          const receivedStatus = knowledgeStatusStreamHadMessage.value === true;
          if (knowledgeStatusStream.value === stream) {
              closeKnowledgeStatusStream();
          }
          if (receivedStatus !== true && isActive === true) {
              refreshKnowledgeStatusesOnce(normalizedAgentId, false);
          }
        }
      });
      knowledgeStatusStream.value = stream;
      knowledgeStatusStreamAgentId.value = normalizedAgentId;
      knowledgeStatusStreamHadMessage.value = false;
  }
  
  function syncKnowledgeStatusStreamForCurrentAgent(agentIdValue = null) {
      const normalizedAgentId = agentIdValue !== undefined && agentIdValue !== null ? String(agentIdValue) : (route.query.edit !== undefined && route.query.edit !== null ? String(route.query.edit) : '');
      if (normalizedAgentId.length === 0) {
          closeKnowledgeStatusStream();
          return;
      }
      if (hasKnowledgeProcessingFiles() === true) {
          startKnowledgeStatusStream(normalizedAgentId);
          return;
      }
      if (knowledgeStatusStreamAgentId.value === normalizedAgentId) {
          closeKnowledgeStatusStream();
      }
  }
  
  function startLoadingTips() {
    let index = 0;
    currentTip.value = loadingTips[0];
    
    tipInterval.value = setInterval(() => {
      index = (index + 1) % loadingTips.length;
      currentTip.value = loadingTips[index];
    }, 30000);
  }
  
  function stopLoadingTips() {
    if (tipInterval.value !== null) {
      clearInterval(tipInterval.value);
      tipInterval.value = null;
    }
  }
  
  const filteredExistingFiles = computed(() => {
    const query = searchQuery.value.toLowerCase();
    const folderId = currentFolderId.value;
  
    return existingFiles.value.filter(file => {
      const fileAssignment = knowledgeBaseFileAssignments.value[file.fileName];
      const isInCurrentFolder = (folderId === null && (fileAssignment === undefined || fileAssignment === null)) || fileAssignment === folderId;
  
      if (isInCurrentFolder === false) {
        return false;
      }
  
      if (file.status === 'deleted') {
        const isReplacedByEdit = uploadedFiles.value.some(upFile => upFile.replacesFile === file.fileName && upFile.isEditedVersion === true);
        if (isReplacedByEdit === true) {
          return false; 
        }
      }
  
      return file.fileType !== 'fineTuneAnswer' && file.fileType !== 'questionAnswer' &&
        (query === '' || 
          (file.fileName.toLowerCase().includes(query) === true) ||
          ((file.fileType === 'websiteurl' ? 'Website URL' : file.fileType === 'website' ? 'Website' : file.fileType !== undefined && file.fileType !== null ? file.fileType : 'File').toLowerCase().includes(query) === true) ||
          ((file.status === 'existing' ? 'Available' : file.status === 'deleted' ? 'To Delete' : 'New').toLowerCase().includes(query) === true)
        );
    });
  });
  
  const filteredUploadedFiles = computed(() => {
    const query = searchQuery.value.toLowerCase();
    const folderId = currentFolderId.value;
  
    return uploadedFiles.value.filter(file => {
      const fileAssignment = knowledgeBaseFileAssignments.value[file.fileName];
      const isInCurrentFolder = (folderId === null && (fileAssignment === undefined || fileAssignment === null)) || fileAssignment === folderId;
  
      if (isInCurrentFolder === false) {
        return false;
      }
      
      return file.fileType !== 'fineTuneAnswer' && file.fileType !== 'questionAnswer' &&
        (query === '' ||
          (file.fileName.toLowerCase().includes(query) === true) ||
          ((file.fileType === 'websiteurl' ? 'Website URL' : file.fileType === 'website' ? 'Website' : file.fileType !== undefined && file.fileType !== null ? file.fileType : 'File').toLowerCase().includes(query) === true) ||
          ('New'.toLowerCase().includes(query) === true)
        );
    });
  });
   
  const displayedFoldersInCurrentView = computed(() => {
    const folderId = currentFolderId.value;
     const query = searchQuery.value.toLowerCase();
    return knowledgeBaseFolders.value.filter(folder => 
      (folder.parentId === folderId) &&
      (query === '' || (folder.name.toLowerCase().includes(query) === true))
    );
  });
  
  
  const fineTunedAnswers = computed(() => {
    return existingFiles.value.filter(file => file.fileType === 'fineTuneAnswer' && file.status !== 'deleted');
  });
  
  const maxFileSizeMb = ref(600);
  const maxFileSizeBytes = computed(() => maxFileSizeMb.value * 1024 * 1024);

  async function loadKnowledgeUploadLimit() {
    try {
      const response = await axios.get(`${globalState.apiEndpoint}/api/config/runtime`);
      const configuredLimit = Number(response?.data?.maxKnowledgeFileSizeMb);
      if (Number.isFinite(configuredLimit) === true && configuredLimit > 0) {
        maxFileSizeMb.value = configuredLimit;
      }
    } catch (error) {}
  }
  
  function handleFileUpload(event) {
    const files = Array.from(event.target.files);
    for (let i = 0; i < files.length; i = i + 1) {
      const file = files[i];
      if (file.size > maxFileSizeBytes.value) {
          toast.error(`File ${file.name} exceeds the ${maxFileSizeMb.value}MB size limit.`);
          continue;
      }
      const isDuplicateInUploaded = uploadedFiles.value.some(existing => existing.fileName === file.name);
      const isDuplicateInExisting = existingFiles.value.some(existing => existing.fileName === file.name && existing.status !== 'deleted');
      if (isDuplicateInUploaded === false && isDuplicateInExisting === false) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64Data = e.target.result.split(',')[1];
          const fileType = file.name.split('.').pop().toLowerCase();
          const allowedTypes = ['pdf', 'docx', 'txt', 'xlsx'];
          if (allowedTypes.includes(fileType) === true) {
            const newFileEntry = {
              fileName: file.name,
              fileType: fileType === 'txt' ? 'text' : fileType,
              fileData: base64Data,
              content: (fileType === 'txt' || fileType === 'text') ? decodeBase64UTF8(base64Data) : null, 
               status: 'new',
               hiddenFromSources: false,
               rescrapeEnabled: false,
               rescrapeIntervalDays: '',
               rescrapeAtTime: ''
            };
            uploadedFiles.value.push(newFileEntry);
            if (knowledgeBaseFileAssignments.value[newFileEntry.fileName] === undefined) {
              knowledgeBaseFileAssignments.value[newFileEntry.fileName] = currentFolderId.value;
            }
          } else {
            toast.error(`Unsupported file type: ${fileType}`);
          }
        };
        reader.readAsDataURL(file);
      } else {
        toast.warning(`File ${file.name} already exists or is queued for upload, skipping.`);
      }
    }
     event.target.value = '';
  }
  
  async function downloadFile(file) {
    const agentId = route.query.edit;
    if (agentId === undefined || agentId === null) {
      toast.error("Cannot download file: Agent ID is missing.");
      return;
    }
  
    const cfg = getSecuredAxiosConfig({ responseType: 'blob' });
    if (cfg === null) {
      return;
    }
    toast.info(`Downloading ${decodeURIComponentSafe(file.fileName)}...`);
    try {
      const response = await axios.get(`${globalState.apiEndpoint}/api/agents/${encodePathSegment(agentId)}/files/${encodeURIComponent(file.fileName)}`, cfg);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const decodedFileName = decodeURIComponentSafe(file.fileName);
      link.setAttribute('download', decodedFileName);
      document.body.appendChild(link);
      link.click();
      
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success(`'${decodedFileName}' downloaded.`);
    } catch (error) {
      toast.dismiss();
      handleApiError(error, `downloading '${decodeURIComponentSafe(file.fileName)}'`);
    }
  }
  
  function removeFile(index, type) { 
    let fileNameToRemove;
    if (type === 'existing') {
       const file = filteredExistingFiles.value[index]; 
      if (file !== undefined) {
          const originalIndex = existingFiles.value.findIndex(ef => ef.fileName === file.fileName);
          if (originalIndex !== -1) {
              existingFiles.value[originalIndex].status = 'deleted';
              fileNameToRemove = existingFiles.value[originalIndex].fileName;
             
          }
      }
     } else { 
       const file = filteredUploadedFiles.value[index];
      if (file !== undefined) {
          const originalIndex = uploadedFiles.value.findIndex(uf => uf.fileName === file.fileName);
          if (originalIndex !== -1) {
              fileNameToRemove = uploadedFiles.value[originalIndex].fileName;
              uploadedFiles.value.splice(originalIndex, 1);
              delete knowledgeBaseFileAssignments.value[fileNameToRemove];
          }
       }
    }
  }
  
  function removeFileByName(file, type) {
    if (type === 'existing') {
      const originalIndex = existingFiles.value.findIndex(ef => ef.fileName === file.fileName);
      if (originalIndex > -1) {
        existingFiles.value[originalIndex].status = 'deleted';
      }
    } else {
      const originalIndex = uploadedFiles.value.findIndex(uf => uf.fileName === file.fileName);
      if (originalIndex > -1) {
        uploadedFiles.value.splice(originalIndex, 1);
        delete knowledgeBaseFileAssignments.value[file.fileName];
      }
    }
  }
  
  
  function removeFineTunedAnswer(index) {
   
    const originalIndex = existingFiles.value.findIndex(file => file.fileName === fineTunedAnswers.value[index].fileName);
    if (originalIndex !== -1) {
      existingFiles.value[originalIndex].status = 'deleted';
      toast.info('Fine-tuned answer marked for deletion.');
    } else {
      toast.error('Could not find the fine-tuned answer to remove.');
    }
  }
  
  function updateAutoModelMappings(models) {
    const list = Array.isArray(models) === true ? models : [];
    const detectedAuto = list.find(model => (typeof model.id === 'string' && model.id === autoLlmModelId) || model.isAuto === true);
    if (detectedAuto !== undefined && detectedAuto !== null) {
      const baseAnswerRaw = typeof detectedAuto.autoAnswerModelId === 'string' ? detectedAuto.autoAnswerModelId.trim() : '';
      const baseAnswer = baseAnswerRaw.length > 0 ? baseAnswerRaw : autoLlmModelId;
      const fastCandidateRaw = typeof detectedAuto.fastModeModelId === 'string' ? detectedAuto.fastModeModelId.trim() : '';
      autoFastModeModelId.value = fastCandidateRaw.length > 0 ? fastCandidateRaw : baseAnswer;
      const superFastCandidateRaw = typeof detectedAuto.superFastModeModelId === 'string' ? detectedAuto.superFastModeModelId.trim() : '';
      autoSuperFastModeModelId.value = superFastCandidateRaw.length > 0 ? superFastCandidateRaw : autoFastModeModelId.value;
      const verifyCandidateRaw = typeof detectedAuto.autoVerificationModelId === 'string' ? detectedAuto.autoVerificationModelId.trim() : '';
      autoVerificationModelId.value = verifyCandidateRaw.length > 0 ? verifyCandidateRaw : baseAnswer;
    } else {
      autoFastModeModelId.value = null;
      autoSuperFastModeModelId.value = null;
      autoVerificationModelId.value = null;
    }
    syncAutoVerificationModel();
  }
  
  function syncAutoVerificationModel() {
    if (selectedLLMModel.value !== autoLlmModelId) {
        return;
      }
    const isFastMode = agentMode.value === 'Fast';
    const isSuperFastMode = agentMode.value === 'SuperFast';
    if (isFastMode === true || isSuperFastMode === true) {
      if (verifyAnswerEnabled.value !== false) {
        verifyAnswerEnabled.value = false;
      }
      if (selectedVerificationLLMModel.value !== '') {
        selectedVerificationLLMModel.value = '';
      }
      return;
    }
    if (verifyAnswerEnabled.value !== true) {
      verifyAnswerEnabled.value = true;
    }
    const targetVerificationModel = typeof autoVerificationModelId.value === 'string' && autoVerificationModelId.value.length > 0 ? autoVerificationModelId.value : '';
    if (selectedVerificationLLMModel.value !== targetVerificationModel) {
      selectedVerificationLLMModel.value = targetVerificationModel;
    }
  }
  
  const fetchLLMModels = async () => {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        availableLLMModels.value = [];
        return;
      }
      const response = await axios.get(`${apiEndpoint}/api/config/llmModels`, cfg);
      let models = Array.isArray(response.data) === true ? response.data : [];
      const allowed = Array.isArray(effectiveOrgPolicies.value?.features?.allowedLlmModelIds) === true ? effectiveOrgPolicies.value.features.allowedLlmModelIds : null;
      if (showAllFields.value !== true && Array.isArray(allowed) === true && allowed.length > 0) {
        models = models.map(m => ({ ...m, hasAccess: allowed.includes(m.id) }));
      }
      availableLLMModels.value = models;
      updateAutoModelMappings(availableLLMModels.value);
      const autoModelExists = availableLLMModels.value.some(model => model.id === autoLlmModelId);
      if (llmLockedId.value !== null) {
        selectedLLMModel.value = llmLockedId.value;
      } else if (isEditMode.value === false && availableLLMModels.value.length > 0 && selectedLLMModel.value === null) {
        selectedLLMModel.value = autoModelExists === true ? autoLlmModelId : availableLLMModels.value[0].id;
      }
    } catch (error) {
      handleApiError(error, 'loading LLM models');
      availableLLMModels.value = []; 
    }
  };
  
  async function loadOrgPolicies() {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        orgPolicies.value = {};
        return;
      }
      const userRes = await axios.get(`${apiEndpoint}/api/users/me`, cfg);
      const orgId = userRes?.data?.organizationID;
      if (Number.isFinite(Number(orgId)) === true) {
          const polRes = await axios.get(`${apiEndpoint}/api/agents/policies/${encodePathSegment(orgId)}`, cfg);
          orgPolicies.value = polRes.data !== null && polRes.data !== undefined ? polRes.data : {};
          if (llmLockedId.value !== null) {
            selectedLLMModel.value = llmLockedId.value;
          }
        }
    } catch (error) {
      orgPolicies.value = {};
    }
  }
  
  function getModelDisplayName(model) {
    if (model === undefined || model === null) return 'Unknown Model';
    const providerName = isDefinedAndNotEmpty(model.provider) != false ? model.provider : 'N/A';
    const variant = isDefinedAndNotEmpty(model.productVariant) != false ? model.productVariant : 'N/A';
    const version = isDefinedAndNotEmpty(model.productVersion) != false ? model.productVersion : '';
    return `${providerName} - ${variant} ${version}`.trim();
  }
  
  
  onMounted(async () => {
    const initializationWatchdogId = window.setTimeout(() => {
      if (isActive === true && initializing.value === true) {
        initializing.value = false;
        hasInitializedPhoneSelection.value = true;
        toast.warning(t('aiChatAgentSetup.loadingTimeout', 'Some settings are taking longer to load. The editor will continue loading them in the background.'));
      }
    }, 4000);
    try {
      fetchVoices();
      const existingUser = globalState.userInfo;
      const shouldLoadFreshUserData = existingUser === null || existingUser === undefined || existingUser.organizationID === undefined || existingUser.organizationID === null || existingUser.subscriptionConfig === undefined || existingUser.subscriptionConfig === null;
      if (shouldLoadFreshUserData === false) {
        organizationID.value = existingUser.organizationID;
      } else {
        try {
          const userData = await Promise.race([
            fetchUserData(),
            new Promise(resolve => window.setTimeout(() => resolve(null), 6000))
          ]);
          if (userData !== null && userData !== undefined && userData.organizationID !== undefined && userData.organizationID !== null) {
            globalState.userInfo = userData;
            organizationID.value = userData.organizationID;
          }
        } catch (error) {}
      }
      const metadataTasks = [
        loadKnowledgeUploadLimit(),
        loadOrgPolicies().then(() => fetchLLMModels()),
        refreshTools(),
        loadSharedKnowledgeBases(),
        loadAvailableAgentKnowledgeBases(),
        loadStyles(),
        loadDivisions()
      ];
      await Promise.race([
        Promise.allSettled(metadataTasks),
        new Promise(resolve => window.setTimeout(resolve, 8000))
      ]);
    
     currentKbPath.value = [{ id: null, name: 'Knowledge Base' }];
  
    const editId = route.query.edit;
    if (editId !== undefined && editId !== null) {
      isEditMode.value = true;
      await refreshAgentData();
      if (Array.isArray(loadedAgentData?.agentdata?.sharedKnowledgeBaseIds) === true) {
        selectedSharedKbIds.value = loadedAgentData.agentdata.sharedKnowledgeBaseIds;
      } else if (isDefinedAndNotEmpty(loadedAgentData?.agentdata?.sharedKnowledgeBaseId) != false) {
        selectedSharedKbIds.value = [loadedAgentData.agentdata.sharedKnowledgeBaseId];
      }
      try {
        const cfg = getSecuredAxiosConfig(null);
        if (cfg !== null) {
          const bindRes = await axios.get(`${apiEndpoint}/api/whatsapp/binding/${encodePathSegment(route.query.edit)}`, cfg);
        if (bindRes.data !== null && bindRes.data !== undefined) {
          selectedWhatsappSourceId.value = Number.isFinite(Number(bindRes.data.sourceId)) === true ? Number(bindRes.data.sourceId) : null;
          const num = typeof bindRes.data.number === 'string' ? bindRes.data.number : (typeof bindRes.data.externalNumber === 'string' ? bindRes.data.externalNumber : (typeof bindRes.data.display === 'string' ? bindRes.data.display : ''));
          whatsappDisplayNumber.value = num;
          if (selectedDeploymentChannels.value.includes('Whatsapp') === false && selectedDeploymentChannels.value.includes('Whatsapp Voice') === false) {
            selectedDeploymentChannels.value.push('Whatsapp');
          }
        } else {
          selectedWhatsappSourceId.value = null;
          whatsappDisplayNumber.value = '';
        }
        }
      } catch (error) {}
      try {
        const cfg = getSecuredAxiosConfig(null);
        if (cfg !== null) {
          const phoneBindRes = await axios.get(`${apiEndpoint}/api/phone/binding/${encodePathSegment(route.query.edit)}`, cfg);
        if (phoneBindRes.data !== null && phoneBindRes.data !== undefined) {
          selectedPhoneSourceId.value = Number.isFinite(Number(phoneBindRes.data.sourceId)) === true ? Number(phoneBindRes.data.sourceId) : null;
          const pnum = typeof phoneBindRes.data.number === 'string' ? phoneBindRes.data.number : (typeof phoneBindRes.data.externalNumber === 'string' ? phoneBindRes.data.externalNumber : (typeof phoneBindRes.data.display === 'string' ? phoneBindRes.data.display : ''));
          phoneDisplayNumber.value = pnum;
          if (selectedDeploymentChannels.value.includes('Phone') === false) {
            selectedDeploymentChannels.value.push('Phone');
          }
        } else {
          selectedPhoneSourceId.value = null;
          phoneDisplayNumber.value = '';
        }
        }
      } catch (error) {}
      if (isActive === false) {
        return;
      }
      const requestedSetupType = getRequestedSetupType();
      const loadedSetupType = typeof loadedAgentData?.agentdata?.setupType === 'string' ? loadedAgentData.agentdata.setupType : '';
      if (requestedSetupType === 'light') {
        viewMode.value = 'light';
      } else if (loadedSetupType === 'light') {
        viewMode.value = 'light';
        const agentId = route.query.edit;
        router.push({ path: '/aiChatAgentSetupLight', query: { edit: agentId } });
        return;
      } else if (requestedSetupType === 'full') {
        viewMode.value = 'full';
      } else {
        viewMode.value = 'full';
      }
    } else {
      
      if (availableLLMModels.value.length > 0 && selectedLLMModel.value === null) {
          const autoModelExists = availableLLMModels.value.some(model => model.id === autoLlmModelId);
          selectedLLMModel.value = autoModelExists === true ? autoLlmModelId : availableLLMModels.value[0].id;
      }
      
      enableFileUploadInChat.value = agentType.value === 'PrivateAgent';
      selectedDeploymentChannels.value = sanitizeDeploymentChannels(selectedDeploymentChannels.value);
      selectedAvatarProvider.value = 'did';
      avatarProviderConfigs.value = {
        did: defaultAvatarProviderConfig('did')
      };
      viewMode.value = 'full';
    }
    
    if (isEditMode.value === true && loadedAgentData?.agentdata?.files !== undefined) {
  
        selectedAnalysisCollectionIds.value = normalizeAnalysisCollectionIds(isDefinedAndNotEmpty(loadedAgentData?.agentdata?.selectedAnalysisCollectionIds) != false ? loadedAgentData?.agentdata?.selectedAnalysisCollectionIds : []);
        
        if (selectedDivisionIds.value.length > 0) {
            await fetchFilteredCollections(selectedDivisionIds.value);
        }
  
        const fineTuneFiles = loadedAgentData.agentdata.files.filter(f => f.fileType === 'fineTuneAnswer');
        for (let i = 0; i < fineTuneFiles.length; i = i + 1) {
            const ftFile = fineTuneFiles[i];
            if (existingFiles.value.some(ef => ef.fileName === ftFile.fileName) === false) {
                existingFiles.value.push({ ...ftFile, status: 'existing' });
            }
        }
    }
   
    if (currentKbPath.value.length === 0 || currentKbPath.value[0].id !== null || currentKbPath.value[0].name !== 'Knowledge Base') {
       currentKbPath.value = [{ id: null, name: 'Knowledge Base' }];
    }
  
    await nextTick(async () => {
      await nextTick();
      if (isActive === true) {
        if (aiAgentSetupRoot.value !== null) {
          applyTranslationsToDOM(aiAgentSetupRoot.value);
        } else {
          console.error('AIChatAgentSetup.vue: aiAgentSetupRoot is null on mount. Initial translations cannot be applied to component scope.');
        }
      }
    });
  
    watch(currentLanguage, async () => {
      if (isActive === false) {
        return;
      }
      await nextTick();
      if (aiAgentSetupRoot.value !== null) {
        applyTranslationsToDOM(aiAgentSetupRoot.value);
      } else {
        console.error('AIChatAgentSetup.vue: aiAgentSetupRoot is null in language watcher. Translations cannot be applied to component scope.');
      }
    });
  
    if (isActive === false) {
      return;
    }
    const requestedSetupType = getRequestedSetupType();
    if (requestedSetupType === 'light') {
      viewMode.value = 'light';
      if (route.path !== '/aiChatAgentSetupLight') {
        const nextQuery = { ...route.query, setupType: 'light' };
        if (nextQuery.lightMode !== undefined) {
          delete nextQuery.lightMode;
        }
        router.push({ path: '/aiChatAgentSetupLight', query: nextQuery });
        return;
      }
    } else if (requestedSetupType === 'full') {
      viewMode.value = 'full';
      if (route.path !== '/aiChatAgentSetup') {
        const nextQuery = { ...route.query, setupType: 'full' };
        if (nextQuery.lightMode !== undefined) {
          delete nextQuery.lightMode;
        }
        router.push({ path: '/aiChatAgentSetup', query: nextQuery });
        return;
      }
    }
    initializing.value = false;
  
    if (route.query.preview === 'left') {
      try {
        const existing = document.getElementById('setup-live-preview-container');
        if (existing !== null && isEditMode.value === true && route.query.edit !== undefined) {
          const endpointRaw = String(apiEndpoint);
          const isHttpsPage = typeof window !== 'undefined' && window.location !== undefined && window.location !== null && window.location.protocol === 'https:';
          const isHttpEndpoint = endpointRaw.startsWith('http://') === true;
          if (isHttpsPage === true && isHttpEndpoint === true) {
            toast.error('Cannot load preview with an insecure API endpoint.');
            return;
          }
          const script = document.createElement('script');
          script.src = '/publicAgentSnippet/publicChatAgent.js';
          script.async = true;
          script.setAttribute('data-agent-id', String(route.query.edit));
          script.setAttribute('data-api-endpoint', endpointRaw);
          script.setAttribute('data-target-element', '#setup-live-preview-container');
          script.setAttribute('data-open-on-load', 'true');
          while (existing.firstChild !== null) {
            existing.removeChild(existing.firstChild);
          }
          existing.appendChild(script);
        }
      } catch (error) {
        console.error('Failed to load live preview snippet:', error);
      }
    }
      hasInitializedPhoneSelection.value = true;
    } catch (error) {
      toast.error(t('aiChatAgentSetup.loadingFailed', 'Some agent settings could not be loaded.'));
    } finally {
      window.clearTimeout(initializationWatchdogId);
      if (isActive === true) {
        initializing.value = false;
        hasInitializedPhoneSelection.value = true;
      }
    }
  });
  
  function isAgentFlowTabVisible() {
    if (agentMode.value === 'Flow') { return true; }
    if (agentMode.value === 'FlowBased') { return true; }
    return false;
  }

  function resolveAdjacentSetupPage(currentPageNumber, direction) {
    const voiceSettingsPage = 12;
    if (isVoiceOnlyChannelSelection.value === true || showAllFields.value === true) {
      if (currentPageNumber === 8 && direction === 1) { return 6; }
      if (currentPageNumber === 6 && direction === -1) { return 8; }
      if (currentPageNumber === 6 && direction === 1) { return 9; }
      if (currentPageNumber === 9 && direction === -1) { return 6; }
    }
    if (showVoiceSettingsTab.value !== true) {
      return currentPageNumber + direction;
    }
    if (direction === 1) {
      if (currentPageNumber === 1) { return voiceSettingsPage; }
      if (currentPageNumber === voiceSettingsPage) { return 2; }
      return currentPageNumber + direction;
    }
    if (direction === -1) {
      if (currentPageNumber === voiceSettingsPage) { return 1; }
      if (currentPageNumber === 2) { return voiceSettingsPage; }
      if (currentPageNumber === 3 && isAgentFlowTabVisible() !== true) { return voiceSettingsPage; }
    }
    return currentPageNumber + direction;
  }

  function goNextStep(currentPageNumber, direction) {
      const maxPage = 14;
      const minPage = 1;
      let targetPage = resolveAdjacentSetupPage(currentPageNumber, direction);

      console.log(`goNextStep called. Current: ${currentPageNumber}, Direction: ${direction}, Initial Target: ${targetPage}`);
  
      
      if (currentPageNumber === 1 && direction === 1) {
          if (validateTab1() === false) {
              return; 
          }
      }
  
      while (targetPage >= minPage && targetPage <= maxPage) {
          let isVisible = true;
          if (targetPage === 2 && agentMode.value !== 'Flow') {
              isVisible = false; 
          }
          if (targetPage === 4 && isEditMode.value !== true) {
              isVisible = false; 
          }
          if (targetPage === 7 && agentMode.value === 'Flow') {
              isVisible = false;
          }
          if (targetPage === 8 && agentMode.value === 'Flow') {
              isVisible = false;
          }
          if ((targetPage === 6 || targetPage === 11 || targetPage === 14) && isVoiceOnlyChannelSelection.value !== true && showAllFields.value !== true) {
              isVisible = false;
          }
          if (targetPage === 12 && showVoiceSettingsTab.value !== true) {
              isVisible = false;
          }
          if (targetPage === 13) {
              isVisible = false;
          }
  
          if (isVisible === true) {
              console.log(`Found visible target page: ${targetPage}`);
              break;
          }
          
          console.log(`Page ${targetPage} is hidden. Checking next page in direction ${direction}.`);
          targetPage += direction; 
      }
  
      
      if (targetPage < minPage || targetPage > maxPage) {
          console.log(`Navigation bounded. Target ${targetPage} is out of range [${minPage}-${maxPage}].`);
          return; 
      }
  
      isNavigating.value = targetPage;
      try {
          const radio = document.querySelector(`input[data-currentPage='${targetPage}']`);
          if (radio !== undefined && radio !== null) {
              console.log(`Clicking radio for page ${targetPage}`);
              radio.click();
              currentPage.value = targetPage;
          } else {
               console.error(`Radio button for page ${targetPage} not found.`);
          }
      } finally {
          setTimeout(() => { 
              isNavigating.value = null; 
              const currentTabPanel = document.querySelector(`.tab-content[data-currentPage='${targetPage}']`);
              const tabContents = document.querySelectorAll('.tab-content');
              for (let i = 0; i < tabContents.length; i = i + 1) {
                  const el = tabContents[i];
                  el.classList.remove('active');
              }
              if(currentTabPanel !== null) currentTabPanel.classList.add('active');
          }, 100); 
      }
  }
  
  function pushValidatedRule(rawRule, fieldName = 'Rule') {
    const normalizedRule = normalizeRuleLinkTrailingSlashes(rawRule);
    const validation = secureUserText(normalizedRule, {
      fieldName: fieldName,
      allowNewLines: true,
      maxLength: 3500,
      disallowAngleBrackets: true
    });
    if (validation.error !== null) {
      toast.error(validation.error);
      return false;
    }
    rules.value.push(validation.value);
    return true;
  }
  
  function addRule() {
    if (newRule.value.trim().length === 0) {
      return;
    }
    const added = pushValidatedRule(newRule.value, 'Rule');
    if (added === true) {
      newRule.value = '';
    }
  }
  
  function addRuleFromLibrary(ruleText) {
    if (typeof ruleText !== 'string' || ruleText.trim().length === 0) {
      return;
    }
    pushValidatedRule(ruleText, 'Library Rule');
  }

  function approveGeneratedRulesFromAdvanced(generatedRules) {
    const source = Array.isArray(generatedRules) === true ? generatedRules : [];
    let addedCount = 0;
    for (let index = 0; index < source.length; index = index + 1) {
      const ruleText = normalizeGeneratedRuleForInjection(source[index]);
      if (ruleText.length === 0) {
        continue;
      }
      if (rules.value.includes(ruleText) === true) {
        continue;
      }
      const added = pushValidatedRule(ruleText, 'Generated Rule');
      if (added === true) {
        addedCount = addedCount + 1;
      }
    }
    if (addedCount > 0) {
      toast.success(`${addedCount} generated rules approved. Click Save or Save & Close to persist them.`);
    }
  }
  
  function removeRule(index) {
    rules.value.splice(index, 1);
  }
  
  function removeTool(index) {
    const toolToRemove = tools.value[index];
    const toolId = getToolIdentifier(toolToRemove);
    tools.value.splice(index, 1);
    const toolIdIndex = selectedToolIds.value.findIndex(id => toolIdsMatch(id, toolId) === true);
    if (toolIdIndex > -1) {
      selectedToolIds.value.splice(toolIdIndex, 1);
    }
    clearToolSettingsEntry(toolToRemove);
  }
  
  function addToolEndpoint(toolIndex) {
    tools.value[toolIndex].apiEndpoints.push({
      url: '',
      method: 'GET'
    });
  }
  
  function removeToolEndpoint(toolIndex, endpointIndex) {
    tools.value[toolIndex].apiEndpoints.splice(endpointIndex, 1);
  }
  
  function startEditingRule(index) {
    editingRuleIndex.value = index;
    console.log(`Started editing rule ${index}, initial direction set to: ${inputDirection.value}`);
    nextTick(() => {
      let el = editingRuleInput.value;
      if (Array.isArray(el) === true) {
        el = el.length > 0 ? el[0] : null;
      }
      if (el !== null && el !== undefined) {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
        el.focus();
      }
    });
  }
  
  function stopEditingRule() {
    editingRuleIndex.value = null;
    inputDirection.value = 'ltr';
  }
  
  function addRequiredParam(toolIndex) {
    if (tools.value[toolIndex].requiredParams === undefined || tools.value[toolIndex].requiredParams.length === 0) {
      tools.value[toolIndex].requiredParams = [];
    }
    tools.value[toolIndex].requiredParams.push('');
  }
  
  function removeRequiredParam(toolIndex, paramIndex) {
    tools.value[toolIndex].requiredParams.splice(paramIndex, 1);
  }
  
  async function validateFunction(index) {
    const tool = tools.value[index];
    tool.validationError = null;
    const cfg = getSecuredAxiosConfig(null);
    if (cfg === null) {
      tool.validationError = 'Authentication required';
      toast.error('Function validation failed');
      return;
    }
    const functionConfig = typeof tool.functionConfig === 'string' ? tool.functionConfig : '';
    try {
      const res = await axios.post(`${apiEndpoint}/api/tools/validateFunctionConfig`, { functionConfig: functionConfig }, cfg);
      const valid = res !== undefined && res.data !== undefined && res.data.valid === true;
      if (valid === true) {
        tool.validationError = null;
        toast.success('Function validated successfully');
        return;
      }
      const serverError = res !== undefined && res.data !== undefined && typeof res.data.error === 'string' ? res.data.error : 'Function validation failed';
      tool.validationError = serverError;
      toast.error('Function validation failed');
    } catch (error) {
      const serverErr = error !== undefined && error !== null && error.response !== undefined && error.response !== null && error.response.data !== undefined && error.response.data !== null && typeof error.response.data.error === 'string' ? error.response.data.error : null;
      const msg = serverErr !== null ? serverErr : (error !== undefined && error !== null && typeof error.message === 'string' ? error.message : 'Function validation failed');
      tool.validationError = msg;
      toast.error('Function validation failed');
    }
  }
  
  function validateTab1() {
    let isValid = true;
  
    agentNameError.value = false;
    agentNameErrorMessage.value = '';
    agentDescriptionError.value = false;
    agentDescriptionErrorMessage.value = '';
    agentShortDescriptionError.value = false;
    agentShortDescriptionErrorMessage.value = '';
    startingMessageError.value = false;
    startingMessageErrorMessage.value = '';
    divisionError.value = false;
    divisionErrorMessage.value = '';
  
    const sanitizedName = secureUserText(agentName.value, {
      fieldName: 'Agent Name',
      maxLength: 100,
      disallowAngleBrackets: true
    });
    if (sanitizedName.error !== null) {
      agentNameError.value = true;
      agentNameErrorMessage.value = sanitizedName.error;
      isValid = false;
    } else {
      agentName.value = sanitizedName.value;
    }
    const sanitizedDescription = secureUserText(agentDescription.value, {
      fieldName: 'Agent Description',
      allowNewLines: true,
      maxLength: maxAgentDescriptionCharacters
    });
    if (sanitizedDescription.error !== null) {
      agentDescriptionError.value = true;
      agentDescriptionErrorMessage.value = sanitizedDescription.error;
      isValid = false;
    } else {
      agentDescription.value = sanitizedDescription.value;
    }
    const shortDescriptionValidation = secureUserText(agentShortDescription.value, {
      fieldName: 'Agent Short Description',
      allowNewLines: true,
      maxLength: 250,
      disallowAngleBrackets: true,
      allowEmpty: agentType.value !== 'PrivateAgent'
    });
    if (shortDescriptionValidation.error !== null) {
      agentShortDescriptionError.value = true;
      agentShortDescriptionErrorMessage.value = shortDescriptionValidation.error;
      isValid = false;
    } else {
      agentShortDescription.value = shortDescriptionValidation.value;
    }

    if (agentMode.value !== 'Flow') {
      const sanitizedStarting = sanitizeLocalizationObject(startingMessage.value, {
        fieldName: 'Starting Message',
        allowNewLines: true,
        maxLength: 2000,
        disallowAngleBrackets: false
      });
      if (sanitizedStarting.error !== null) {
        startingMessageError.value = true;
        startingMessageErrorMessage.value = sanitizedStarting.error;
        isValid = false;
      } else if (typeof sanitizedStarting.sanitized.default !== 'string' || sanitizedStarting.sanitized.default.length === 0) {
        startingMessageError.value = true;
        startingMessageErrorMessage.value = 'Starting Message (in Default) is required.';
        isValid = false;
      } else {
        startingMessage.value = sanitizedStarting.sanitized;
      }
    } else {
      const sanitizedFlowStarting = sanitizeLocalizationObject(startingMessage.value, {
        fieldName: 'Starting Message',
        allowNewLines: true,
        maxLength: 2000,
        disallowAngleBrackets: false,
        allowEmpty: true
      });
      if (sanitizedFlowStarting.error === null) {
        startingMessage.value = sanitizedFlowStarting.sanitized;
      }
    }
    const sanitizedHumanTransferMessage = sanitizeLocalizationObject(humanInTheLoopHumanTransferMessage.value, {
      fieldName: 'Human Transfer Message',
      allowNewLines: true,
      maxLength: 1000,
      disallowAngleBrackets: false,
      allowEmpty: true
    });
    if (sanitizedHumanTransferMessage.error !== null) {
      toast.error(sanitizedHumanTransferMessage.error);
      isValid = false;
    } else {
      humanInTheLoopHumanTransferMessage.value = sanitizedHumanTransferMessage.sanitized;
    }
    const sanitizedAiBackMessage = sanitizeLocalizationObject(humanInTheLoopAiBackMessage.value, {
      fieldName: 'AI Back Message',
      allowNewLines: true,
      maxLength: 1000,
      disallowAngleBrackets: false,
      allowEmpty: true
    });
    if (sanitizedAiBackMessage.error !== null) {
      toast.error(sanitizedAiBackMessage.error);
      isValid = false;
    } else {
      humanInTheLoopAiBackMessage.value = sanitizedAiBackMessage.sanitized;
    }

    if (selectedDivisionIds.value.length === 0) {
      divisionError.value = true;
      divisionErrorMessage.value = 'At least one Division must be selected.';
      isValid = false;
    }
    if (selectedLLMModel.value === null || selectedLLMModel.value === '') {
          toast.error('LLM Model must be selected.');
          isValid = false;
      }
    const sanitizedRules = sanitizeRuleEntries(rules.value);
    if (sanitizedRules.error !== null) {
      toast.error(sanitizedRules.error);
      isValid = false;
    } else {
      rules.value = sanitizedRules.sanitized;
    }
    if (isPhoneChannelSelected.value === true) {
      const phoneAgentDescriptionAndRulesLength = getPhoneAgentDescriptionAndRulesLength(agentDescription.value, rules.value);
      if (phoneAgentDescriptionAndRulesLength >= phoneAgentDescriptionAndRulesLimit) {
        const phoneAgentLengthMessage = `For phone agents, description and rules together must be under ${phoneAgentDescriptionAndRulesLimit} characters. Current length: ${phoneAgentDescriptionAndRulesLength}.`;
        agentDescriptionError.value = true;
        agentDescriptionErrorMessage.value = phoneAgentLengthMessage;
        toast.error(phoneAgentLengthMessage);
        isValid = false;
      }
    }
    return isValid;
  }
  
  const createAgent = async (shouldRedirect = false) => {
    
    if (validateTab1() === false) {
  
      const firstTabRadio = document.querySelector('input[data-currentPage="1"]');
      const firstTabDiv = document.querySelector('div[data-currentPage="1"]');
      if (firstTabRadio !== null && firstTabRadio !== undefined) {
        firstTabRadio.checked = true;
        if(firstTabDiv !== null && firstTabDiv !== undefined) firstTabDiv.classList.add('active');
      }
      return;
    }
  
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg !== null) {
        const userRes = await axios.get(`${apiEndpoint}/api/users/me`, cfg);
        const orgId = userRes?.data?.organizationID;
        if (Number.isFinite(Number(orgId)) === true) {
          const polRes = await axios.get(`${apiEndpoint}/api/agents/policies/${encodePathSegment(orgId)}`, cfg);
        const policies = polRes.data;
        if (showAllFields.value !== true && Array.isArray(policies?.features?.allowedLlmModelIds) === true && policies.features.allowedLlmModelIds.length > 0) {
          const selectedModelIdForPolicy = typeof resolvedSelectedLLMModelId.value === 'string' && resolvedSelectedLLMModelId.value.length > 0 ? resolvedSelectedLLMModelId.value : selectedLLMModel.value;
          if (policies.features.allowedLlmModelIds.includes(selectedModelIdForPolicy) === false) {
            const firstAllowed = availableLLMModels.value.find(m => policies.features.allowedLlmModelIds.includes(m.id) === true);
            if (firstAllowed !== undefined) {
              selectedLLMModel.value = firstAllowed.id;
              toast.info('Selected LLM is not allowed for your organization. Switched to the first allowed model.');
            } else {
              toast.error('No allowed LLM models configured for your organization.');
              isSaving.value = false;
              showLoadingModal.value = false;
              return;
            }
          }
        }
        if (agentDescription.value.length > maxAgentDescriptionCharacters) {
          agentDescription.value = agentDescription.value.substring(0, maxAgentDescriptionCharacters);
          toast.info('System prompt was truncated to fit the 300k token limit.');
        }
      }
      }
    } catch (error) {}

    const sanitizedInappropriateContent = sanitizeLocalizationObject(inappropriateContentMessage.value, {
      fieldName: 'Inappropriate Content Message',
      allowNewLines: true,
      maxLength: 1000,
      disallowAngleBrackets: true,
      allowEmpty: true
    });
    if (sanitizedInappropriateContent.error !== null) {
      toast.error(sanitizedInappropriateContent.error);
      return;
    }
    inappropriateContentMessage.value = sanitizedInappropriateContent.sanitized;

    const sanitizedDontKnowMessage = sanitizeLocalizationObject(dontKnowMessage.value, {
      fieldName: 'Fallback Answer Message',
      allowNewLines: true,
      maxLength: 1000,
      disallowAngleBrackets: true,
      allowEmpty: true
    });
    if (sanitizedDontKnowMessage.error !== null) {
      toast.error(sanitizedDontKnowMessage.error);
      return;
    }
    dontKnowMessage.value = sanitizedDontKnowMessage.sanitized;

    const sanitizedNetworkError = sanitizeLocalizationObject(networkErrorMessage.value, {
      fieldName: 'Network Error Message',
      allowNewLines: true,
      maxLength: 1000,
      disallowAngleBrackets: true,
      allowEmpty: true
    });
    if (sanitizedNetworkError.error !== null) {
      toast.error(sanitizedNetworkError.error);
      return;
    }
    networkErrorMessage.value = sanitizedNetworkError.sanitized;

    const sanitizedLimitReached = sanitizeOptionalText(limitReachedMessage.value, 'Limit Reached Message', {
      allowNewLines: true,
      maxLength: 500,
      disallowAngleBrackets: true
    });
    if (sanitizedLimitReached.error !== null) {
      toast.error(sanitizedLimitReached.error);
      return;
    }
    limitReachedMessage.value = sanitizedLimitReached.value;

    const sanitizedWhatsappClosing = sanitizeOptionalText(whatsappClosingMessage.value, 'WhatsApp closing message', {
      maxLength: 300,
      disallowAngleBrackets: true
    });
    if (sanitizedWhatsappClosing.error !== null) {
      toast.error(sanitizedWhatsappClosing.error);
      return;
    }
    whatsappClosingMessage.value = sanitizedWhatsappClosing.value;

    const sanitizedVoicePrompt = sanitizeOptionalText(voiceAgentExtraPrompt.value, 'Voice Agent prompt', {
      allowNewLines: true,
      maxLength: 4000
    });
    if (sanitizedVoicePrompt.error !== null) {
      toast.error(sanitizedVoicePrompt.error);
      return;
    }
    voiceAgentExtraPrompt.value = sanitizedVoicePrompt.value;

    pruneVoicePromptKnowledgeBaseFileNames();
    const sanitizedVoicePromptKbLoadStatement = sanitizeOptionalText(voicePromptKnowledgeBaseLoadStatement.value, 'Voice Agent prompt knowledge load statement', {
      allowNewLines: true,
      maxLength: 1200
    });
    if (sanitizedVoicePromptKbLoadStatement.error !== null) {
      toast.error(sanitizedVoicePromptKbLoadStatement.error);
      return;
    }
    voicePromptKnowledgeBaseLoadStatement.value = sanitizedVoicePromptKbLoadStatement.value !== '' ? sanitizedVoicePromptKbLoadStatement.value : defaultVoicePromptKnowledgeBaseLoadStatement;
    voicePromptKnowledgeBaseFileNames.value = normalizeVoicePromptKnowledgeBaseFileNames(voicePromptKnowledgeBaseFileNames.value);

    const sanitizedAuthWelcome = sanitizeOptionalText(authenticatorWelcomeMessage.value, 'Authenticator welcome message', {
      allowNewLines: true,
      maxLength: 1000
    });
    if (sanitizedAuthWelcome.error !== null) {
      toast.error(sanitizedAuthWelcome.error);
      return;
    }
    authenticatorWelcomeMessage.value = sanitizedAuthWelcome.value;

    const sanitizedAuthRetry = sanitizeOptionalText(authenticatorRetryMessage.value, 'Authenticator retry message', {
      allowNewLines: true,
      maxLength: 1000
    });
    if (sanitizedAuthRetry.error !== null) {
      toast.error(sanitizedAuthRetry.error);
      return;
    }
    authenticatorRetryMessage.value = sanitizedAuthRetry.value;

    const sanitizedAuthSuccess = sanitizeOptionalText(authenticatorSuccessMessage.value, 'Authenticator success message', {
      allowNewLines: true,
      maxLength: 1000
    });
    if (sanitizedAuthSuccess.error !== null) {
      toast.error(sanitizedAuthSuccess.error);
      return;
    }
    authenticatorSuccessMessage.value = sanitizedAuthSuccess.value;

    const sanitizedActionButtons = sanitizeActionButtonsList(actionButtons.value);
    if (sanitizedActionButtons.error !== null) {
      toast.error(sanitizedActionButtons.error);
      return;
    }
    actionButtons.value = sanitizedActionButtons.sanitized;

    const sanitizedCustomRegex = sanitizeRegexEntries(customBlockRegex.value);
    if (sanitizedCustomRegex.error !== null) {
      toast.error(sanitizedCustomRegex.error);
      return;
    }
    customBlockRegex.value = sanitizedCustomRegex.sanitized;
    if (validateSelectedToolSettings() !== true) {
      return;
    }
  
    const baseCfg = getSecuredAxiosConfig({ timeout: 0 });
    if (baseCfg === null) {
      return;
    }
    modalState.type = 'loading';
    modalState.title = isEditMode.value ? 'Updating Agent...' : 'Creating Agent...';
    modalState.message = '';
    showLoadingModal.value = true;
    startLoadingTips();
    isSaving.value = true;
    isSavingAndRedirecting.value = shouldRedirect;
    
    const flowData = getFlowDataForSave();
    const voiceTypeForSave = normalizeVoiceSettingsVoiceTypeForVoice(voiceSettingsVoiceType.value, selectedVoiceOptionForCalls.value);
  
    try {
      const hasNewFiles = uploadedFiles.value.length > 0;
      const promptVersionsForSave = buildPromptVersionsForSave();
      const agentPayload = {
        aiAgentName: agentName.value,
        divisionIds: normalizeDivisionIds(selectedDivisionIds.value),
        aiAgentStatus: agentActive.value === true ? 'active' : 'inactive',
        agentType: agentType.value,
        agentMode: agentMode.value,
        agentShortDescription: agentShortDescription.value,
        analysisEngine: getLoadedAnalysisEngineForSave(),
        agentData: {
          agentMode: agentMode.value,
          description: agentDescription.value,
          startingMessage: startingMessage.value,
          language: agentLanguage.value,
          setupType: viewMode.value === 'light' ? 'light' : 'full',
          rules: rules.value,
          styleGuid: normalizeStyleGuidValue(selectedStyleGuid.value),
          avatarProvider: selectedAvatarProvider.value,
          avatarProviderConfigs: avatarProviderConfigs.value,
          agenticBehaviour: agenticBehaviour.value,
          voiceSettings: {
            voiceType: voiceTypeForSave,
            defaultEngineVersion: normalizeVoiceSettingsDefaultEngineVersion(voiceSettingsDefaultEngineVersion.value),
            vadEngine: normalizeVoiceSettingsVadEngine(voiceSettingsVadEngine.value),
            reasoningLevel: normalizeVoiceReasoningLevel(voiceSettingsReasoningLevel.value),
            allowAgentHangup: voiceAllowAgentHangup.value === true,
            allowCallTransfer: voiceAllowCallTransfer.value === true,
            voiceId: resolvedVoiceIdForCalls.value,
            voiceSupplier: '',
            voiceModel: '',
            ttsId: '',
            llmId: '',
            sttId: '',
            accent: voiceSettingsAccent.value,
            language: voiceSettingsLanguage.value,
            speed: Number.isFinite(Number(voiceSettingsSpeed.value)) === true ? Number(voiceSettingsSpeed.value) : 1,
            customPacingEnabled: voiceSettingsCustomPacingEnabled.value === true,
            pacing: Number.isFinite(Number(voiceSettingsPacing.value)) === true ? Number(voiceSettingsPacing.value) : defaultVoicePacing,
            durationStretch: Number.isFinite(Number(voiceSettingsDurationStretch.value)) === true ? Number(voiceSettingsDurationStretch.value) : defaultVoiceDurationStretch,
            useDashPauses: voiceSettingsUseDashPauses.value === true,
            pauseEveryWords: Number.isFinite(Number(voiceSettingsPauseEveryWords.value)) === true ? Number(voiceSettingsPauseEveryWords.value) : defaultVoicePauseEveryWords,
            dashPauseMs: Number.isFinite(Number(voiceSettingsDashPauseMs.value)) === true ? Number(voiceSettingsDashPauseMs.value) : defaultVoiceDashPauseMs,
            commaPauseMs: Number.isFinite(Number(voiceSettingsCommaPauseMs.value)) === true ? Number(voiceSettingsCommaPauseMs.value) : defaultVoiceCommaPauseMs,
            periodPauseMs: Number.isFinite(Number(voiceSettingsPeriodPauseMs.value)) === true ? Number(voiceSettingsPeriodPauseMs.value) : defaultVoicePeriodPauseMs,
            idleAutoResponseEnabled: voiceSettingsIdleAutoResponseEnabled.value === true,
            startingMessageIdleAutoResponseSeconds: Number.isFinite(Number(voiceSettingsStartingMessageIdleAutoResponseSeconds.value)) === true && Number(voiceSettingsStartingMessageIdleAutoResponseSeconds.value) >= 1 ? Math.min(300, Number(voiceSettingsStartingMessageIdleAutoResponseSeconds.value)) : 6,
            idleAutoResponseSeconds: Number.isFinite(Number(voiceSettingsIdleAutoResponseSeconds.value)) === true && Number(voiceSettingsIdleAutoResponseSeconds.value) >= 1 ? Math.min(300, Number(voiceSettingsIdleAutoResponseSeconds.value)) : 3,
            toolUseAutoResponseSeconds: Number.isFinite(Number(voiceSettingsToolUseAutoResponseSeconds.value)) === true && Number(voiceSettingsToolUseAutoResponseSeconds.value) >= 0 ? Math.min(300, Number(voiceSettingsToolUseAutoResponseSeconds.value)) : 2,
            maxCallMinutes: Number.isFinite(Number(voiceSettingsMaxCallMinutes.value)) === true && Number(voiceSettingsMaxCallMinutes.value) >= 0.25 ? Math.min(1440, Number(voiceSettingsMaxCallMinutes.value)) : 19,
            maxCallerMinutes: Number.isFinite(Number(voiceSettingsMaxCallerMinutes.value)) === true && Number(voiceSettingsMaxCallerMinutes.value) >= 0.25 ? Math.min(1440, Number(voiceSettingsMaxCallerMinutes.value)) : 19,
            callerCooldownMinutes: Number.isFinite(Number(voiceSettingsCallerCooldownMinutes.value)) === true && Number(voiceSettingsCallerCooldownMinutes.value) >= 1 ? Math.min(43200, Number(voiceSettingsCallerCooldownMinutes.value)) : 60,
            promptToVoiceEnabled: voiceSettingsPromptToVoiceEnabled.value === true,
            promptToVoicePrompt: typeof voiceSettingsPromptToVoicePrompt.value === 'string' ? voiceSettingsPromptToVoicePrompt.value : '',
            tone: voiceSettingsTone.value,
            mode: voiceSettingsMode.value,
            cancellingMode: normalizeVoiceSettingsCancellingMode(voiceSettingsCancellingMode.value),
            audioProcessorEngine: normalizeVoiceSettingsAudioProcessorEngine(voiceSettingsAudioProcessorEngine.value),
            termsPackages: Array.isArray(voiceSettingsTermsPackages.value) === true ? voiceSettingsTermsPackages.value : [],
            fillerSentences: normalizeWaitingFillerSentences(voiceSettingsFillerSentences.value),
            quickResponseFillerEnabled: voiceSettingsQuickResponseFillerEnabled.value === true,
            quickResponseFillerText: typeof voiceSettingsQuickResponseFillerText.value === 'string' ? voiceSettingsQuickResponseFillerText.value.trim().substring(0, 80) : '',
            sonioxContext: {
              terms: Array.isArray(voiceSettingsSonioxTerms.value) === true ? voiceSettingsSonioxTerms.value : []
            },
            transcriptionOptimizer: {
              enabled: voiceSettingsTranscriptionOptimizerEnabled.value === true,
              terms: Array.isArray(voiceSettingsTranscriptionOptimizerTerms.value) === true ? voiceSettingsTranscriptionOptimizerTerms.value : []
            }
          },
          webVoiceVisualSettings: {
            enabled: webVoiceVisualSettings.value.enabled === true,
            items: Array.isArray(webVoiceVisualSettings.value.items) === true ? webVoiceVisualSettings.value.items : []
          },
          flow: flowData,
          agentsToAgentsFlow: agentsToAgentsFlowRef.value !== null ? agentsToAgentsFlowRef.value.getFlowData() : null,
          cachedFileNames: cachedFileNames.value, 
          files: [ 
            ...existingFiles.value.map(file => ({ 
              fileName: file.fileName,
              fileType: file.fileType,
              status: file.status,
              url: file.url,
              maxPages: file.maxPages,
              rescrapeEnabled: file.rescrapeEnabled === true ? true : false,
              rescrapeIntervalDays: (Number.isFinite(Number(file.rescrapeIntervalDays)) === true && Number(file.rescrapeIntervalDays) >= 1) ? Number(file.rescrapeIntervalDays) : undefined,
              rescrapeAtHour: (typeof file.rescrapeAtTime === 'string' && /^\d{2}:\d{2}$/.test(file.rescrapeAtTime) === true) ? toUtcHourFromLocalTime(file.rescrapeAtTime) : undefined,
              rescrapeAtMinute: (typeof file.rescrapeAtTime === 'string' && /^\d{2}:\d{2}$/.test(file.rescrapeAtTime) === true) ? toUtcMinuteFromLocalTime(file.rescrapeAtTime) : undefined,
              originalQuery: file.originalQuery,
              originalResponse: file.originalResponse,
              fineTunedAnswer: file.fineTunedAnswer,
              fileData: file.fileType === 'questionAnswer' ? file.fileData : undefined,
              content: file.fileType === 'questionAnswer' ? file.content : undefined,
              hiddenContext: file.fileType === 'questionAnswer' ? file.hiddenContext : undefined,
              companyKnowHow: file.fileType === 'questionAnswer' ? file.companyKnowHow : undefined,
              forceExactAnswer: file.fileType === 'questionAnswer' && file.forceExactAnswer === true,
              hiddenFromSources: file.hiddenFromSources === true ? true : false,
              voiceScriptInjectionRegexSchemas: getFileVoiceScriptInjectionRegexSchemas(file)
            })),
            ...uploadedFiles.value.map(file => ({ 
              fileName: file.fileName,
              fileType: file.fileType,
              fileData: file.fileData,
              status: 'new',
              url: file.url,
              content: file.content,
              maxPages: file.maxPages,
              rescrapeEnabled: file.rescrapeEnabled === true ? true : false,
              rescrapeIntervalDays: (Number.isFinite(Number(file.rescrapeIntervalDays)) === true && Number(file.rescrapeIntervalDays) >= 1) ? Number(file.rescrapeIntervalDays) : undefined,
              rescrapeAtHour: (typeof file.rescrapeAtTime === 'string' && /^\d{2}:\d{2}$/.test(file.rescrapeAtTime) === true) ? toUtcHourFromLocalTime(file.rescrapeAtTime) : undefined,
              rescrapeAtMinute: (typeof file.rescrapeAtTime === 'string' && /^\d{2}:\d{2}$/.test(file.rescrapeAtTime) === true) ? toUtcMinuteFromLocalTime(file.rescrapeAtTime) : undefined,
              hiddenFromSources: file.hiddenFromSources === true ? true : false,
              voiceScriptInjectionRegexSchemas: getFileVoiceScriptInjectionRegexSchemas(file)
            }))
          ],
          selectedToolIds: selectedToolIds.value,
          selectedLlmModelId: resolvedSelectedLLMModelId.value, 
          configurations: {
            agentMode: agentMode.value,
            enableFeedbackButtons: enableFeedbackButtons.value,
            enableSuggestedResponses: enableSuggestedResponses.value,
            enableStreamingText: enableStreamingText.value !== false,
            agentsToAgentsRouterLlmModelId: agentsToAgentsRouterLlmModelId.value,
            showSubAgentConversationsInReports: showSubAgentConversationsInReports.value === true,
            selectedLlmModelChoiceId: selectedLLMModel.value,
           enableUserFineTune: enableUserFineTune.value === true,
           retrieveUserLocation: retrieveUserLocation.value,
           enableFileUploadInChat: enableFileUploadInChat.value,
           makeLinksClickable: makeLinksClickable.value,
          clearSessionOnRefresh: clearSessionOnRefresh.value,
          enabledSavedSessionHistory: enabledSavedSessionHistory.value === true,
          regularOpenAiContentFilterFailoverEnabled: regularOpenAiContentFilterFailoverEnabled.value === true,
           toolSettings: buildSelectedToolSettingsPayload(),
           showMap: showMap.value !== false,
           inappropriateContentMessage: inappropriateContentMessage.value,
           dontKnowMessage: dontKnowMessage.value,
           networkErrorMessage: networkErrorMessage.value,
           promptVersions: promptVersionsForSave,
           limitReachedMessage: limitReachedMessage.value,
           messageLimitPerConversation: (limitMessagesPerConversationEnabled.value === true) && (Number.isFinite(Number(messageLimitPerConversation.value)) != false) && (Number(messageLimitPerConversation.value) >= 0) ? Number(messageLimitPerConversation.value) : 0,
           messageLimitPerIP: (limitMessagesPerIPEnabled.value === true) && (Number.isFinite(Number(messageLimitPerIP.value)) != false) && (Number(messageLimitPerIP.value) >= 0) ? Number(messageLimitPerIP.value) : 0,
           messageLimitPerIPDurationMinutes: (limitMessagesPerIPEnabled.value === true) && (Number.isFinite(Number(messageLimitPerIPDurationMinutes.value)) != false) && (Number(messageLimitPerIPDurationMinutes.value) >= 1) ? Number(messageLimitPerIPDurationMinutes.value) : 3,
           messageLimitPerPhoneNumber: (limitMessagesPerIPEnabled.value === true) && (isWhatsappChannelSelected.value === true) && (Number.isFinite(Number(messageLimitPerPhoneNumber.value)) != false) && (Number(messageLimitPerPhoneNumber.value) >= 0) ? Number(messageLimitPerPhoneNumber.value) : 0,
           messageLimitPerPhoneNumberDurationMinutes: (limitMessagesPerIPEnabled.value === true) && (isWhatsappChannelSelected.value === true) && (Number.isFinite(Number(messageLimitPerPhoneNumberDurationMinutes.value)) != false) && (Number(messageLimitPerPhoneNumberDurationMinutes.value) >= 1) ? Number(messageLimitPerPhoneNumberDurationMinutes.value) : 3,
           enablePIIRedaction: enablePIIRedaction.value === true,
           redactEmails: redactEmails.value === true,
           redactPhoneNumbers: redactPhoneNumbers.value === true,
          customBlockRegex: customBlockRegex.value,
          responseFilters: responseFilters.value,
          kbToolUseMode: kbToolUseMode.value,
            deploymentChannels: selectedDeploymentChannels.value,
            allowCallControl: isPhoneChannelSelected.value === true && (voiceAllowAgentHangup.value === true || voiceAllowCallTransfer.value === true),
            allowAgentHangup: isPhoneChannelSelected.value === true && voiceAllowAgentHangup.value === true,
            allowCallTransfer: isPhoneChannelSelected.value === true && voiceAllowCallTransfer.value === true,
            allowModelInitiatedCallEnding: isPhoneChannelSelected.value === true && voiceAllowAgentHangup.value === true,
            agentToneId: selectedAgentTone.value,
            whatsappClosingMessage: whatsappClosingMessage.value,
            retrieverKValue: Number.isFinite(Number(retrieverKValue.value)) === true && Number(retrieverKValue.value) >= 1 ? Number(retrieverKValue.value) : 30,
            vectorDbQueriesLimit: Number.isFinite(Number(vectorDbQueriesLimit.value)) === true && Number(vectorDbQueriesLimit.value) >= 1 ? Number(vectorDbQueriesLimit.value) : 3,
            vectorQueryAdditionalText: typeof vectorQueryAdditionalText.value === 'string' ? vectorQueryAdditionalText.value.trim() : '',
            prependHistoryToShortVectorQueries: prependHistoryToShortVectorQueries.value === true,
            retrievalStrategy: ['standard', 'auto', 'multiEntity'].includes(retrievalStrategy.value) === true ? retrievalStrategy.value : 'standard',
            maxOutputTokens: Number.isFinite(Number(maxOutputTokens.value)) === true && Number(maxOutputTokens.value) >= 1 ? Number(maxOutputTokens.value) : 2048,
            actionButtons: actionButtons.value,
            verifyAnswerEnabled: verifyAnswerEnabled.value === true,
            verifyLinks: verifyLinks.value === true,
            verificationModelId: typeof selectedVerificationLLMModel.value === 'string' && selectedVerificationLLMModel.value.length > 0 ? selectedVerificationLLMModel.value : undefined,
            groqGptOssFirstAnswerReasoningEffort: showGroqGptOssFirstAnswerReasoningSelector.value === true ? normalizeGroqGptOssReasoningEffort(groqGptOssFirstAnswerReasoningEffort.value) : undefined,
            groqGptOssVerifyReasoningEffort: showGroqGptOssVerifyReasoningSelector.value === true ? normalizeGroqGptOssReasoningEffort(groqGptOssVerifyReasoningEffort.value) : undefined,
            authenticator: {
              enabled: authenticatorEnabled.value === true,
              provider: typeof authenticatorProvider.value === 'string' && authenticatorProvider.value.trim().length > 0 ? authenticatorProvider.value : 'google',
              welcomeMessageTemplate: typeof authenticatorWelcomeMessage.value === 'string' && authenticatorWelcomeMessage.value.trim().length > 0 ? authenticatorWelcomeMessage.value : defaultAuthenticatorWelcomeTemplate,
              retryMessageTemplate: typeof authenticatorRetryMessage.value === 'string' && authenticatorRetryMessage.value.trim().length > 0 ? authenticatorRetryMessage.value : defaultAuthenticatorRetryTemplate,
              successMessageTemplate: typeof authenticatorSuccessMessage.value === 'string' && authenticatorSuccessMessage.value.trim().length > 0 ? authenticatorSuccessMessage.value : defaultAuthenticatorSuccessTemplate,
              runAiToolOnLogin: authenticatorRunAiToolOnLogin.value === true
            },
            limitMessagesPerConversationEnabled: limitMessagesPerConversationEnabled.value === true,
            limitMessagesPerIPEnabled: limitMessagesPerIPEnabled.value === true,
            phoneVoiceId: resolvedVoiceIdForCalls.value,
            voiceAgent: (isPhoneChannelSelected.value === true || isWebVoiceAgentChannelSelected.value === true || isWhatsappVoiceChannelSelected.value === true || isTelegramVoiceChannelSelected.value === true || showVoiceSettingsTab.value === true) ? {
              provider: voiceAgentProvider.value,
              voice: resolvedVoiceIdForCalls.value,
              extraPrompt: voiceAgentExtraPrompt.value,
              promptKnowledgeBase: {
                enabled: voicePromptKnowledgeBaseEnabled.value === true && voicePromptKnowledgeBaseFileNames.value.length > 0,
                fileNames: voicePromptKnowledgeBaseFileNames.value,
                loadStatement: voicePromptKnowledgeBaseLoadStatement.value
              }
            } : undefined,
            humanInTheLoop: {
              enabled: humanInTheLoopEnabled.value === true,
              startConversationWebhook: typeof humanInTheLoopStartConversationWebhook.value === 'string' ? humanInTheLoopStartConversationWebhook.value.trim() : '',
              newMessageWebhook: typeof humanInTheLoopNewMessageWebhook.value === 'string' ? humanInTheLoopNewMessageWebhook.value.trim() : '',
              inboundSecret: typeof humanInTheLoopInboundSecret.value === 'string' ? humanInTheLoopInboundSecret.value.trim() : '',
              humanTransferMessage: humanInTheLoopHumanTransferMessage.value,
              aiBackMessage: humanInTheLoopAiBackMessage.value
            }
         },
          selectedVectorDBId: typeof selectedVectorDBId.value === 'string' && selectedVectorDBId.value.length > 0 ? selectedVectorDBId.value : undefined,
          selectedEmbeddingModelId: typeof selectedEmbeddingModelId.value === 'string' && selectedEmbeddingModelId.value.length > 0 ? selectedEmbeddingModelId.value : undefined,
          selectedAnalysisCollectionIds: normalizeAnalysisCollectionIds(selectedAnalysisCollectionIds.value),
          sharedKnowledgeBaseIds: Array.isArray(selectedSharedKbIds.value) === true ? selectedSharedKbIds.value : [],
          linkedAgentKnowledgeBaseIds: Array.isArray(selectedLinkedAgentKbIds.value) === true ? selectedLinkedAgentKbIds.value : [],
        
          knowledgeBaseFolders: knowledgeBaseFolders.value,
          knowledgeBaseFileAssignments: knowledgeBaseFileAssignments.value,
        }
      };
      
      const uploadedFilesSnapshot = uploadedFiles.value.map(file => ({
        ...file
      }));
  
      let response;
      if (isEditMode.value === true) {
        const agentIdRaw = route.query.edit;
        const agentId = agentIdRaw !== undefined && agentIdRaw !== null ? String(agentIdRaw) : '';
        if (agentId.length === 0) {
          throw new Error('Missing agent id');
        }
        response = await streamModifyAgentRequest(agentId, agentPayload);
        try {
          const sidVal = selectedWhatsappSourceId.value;
          if (selectedDeploymentChannels.value.includes('Whatsapp') === true || selectedDeploymentChannels.value.includes('Whatsapp Voice') === true) {
            const coerced = Number.isFinite(Number(sidVal)) === true ? Number(sidVal) : null;
            if (coerced !== null) {
              await axios.post(`${apiEndpoint}/api/whatsapp/bind`, { agentId: agentId, sourceId: coerced }, { ...baseCfg, timeout: undefined });
            }
          } else {
            const sidNum = Number(sidVal);
            if (Number.isFinite(sidNum) === true && sidNum > 0) {
              await axios.delete(`${apiEndpoint}/api/whatsapp/unbind`, { ...baseCfg, timeout: undefined, params: { agentId: agentId, sourceId: sidNum } });
            } else {
              const currentBind = await axios.get(`${apiEndpoint}/api/whatsapp/binding/${encodePathSegment(agentId)}`, { ...baseCfg, timeout: undefined });
              const boundId = Number(currentBind?.data?.sourceId);
              if (Number.isFinite(boundId) === true && boundId > 0) {
                await axios.delete(`${apiEndpoint}/api/whatsapp/unbind`, { ...baseCfg, timeout: undefined, params: { agentId: agentId, sourceId: boundId } });
              }
            }
          }
        } catch (error) {}
        try {
          const phoneSidVal = (selectedPhoneSourceId !== undefined && selectedPhoneSourceId !== null && typeof selectedPhoneSourceId === 'object') ? selectedPhoneSourceId.value : selectedPhoneSourceId;
          if (selectedDeploymentChannels.value.includes('Phone') === true) {
            const coercedPhone = Number.isFinite(Number(phoneSidVal)) === true ? Number(phoneSidVal) : null;
            if (coercedPhone !== null) {
              await axios.post(`${apiEndpoint}/api/phone/bind`, { agentId: agentId, sourceId: coercedPhone }, { ...baseCfg, timeout: undefined });
            }
          } else {
            const phoneSidNum = Number(phoneSidVal);
            if (Number.isFinite(phoneSidNum) === true && phoneSidNum > 0) {
              await axios.delete(`${apiEndpoint}/api/phone/unbind`, { ...baseCfg, timeout: undefined, params: { agentId: agentId, sourceId: phoneSidNum } });
            } else {
              const currentPhoneBind = await axios.get(`${apiEndpoint}/api/phone/binding/${encodePathSegment(agentId)}`, { ...baseCfg, timeout: undefined });
              const phoneBoundId = Number(currentPhoneBind?.data?.sourceId);
              if (Number.isFinite(phoneBoundId) === true && phoneBoundId > 0) {
                await axios.delete(`${apiEndpoint}/api/phone/unbind`, { ...baseCfg, timeout: undefined, params: { agentId: agentId, sourceId: phoneBoundId } });
              }
            }
          }
        } catch (error) {}
        try {
          if (immediateRescrapeRequested.value === true && Array.isArray(immediateRescrapeQueue.value) === true && immediateRescrapeQueue.value.length > 0) {
            await runImmediateRescrape(agentId);
          }
        } catch (error) {}
        toast.success('Agent updated successfully'); 
      } else {
        response = await axios.post(`${apiEndpoint}/createNewAIChat`, agentPayload, baseCfg);
        try {
          const rawNewId = isDefinedAndNotEmpty(response?.data?.agentid) != false ? response.data.agentid : (isDefinedAndNotEmpty(response?.data?.agentId) != false ? response.data.agentId : null);
          const newIdStr = rawNewId !== null && rawNewId !== undefined ? String(rawNewId) : '';
          if (newIdStr.length > 0 && (selectedDeploymentChannels.value.includes('Whatsapp') === true || selectedDeploymentChannels.value.includes('Whatsapp Voice') === true)) {
            const sidVal = selectedWhatsappSourceId.value;
            const coerced = Number.isFinite(Number(sidVal)) === true ? Number(sidVal) : null;
            if (coerced !== null) {
              await axios.post(`${apiEndpoint}/api/whatsapp/bind`, { agentId: newIdStr, sourceId: coerced }, { ...baseCfg, timeout: undefined });
            }
          }
          if (newIdStr.length > 0 && selectedDeploymentChannels.value.includes('Phone') === true) {
            const phoneSidVal = (selectedPhoneSourceId !== undefined && selectedPhoneSourceId !== null && typeof selectedPhoneSourceId === 'object') ? selectedPhoneSourceId.value : selectedPhoneSourceId;
            const coercedPhone = Number.isFinite(Number(phoneSidVal)) === true ? Number(phoneSidVal) : null;
            if (coercedPhone !== null) {
              await axios.post(`${apiEndpoint}/api/phone/bind`, { agentId: newIdStr, sourceId: coercedPhone }, { ...baseCfg, timeout: undefined });
            }
          }
          if (newIdStr.length > 0 && immediateRescrapeRequested.value === true && Array.isArray(immediateRescrapeQueue.value) === true && immediateRescrapeQueue.value.length > 0) {
            await runImmediateRescrape(newIdStr);
          }
        } catch (error) {}
        toast.success('Agent created successfully'); 
      }
      console.log("Agent save/create response:", response.data);
      promptVersions.value = promptVersionsForSave;
  
      stopLoadingTips();
      showLoadingModal.value = false; 
      isSaving.value = false;
      isSavingAndRedirecting.value = false;
  
       uploadedFiles.value = []; 
      
      if (shouldRedirect === true) {
          await new Promise(resolve => setTimeout(resolve, 500)); 
          
          const newAgentId = isDefinedAndNotEmpty(response.data.agentid) != false ? response.data.agentid : isDefinedAndNotEmpty(response.data.agentId) != false ? response.data.agentId : response.data;
          const query = { agentId: newAgentId };
          if (hasNewFiles === true) {
            query.processing = 'true';
          }
          router.push({ path: '/aiChatAgents', query }); 
      } else {
          const currentAgentId = isDefinedAndNotEmpty(response?.data?.agentid) != false ? response.data.agentid : (isDefinedAndNotEmpty(response?.data?.agentId) != false ? response.data.agentId : route.query.edit);
          const currentAgentIdStr = currentAgentId !== undefined && currentAgentId !== null ? String(currentAgentId) : '';
          mergeSavedFilesIntoExistingFiles(uploadedFilesSnapshot);
          if (currentAgentIdStr.length > 0) {
            await refreshKnowledgeStatusesOnce(currentAgentIdStr, false);
            syncKnowledgeStatusStreamForCurrentAgent(currentAgentIdStr);
          }
          const hasLiveKnowledgeUpdates = uploadedFilesSnapshot.length > 0 || hasKnowledgeProcessingFiles() === true;
          if (hasLiveKnowledgeUpdates === true) {
            toast.success('Agent saved. Knowledge base statuses are updating live.');
          } else {
            toast.success('Agent saved successfully.');
          }
      }
  
    } catch (error) {
      stopLoadingTips(); 
      isSaving.value = false;
      isSavingAndRedirecting.value = false;
      showLoadingModal.value = true; 
      
      const statusCode = isDefinedAndNotEmpty(error.response?.status) != false ? error.response?.status : 0;
      const errorData = isDefinedAndNotEmpty(error.response?.data) != false ? error.response?.data : {};
      const errorMessageText = isDefinedAndNotEmpty(errorData?.message) != false ? errorData?.message : isDefinedAndNotEmpty(errorData?.error) != false ? errorData?.error : 'An unexpected error occurred.';
      console.error("Error saving agent:", isDefinedAndNotEmpty(error.response) != false ? error.response : error);
  
      modalState.type = 'error';
      modalState.title = 'Error Saving Agent';
      if (statusCode === 400 && typeof error.response?.data?.error === 'object') {
          modalState.message = "Validation errors occurred. Please check your input.";
      } else if (statusCode === 400 || statusCode === 409) {
         modalState.message = errorMessageText;
      } else {
        modalState.message = errorMessageText;
        if (statusCode >= 500) {
            modalState.message += " Please try again later or contact support.";
        }
      }
    } 
  };
  
  const saveAgentWithoutRedirect = async () => {
      if (isEditMode.value === true) {
          await createAgent(false);
      } else {
          toast.info("Please save the agent first to enable this action.");
      }
  };
  
  async function addWebsiteLink() {
    if (websiteLink.value.trim().length > 0) {
      const newFile = {
        fileName: websiteLink.value,
        fileType: 'websiteurl',
         status: 'new',
        url: websiteLink.value,
        rescrapeEnabled: false,
        rescrapeIntervalDays: undefined,
        rescrapeAtTime: '',
        hiddenFromSources: false
      };
    
      const isDuplicate = uploadedFiles.value.some(f => f.fileName === newFile.fileName) != false || 
                          existingFiles.value.some(f => f.fileName === newFile.fileName && f.status !== 'deleted') != false;
      if (isDuplicate === false) {
           uploadedFiles.value.push(newFile); 
          knowledgeBaseFileAssignments.value[newFile.fileName] = currentFolderId.value;
          showWebsiteLinkModal.value = false;
          websiteLink.value = '';
      } else {
          toast.warning('Website link already exists.');
      }
    }
  }
  
  function addWebsiteBatchUrls() {
    const text = websiteBatchText.value;
    if (text.trim().length === 0) {
      toast.error('Please enter at least one URL.');
      return;
    }
    const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    let added = 0;
    let skipped = 0;
    for (let i = 0; i < lines.length; i = i + 1) {
      const url = lines[i];
      const isHttp = (url.startsWith('http://') === true) || (url.startsWith('https://') === true);
      if (isHttp === false) {
        skipped = skipped + 1;
        continue;
      }
      const isDuplicate = (uploadedFiles.value.some(f => f.fileName === url) === true) || (existingFiles.value.some(f => f.fileName === url && f.status !== 'deleted') === true);
      if (isDuplicate === false) {
        const newFile = { fileName: url, fileType: 'websiteurl', status: 'new', url: url, rescrapeEnabled: rescrapeEnabled.value === true, rescrapeIntervalDays: rescrapeIntervalDays.value, rescrapeAtTime: typeof rescrapeAtHour.value === 'number' ? String(rescrapeAtHour.value).padStart(2,'0') + ':00' : '' , hiddenFromSources: false };
        uploadedFiles.value.push(newFile);
        knowledgeBaseFileAssignments.value[newFile.fileName] = currentFolderId.value;
        added = added + 1;
      } else {
        skipped = skipped + 1;
      }
    }
    showWebsiteBatchModal.value = false;
    websiteBatchText.value = '';
    if (added > 0) {
      toast.success(added + ' URL(s) added.');
    }
    if (skipped > 0) {
      toast.info(skipped + ' URL(s) skipped.');
    }
  }
  
  async function scrapeWebsite() {
    if (websiteToScrape.value.trim().length > 0) {
       const newFile = {
          fileName: websiteToScrape.value,
          fileType: 'website',
          status: 'new',
          url: websiteToScrape.value,
          maxPages: isDefinedAndNotEmpty(maxPages.value) != false ? maxPages.value : undefined,
          rescrapeEnabled: false,
          rescrapeIntervalDays: undefined,
          rescrapeAtTime: '',
          hiddenFromSources: false
        };
      const isDuplicate = uploadedFiles.value.some(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === newFile.fileName) != false || 
                          existingFiles.value.some(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === newFile.fileName && f.status !== 'deleted') != false;
      if (isDuplicate === false) {
          uploadedFiles.value.push(newFile);
          knowledgeBaseFileAssignments.value[newFile.fileName] = currentFolderId.value;
          showWebsiteScrapeModal.value = false;
          websiteToScrape.value = '';
          maxPages.value = '';
          
      } else {
          toast.warning('Website scrape target already exists.');
      }
    }
  }
  
  async function addTextContent() {
    if (textFileName.value.trim().length === 0 || textContent.value.trim().length === 0) {
      toast.error('Please enter both file name and content for text input.');
      return;
    }
    const normalizedRegexSchemas = normalizeTextRegexSchemas(textRegexSchemas.value);
    for (let i = 0; i < normalizedRegexSchemas.length; i = i + 1) {
      if (compileTextRegexSchema(normalizedRegexSchemas[i]) === null) {
        toast.error(t('aiChatAgentSetup.modals.addTextContent.regexSchemaInvalid', 'Regex schema is invalid.'));
        return;
      }
    }
    textRegexSchemas.value = normalizedRegexSchemas;
  
      const fileName = textFileName.value.endsWith('.txt') === true ? textFileName.value : `${textFileName.value}.txt`;
      const isDuplicateInUploaded = uploadedFiles.value.some(f => f.fileName === fileName) != false;
      const isDuplicateInExisting = existingFiles.value.some(f => f.fileName === fileName && f.status !== 'deleted');
  
    if (isEditingTextFile.value === false && (isDuplicateInUploaded === true || isDuplicateInExisting === true)) {
      toast.error('A file or text input with this name already exists.');
      return;
    }
  
        const textBlob = new Blob([textContent.value], { type: 'text/plain' });
        if (textBlob.size > maxFileSizeBytes.value) {
            toast.error(`Text content for "${fileName}" exceeds the ${maxFileSizeMb.value}MB size limit.`);
            return;
        }
  
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64Data = e.target.result.split(',')[1];
          const newFileEntry = {
            fileName: fileName,
            fileType: 'text',
             status: 'new',
             content: textContent.value,
        fileData: base64Data,
        isEditedVersion: false, 
        replacesFile: null,
        hiddenFromSources: false,
        voiceScriptInjectionRegexSchemas: normalizedRegexSchemas
          };
          uploadedFiles.value.push(newFileEntry);
          knowledgeBaseFileAssignments.value[newFileEntry.fileName] = currentFolderId.value;
  
      if (isEditingTextFile.value === true && originalTextFileToEdit.value !== null) {
        const originalFile = originalTextFileToEdit.value;
        newFileEntry.isEditedVersion = true;
        newFileEntry.replacesFile = originalFile.fileName;
  
        if (originalFile.status === 'new') {
          const idx = uploadedFiles.value.findIndex(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === originalFile.fileName);
          if (idx > -1) uploadedFiles.value.splice(idx, 1);
        } else if (originalFile.status === 'existing') {
          const existingIdx = existingFiles.value.findIndex(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === originalFile.fileName);
          if (existingIdx > -1) existingFiles.value[existingIdx].status = 'deleted';
        }
        
        if (isCached(originalFile.fileName) != false) {
          toggleCacheFile(originalFile); 
          toggleCacheFile(newFileEntry); 
        }
        toast.success(`File "${originalFile.fileName}" updated to "${fileName}". Old version marked for deletion.`);
      } else {
        toast.success('Text content added successfully.');
      }
          
          stopTextTranscription();
          showTextInputModal.value = false;
          resetTextInputModalFields();
        };
        reader.readAsDataURL(textBlob);
  }

  const refreshTools = async () => {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        availableTools.value = [];
        return;
      }
      const response = await axios.get(`${apiEndpoint}/api/tools/getAllAITools`, {
        ...cfg,
        params: {
          ...(cfg.params !== undefined && cfg.params !== null && typeof cfg.params === 'object' ? cfg.params : {}),
          includeSystemTools: true
        }
      });
      console.log('Available tools:', response.data);
      if (Array.isArray(response.data) === true) {
        availableTools.value = response.data.map(tool => {
          const normalizedId = isDefinedAndNotEmpty(tool.toolid) != false ? tool.toolid : (isDefinedAndNotEmpty(tool.toolId) != false ? tool.toolId : (isDefinedAndNotEmpty(tool.toolGuid) != false ? tool.toolGuid : (isDefinedAndNotEmpty(tool.id) != false ? tool.id : null)));
          const normalizedName = isDefinedAndNotEmpty(tool.toolname) != false ? tool.toolname : (isDefinedAndNotEmpty(tool.toolName) != false ? tool.toolName : (isDefinedAndNotEmpty(tool.name) != false ? tool.name : ''));
          const normalizedData = isDefinedAndNotEmpty(tool.tooldata) != false ? tool.tooldata : (isDefinedAndNotEmpty(tool.toolData) != false ? tool.toolData : {});
          return {
            ...tool,
            toolid: normalizedId,
            toolname: normalizedName,
            tooldata: normalizedData
          };
        });
      } else {
        availableTools.value = [];
      }
    } catch (error) {
      handleApiError(error, 'loading tools');
    }
  };
  
  const isToolEnabled = (toolId) => {
    return selectedToolIds.value.some(id => toolIdsMatch(id, toolId) === true) === true;
  };
  
  function addSelectedTool() {
    if (selectedTool.value !== '') {
      const toolToAdd = availableTools.value.find(tool => {
        const currentToolId = getToolIdentifier(tool);
        return currentToolId !== null && toolIdsMatch(currentToolId, selectedTool.value) === true;
      });
      if (isDefinedAndNotEmpty(toolToAdd) != false && selectedToolIds.value.some(id => toolIdsMatch(id, selectedTool.value) === true) === false) {
        tools.value.push(toolToAdd);
        const selectedToolIdentifier = getToolIdentifier(toolToAdd);
        if (selectedToolIdentifier !== null) {
          selectedToolIds.value.push(selectedToolIdentifier);
        }
        if (isSmsTool(toolToAdd) === true) {
          getToolSettingsEntry(toolToAdd);
          initializeSmsToolData();
        }
        if (isEmailTool(toolToAdd) === true) {
          getToolSettingsEntry(toolToAdd);
          initializeEmailToolData();
        }
        if (isWhatsappTemplateTool(toolToAdd) === true) {
          getToolSettingsEntry(toolToAdd);
          initializeWhatsappTemplateToolData();
        }
        selectedTool.value = '';
      }
    }
  }
  
  const removeToolFromAgent = (toolId) => {
    const index = selectedToolIds.value.findIndex(id => toolIdsMatch(id, toolId) === true);
    if (index > -1) {
      selectedToolIds.value.splice(index, 1);
    }
    const toolToRemove = tools.value.find(tool => toolIdsMatch(getToolIdentifier(tool), toolId) === true);
    if (toolToRemove !== undefined) {
      clearToolSettingsEntry(toolToRemove);
    }
  };
  
  const loadStyles = async () => {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        agentStyles.value = [];
        return;
      }
      const response = await axios.get(`${apiEndpoint}/api/styles/getAllAgentStyles`, cfg);
      console.log('Loaded styles:', response.data);
      
      agentStyles.value = response.data.map(style => {
        const normalizedId = style.styleguid !== undefined && style.styleguid !== null && style.styleguid !== '' ? style.styleguid : (style.styleGuid !== undefined && style.styleGuid !== null && style.styleGuid !== '' ? style.styleGuid : (style.styleid !== undefined && style.styleid !== null && style.styleid !== '' ? style.styleid : (style.styleId !== undefined && style.styleId !== null && style.styleId !== '' ? style.styleId : (style.id !== undefined && style.id !== null && style.id !== '' ? style.id : ''))));
        return { ...style, styleid: normalizedId };
      });
      if (isEditMode.value === true && selectedStyleGuid.value !== '') {
        const selectedStyle = agentStyles.value.find(style => style.styleid === selectedStyleGuid.value);
        if (selectedStyle !== undefined) {
          console.log('Found selected style:', selectedStyle);
        }
      }
    } catch (error) {
      handleApiError(error, 'loading styles');
    }
  };
  
  const handleStyleChange = () => {
    console.log('Style changed to:', selectedStyleGuid.value);
  };
  
  const loadDivisions = async () => {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        divisions.value = [];
        return;
      }
      const response = await axios.get(`${apiEndpoint}/api/divisions`, cfg);
      divisions.value = response.data; 
    } catch (error) {
      handleApiError(error, 'loading divisions');
    }
  };
  
  function toggleDivisionSelection(divisionId) {
    const index = selectedDivisionIds.value.indexOf(divisionId);
    if (index === -1) {
      selectedDivisionIds.value.push(divisionId);
    } else {
      selectedDivisionIds.value.splice(index, 1);
    }
  }
  
  function getDivisionName(divisionId) {
    const division = divisions.value.find(d => d.divisionid === divisionId);
    return isDefinedAndNotEmpty(division) != false ? division.divisionname : 'Unknown';
  }
  
  function handleToggleChange(event) {
      const isChecked = event.target.checked;
      const newMode = isChecked !=false ? 'Flow' : 'Agentic';
      console.log(`Toggle changed. Checked: ${isChecked}, Requesting mode: ${newMode}`);
      requestModeChange(newMode, true);
  }
  
  function handleAgentModeSelection(selection) {
    if (selection !== undefined && selection !== null && typeof selection === 'object') {
      const modeId = typeof selection.modeId === 'string' ? selection.modeId : agentMode.value;
      const desiredAgenticBehaviour = selection.agenticBehaviour === false ? false : true;
      requestModeChange(modeId, desiredAgenticBehaviour);
      return;
    }
    const fallbackMode = typeof selection === 'string' ? selection : agentMode.value;
    const fallbackAgentic = fallbackMode === 'SuperFast' ? false : true;
    requestModeChange(fallbackMode, fallbackAgentic);
  }
  
  function applyAgentModeChange(mode, desiredAgenticBehaviour) {
    agentMode.value = mode;
    if (mode === 'Flow') {
      agenticBehaviour.value = true;
    } else if (mode === 'SuperFast') {
      agenticBehaviour.value = false;
    } else {
      agenticBehaviour.value = desiredAgenticBehaviour === false ? false : true;
    }
    pendingAgenticBehaviour.value = agenticBehaviour.value;
  }
  
  function requestModeChange(newMode, desiredAgenticBehaviour) {
    const nextAgenticValue = desiredAgenticBehaviour === false ? false : true;
    if (agentMode.value === newMode) {
      applyAgentModeChange(newMode, nextAgenticValue);
      return;
    }
    if ((agentMode.value === 'Flow' || agentMode.value === 'FlowBased') && newMode !== agentMode.value) {
      let flowExists = false;
      if (agentMode.value === 'Flow') {
        if (isDefinedAndNotEmpty(llmFlowRef.value) != false) {
            const currentFlow = llmFlowRef.value.getFlowData();
            flowExists = Array.isArray(currentFlow) != false && currentFlow.length > 1;
        } else if (isDefinedAndNotEmpty(loadedAgentData?.agentdata?.flow) != false) {
            flowExists = Array.isArray(loadedAgentData.agentdata.flow) != false && loadedAgentData.agentdata.flow.length > 1;
        }
      } else if (agentMode.value === 'FlowBased') {
        flowExists = Array.isArray(flowBasedFlow.value) != false && flowBasedFlow.value.length > 1;
      }
  
      if (flowExists === true) {
          targetAgentMode.value = newMode;
          pendingAgenticBehaviour.value = nextAgenticValue;
          isSwitchConfirmOpen.value = true;
      } else {
          applyAgentModeChange(newMode, nextAgenticValue);
      }
    } else {
      applyAgentModeChange(newMode, nextAgenticValue);
    }
  }
  
  function confirmSwitchMode() {
    if (targetAgentMode.value !== null) {
        const confirmedAgentic = pendingAgenticBehaviour.value === false ? false : true;
        applyAgentModeChange(targetAgentMode.value, confirmedAgentic);
    }
    cancelSwitchMode(); 
  }
  
  function cancelSwitchMode() {
    console.log("Switch cancelled.");
    isSwitchConfirmOpen.value = false;
    targetAgentMode.value = null;
    pendingAgenticBehaviour.value = agenticBehaviour.value;
  }
  
  watch(agentMode, (newMode, oldMode) => {
    console.log(`AIChatAgentSetup: Watcher triggered. Mode changed from ${oldMode} to ${newMode}. Edit mode: ${isEditMode.value}`);
    if (newMode === 'Flow') {
      
    } else {
      
    }
  });
  
  watch(isFastOrSuperFastMode, (isFastModeActive) => {
    if (isFastModeActive === true && verifyAnswerEnabled.value !== false) {
      verifyAnswerEnabled.value = false;
    }
  }, { immediate: true });

  watch(isVoiceOnlyChannelSelection, (voiceOnlyChannelsSelected) => {
    if (voiceOnlyChannelsSelected !== true) {
      return;
    }
    if (agentMode.value !== 'Agentic' && agentMode.value !== 'Flow') {
      applyAgentModeChange('Agentic', true);
    }
  });

  watch(showVoiceSettingsTab, (voiceChannelSelected, previousVoiceChannelSelected) => {
    if (voiceChannelSelected !== true || previousVoiceChannelSelected === true) {
      return;
    }
    if (isEditMode.value === false) {
      voiceSettingsDefaultEngineVersion.value = 'v3';
    }
  });
  
  watch([selectedLLMModel, agentMode], () => {
    syncAutoVerificationModel();
  });
  
  watch(verifyAnswerEnabled, () => {
    if (selectedLLMModel.value === autoLlmModelId) {
      syncAutoVerificationModel();
    }
  });
  
  watch(selectedAvatarProvider, value => {
    if (typeof value !== 'string' || value.length === 0) {
      selectedAvatarProvider.value = 'did';
      return;
    }
    if (avatarProviderConfigs.value[value] === undefined) {
      avatarProviderConfigs.value = {
        ...avatarProviderConfigs.value,
        [value]: defaultAvatarProviderConfig(value)
      };
    }
  });
  
  watch(selectedDivisionIds, async (newDivisionIds, oldDivisionIds) => {
    if (JSON.stringify(newDivisionIds) !== JSON.stringify(oldDivisionIds)) {
      if (Array.isArray(newDivisionIds) === true && newDivisionIds?.length > 0) {
        await fetchFilteredCollections(newDivisionIds);
      } else {
        availableCollectionsForAgent.value = [];
        selectedAnalysisCollectionIds.value = [];
      }
    }
  }, { deep: true });
  
  async function fetchFilteredCollections(divisionIds) {
    if (Array.isArray(divisionIds) === false || divisionIds.length === 0) {
      availableCollectionsForAgent.value = [];
      selectedAnalysisCollectionIds.value = [];
      return;
    }
    isLoadingCollections.value = true;
    const cfg = getSecuredAxiosConfig(null);
    if (cfg === null) {
      isLoadingCollections.value = false;
      return;
    }
  
    try {
      const params = { divisionIds: divisionIds.join(',') };
      const response = await axios.get(`${globalState.apiEndpoint}/api/tags/collections`, { ...cfg, params });
      const responseRows = Array.isArray(response.data) === true ? response.data : [];
      const normalizedCollections = [];
      for (let index = 0; index < responseRows.length; index = index + 1) {
        const normalizedCollection = normalizeAnalysisCollectionRow(responseRows[index]);
        if (normalizedCollection === null) {
          continue;
        }
        if (normalizedCollections.some((item) => String(item.collectionId) === String(normalizedCollection.collectionId)) === true) {
          continue;
        }
        normalizedCollections.push(normalizedCollection);
      }
      availableCollectionsForAgent.value = normalizedCollections;
      selectedAnalysisCollectionIds.value = normalizeAnalysisCollectionIds(selectedAnalysisCollectionIds.value).filter((id) =>
        normalizedCollections.some((collection) => String(collection.collectionId) === String(id))
      );
    } catch (error) {
      handleApiError(error, 'fetching filtered collections');
      availableCollectionsForAgent.value = [];
      selectedAnalysisCollectionIds.value = [];
    } finally {
      isLoadingCollections.value = false;
    }
  }
  
  function handleFineTuneSave(data) {
    console.log('Received fine-tune data:', data);
    if (data.originalQuery === undefined || data.originalResponse === undefined || data.fineTunedAnswer === undefined) {
        toast.error('Invalid fine-tuning data received.');
        return;
    }
  
    const fineTuneContent = `Original Question: ${data.originalQuery}
  Original Answer: ${data.originalResponse}
  Fine-tuned Answer: ${data.fineTunedAnswer}`;
    const fineTuneFileName = `fine-tune-${uuidv4()}.txt`; 
    
    const textBlob = new Blob([fineTuneContent], { type: 'text/plain' });
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Data = e.target.result.split(',')[1];
      
   
      const newFineTuneFile = {
        fileName: fineTuneFileName,
         fileType: 'fineTuneAnswer', 
         status: 'new', 
        content: fineTuneContent,
         fileData: base64Data, 
        originalQuery: data.originalQuery, 
        originalResponse: data.originalResponse,
        fineTunedAnswer: data.fineTunedAnswer,
        hiddenFromSources: true
      };
      
       existingFiles.value.push(newFineTuneFile); 
       knowledgeBaseFileAssignments.value[newFineTuneFile.fileName] = currentFolderId.value; 
   
      if (data.suppressToast !== true) {
        toast.dismiss(); 
        toast.success('Fine-tuned answer added and assigned to current folder.');
      }
    };
    reader.onerror = (error) => {
      console.error('Error converting fine-tune content to base64:', error);
      toast.error('Failed to process fine-tuned answer.');
    };
    reader.readAsDataURL(textBlob);
  }

  function handleLearnQuestionAnswers(entries) {
    const source = Array.isArray(entries) === true ? entries : [];
    for (let index = 0; index < source.length; index = index + 1) {
      const data = source[index];
      if (data === undefined || data === null) {
        continue;
      }
      const question = typeof data.originalQuery === 'string' ? data.originalQuery.trim() : '';
      const answer = typeof data.fineTunedAnswer === 'string' ? data.fineTunedAnswer.trim() : '';
      if (question.length === 0 || answer.length === 0) {
        continue;
      }
      const hiddenContext = typeof data.hiddenContext === 'string' ? data.hiddenContext.trim() : '';
      const companyKnowHow = typeof data.companyKnowHow === 'string' ? data.companyKnowHow.trim() : '';
      const questionAnswerContent = `Question: ${question}
Answer: ${answer}
Hidden Context: ${hiddenContext}
Company Know-How: ${companyKnowHow}
Force Exact Answer: No`;
      const questionAnswerFileName = `question-answer-${uuidv4()}.txt`;
      const textBlob = new Blob([questionAnswerContent], { type: 'text/plain' });
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event !== undefined && event !== null && event.target !== undefined && event.target !== null ? event.target.result : '';
        const base64Data = typeof result === 'string' && result.includes(',') === true ? result.split(',')[1] : '';
        existingFiles.value.push({
          fileName: questionAnswerFileName,
          fileType: 'questionAnswer',
          status: 'new',
          content: questionAnswerContent,
          fileData: base64Data,
          originalQuery: question,
          originalResponse: '',
          fineTunedAnswer: answer,
          hiddenContext,
          companyKnowHow,
          forceExactAnswer: false,
          processingStatus: 'Available',
          hiddenFromSources: true
        });
        knowledgeBaseFileAssignments.value[questionAnswerFileName] = currentFolderId.value;
      };
      reader.onerror = () => {
        toast.error('Failed to process question and answer.');
      };
      reader.readAsDataURL(textBlob);
    }
  }
  
  function openInspectModal(file) {
    let fileToInspect = existingFiles.value.find(f => f.fileName === file.fileName);
    if (fileToInspect === undefined) {
        fileToInspect = uploadedFiles.value.find(f => f.fileName === file.fileName);
    }
    
    if (fileToInspect === undefined) {
      console.error("Could not find file object for inspection:", file.fileName);
      toast.error("Could not load file details.");
      return;
    }
  
    const needsFetching = (fileToInspect.content === undefined || fileToInspect.content === null) && 
                          (fileToInspect.fileData === undefined || fileToInspect.fileData === null) && 
                          fileToInspect.status === 'existing';
  
    if (needsFetching === true) {
        const agentId = route.query.edit;
        if (agentId === undefined || agentId === null) {
            toast.error("Cannot fetch file content: Agent ID is missing.");
            return;
        }
        const cfg = getSecuredAxiosConfig({ responseType: 'blob' });
        if (cfg === null) {
          toast.error("Could not load file details.");
          return;
        }
        
        fileToInspect.isLoading = true;
        selectedFileToInspect.value = { ...fileToInspect };
        isInspectModalOpen.value = true;
        
        axios.get(`${globalState.apiEndpoint}/api/agents/${encodePathSegment(agentId)}/files/${encodeURIComponent(fileToInspect.fileName)}`, cfg).then(response => {
            const reader = new FileReader();
            reader.onload = (e) => {
                fileToInspect.fileData = e.target.result.split(',')[1];
                fileToInspect.isLoading = false;
                selectedFileToInspect.value = { ...fileToInspect };
            };
            reader.onerror = () => {
                toast.error("Failed to read file content.");
                fileToInspect.isLoading = false;
                selectedFileToInspect.value = { ...fileToInspect };
            }
            reader.readAsDataURL(response.data);
        }).catch(error => {
            handleApiError(error, `loading content for ${fileToInspect.fileName}`);
            fileToInspect.isLoading = false;
            selectedFileToInspect.value = { ...fileToInspect, error: 'Failed to load content.' };
            isInspectModalOpen.value = false;
            closeInspectModal();
        });
    } else {
      selectedFileToInspect.value = { ...fileToInspect };
      isInspectModalOpen.value = true;
    }
  }
  
  function closeInspectModal() {
    isInspectModalOpen.value = false;
    selectedFileToInspect.value = null;
  }
  
  function closeModal() {
      showLoadingModal.value = false;
      modalState.type = 'loading';
      modalState.title = 'Processing...';
      modalState.message = '';
      stopLoadingTips(); 
      isEditingTextFile.value = false; 
      originalTextFileToEdit.value = null;
  }

  async function copyTextToClipboard(textValue) {
    const value = typeof textValue === 'string' ? textValue.trim() : '';
    if (value.length === 0) {
      toast.error('Nothing to copy.');
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied to clipboard.');
    } catch (error) {
      toast.error('Copy failed.');
    }
  }

  function downloadHumanInTheLoopPostman() {
    const sendMessageUrl = humanInTheLoopSendMessageEndpoint.value;
    const inboundSecret = typeof humanInTheLoopInboundSecret.value === 'string' ? humanInTheLoopInboundSecret.value.trim() : '';
    if (sendMessageUrl.length === 0) {
      toast.error('API endpoint is missing.');
      return;
    }
    const collection = {
      info: {
        name: 'HumanInTheLoop',
        schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json'
      },
      item: [
        {
          name: 'Send Human Message',
          request: {
            method: 'POST',
            header: [
              { key: 'Content-Type', value: 'application/json' },
              { key: 'x-human-in-the-loop-secret', value: inboundSecret }
            ],
            body: {
              mode: 'raw',
              raw: JSON.stringify({
                agentId: '{{agentId}}',
                sessionId: '{{conversationId}}',
                message: 'Hello, this is a human agent.'
              }, null, 2)
            },
            url: sendMessageUrl
          }
        }
      ],
      variable: [
        { key: 'agentId', value: '' },
        { key: 'conversationId', value: '' }
      ]
    };
    const fileBlob = new Blob([JSON.stringify(collection, null, 2)], { type: 'application/json' });
    const objectUrl = window.URL.createObjectURL(fileBlob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = 'human-in-the-loop-postman.json';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(objectUrl);
    toast.success('Postman collection downloaded.');
  }
  
  function closeModalOnErrorState(event) {
  
      console.log('Backdrop click ignored.');
    
  }
   
  const getAuthToken = () => {
    if (globalState.authUsesHttpOnlyCookies === true) {
      return '';
    }
    const authToken = document.cookie.split('; ').find(row => row.startsWith('authToken=') === true)?.split('=')[1];
    const jwtToken = document.cookie.split('; ').find(row => row.startsWith('jwtToken=') === true)?.split('=')[1];
    const token = authToken !== undefined && authToken !== null && authToken !== '' ? authToken : jwtToken;
    if (token === undefined || token === null) {
      return '';
    }
    return token !== undefined && token !== null ? token : '';
  };
  
  const getAuthHeaders = () => {
    const token = getAuthToken();
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token !== '' && token !== undefined && token !== null) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };
  
  const getSecuredAxiosConfig = (extraConfig) => {
    const headers = getAuthHeaders();
    const cfg = extraConfig !== undefined && extraConfig !== null && typeof extraConfig === 'object' ? { ...extraConfig } : {};
    cfg.headers = headers;
    const authHeader = headers['Authorization'];
    const hasAuthHeader = typeof authHeader === 'string' && authHeader.trim().length > 0;
    if (hasAuthHeader === false) {
      cfg.withCredentials = true;
    }
    return cfg;
  };
  
  const encodePathSegment = (value) => {
    const raw = value !== undefined && value !== null ? String(value) : '';
    return encodeURIComponent(raw);
  };
  
  const createStreamRequestError = (statusCode, payload) => {
    const message = typeof payload?.message === 'string' && payload.message.length > 0 ? payload.message : (typeof payload?.error === 'string' && payload.error.length > 0 ? payload.error : 'An unexpected error occurred.');
    const error = new Error(message);
    error.response = {
      status: Number.isFinite(Number(statusCode)) === true ? Number(statusCode) : 500,
      data: payload !== undefined && payload !== null ? payload : { message: message }
    };
    return error;
  };
  
  const parseEventStreamBlock = (rawBlock) => {
    const normalizedBlock = typeof rawBlock === 'string' ? rawBlock.replace(/\r/g, '') : '';
    if (normalizedBlock.trim().length === 0) {
      return null;
    }
    const lines = normalizedBlock.split('\n');
    let eventName = 'message';
    const dataLines = [];
    for (let lineIndex = 0; lineIndex < lines.length; lineIndex = lineIndex + 1) {
      const line = lines[lineIndex];
      if (line.startsWith('event:') === true) {
        eventName = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:') === true) {
        dataLines.push(line.slice('data:'.length).trim());
      }
    }
    return {
      eventName: eventName,
      dataText: dataLines.join('\n')
    };
  };

  const openAuthenticatedEventStream = (url, handlers = {}) => {
    const controller = new AbortController();
    let closed = false;
    const streamHandle = {
      close: () => {
        closed = true;
        controller.abort();
      }
    };
    const dispatchEvent = (parsedEvent) => {
      if (closed === true) {
        return;
      }
      if (parsedEvent === null) {
        return;
      }
      if (parsedEvent === undefined) {
        return;
      }
      let payload = {};
      if (parsedEvent.dataText.length > 0) {
        try {
          payload = JSON.parse(parsedEvent.dataText);
        } catch (error) {
          payload = { message: parsedEvent.dataText };
        }
      }
      const handler = typeof handlers[parsedEvent.eventName] === 'function' ? handlers[parsedEvent.eventName] : null;
      if (handler !== null) {
        handler(payload);
      }
    };
    const run = async () => {
      try {
        const headers = {
          ...getAuthHeaders(),
          'Accept': 'text/event-stream'
        };
        const response = await fetch(url, {
          method: 'GET',
          headers: headers,
          credentials: 'include',
          signal: controller.signal
        });
        if (response.ok !== true) {
          throw createStreamRequestError(response.status, { message: 'Stream request failed.' });
        }
        if (response.body === undefined) {
          throw createStreamRequestError(response.status, { message: 'Stream response body is missing.' });
        }
        if (response.body === null) {
          throw createStreamRequestError(response.status, { message: 'Stream response body is missing.' });
        }
        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';
        while (closed !== true) {
          const readResult = await reader.read();
          const chunkValue = readResult.value;
          const done = readResult.done === true;
          if (chunkValue !== undefined && chunkValue !== null) {
            buffer += decoder.decode(chunkValue, { stream: done !== true });
          }
          let blockSeparatorIndex = buffer.indexOf('\n\n');
          while (blockSeparatorIndex >= 0) {
            const rawBlock = buffer.slice(0, blockSeparatorIndex);
            buffer = buffer.slice(blockSeparatorIndex + 2);
            dispatchEvent(parseEventStreamBlock(rawBlock));
            blockSeparatorIndex = buffer.indexOf('\n\n');
          }
          if (done === true) {
            const trailing = decoder.decode();
            if (trailing.length > 0) {
              buffer += trailing;
            }
            break;
          }
        }
        if (closed !== true && buffer.trim().length > 0) {
          dispatchEvent(parseEventStreamBlock(buffer));
        }
      } catch (error) {
        if (closed !== true && typeof handlers.error === 'function') {
          handlers.error(error);
        }
      }
    };
    run();
    return streamHandle;
  };
  
  const streamModifyAgentRequest = async (agentId, agentPayload) => {
    const headers = {
      ...getAuthHeaders(),
      'Accept': 'text/event-stream'
    };
    const response = await fetch(`${apiEndpoint}/modifyAIChat/${encodePathSegment(agentId)}`, {
      method: 'PUT',
      headers: headers,
      body: JSON.stringify(agentPayload),
      credentials: 'include'
    });
    const contentType = typeof response.headers.get === 'function' ? String(response.headers.get('content-type') || '') : '';
    if (contentType.includes('text/event-stream') !== true) {
      let payload = null;
      try {
        payload = await response.json();
      } catch (error) {
        const textPayload = await response.text();
        payload = { message: textPayload };
      }
      if (response.ok !== true) {
        throw createStreamRequestError(response.status, payload);
      }
      return { data: payload };
    }
    if (response.body === undefined || response.body === null) {
      throw createStreamRequestError(response.status, { message: 'Stream response body is missing.' });
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let completePayload = null;
    let failedPayload = null;
    const applyParsedEvent = (parsedEvent) => {
      if (parsedEvent === null || parsedEvent === undefined) {
        return;
      }
      let payload = {};
      if (parsedEvent.dataText.length > 0) {
        try {
          payload = JSON.parse(parsedEvent.dataText);
        } catch (error) {
          payload = { message: parsedEvent.dataText };
        }
      }
      if (parsedEvent.eventName === 'status') {
        if (typeof payload.message === 'string' && payload.message.length > 0) {
          modalState.message = payload.message;
        }
        return;
      }
      if (parsedEvent.eventName === 'failed') {
        failedPayload = payload;
        if (typeof payload.message === 'string' && payload.message.length > 0) {
          modalState.message = payload.message;
        }
        return;
      }
      if (parsedEvent.eventName === 'complete') {
        completePayload = payload;
        if (typeof payload.message === 'string' && payload.message.length > 0) {
          modalState.message = payload.message;
        }
      }
    };
    while (true) {
      const readResult = await reader.read();
      const chunkValue = readResult.value;
      const done = readResult.done === true;
      if (chunkValue !== undefined && chunkValue !== null) {
        buffer += decoder.decode(chunkValue, { stream: done !== true });
      }
      let blockSeparatorIndex = buffer.indexOf('\n\n');
      while (blockSeparatorIndex >= 0) {
        const rawBlock = buffer.slice(0, blockSeparatorIndex);
        buffer = buffer.slice(blockSeparatorIndex + 2);
        applyParsedEvent(parseEventStreamBlock(rawBlock));
        blockSeparatorIndex = buffer.indexOf('\n\n');
      }
      if (done === true) {
        const trailing = decoder.decode();
        if (trailing.length > 0) {
          buffer += trailing;
        }
        break;
      }
    }
    if (buffer.trim().length > 0) {
      applyParsedEvent(parseEventStreamBlock(buffer));
    }
    if (failedPayload !== null) {
      throw createStreamRequestError(failedPayload.statusCode, failedPayload);
    }
    if (completePayload === null || completePayload === undefined) {
      throw createStreamRequestError(response.status, { message: 'Save stream ended before completion.' });
    }
    if (completePayload.agent !== undefined && completePayload.agent !== null) {
      return { data: completePayload.agent };
    }
    return { data: completePayload };
  };
  
  const handleApiError = (error, context = 'operation') => {
      console.error(`Error during ${context}:`, error);
      const errorMessage = typeof error?.response?.data?.message === 'string' ? error.response.data.message : (typeof error?.response?.data?.error === 'string' ? error.response.data.error : '');
      const isExpired = errorMessage.toLowerCase().includes('jwt expired');
      if (error?.response?.status === 401 || error?.response?.status === 403 || isExpired === true) {
           toast.error('Authentication error. Redirecting to login.'); 
          router.push('/login');
      } else if (errorMessage !== '') {
          toast.error(`Error: ${String(errorMessage)}`);
      } else {
          toast.error(`An unexpected error occurred during ${context}.`);
      }
  };
   
   
  function openCreateFolderModal() {
    newFolderName.value = '';
    showCreateFolderModal.value = true;
  }
  
  function handleCreateFolder() {
    if (newFolderName.value.trim().length === 0) {
      toast.error('Folder name cannot be empty.');
      return;
    }
   
    const parentId = currentFolderId.value;
    const isDuplicate = knowledgeBaseFolders.value.some(
      folder => folder.parentId === parentId && folder.name.toLowerCase() === newFolderName.value.trim().toLowerCase()
    );
    if (isDuplicate === true) {
      toast.error(`A folder named \"${newFolderName.value.trim()}\" already exists here.`);
      return;
    }
  
    knowledgeBaseFolders.value.push({
      id: uuidv4(),
      name: newFolderName.value.trim(),
      parentId: parentId,
    });
    showCreateFolderModal.value = false;
    newFolderName.value = '';
    toast.success('Folder created.');
  }
  
  function navigateToFolder(folder) {
    currentKbPath.value.push({ id: folder.id, name: folder.name });
     searchQuery.value = ''; 
    currentKbPage.value = 1;
  }
  
  function navigateToBreadcrumb(index) {
    currentKbPath.value = currentKbPath.value.slice(0, index + 1);
     searchQuery.value = ''; 
    currentKbPage.value = 1;
  }
  
  function deleteFolder(folderIdToDelete) {
      const folderToDelete = knowledgeBaseFolders.value.find(f => f.id === folderIdToDelete);
      if (folderToDelete === undefined) return;
  
       
      const hasSubFolders = knowledgeBaseFolders.value.some(f => f.parentId === folderIdToDelete);
   
      const hasFiles = Object.values(knowledgeBaseFileAssignments.value).includes(folderIdToDelete);
  
      if (hasSubFolders === true || hasFiles === true) {
          toast.error(`Folder "${folderToDelete.name}" is not empty. Please remove its contents first.`);
          return;
      }
  
      if (confirm(`Are you sure you want to delete folder "${folderToDelete.name}"? This cannot be undone.`) === true) {
          knowledgeBaseFolders.value = knowledgeBaseFolders.value.filter(f => f.id !== folderIdToDelete);
          toast.success(`Folder "${folderToDelete.name}" deleted.`);
      }
  }
  
  function openMoveFileModal(file, type) {
     fileToMove.value = { ...file, type: type }; 
     targetFolderForMove.value = isDefinedAndNotEmpty(knowledgeBaseFileAssignments.value[file.fileName]) != false ? knowledgeBaseFileAssignments.value[file.fileName] : null; 
    showMoveFileModal.value = true;
  }
  
  function closeMoveFileModal() {
    showMoveFileModal.value = false;
    fileToMove.value = null;
  }

  function handleMoveFile() {
    if (fileToMove.value === null) return;
    const fileName = fileToMove.value.fileName;
    
    if (targetFolderForMove.value === 'root') { 
        knowledgeBaseFileAssignments.value[fileName] = null;
    } else {
        knowledgeBaseFileAssignments.value[fileName] = targetFolderForMove.value;
    }
    
    toast.success(`File "${fileName}" moved.`);
    closeMoveFileModal();
  }
  
  function estimateTokens(text) {
    if (text === null || text === undefined) {
      return 0;
    }
    return Math.ceil(text.length / 4);
  }
  
  function isCached(fileName) {
    return cachedFileNames.value.includes(fileName) === true;
  }
  
  function toggleCacheFile(file) {
    const fileName = file.fileName;
    const isCurrentlyCached = isCached(fileName);
  
    if (isCurrentlyCached === false) { 
      let fileContentToEstimate = null;
      if (file.status === 'new') {
        const uploadedFile = uploadedFiles.value.find(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === fileName);
        if (isDefinedAndNotEmpty(uploadedFile) != false && uploadedFile.fileType === 'text') {
          fileContentToEstimate = uploadedFile.content; 
        }
      } else if (file.status === 'existing') {
        const existingFile = existingFiles.value.find(f => f.fileName === fileName);
        if (existingFile !== undefined && existingFile.fileType === 'text') {
          if (existingFile.content === undefined || existingFile.content === null) {
              if (existingFile.fileData !== undefined && existingFile.fileData !== null) {
                   try {
                      existingFile.content = atob(existingFile.fileData);
                      fileContentToEstimate = existingFile.content;
                   } catch (error) {
                      console.error("Error decoding base64 for existing file content:", e);
                      toast.error("Could not read existing file content for token estimation.");
                      return;
                   }
              } else {
                  toast.error("Existing file content is not available for token estimation.");
                  return;
              }
          } else {
              fileContentToEstimate = existingFile.content;
          }
        }
      }
  
      if (fileContentToEstimate === null) {
        toast.error('Cannot cache: File content not available or not a TXT file.');
        return;
      }
  
      const tokens = estimateTokens(fileContentToEstimate);
      if (tokens > 10000) {
        toast.error(`Cannot cache "${fileName}": Exceeds 10,000 tokens (estimated: ${tokens}).`);
        return;
      } else {
          toast.info(`Estimated tokens for "${fileName}": ${tokens}`);
      }
      cachedFileNames.value.push(fileName);
    } else {
      const index = cachedFileNames.value.indexOf(fileName);
      if (index > -1) {
        cachedFileNames.value.splice(index, 1);
      }
    }
  }
  
  function truncateDisplayFileName(name, maxLength = 60) {
    const decodedName = decodeURIComponentSafe(name);
    if (decodedName === undefined || decodedName === null) return '';
  
    const gridMaxLength = kbViewMode.value === 'grid' ? 25 : maxLength;
     if (decodedName.length <= gridMaxLength) {
      return decodedName;
    }
    return decodedName.substring(0, gridMaxLength) + '...';
  }
  
  function handleFileDragStart(file, fileOriginalType, event) {
  
    const decodedFileName = decodeURIComponentSafe(file.fileName);
    draggedItem.value = { fileName: file.fileName, decodedFileName: decodedFileName, type: fileOriginalType, originalTargetId: knowledgeBaseFileAssignments.value[file.fileName] || null };
    if (event.dataTransfer !== null && event.dataTransfer !== undefined) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', decodedFileName); 
    }
    isDragOverFolder.value = null;
  }
  
  function handleDragEnd() {
  
    draggedItem.value = null;
    isDragOverFolder.value = null;
  }
  
  function handleFolderDragOver(event, folderId) {
  
   event.preventDefault();
   if (draggedItem.value !== null && draggedItem.value !== undefined) {
     if (draggedItem.value.originalTargetId !== folderId) {
         isDragOverFolder.value = folderId;
     }
   }
  }
  
  function handleFolderDragLeave(event, folderId) {
  
   if (isDragOverFolder.value === folderId) {
     isDragOverFolder.value = null;
   }
  }
  
  
  function handleFileDropOnFolder(targetFolderId) {
  
    if (draggedItem.value === null || draggedItem.value === undefined) {
      isDragOverFolder.value = null;
      return;
    }
  
    const currentAssignment = isDefinedAndNotEmpty(knowledgeBaseFileAssignments.value[draggedItem.value.fileName]) != false ? knowledgeBaseFileAssignments.value[draggedItem.value.fileName] : null;
    if (currentAssignment === targetFolderId) {
      toast.info('File is already in this folder.');
      isDragOverFolder.value = null;
      draggedItem.value = null;
      return;
    }
  
    knowledgeBaseFileAssignments.value[draggedItem.value.fileName] = targetFolderId;
   
    toast.success(`File \"${truncateDisplayFileName(draggedItem.value.decodedFileName, 30)}\" moved.`);
    isDragOverFolder.value = null;
    draggedItem.value = null;
  }
  
  
  
  function decodeURIComponentSafe(uri) {
    try {
      return decodeURIComponent(uri);
    } catch (error) {
      console.warn(`Failed to decode URI component: ${uri}`, error);
      return uri; 
    }
  }
  
  
  function startRenamingFolder(folder) {
    folderBeingRenamedId.value = folder.id;
    newFolderNameInput.value = folder.name;
    nextTick(() => {
      const inputElement = document.querySelector(`input[data-folder-rename-id="${folder.id}"]`);
      inputElement?.focus();
    });
  }
  
  function saveFolderName(folder) {
    const newName = newFolderNameInput.value.trim();
    folderBeingRenamedId.value = null; 
  
    if (newName.length === 0) {
      toast.error('Folder name cannot be empty.');
      return; 
    }
  
    if (newName === folder.name) {
      return; 
    }
  
    const isDuplicate = knowledgeBaseFolders.value.some(
      f => isDefinedAndNotEmpty(f.id) != false && f.id !== folder.id && f.parentId === folder.parentId && f.name.toLowerCase() === newName.toLowerCase()
    );
  
    if (isDuplicate === true) {
      toast.error(`Another folder named \"${newName}\" already exists here.`);
      return; 
    }
  
    
    const folderIndex = knowledgeBaseFolders.value.findIndex(f => f.id === folder.id);
    if (folderIndex !== -1) {
      knowledgeBaseFolders.value[folderIndex].name = newName;
      toast.success('Folder renamed successfully.');
    } else {
      toast.error('Error renaming folder: Folder not found.');
    }
  }
  
  function cancelRename() {
      folderBeingRenamedId.value = null;
      newFolderNameInput.value = '';
  }
  function getNextVersionFileName(originalName) {
    const baseNameMatch = originalName.match(/^(.*?)(?:\s*\(v(\d+)\))?\.txt$/i);
    let base = originalName.replace(/\.txt$/i, '');
    let nextVersionNumber = 1;
  
    if (baseNameMatch !== null) {
      base = baseNameMatch[1]; 
      if (baseNameMatch[2] !== undefined) { 
        nextVersionNumber = parseInt(baseNameMatch[2], 10) + 1;
      } else {
        nextVersionNumber = 2;
      }
    } else {
  
      nextVersionNumber = 2; 
    }
  
    let nextFileName = '';
   
    while (true) {
      nextFileName = `${base} (v${nextVersionNumber}).txt`;
      const existsInExisting = existingFiles.value.some(f => f.fileName === nextFileName && f.status !== 'deleted');
      const existsInUploaded = uploadedFiles.value.some(f => f.fileName === nextFileName);
      
      if (existsInExisting === false && existsInUploaded === false) {
        break;
      }
      nextVersionNumber++;
    }
    return nextFileName;
  }
  
  function openEditTextFileModal(file) {
    if (file.fileType !== 'text') return;
  
    originalTextFileToEdit.value = JSON.parse(JSON.stringify(file)); 
    isEditingTextFile.value = true;
  
    textFileName.value = getNextVersionFileName(file.fileName);
    
    let content = '';
    if (file.status === 'new') {
      const uploadedFile = uploadedFiles.value.find(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === file.fileName);
      if (isDefinedAndNotEmpty(uploadedFile) != false) {
        content = isDefinedAndNotEmpty(uploadedFile.content) != false ? uploadedFile.content : (isDefinedAndNotEmpty(uploadedFile.fileData) != false ? decodeBase64UTF8(uploadedFile.fileData) : '');
      }
    } else { 
      const existingFile = existingFiles.value.find(f => isDefinedAndNotEmpty(f.fileName) != false && f.fileName === file.fileName);
      if (isDefinedAndNotEmpty(existingFile) != false) {
          content = isDefinedAndNotEmpty(existingFile.content) != false ? existingFile.content : (isDefinedAndNotEmpty(existingFile.fileData) != false ? decodeBase64UTF8(existingFile.fileData) : '');
      }
    }
    textContent.value = content;
    textRegexSchemas.value = getFileVoiceScriptInjectionRegexSchemas(file);
    textRegexSchemaInput.value = '';
    showTextAdvancedOptions.value = textRegexSchemas.value.length > 0;
    showTextInputModal.value = true;
  }
  
  function openInspectOrEditTextFile(file) {
    if (file.fileType === 'websiteurl' || file.fileType === 'website') {
      if (file.status === 'existing') {
        const raw = isDefinedAndNotEmpty(file.url) != false ? file.url : file.fileName;
        const safe = sanitizeWindowOpenUrl(raw);
        if (safe !== null) {
          window.open(safe, '_blank', 'noopener,noreferrer');
        } else {
          toast.error(t('aiChatAgentSetup.errors.invalidUrl', 'Invalid URL'));
        }
      } else {
        toast.info('Save the agent to make this link clickable.');
      }
      return;
    }
  
    if (file.fileType !== 'text') {
      openInspectModal(file);
      return;
    }
  
    const openEditor = (fileWithContent) => {
      originalTextFileToEdit.value = JSON.parse(JSON.stringify(fileWithContent)); 
      isEditingTextFile.value = true;
      textFileName.value = getNextVersionFileName(fileWithContent.fileName);
      textContent.value = isDefinedAndNotEmpty(fileWithContent.content) != false ? fileWithContent.content : '';
      textRegexSchemas.value = getFileVoiceScriptInjectionRegexSchemas(fileWithContent);
      textRegexSchemaInput.value = '';
      showTextAdvancedOptions.value = textRegexSchemas.value.length > 0;
      showTextInputModal.value = true;
    };
  
    if (file.status === 'new') {
      openEditor(file);
    } else if (file.status === 'existing') {
      if (file.content !== undefined && file.content !== null) {
        openEditor(file);
      } else {
        const agentId = route.query.edit;
        if (agentId === undefined || agentId === null) {
          toast.error("Cannot fetch file content: Agent ID is missing.");
          return;
        }
        const cfg = getSecuredAxiosConfig({ responseType: 'blob' });
        if (cfg === null) {
          return;
        }
        toast.info("Loading file content...");
        axios.get(`${globalState.apiEndpoint}/api/agents/${encodePathSegment(agentId)}/files/${encodeURIComponent(file.fileName)}`, cfg).then(response => {
          toast.dismiss();
          const blob = response.data;
          blob.text().then(text => {
            file.content = text; 
            openEditor(file);
          });
        }).catch(error => {
          toast.dismiss();
          handleApiError(error, `loading content for ${file.fileName}`);
        });
      }
    }
  }
  
  const isRtl = computed(() => currentLanguage.value === 'he' || currentLanguage.value === 'ar');
  
  watch(agentType, (newAgentType) => {
    if (isEditMode.value === false) {
      enableFileUploadInChat.value = newAgentType === 'PrivateAgent';
    }
  });
  
  const itemsPerPageKbOptions = [10, 20, 50, 100];
  const itemsPerPageKb = ref(10);
  const bulkRescrapeIntervalDays = ref(1);
  const bulkRescrapeAtTime = ref('00:00');
  const selectedRowKeys = ref(new Set());
  const websiteSourceFileTypes = ['website','websiteurl','websitepage'];
  const hasSelectedRows = computed(() => selectedRowKeys.value.size > 0);
  const immediateRescrapeRequested = ref(false);
  const immediateRescrapeQueue = ref([]);
  const currentPageRowKeys = computed(() => {
    const keys = [];
    for (let i = 0; i < paginatedCombinedKbItems.value.length; i = i + 1) {
      const entry = paginatedCombinedKbItems.value[i];
      if (entry.type === 'existing' || entry.type === 'new') {
        keys.push(entry.item.fileName);
      }
    }
    return keys;
  });
  const isAllSelectedOnPage = computed(() => {
    const keys = currentPageRowKeys.value;
    if (keys.length === 0) return false;
    for (let i = 0; i < keys.length; i = i + 1) {
      if (selectedRowKeys.value.has(keys[i]) === false) return false;
    }
    return true;
  });
  function isRowSelected(fileName) {
    return selectedRowKeys.value.has(fileName) === true;
  }
  function toggleSelectRow(file) {
    const key = file.fileName;
    const next = new Set(selectedRowKeys.value);
    if (next.has(key) === true) {
      next.delete(key);
    } else {
      next.add(key);
    }
    selectedRowKeys.value = next;
  }
  function toggleSelectAllOnPage(event) {
    const shouldSelect = event.target.checked === true;
    const keys = currentPageRowKeys.value;
    const next = new Set(selectedRowKeys.value);
    for (let i = 0; i < keys.length; i = i + 1) {
      const key = keys[i];
      if (shouldSelect === true) {
        next.add(key);
      } else {
        next.delete(key);
      }
    }
    selectedRowKeys.value = next;
  }
  function selectAllFilteredRows() {
    const keys = [
      ...filteredExistingFiles.value.map(i => i.fileName),
      ...filteredUploadedFiles.value.map(i => i.fileName)
    ];
    selectedRowKeys.value = new Set(keys);
  }
  function clearSelectedRows() {
    selectedRowKeys.value = new Set();
  }
  async function deleteSelectedRows() {
    if (selectedRowKeys.value.size === 0) {
      toast.error('Please select at least one item.');
      return;
    }
    const toDelete = Array.from(selectedRowKeys.value);
    const existingFileNames = [];
    for (let i = 0; i < toDelete.length; i = i + 1) {
      const name = toDelete[i];
      const idxExisting = existingFiles.value.findIndex(ef => ef.fileName === name);
      if (idxExisting > -1) {
        existingFileNames.push(name);
        continue;
      }
      const idxUploaded = uploadedFiles.value.findIndex(uf => uf.fileName === name);
      if (idxUploaded > -1) {
        uploadedFiles.value.splice(idxUploaded, 1);
        delete knowledgeBaseFileAssignments.value[name];
      }
    }
    if (existingFileNames.length === 0) {
      selectedRowKeys.value = new Set();
      toast.success('Selected unsaved files deleted.');
      return;
    }
    const agentId = typeof route.query.edit === 'string' ? route.query.edit.trim() : '';
    if (agentId.length === 0) {
      toast.error('Save the agent before deleting stored knowledge files.');
      selectedRowKeys.value = new Set(existingFileNames);
      return;
    }
    const config = getSecuredAxiosConfig();
    if (config === null) {
      toast.error('Could not delete selected knowledge files.');
      selectedRowKeys.value = new Set(existingFileNames);
      return;
    }
    try {
      const response = await axios.post(`${globalState.apiEndpoint}/api/agents/${encodePathSegment(agentId)}/knowledgeBase/deleteSelected`, {
        fileNames: existingFileNames
      }, config);
      const deletedFileNames = Array.isArray(response.data.deletedFileNames) === true ? response.data.deletedFileNames : [];
      const deletedFileNameSet = new Set(deletedFileNames);
      existingFiles.value = existingFiles.value.filter(file => deletedFileNameSet.has(file.fileName) === false);
      for (const fileName of deletedFileNames) {
        delete knowledgeBaseFileAssignments.value[fileName];
      }
      const failedFiles = Array.isArray(response.data.failedFiles) === true ? response.data.failedFiles : [];
      const remainingSelections = new Set();
      for (const fileName of existingFileNames) {
        if (deletedFileNameSet.has(fileName) === false) {
          remainingSelections.add(fileName);
        }
      }
      selectedRowKeys.value = remainingSelections;
      if (failedFiles.length > 0) {
        toast.error(`${failedFiles.length} selected knowledge file(s) could not be deleted.`);
        return;
      }
      toast.success(`${deletedFileNames.length} selected knowledge file(s) deleted from storage and the vector database.`);
    } catch (error) {
      selectedRowKeys.value = new Set(existingFileNames);
      handleApiError(error, 'deleting selected knowledge files');
    }
  }

  function getKnowledgeBaseCsvType(file) {
    const fileType = file !== undefined && file !== null && typeof file.fileType === 'string' ? file.fileType : '';
    if (fileType === 'websiteurl') {
      return 'Website URL';
    }
    if (fileType === 'website') {
      return 'Website Domain';
    }
    if (fileType === 'websitepage') {
      return 'Website Page';
    }
    if (fileType !== '') {
      return fileType.toUpperCase();
    }
    return 'File';
  }

  function getKnowledgeBaseCsvValue(file) {
    const fileType = file !== undefined && file !== null && typeof file.fileType === 'string' ? file.fileType : '';
    if (fileType !== 'websiteurl' && fileType !== 'website' && fileType !== 'websitepage') {
      return '';
    }
    const sourceValue = file.url !== undefined && file.url !== null && String(file.url).trim() !== '' ? file.url : file.fileName;
    return decodeURIComponentSafe(sourceValue);
  }

  function shouldExportKnowledgeBaseFile(file) {
    if (file === undefined) {
      return false;
    }
    if (file === null) {
      return false;
    }
    if (typeof file !== 'object') {
      return false;
    }
    if (typeof file.fileName !== 'string') {
      return false;
    }
    if (file.fileName.trim() === '') {
      return false;
    }
    if (file.status === 'deleted') {
      return false;
    }
    if (file.fileType === 'fineTuneAnswer') {
      return false;
    }
    if (file.fileType === 'questionAnswer') {
      return false;
    }
    return true;
  }

  function collectKnowledgeBaseCsvRows() {
    const rows = [];
    const sharedItems = Array.isArray(linkedSharedKbItems.value) === true ? linkedSharedKbItems.value : [];
    for (let i = 0; i < sharedItems.length; i = i + 1) {
      const item = sharedItems[i];
      rows.push({
        name: item !== undefined && item !== null && typeof item.name === 'string' ? item.name : '',
        type: 'Shared KB',
        value: ''
      });
    }
    const agentItems = Array.isArray(linkedAgentKbItems.value) === true ? linkedAgentKbItems.value : [];
    for (let i = 0; i < agentItems.length; i = i + 1) {
      const item = agentItems[i];
      rows.push({
        name: item !== undefined && item !== null && typeof item.agentName === 'string' ? item.agentName : '',
        type: 'Agent KB',
        value: ''
      });
    }
    const fileGroups = [existingFiles.value, uploadedFiles.value];
    for (let groupIndex = 0; groupIndex < fileGroups.length; groupIndex = groupIndex + 1) {
      const files = Array.isArray(fileGroups[groupIndex]) === true ? fileGroups[groupIndex] : [];
      for (let fileIndex = 0; fileIndex < files.length; fileIndex = fileIndex + 1) {
        const file = files[fileIndex];
        if (shouldExportKnowledgeBaseFile(file) === true) {
          rows.push({
            name: decodeURIComponentSafe(file.fileName),
            type: getKnowledgeBaseCsvType(file),
            value: getKnowledgeBaseCsvValue(file)
          });
        }
      }
    }
    return rows;
  }

  function escapeKnowledgeBaseCsvCell(value) {
    const text = value !== undefined && value !== null ? String(value) : '';
    let needsQuotes = false;
    if (text.includes('"') === true) {
      needsQuotes = true;
    }
    if (text.includes(',') === true) {
      needsQuotes = true;
    }
    if (text.includes('\n') === true) {
      needsQuotes = true;
    }
    if (text.includes('\r') === true) {
      needsQuotes = true;
    }
    const escapedText = text.replace(/"/g, '""');
    if (needsQuotes === true) {
      return '"' + escapedText + '"';
    }
    return escapedText;
  }

  function buildKnowledgeBaseCsvContent(rows) {
    const csvRows = [{ name: 'Name', type: 'Type', value: 'Value' }];
    for (let i = 0; i < rows.length; i = i + 1) {
      csvRows.push(rows[i]);
    }
    const lines = [];
    for (let i = 0; i < csvRows.length; i = i + 1) {
      const row = csvRows[i];
      lines.push(escapeKnowledgeBaseCsvCell(row.name) + ',' + escapeKnowledgeBaseCsvCell(row.type) + ',' + escapeKnowledgeBaseCsvCell(row.value));
    }
    return String.fromCharCode(65279) + lines.join('\r\n');
  }

  function downloadKnowledgeBaseCsv() {
    const rows = collectKnowledgeBaseCsvRows();
    if (rows.length === 0) {
      toast.info('No knowledge base items to export.');
      return;
    }
    const csvContent = buildKnowledgeBaseCsvContent(rows);
    const objectUrl = window.URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    const datePart = new Date().toISOString().slice(0, 10);
    link.href = objectUrl;
    link.setAttribute('download', 'knowledge-base-' + datePart + '.csv');
    document.body.appendChild(link);
    link.click();
    if (link.parentNode !== null) {
      link.parentNode.removeChild(link);
    }
    window.URL.revokeObjectURL(objectUrl);
    toast.success('Knowledge base CSV downloaded.');
  }

  function getKnowledgeBaseExportText(value) {
    if (value === undefined || value === null) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    return String(value);
  }

  function getKnowledgeBaseExportFolderName(fileName) {
    const folderId = knowledgeBaseFileAssignments.value[fileName];
    if (folderId === undefined || folderId === null) {
      return '';
    }
    const folder = knowledgeBaseFolders.value.find(item => item.id === folderId);
    if (folder === undefined || folder === null) {
      return '';
    }
    return getKnowledgeBaseExportText(folder.name);
  }

  function getKnowledgeBaseExportStatus(file, source) {
    if (file.status === 'deleted') {
      return 'To Delete';
    }
    if (source === 'new') {
      if (file.isEditedVersion === true) {
        return 'Waiting To Update';
      }
      return 'New';
    }
    const processingStatus = getKnowledgeBaseExportText(file.processingStatus);
    if (processingStatus.length > 0) {
      return processingStatus;
    }
    return 'Available';
  }

  function buildKnowledgeBaseFileExportRow(file, source) {
    const url = getKnowledgeBaseCsvValue(file);
    const content = getKnowledgeBaseExportText(file.content);
    const question = getKnowledgeBaseExportText(file.originalQuery);
    const originalAnswer = getKnowledgeBaseExportText(file.originalResponse);
    const answer = getKnowledgeBaseExportText(file.fineTunedAnswer);
    return {
      Name: decodeURIComponentSafe(file.fileName),
      Type: getKnowledgeBaseCsvType(file),
      Status: getKnowledgeBaseExportStatus(file, source),
      URL: url,
      Question: question,
      Answer: answer,
      'Original Answer': originalAnswer,
      Content: content,
      Folder: getKnowledgeBaseExportFolderName(file.fileName),
      'Hide Source': file.hiddenFromSources === true ? 'Yes' : 'No',
      Cached: isCached(file.fileName) !== false ? 'Yes' : 'No',
      'Rescrape Enabled': file.rescrapeEnabled === true ? 'Yes' : 'No',
      'Rescrape Interval Days': file.rescrapeEnabled === true ? getKnowledgeBaseExportText(file.rescrapeIntervalDays) : '',
      'Rescrape Time': file.rescrapeEnabled === true ? getKnowledgeBaseExportText(file.rescrapeAtTime) : '',
      'Force Exact Answer': file.forceExactAnswer === true ? 'Yes' : 'No'
    };
  }

  function buildKnowledgeBaseLinkedExportRow(name, type, status) {
    return {
      Name: name,
      Type: type,
      Status: status,
      URL: '',
      Question: '',
      Answer: '',
      'Original Answer': '',
      Content: '',
      Folder: '',
      'Hide Source': '',
      Cached: '',
      'Rescrape Enabled': '',
      'Rescrape Interval Days': '',
      'Rescrape Time': '',
      'Force Exact Answer': ''
    };
  }

  function collectKnowledgeBaseXlsxRows() {
    const rows = [];
    const selectedOnly = selectedRowKeys.value.size > 0;
    if (selectedOnly === false) {
      const sharedItems = Array.isArray(linkedSharedKbItems.value) === true ? linkedSharedKbItems.value : [];
      for (let index = 0; index < sharedItems.length; index = index + 1) {
        const item = sharedItems[index];
        const name = item !== undefined && item !== null ? getKnowledgeBaseExportText(item.name) : '';
        rows.push(buildKnowledgeBaseLinkedExportRow(name, 'Shared KB', 'Linked'));
      }
      const agentItems = Array.isArray(linkedAgentKbItems.value) === true ? linkedAgentKbItems.value : [];
      for (let index = 0; index < agentItems.length; index = index + 1) {
        const item = agentItems[index];
        const name = item !== undefined && item !== null ? getKnowledgeBaseExportText(item.agentName) : '';
        rows.push(buildKnowledgeBaseLinkedExportRow(name, 'Agent KB', 'Linked'));
      }
    }
    const fileGroups = [
      { files: existingFiles.value, source: 'existing' },
      { files: uploadedFiles.value, source: 'new' }
    ];
    for (let groupIndex = 0; groupIndex < fileGroups.length; groupIndex = groupIndex + 1) {
      const group = fileGroups[groupIndex];
      const files = Array.isArray(group.files) === true ? group.files : [];
      for (let fileIndex = 0; fileIndex < files.length; fileIndex = fileIndex + 1) {
        const file = files[fileIndex];
        if (file === undefined || file === null || typeof file.fileName !== 'string' || file.fileName.trim().length === 0) {
          continue;
        }
        if (file.status === 'deleted') {
          continue;
        }
        if (selectedOnly === true && selectedRowKeys.value.has(file.fileName) === false) {
          continue;
        }
        rows.push(buildKnowledgeBaseFileExportRow(file, group.source));
      }
    }
    return { rows: rows, selectedOnly: selectedOnly };
  }

  function downloadKnowledgeBaseXlsx() {
    const exportData = collectKnowledgeBaseXlsxRows();
    if (exportData.rows.length === 0) {
      toast.info('No knowledge base items to export.');
      return;
    }
    const worksheet = XLSX.utils.json_to_sheet(exportData.rows);
    worksheet['!cols'] = [
      { wch: 38 },
      { wch: 18 },
      { wch: 18 },
      { wch: 55 },
      { wch: 55 },
      { wch: 55 },
      { wch: 55 },
      { wch: 70 },
      { wch: 24 },
      { wch: 14 },
      { wch: 12 },
      { wch: 20 },
      { wch: 24 },
      { wch: 18 },
      { wch: 22 }
    ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Knowledge Base');
    const datePart = new Date().toISOString().slice(0, 10);
    const scopePart = exportData.selectedOnly === true ? 'selected' : 'all';
    XLSX.writeFile(workbook, 'knowledge-base-' + scopePart + '-' + datePart + '.xlsx');
    toast.success(exportData.selectedOnly === true ? 'Selected knowledge items exported to XLSX.' : 'All knowledge items exported to XLSX.');
  }
  
  function enableRescrapeAllWebsiteUrls() {
    const interval = Number(bulkRescrapeIntervalDays.value);
    const timeVal = typeof bulkRescrapeAtTime.value === 'string' ? bulkRescrapeAtTime.value : '';
    let updated = 0;
    const types = ['website','websiteurl','websitepage'];
    for (let i = 0; i < filteredExistingFiles.value.length; i = i + 1) {
      const file = filteredExistingFiles.value[i];
      const isWeb = types.includes(file.fileType) === true;
      if (isWeb === true && file.status !== 'deleted') {
        file.rescrapeEnabled = true;
        file.rescrapeIntervalDays = interval;
        file.rescrapeAtTime = timeVal !== '' ? timeVal : '00:00';
        updated = updated + 1;
      }
    }
    for (let j = 0; j < filteredUploadedFiles.value.length; j = j + 1) {
      const file = filteredUploadedFiles.value[j];
      const isWeb = types.includes(file.fileType) === true;
      if (isWeb === true && file.status !== 'deleted') {
        file.rescrapeEnabled = true;
        file.rescrapeIntervalDays = interval;
        file.rescrapeAtTime = timeVal !== '' ? timeVal : '00:00';
        updated = updated + 1;
      }
    }
    if (updated > 0) {
      toast.success(updated + ' website source(s) rescrape info updated. Changes will start on save.');
    } else {
      toast.info('No website sources found to update.');
    }
  }
  
  function enableRescrapeForSelected() {
    if (selectedRowKeys.value.size === 0) {
      toast.error('Please select at least one item.');
      return;
    }
    const interval = Number(bulkRescrapeIntervalDays.value);
    const timeVal = typeof bulkRescrapeAtTime.value === 'string' ? bulkRescrapeAtTime.value : '';
    const types = ['website','websiteurl','websitepage'];
    let updated = 0;
    const keys = Array.from(selectedRowKeys.value);
    for (let i = 0; i < keys.length; i = i + 1) {
      const name = keys[i];
      const existingIdx = existingFiles.value.findIndex(f => f.fileName === name);
      if (existingIdx > -1) {
        const f = existingFiles.value[existingIdx];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          f.rescrapeEnabled = true;
          f.rescrapeIntervalDays = interval;
          f.rescrapeAtTime = timeVal !== '' ? timeVal : '00:00';
          updated = updated + 1;
        }
        continue;
      }
      const uploadedIdx = uploadedFiles.value.findIndex(f => f.fileName === name);
      if (uploadedIdx > -1) {
        const f = uploadedFiles.value[uploadedIdx];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          f.rescrapeEnabled = true;
          f.rescrapeIntervalDays = interval;
          f.rescrapeAtTime = timeVal !== '' ? timeVal : '00:00';
          updated = updated + 1;
        }
      }
    }
    if (updated > 0) {
      toast.success(updated + ' website source(s) rescrape info updated. Changes will start on save.');
    } else {
      toast.info('No selected website sources found to update.');
    }
  }
  
  function disableRescrapeForSelected() {
    if (selectedRowKeys.value.size === 0) {
      toast.error('Please select at least one item.');
      return;
    }
    const types = ['website','websiteurl','websitepage'];
    let updated = 0;
    const keys = Array.from(selectedRowKeys.value);
    for (let i = 0; i < keys.length; i = i + 1) {
      const name = keys[i];
      const existingIdx = existingFiles.value.findIndex(f => f.fileName === name);
      if (existingIdx > -1) {
        const f = existingFiles.value[existingIdx];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          f.rescrapeEnabled = false;
          f.rescrapeIntervalDays = '';
          f.rescrapeAtTime = '';
          updated = updated + 1;
        }
        continue;
      }
      const uploadedIdx = uploadedFiles.value.findIndex(f => f.fileName === name);
      if (uploadedIdx > -1) {
        const f = uploadedFiles.value[uploadedIdx];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          f.rescrapeEnabled = false;
          f.rescrapeIntervalDays = '';
          f.rescrapeAtTime = '';
          updated = updated + 1;
        }
      }
    }
    if (updated > 0) {
      toast.success(updated + ' website source(s) rescrape disabled. Changes will start on save.');
    } else {
      toast.info('No selected website sources found to update.');
    }
  }

  function getWebsiteSourceUrl(file) {
    if (file === undefined || file === null) return '';
    if (typeof file.url === 'string' && file.url.length > 0) {
      return file.url;
    }
    if (typeof file.fileName === 'string') {
      return file.fileName;
    }
    return '';
  }

  function getSelectedWebsiteFileEntries() {
    const entries = [];
    const keys = Array.from(selectedRowKeys.value);
    for (let i = 0; i < keys.length; i = i + 1) {
      const name = keys[i];
      const existingIdx = existingFiles.value.findIndex(f => f.fileName === name);
      if (existingIdx > -1) {
        const existingFile = existingFiles.value[existingIdx];
        if (websiteSourceFileTypes.includes(existingFile.fileType) === true && existingFile.status !== 'deleted') {
          entries.push({ file: existingFile, source: 'existing', index: existingIdx });
        }
        continue;
      }
      const uploadedIdx = uploadedFiles.value.findIndex(f => f.fileName === name);
      if (uploadedIdx > -1) {
        const uploadedFile = uploadedFiles.value[uploadedIdx];
        if (websiteSourceFileTypes.includes(uploadedFile.fileType) === true && uploadedFile.status !== 'deleted') {
          entries.push({ file: uploadedFile, source: 'uploaded', index: uploadedIdx });
        }
      }
    }
    return entries;
  }

  function normalizeWebsiteDomainInput(value) {
    const raw = typeof value === 'string' ? value.trim() : '';
    if (raw === '') return null;
    let hasProtocol = false;
    if (raw.startsWith('http://') === true) hasProtocol = true;
    if (raw.startsWith('https://') === true) hasProtocol = true;
    const normalized = hasProtocol === true ? raw : 'https://' + raw;
    try {
      const parsed = new URL(normalized);
      if (parsed.hostname.trim() === '') return null;
      return { protocol: parsed.protocol, hostname: parsed.hostname, port: parsed.port };
    } catch (error) {
      return null;
    }
  }

  function buildWebsiteUrlWithDomain(sourceUrl, domainInfo) {
    const raw = typeof sourceUrl === 'string' ? sourceUrl.trim() : '';
    if (raw === '') return '';
    let hasProtocol = false;
    if (raw.startsWith('http://') === true) hasProtocol = true;
    if (raw.startsWith('https://') === true) hasProtocol = true;
    const normalized = hasProtocol === true ? raw : 'https://' + raw;
    try {
      const parsed = new URL(normalized);
      parsed.protocol = domainInfo.protocol;
      parsed.hostname = domainInfo.hostname;
      parsed.port = domainInfo.port;
      let nextUrl = parsed.toString();
      let hasPathValue = false;
      if (parsed.pathname !== '/') hasPathValue = true;
      if (parsed.search !== '') hasPathValue = true;
      if (parsed.hash !== '') hasPathValue = true;
      if (hasPathValue === false && raw.endsWith('/') === false && nextUrl.endsWith('/') === true) {
        nextUrl = nextUrl.slice(0, nextUrl.length - 1);
      }
      return nextUrl;
    } catch (error) {
      return '';
    }
  }

  function getActiveKnowledgeFileNameSet(excludedNames) {
    const names = new Set();
    for (let i = 0; i < existingFiles.value.length; i = i + 1) {
      const file = existingFiles.value[i];
      if (file !== undefined && file !== null && file.status !== 'deleted' && typeof file.fileName === 'string' && excludedNames.has(file.fileName) === false) {
        names.add(file.fileName);
      }
    }
    for (let i = 0; i < uploadedFiles.value.length; i = i + 1) {
      const file = uploadedFiles.value[i];
      if (file !== undefined && file !== null && file.status !== 'deleted' && typeof file.fileName === 'string' && excludedNames.has(file.fileName) === false) {
        names.add(file.fileName);
      }
    }
    return names;
  }

  function rekeyKnowledgeBaseFileAssignment(oldName, newName) {
    if (oldName === newName) return;
    const assignments = knowledgeBaseFileAssignments.value;
    if (assignments !== undefined && assignments !== null && Object.prototype.hasOwnProperty.call(assignments, oldName) === true) {
      assignments[newName] = assignments[oldName];
      delete assignments[oldName];
    }
  }

  function openChangeSelectedWebsiteDomainModal() {
    if (selectedRowKeys.value.size === 0) {
      toast.error('Please select at least one item.');
      return;
    }
    const entries = getSelectedWebsiteFileEntries();
    if (entries.length === 0) {
      toast.info('No selected website sources found to update.');
      return;
    }
    selectedWebsiteNewDomain.value = '';
    showChangeSelectedWebsiteDomainModal.value = true;
    isAdvancedOpen.value = false;
  }

  function closeChangeSelectedWebsiteDomainModal() {
    showChangeSelectedWebsiteDomainModal.value = false;
    selectedWebsiteNewDomain.value = '';
  }

  async function changeSelectedWebsiteDomainsAndRescan() {
    const domainInfo = normalizeWebsiteDomainInput(selectedWebsiteNewDomain.value);
    if (domainInfo === null) {
      toast.error('Enter a valid domain.');
      return;
    }
    const entries = getSelectedWebsiteFileEntries();
    if (entries.length === 0) {
      toast.info('No selected website sources found to update.');
      return;
    }
    const excludedNames = new Set();
    for (let i = 0; i < entries.length; i = i + 1) {
      const fileName = typeof entries[i].file.fileName === 'string' ? entries[i].file.fileName : '';
      if (fileName !== '') {
        excludedNames.add(fileName);
      }
    }
    const activeNames = getActiveKnowledgeFileNameSet(excludedNames);
    const pendingNames = new Set();
    const changes = [];
    let skipped = 0;
    let hasConflict = false;
    for (let i = 0; i < entries.length; i = i + 1) {
      const file = entries[i].file;
      const oldName = typeof file.fileName === 'string' ? file.fileName : '';
      const sourceUrl = getWebsiteSourceUrl(file);
      const newUrl = buildWebsiteUrlWithDomain(sourceUrl, domainInfo);
      if (newUrl === '') {
        skipped = skipped + 1;
        continue;
      }
      if (newUrl === oldName && sourceUrl === newUrl) {
        skipped = skipped + 1;
        continue;
      }
      if (activeNames.has(newUrl) === true) {
        hasConflict = true;
      }
      if (pendingNames.has(newUrl) === true) {
        hasConflict = true;
      }
      pendingNames.add(newUrl);
      const entryType = file.fileType === 'websitepage' ? 'websiteurl' : file.fileType;
      changes.push({ entry: entries[i], oldName: oldName, newUrl: newUrl, type: entryType, maxPages: Number.isFinite(Number(file.maxPages)) === true ? Number(file.maxPages) : 0 });
    }
    if (hasConflict === true) {
      toast.error('One or more selected website URLs would duplicate an existing knowledge source.');
      return;
    }
    if (changes.length === 0) {
      toast.info('No selected website sources changed.');
      return;
    }
    const nextSelectedKeys = new Set(selectedRowKeys.value);
    for (let i = 0; i < changes.length; i = i + 1) {
      const change = changes[i];
      const file = change.entry.file;
      if (change.entry.source === 'existing') {
        const newFile = {
          ...file,
          fileName: change.newUrl,
          fileType: change.type,
          status: 'new',
          url: change.newUrl,
          processingStatus: 'Processing',
          processingDetails: null,
          lastRescrapedAt: undefined
        };
        file.status = 'deleted';
        uploadedFiles.value.push(newFile);
      } else {
        file.fileName = change.newUrl;
        file.fileType = change.type;
        file.url = change.newUrl;
      }
      rekeyKnowledgeBaseFileAssignment(change.oldName, change.newUrl);
      nextSelectedKeys.delete(change.oldName);
      nextSelectedKeys.add(change.newUrl);
    }
    selectedRowKeys.value = nextSelectedKeys;
    closeChangeSelectedWebsiteDomainModal();
    currentKbPage.value = 1;
    toast.info('Saving changed website domains and starting rescrape.');
    await createAgent(false);
    if (skipped > 0) {
      toast.info(skipped + ' selected website source(s) skipped.');
    }
  }
  
  async function rescanAllWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Set();
      let sent = 0;
      for (let i = 0; i < existingFiles.value.length; i = i + 1) {
        const f = existingFiles.value[i];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
          if (unique.has(rootUrl) === false) {
            unique.add(rootUrl);
            const body = { rootUrl: rootUrl, type: f.fileType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 };
            try {
              await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, body, cfg);
              sent = sent + 1;
            } catch (error) {}
          }
        }
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No website sources to rescan.');
      }
    } catch (error) {}
  }
  
  async function rescanSelectedWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      if (selectedRowKeys.value.size === 0) {
        toast.error('Please select at least one item.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Map();
      const keys = Array.from(selectedRowKeys.value);
      for (let i = 0; i < keys.length; i = i + 1) {
        const name = keys[i];
        const existingIdx = existingFiles.value.findIndex(f => f.fileName === name);
        if (existingIdx > -1) {
          const f = existingFiles.value[existingIdx];
          if (types.includes(f.fileType) === true && f.status !== 'deleted') {
            const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
            const entryType = f.fileType === 'websitepage' ? 'websiteurl' : f.fileType;
            if (unique.has(rootUrl) === false) unique.set(rootUrl, { type: entryType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 });
          }
        }
      }
      let sent = 0;
      for (const [rootUrl, data] of unique.entries()) {
        try {
          await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, { rootUrl: rootUrl, type: data.type, maxPages: data.maxPages }, cfg);
          sent = sent + 1;
        } catch (error) {}
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No selected website sources to rescan.');
      }
    } catch (error) {}
  }
  
  async function retryAllProcessingWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Map();
      for (let i = 0; i < existingFiles.value.length; i = i + 1) {
        const f = existingFiles.value[i];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          let needsRescan = false;
          const ps = typeof f.processingStatus === 'string' ? f.processingStatus : '';
          if (ps === 'Processing') {
            needsRescan = true;
          } else if (shouldShowContinue(f) === true) {
            needsRescan = true;
          }
          if (needsRescan === true) {
            const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
            if (unique.has(rootUrl) === false) {
              unique.set(rootUrl, { type: f.fileType === 'websitepage' ? 'websiteurl' : f.fileType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 });
            }
          }
        }
      }
      let sent = 0;
      for (const [rootUrl, data] of unique.entries()) {
        const body = { rootUrl: rootUrl, type: data.type, maxPages: data.maxPages };
        try {
          await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, body, cfg);
          sent = sent + 1;
        } catch (error) {}
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No website sources in processing state.');
      }
    } catch (error) {}
  }
  
  async function retryProcessingSelectedWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      if (selectedRowKeys.value.size === 0) {
        toast.error('Please select at least one item.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Map();
      const keys = Array.from(selectedRowKeys.value);
      for (let i = 0; i < keys.length; i = i + 1) {
        const name = keys[i];
        const existingIdx = existingFiles.value.findIndex(f => f.fileName === name);
        if (existingIdx > -1) {
          const f = existingFiles.value[existingIdx];
          if (types.includes(f.fileType) === true && f.status !== 'deleted') {
            let needsRescan = false;
            const ps = typeof f.processingStatus === 'string' ? f.processingStatus : '';
            if (ps === 'Processing') {
              needsRescan = true;
            } else if (shouldShowContinue(f) === true) {
              needsRescan = true;
            }
            if (needsRescan === true) {
              const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
              if (unique.has(rootUrl) === false) unique.set(rootUrl, { type: f.fileType === 'websitepage' ? 'websiteurl' : f.fileType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 });
            }
          }
        }
      }
      let sent = 0;
      for (const [rootUrl, data] of unique.entries()) {
        const body = { rootUrl: rootUrl, type: data.type, maxPages: data.maxPages };
        try {
          await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, body, cfg);
          sent = sent + 1;
        } catch (error) {}
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No selected website sources in processing state.');
      }
    } catch (error) {}
  }
  
  watch(itemsPerPageKb, () => {
    currentKbPage.value = 1;
  });
  
  function shouldShowRescanWebsiteUrl(file) {
    if (file === undefined || file === null) return false;
    if (file.status !== 'existing') return false;
    if (isEditMode.value !== true) return false;
    return ['website','websiteurl','websitepage'].includes(file.fileType) === true;
  }
  
  function shouldShowContinue(file) {
    if (['website','websiteurl','websitepage'].includes(file.fileType) === false) return false;
    const statusInfo = loadedAgentData !== null && Array.isArray(loadedAgentData.fileStatuses) === true ? loadedAgentData.fileStatuses.find(s => s.name === file.fileName) : null;
    if (statusInfo === null || statusInfo === undefined) return false;
    if (statusInfo.status === 'Completed' || statusInfo.status === 'Failed') return false;
    const prog = statusInfo.progress;
    if (prog === undefined || prog === null) return false;
    const totalVal = Number(prog.total);
    const currentVal = Number(prog.current);
    const totalIsNum = Number.isFinite(totalVal) === true;
    const currentIsNum = Number.isFinite(currentVal) === true;
    if (totalIsNum === true && currentIsNum === true) {
      if (totalVal > 0 && currentVal < totalVal) return true;
    }
    return false;
  }
  
  function shouldShowRetryFailed(file) {
    if (['website','websiteurl','websitepage'].includes(file.fileType) === false) return false;
    const statusInfo = loadedAgentData !== null && Array.isArray(loadedAgentData.fileStatuses) === true ? loadedAgentData.fileStatuses.find(s => s.name === file.fileName) : null;
    if (statusInfo === null || statusInfo === undefined) return false;
    if (Array.isArray(statusInfo.discoveredLinks) === true) {
      const failed = statusInfo.discoveredLinks.filter(l => l !== undefined && l !== null && l.status === 'failed').length;
      if (failed > 0) return true;
    }
    if (Number.isFinite(Number(statusInfo.failedCount)) === true && Number(statusInfo.failedCount) > 0) return true;
    return false;
  }
  async function runImmediateRescrape(agentId) {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const jobs = Array.isArray(immediateRescrapeQueue.value) === true ? immediateRescrapeQueue.value : [];
      for (let i = 0; i < jobs.length; i = i + 1) {
        const job = jobs[i];
        const rootUrl = job.rootUrl;
        const maxPages = Number.isFinite(Number(job.maxPages)) === true ? Number(job.maxPages) : 0;
        const action = job.action === 'retryFailed' ? 'retryFailed' : 'continue';
        if (action === 'retryFailed') {
          try {
            await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/retryFailed`, { rootUrl: rootUrl, maxPages: maxPages }, cfg);
          } catch (error) {
          }
        } else {
          try {
            await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/continue`, { rootUrl: rootUrl, maxPages: maxPages }, cfg);
          } catch (error) {
          }
        }
      }
      immediateRescrapeRequested.value = false;
      immediateRescrapeQueue.value = [];
    } catch (error) {}
  }
  const currentKbPage = ref(1);
  const totalKbPages = computed(() => {
    const totalItems = displayedFoldersInCurrentView.value.length + filteredExistingFiles.value.length + filteredUploadedFiles.value.length;
    const perPage = Number(itemsPerPageKb.value);
    return perPage > 0 ? Math.ceil(totalItems / perPage) : 1;
  });
  const paginatedCombinedKbItems = computed(() => {
    const combined = [
      ...displayedFoldersInCurrentView.value.map(i => ({ type: 'folder', item: i })),
      ...filteredExistingFiles.value.map(i => ({ type: 'existing', item: i })),
      ...filteredUploadedFiles.value.map(i => ({ type: 'new', item: i }))
    ];
    const perPage = Number(itemsPerPageKb.value);
    if (perPage <= 0) {
      return combined;
    }
    const start = (currentKbPage.value - 1) * perPage;
    const end = start + perPage;
    return combined.slice(start, end);
  });
  const paginatedFoldersInCurrentView = computed(() => paginatedCombinedKbItems.value.filter(i => i.type === 'folder').map(i => i.item));
  const paginatedFilteredExistingFiles = computed(() => paginatedCombinedKbItems.value.filter(i => i.type === 'existing').map(i => i.item));
  const paginatedFilteredUploadedFiles = computed(() => paginatedCombinedKbItems.value.filter(i => i.type === 'new').map(i => i.item));
  
  const selectedInappropriateContentLang = ref('en');
  const selectedDontKnowLang = ref('en');
  
  const currentInappropriateContentMessage = computed({
    get: () => isDefinedAndNotEmpty(inappropriateContentMessage.value[selectedInappropriateContentLang.value]) != false ? inappropriateContentMessage.value[selectedInappropriateContentLang.value] : '',
    set: (value) => {
      inappropriateContentMessage.value[selectedInappropriateContentLang.value] = value;
    }
  });
  
  const currentDontKnowMessage = computed({
    get: () => isDefinedAndNotEmpty(dontKnowMessage.value[selectedDontKnowLang.value]) != false ? dontKnowMessage.value[selectedDontKnowLang.value] : '',
    set: (value) => {
      dontKnowMessage.value[selectedDontKnowLang.value] = value;
    }
  });
  
  const selectedStartingMessageLang = ref('default');
  const currentStartingMessage = computed({
    get: () => isDefinedAndNotEmpty(startingMessage.value[selectedStartingMessageLang.value]) != false ? startingMessage.value[selectedStartingMessageLang.value] : '',
    set: (value) => {
      startingMessage.value[selectedStartingMessageLang.value] = value;
    }
  });
  const selectedHumanInTheLoopTransferMessageLang = ref('default');
  const currentHumanInTheLoopTransferMessage = computed({
    get: () => isDefinedAndNotEmpty(humanInTheLoopHumanTransferMessage.value[selectedHumanInTheLoopTransferMessageLang.value]) != false ? humanInTheLoopHumanTransferMessage.value[selectedHumanInTheLoopTransferMessageLang.value] : '',
    set: (value) => {
      humanInTheLoopHumanTransferMessage.value[selectedHumanInTheLoopTransferMessageLang.value] = value;
    }
  });
  const selectedHumanInTheLoopAiBackMessageLang = ref('default');
  const currentHumanInTheLoopAiBackMessage = computed({
    get: () => isDefinedAndNotEmpty(humanInTheLoopAiBackMessage.value[selectedHumanInTheLoopAiBackMessageLang.value]) != false ? humanInTheLoopAiBackMessage.value[selectedHumanInTheLoopAiBackMessageLang.value] : '',
    set: (value) => {
      humanInTheLoopAiBackMessage.value[selectedHumanInTheLoopAiBackMessageLang.value] = value;
    }
  });
  
  const viewMode = ref('full');

  function normalizeSetupType(value) {
    return value === 'light' ? 'light' : 'full';
  }

  function getSetupTypePreferenceKey() {
    const agentId = typeof route.query.edit === 'string' && route.query.edit.trim().length > 0 ? route.query.edit.trim() : 'new';
    return `aiChatAgentSetupView:${agentId}`;
  }

  function getRememberedSetupType() {
    if (typeof window === 'undefined' || window.sessionStorage === undefined) {
      return '';
    }
    try {
      const value = window.sessionStorage.getItem(getSetupTypePreferenceKey());
      return value === 'light' || value === 'full' ? value : '';
    } catch (error) {
      return '';
    }
  }

  function rememberSetupType(value) {
    if (typeof window === 'undefined' || window.sessionStorage === undefined) {
      return;
    }
    try {
      window.sessionStorage.setItem(getSetupTypePreferenceKey(), normalizeSetupType(value));
    } catch (error) {}
  }

  function getRequestedSetupType() {
    const rawRouteSetupType = Array.isArray(route.query.setupType) === true ? route.query.setupType[0] : route.query.setupType;
    const routeSetupType = typeof rawRouteSetupType === 'string' ? rawRouteSetupType : '';
    if (routeSetupType === 'light' || routeSetupType === 'full') {
      return routeSetupType;
    }
    const rememberedSetupType = getRememberedSetupType();
    if (rememberedSetupType === 'light' || rememberedSetupType === 'full') {
      return rememberedSetupType;
    }
    const rawLightMode = Array.isArray(route.query.lightMode) === true ? route.query.lightMode[0] : route.query.lightMode;
    if (rawLightMode === 'true') {
      return 'light';
    }
    return '';
  }

  function switchView(selectedMode = viewMode.value) {
    const nextMode = normalizeSetupType(selectedMode);
    viewMode.value = nextMode;
    rememberSetupType(nextMode);
    const nextPath = nextMode === 'light' ? '/aiChatAgentSetupLight' : '/aiChatAgentSetup';
    if (route.path === nextPath) {
      return;
    }
    const nextQuery = { ...route.query, setupType: nextMode };
    if (nextQuery.lightMode !== undefined) {
      delete nextQuery.lightMode;
    }
    router.push({ path: nextPath, query: nextQuery });
  }
  
  function openChat() {
    if (availableOpenAgentChannels.value.length === 0) {
      toast.error(t('aiChatAgentSetup.openAgent.noAvailableChannels', 'No openable deployment channel is connected for this agent.'));
      return;
    }
    if (shouldOpenSingleAgentChannelDirectly(availableOpenAgentChannels.value) === true) {
      openAgentChannel(availableOpenAgentChannels.value[0].id);
      return;
    }
    showOpenAgentModal.value = true;
  }
  function getOpenWebAgentTarget() {
    const agentIdRaw = route.query.edit;
    const agentId = agentIdRaw !== undefined && agentIdRaw !== null ? String(agentIdRaw) : '';
    if (agentId.length === 0) {
      toast.error(t('aiChatAgentSetup.errors.missingAgentId', 'Cannot open chat: Agent ID is missing.'));
      return null;
    }
    const safeAgentId = encodePathSegment(agentId);
    const type = agentType.value;
    const basePath = type === 'PublicAgent' ? '/agent' : '/aiChatAgentAssistance';
    const customDomains = loadedAgentData !== null && typeof loadedAgentData.customDomains === 'object' && loadedAgentData.customDomains !== null ? loadedAgentData.customDomains : {};
    if (type === 'PublicAgent') {
      let external = '';
      const agentsDomain = getCustomDomainBase(customDomains, 'agents');
      if (agentsDomain !== '') {
        external = agentsDomain;
      } else if (loadedAgentData !== null && typeof loadedAgentData.publicAgentServiceEndpoint === 'string' && loadedAgentData.publicAgentServiceEndpoint.trim() !== '') {
        external = loadedAgentData.publicAgentServiceEndpoint.trim();
      }
      const base = normalizeExternalBaseUrl(external);
      const safe = base !== '' ? sanitizeWindowOpenUrl(`${base}/${safeAgentId}`) : null;
      if (safe !== null) {
        return { mode: 'window', url: safe };
      }
      return { mode: 'window', url: `${window.location.origin}${basePath}/${safeAgentId}` };
    }
    const assistantDomain = getCustomDomainBase(customDomains, 'assistant');
    const safe = assistantDomain !== '' ? sanitizeWindowOpenUrl(`${assistantDomain}${basePath}/${safeAgentId}`) : null;
    if (safe !== null) {
      return { mode: 'window', url: safe };
    }
    return { mode: 'router', url: `${basePath}/${safeAgentId}` };
  }
  function openWebAgentChannel() {
    const target = getOpenWebAgentTarget();
    if (target === null) {
      return;
    }
    showOpenAgentModal.value = false;
    if (target.mode === 'window') {
      window.open(target.url, '_blank', 'noopener,noreferrer');
      return;
    }
    router.push(target.url);
  }
  function openAgentChannel(channelId) {
    if (channelId === 'Web' || channelId === 'Web Voice Agent') {
      openWebAgentChannel();
      return;
    }
    if (channelId === 'Whatsapp' || channelId === 'Whatsapp Voice') {
      openWhatsappAgentChannel();
      return;
    }
    if (channelId === 'Phone') {
      openPhoneAgentChannel();
    }
  }
  function openPhoneAgentChannel() {
    const normalized = normalizeOpenAgentTelValue(phoneDisplayNumber.value);
    if (normalized.length === 0) {
      toast.error(t('aiChatAgentSetup.openAgent.noAvailableChannels', 'No openable deployment channel is connected for this agent.'));
      return;
    }
    showOpenAgentModal.value = false;
    window.location.href = `tel:${normalized}`;
  }
  function openWhatsappAgentChannel() {
    const digits = normalizeOpenAgentPhoneDigits(whatsappDisplayNumber.value);
    if (digits.length === 0) {
      toast.error(t('aiChatAgentSetup.openAgent.missingWhatsappNumber', 'Cannot open WhatsApp: no number is connected.'));
      return;
    }
    const safe = sanitizeWindowOpenUrl(`https://web.whatsapp.com/send?phone=${encodeURIComponent(digits)}`);
    if (safe === null) {
      return;
    }
    showOpenAgentModal.value = false;
    window.open(safe, '_blank', 'noopener,noreferrer');
  }
  
  const showScrape = ref(false);
  
  const showCreateStyleModal = ref(false);
  const onStyleCreated = (newStyle) => {
    let normalizedId = '';
    if (newStyle.styleguid !== undefined && newStyle.styleguid !== null && newStyle.styleguid !== '') {
      normalizedId = newStyle.styleguid;
    } else if (newStyle.styleGuid !== undefined && newStyle.styleGuid !== null && newStyle.styleGuid !== '') {
      normalizedId = newStyle.styleGuid;
    } else if (newStyle.styleid !== undefined && newStyle.styleid !== null && newStyle.styleid !== '') {
      normalizedId = newStyle.styleid;
    } else if (newStyle.styleId !== undefined && newStyle.styleId !== null && newStyle.styleId !== '') {
      normalizedId = newStyle.styleId;
    } else if (newStyle.id !== undefined && newStyle.id !== null && newStyle.id !== '') {
      normalizedId = newStyle.id;
    }
    newStyle.styleid = normalizedId;
    agentStyles.value.push(newStyle);
    selectedStyleGuid.value = normalizedId;
    showCreateStyleModal.value = false;
  };
  
  async function loadSharedKnowledgeBases() {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        sharedKnowledgeBases.value = [];
        return;
      }
      const res = await axios.get(`${apiEndpoint}/api/sharedKnowledgeBases`, cfg);
      sharedKnowledgeBases.value = Array.isArray(res.data) === true ? res.data : [];
    } catch (error) {
      sharedKnowledgeBases.value = [];
    }
  }
  
  async function loadAvailableAgentKnowledgeBases() {
    try {
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        availableAgentKnowledgeBases.value = [];
        return;
      }
      const res = await axios.get(`${apiEndpoint}/api/agents/listMinimal`, cfg);
      availableAgentKnowledgeBases.value = Array.isArray(res.data) === true ? res.data : [];
    } catch (error) {
      availableAgentKnowledgeBases.value = [];
    }
  }
  
  const initializing = ref(true);
  
  const limitAgentSetupOptionRaw = process.env.VUE_APP_LIMIT_AGENT_SETUP_OPTION;
  const limitAgentSetupOption = limitAgentSetupOptionRaw !== undefined ? limitAgentSetupOptionRaw.toLowerCase() : null;
  const hideModeSelector = false;
  
  function openScrapeReport(file) {
    scrapeReportSource.value = { name: file.fileName, url: file.fileName };
    showScrapeReportModal.value = true;
  }
  
  function closeScrapeReportModal() {
    showScrapeReportModal.value = false;
    scrapeReportSource.value = null;
  }
  
  async function rescanWebsiteUrlForSetup(file) {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      if (file === undefined || file === null) {
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) {
        return;
      }
      const sourceUrl = typeof file.url === 'string' && file.url.length > 0 ? file.url : file.fileName;
      const entryType = file.fileType === 'websitepage' ? 'websiteurl' : file.fileType;
      const maxPagesValue = Number.isFinite(Number(file.maxPages)) === true ? Number(file.maxPages) : 0;
      await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, { rootUrl: sourceUrl, type: entryType, maxPages: maxPagesValue }, cfg);
      await refreshAgentData();
      toast.success('Rescan started for website source.');
    } catch (error) {
      toast.error('Failed to rescan website source.');
    }
  }
  
  async function continueWebsiteScanForSetup(file) {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) return;
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const sourceUrl = file.url !== undefined && file.url !== null ? file.url : file.fileName;
      await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/continue`, { rootUrl: sourceUrl, maxPages: file.maxPages || 0 }, cfg);
      await refreshAgentData();
      toast.success('Continue command sent. Refreshing statuses.');
    } catch (error) {
      toast.error('Failed to continue website scan.');
    }
  }
  
  async function retryFailedWebsiteScanForSetup(file) {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) return;
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const sourceUrl = file.url !== undefined && file.url !== null ? file.url : file.fileName;
      await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/retryFailed`, { rootUrl: sourceUrl, maxPages: file.maxPages || 0 }, cfg);
      await refreshAgentData();
      toast.success('Retry failed command sent. Refreshing statuses.');
    } catch (error) {
      toast.error('Failed to retry failed website scan.');
    }
  }
  
  const fieldDirectionOverrides = reactive({});
  function handleDirKeydown(event, fieldKey) {
    const isCtrl = event.ctrlKey === true || event.metaKey === true;
    if (isCtrl !== true) return;
    const key = typeof event.key === 'string' ? event.key.toLowerCase() : '';
    if (key === 'shift') {
      if (event.ctrlKey === true || event.metaKey === true) {
        const loc = Number(event.location);
        if (loc === 1) {
          event.preventDefault();
          fieldDirectionOverrides[fieldKey] = 'ltr';
        } else if (loc === 2) {
          event.preventDefault();
          fieldDirectionOverrides[fieldKey] = 'rtl';
        }
        return;
      }
    }
    if (key === 'l') {
      event.preventDefault();
      fieldDirectionOverrides[fieldKey] = 'ltr';
    } else if (key === 'r') {
      event.preventDefault();
      fieldDirectionOverrides[fieldKey] = 'rtl';
    } else if (key === 'a') {
      if (event.shiftKey === true) {
        event.preventDefault();
        fieldDirectionOverrides[fieldKey] = 'auto';
      }
    }
  }
  function handleAgentDescriptionInput() {
    agentDescriptionError.value = false;
    if (typeof agentDescription.value === 'string') {
      const normalized = agentDescription.value.replace(/^\n+/, '');
      if (normalized !== agentDescription.value) {
        agentDescription.value = normalized;
      }
    }
  }
  function applyPersonalitySelection(personality) {
    agentDescription.value = personality.template;
    selectedAgentPersonality.value = personality.id;
  }
  function handlePersonalityChange(personality) {
    if (personality === undefined || personality === null) {
      return;
    }
    const hasDescription = typeof agentDescription.value === 'string' && agentDescription.value.trim().length > 0;
    if (hasDescription === true) {
      pendingPersonalitySelection.value = personality;
      showPersonalityReplaceModal.value = true;
      return;
    }
    applyPersonalitySelection(personality);
  }
  function confirmPersonalityReplace() {
    if (pendingPersonalitySelection.value !== null) {
      applyPersonalitySelection(pendingPersonalitySelection.value);
    }
    showPersonalityReplaceModal.value = false;
    pendingPersonalitySelection.value = null;
  }
  function cancelPersonalityReplace() {
    showPersonalityReplaceModal.value = false;
    pendingPersonalitySelection.value = null;
  }
  function getDirStyle(fieldKey) {
    const override = fieldDirectionOverrides[fieldKey];
    let dir = null;
    if (override === 'ltr') {
      dir = 'ltr';
    } else if (override === 'rtl') {
      dir = 'rtl';
    } else {
      dir = isRtl.value === true ? 'rtl' : 'ltr';
    }
    return dir === 'rtl' ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' };
  }
  
  function toUtcHourFromLocalTime(hhmm) {
    if (typeof hhmm !== 'string' || /^\d{2}:\d{2}$/.test(hhmm) === false) {
      return undefined;
    }
    const parts = hhmm.split(':');
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (Number.isFinite(h) === false || Number.isFinite(m) === false) {
      return undefined;
    }
    const now = new Date();
    const local = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0);
    return local.getUTCHours();
  }
  
  function toUtcMinuteFromLocalTime(hhmm) {
    if (typeof hhmm !== 'string' || /^\d{2}:\d{2}$/.test(hhmm) === false) {
      return undefined;
    }
    const parts = hhmm.split(':');
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (Number.isFinite(h) === false || Number.isFinite(m) === false) {
      return undefined;
    }
    const now = new Date();
    const local = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m, 0, 0);
    return local.getUTCMinutes();
  }
  
  function toLocalHHMMFromUtc(hour, minute) {
    if (Number.isFinite(Number(hour)) === false) {
      return '';
    }
    const now = new Date();
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), Number(hour), Number.isFinite(Number(minute)) === true ? Number(minute) : 0, 0, 0));
    const lh = String(d.getHours()).padStart(2, '0');
    const lm = String(d.getMinutes()).padStart(2, '0');
    return `${lh}:${lm}`;
  }
  
  async function rescanAllFailedWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Set();
      let sent = 0;
      for (let i = 0; i < existingFiles.value.length; i = i + 1) {
        const f = existingFiles.value[i];
        if (types.includes(f.fileType) === true && f.status !== 'deleted') {
          const ps = typeof f.processingStatus === 'string' ? f.processingStatus : '';
          if (ps === 'Failed' || ps === 'Error') {
            const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
            if (unique.has(rootUrl) === false) {
              unique.add(rootUrl);
              const body = { rootUrl: rootUrl, type: f.fileType === 'websitepage' ? 'websiteurl' : f.fileType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 };
              try {
                await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, body, cfg);
                sent = sent + 1;
              } catch (error) {}
            }
          }
        }
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' failed website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No failed website sources to rescan.');
      }
    } catch (error) {}
  }
  
  async function rescanFailedSelectedWebsiteUrls() {
    try {
      const agentId = route.query.edit;
      if (agentId === undefined || agentId === null) {
        toast.error('Save the agent first.');
        return;
      }
      if (selectedRowKeys.value.size === 0) {
        toast.error('Please select at least one item.');
        return;
      }
      const cfg = getSecuredAxiosConfig(null);
      if (cfg === null) return;
      const types = ['website','websiteurl','websitepage'];
      const unique = new Set();
      let sent = 0;
      const keys = Array.from(selectedRowKeys.value);
      for (let i = 0; i < keys.length; i = i + 1) {
        const name = keys[i];
        const existingIdx = existingFiles.value.findIndex(f => f.fileName === name);
        if (existingIdx > -1) {
          const f = existingFiles.value[existingIdx];
          if (types.includes(f.fileType) === true && f.status !== 'deleted') {
            const ps = typeof f.processingStatus === 'string' ? f.processingStatus : '';
            if (ps === 'Failed' || ps === 'Error') {
              const rootUrl = typeof f.url === 'string' && f.url.length > 0 ? f.url : f.fileName;
              if (unique.has(rootUrl) === false) {
                unique.add(rootUrl);
                try {
                  await axios.post(`${apiEndpoint}/api/agents/${encodePathSegment(agentId)}/scrape/rescan`, { rootUrl: rootUrl, type: f.fileType === 'websitepage' ? 'websiteurl' : f.fileType, maxPages: Number.isFinite(Number(f.maxPages)) === true ? Number(f.maxPages) : 0 }, cfg);
                  sent = sent + 1;
                } catch (error) {}
              }
            }
          }
        }
      }
      if (sent > 0) {
        toast.success('Rescan started for ' + sent + ' failed website source(s).');
        await refreshAgentData();
      } else {
        toast.info('No selected failed website sources to rescan.');
      }
    } catch (error) {}
  }
  
  const isAdvancedOpen = ref(false);
  const uploadKnowledgeDropdownRef = ref(null);
  const kbViewDropdownRef = ref(null);
  const advancedDropdownRef = ref(null);
  const shouldOpenUploadKnowledgeDropdownTop = ref(false);
  const shouldOpenKbViewDropdownTop = ref(false);
  const shouldOpenAdvancedDropdownTop = ref(false);

  function getDropdownShouldOpenTop(dropdownRef) {
    const root = dropdownRef.value;
    if (root === null || root === undefined) {
      return false;
    }
    const panel = root.querySelector('.dropdown-content');
    if (panel === null || panel === undefined) {
      return false;
    }
    const panelHeight = Math.max(panel.scrollHeight, panel.offsetHeight, panel.getBoundingClientRect().height);
    const rootRect = root.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rootRect.bottom;
    const spaceAbove = rootRect.top;
    if (spaceBelow < panelHeight && spaceAbove > spaceBelow) {
      return true;
    }
    return false;
  }

  function prepareDropdownPosition(dropdownKey) {
    if (dropdownKey === 'uploadKnowledge') {
      shouldOpenUploadKnowledgeDropdownTop.value = getDropdownShouldOpenTop(uploadKnowledgeDropdownRef);
      return;
    }
    if (dropdownKey === 'kbView') {
      shouldOpenKbViewDropdownTop.value = getDropdownShouldOpenTop(kbViewDropdownRef);
      return;
    }
    if (dropdownKey === 'advanced') {
      shouldOpenAdvancedDropdownTop.value = getDropdownShouldOpenTop(advancedDropdownRef);
    }
  }

  async function toggleAdvancedDropdown() {
    if (isAdvancedOpen.value === true) {
      isAdvancedOpen.value = false;
      return;
    }
    prepareDropdownPosition('advanced');
    isAdvancedOpen.value = true;
    await nextTick();
    prepareDropdownPosition('advanced');
  }

  function refreshOpenDropdownPositions() {
    if (isAdvancedOpen.value === true) {
      prepareDropdownPosition('advanced');
    }
  }
  
  function onGlobalClickCloseAdvanced(event) {
    if (isAdvancedOpen.value !== true) {
      return;
    }
    const root = advancedDropdownRef.value;
    if (root === null || root === undefined) {
      isAdvancedOpen.value = false;
      return;
    }
    if (root.contains(event.target) === false) {
      isAdvancedOpen.value = false;
    }
  }
  
  function handleAdiAgentSetupRefresh(event) {
    if (isEditMode.value !== true) {
      return;
    }
    const detail = event !== undefined && event !== null && event.detail !== undefined && event.detail !== null ? event.detail : {};
    const refreshedAgentId = typeof detail.agentId === 'string' ? detail.agentId : '';
    const currentEditId = route.query.edit !== undefined && route.query.edit !== null ? String(route.query.edit) : '';
    if (refreshedAgentId !== '' && currentEditId !== '' && refreshedAgentId !== currentEditId) {
      return;
    }
    refreshAgentData();
    flowPreviewKey.value = uuidv4();
  }

  onMounted(() => {
    window.addEventListener('click', onGlobalClickCloseAdvanced, true);
    window.addEventListener('resize', refreshOpenDropdownPositions);
    window.addEventListener('scroll', refreshOpenDropdownPositions, true);
    window.addEventListener('adi-agent-setup-refresh', handleAdiAgentSetupRefresh);
    nextTick(() => {
      syncCurrentPageFromMainTabs();
      const list = document.querySelectorAll('input[name="my_tabs_2"]');
      for (let i = 0; i < list.length; i = i + 1) {
        list[i].addEventListener('change', syncCurrentPageFromMainTabs);
      }
    });
  });
  
  onBeforeUnmount(() => {
    window.removeEventListener('click', onGlobalClickCloseAdvanced, true);
    window.removeEventListener('resize', refreshOpenDropdownPositions);
    window.removeEventListener('scroll', refreshOpenDropdownPositions, true);
    window.removeEventListener('adi-agent-setup-refresh', handleAdiAgentSetupRefresh);
    stopTextTranscription();
    const list = document.querySelectorAll('input[name="my_tabs_2"]');
    for (let i = 0; i < list.length; i = i + 1) {
      list[i].removeEventListener('change', syncCurrentPageFromMainTabs);
    }
  });
  
  function runAdvancedAction(action) {
    try {
      if (action === 'selectAllFiltered') {
        selectAllFilteredRows();
        isAdvancedOpen.value = false;
        return;
      }
      if (action === 'clearSelection') {
        clearSelectedRows();
        isAdvancedOpen.value = false;
        return;
      }
      if (action === 'enableRescrapeSelected') {
        toast.info('Updating rescrape info for selected...');
        isAdvancedOpen.value = false;
        enableRescrapeForSelected();
        return;
      }
      if (action === 'disableRescrapeSelected') {
        toast.info('Disabling rescrape for selected...');
        isAdvancedOpen.value = false;
        disableRescrapeForSelected();
        return;
      }
      if (action === 'deleteSelected') {
        toast.info('Deleting selected items...');
        isAdvancedOpen.value = false;
        deleteSelectedRows();
        return;
      }
      if (action === 'downloadKnowledgeBaseCsv') {
        isAdvancedOpen.value = false;
        downloadKnowledgeBaseCsv();
        return;
      }
      if (action === 'downloadKnowledgeBaseXlsx') {
        isAdvancedOpen.value = false;
        downloadKnowledgeBaseXlsx();
        return;
      }
      if (action === 'rescanSelected') {
        toast.info('Rescan selected - starting...');
        isAdvancedOpen.value = false;
        rescanSelectedWebsiteUrls();
        return;
      }
      if (action === 'changeSelectedWebsiteDomain') {
        openChangeSelectedWebsiteDomainModal();
        return;
      }
      if (action === 'retryProcessingSelected') {
        toast.info('Retry processing for selected - starting...');
        isAdvancedOpen.value = false;
        retryProcessingSelectedWebsiteUrls();
        return;
      }
      if (action === 'rescanFailedSelected') {
        toast.info('Rescan failed for selected - starting...');
        isAdvancedOpen.value = false;
        rescanFailedSelectedWebsiteUrls();
        return;
      }
    } catch (error) {
    } finally {
      isAdvancedOpen.value = false;
    }
  }
  
  </script>
  
  <style>
  .badge-error, .btn-error, .badge-success, .btn-success {
    color: #fff;
  }
  .tab:is(input[type=radio]):after{white-space:nowrap;}
  table {
    font-family: "Heebo", sans-serif !important;
  }

  .ai-agent-setup-chat-mode {
    height: calc(100vh - 112px);
    min-height: 0;
    overflow: hidden;
    animation: agent-setup-chat-panel-enter 220ms ease-out;
  }

  .agent-setup-chat-header {
    background: #f5f5f7;
    animation: agent-setup-chat-header-enter 160ms ease-out;
  }

  .dark .agent-setup-chat-header {
    background: #000000;
  }

  .agent-setup-chat-header .agent-setup-new-chat-btn,
  .agent-setup-chat-header .agent-setup-mode-switch,
  .agent-setup-chat-header .agent-setup-mode-button,
  .agent-setup-chat-header .agent-setup-mode-button-active {
    background: #f5f5f7;
    box-shadow: none;
  }

  .dark .agent-setup-chat-header .agent-setup-new-chat-btn,
  .dark .agent-setup-chat-header .agent-setup-mode-switch,
  .dark .agent-setup-chat-header .agent-setup-mode-button,
  .dark .agent-setup-chat-header .agent-setup-mode-button-active {
    background: #000000;
    box-shadow: none;
  }

  .agent-setup-chat-header .agent-setup-mode-switch {
    backdrop-filter: none;
  }

  @keyframes agent-setup-chat-header-enter {
    from {
      opacity: 0;
      transform: translateY(-8px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes agent-setup-chat-panel-enter {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .ai-agent-setup-chat-mode,
    .agent-setup-chat-header {
      animation: none;
    }
  }

  .ai-agent-setup-chat-mode .adi-page-panel {
    height: 100%;
  }

  .agent-setup-chat-toolbar .agent-setup-new-chat-btn {
    border-radius: 9999px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    font-weight: 600;
  }

  .dark .agent-setup-chat-toolbar .agent-setup-new-chat-btn {
    border-color: rgba(255, 255, 255, 0.1);
    background: rgba(28, 28, 30, 0.92);
  }

  :global(html.agent-setup-main-scroll-locked),
  :global(body.agent-setup-main-scroll-locked),
  :global(#body-scroll.agent-setup-main-scroll-locked) {
    overflow: hidden !important;
    overflow-y: hidden !important;
    overflow-x: hidden !important;
    overscroll-behavior: none;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  :global(#body-scroll.agent-setup-main-scroll-locked::-webkit-scrollbar) {
    display: none;
  }

  :global(#body-scroll.agent-setup-main-scroll-locked .scroll-content) {
    overflow: hidden !important;
  }

  :global(#body-scroll.agent-setup-main-scroll-locked .scrollbar-track) {
    display: none !important;
  }

  .agent-setup-mode-switch {
    display: inline-flex;
    align-items: stretch;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.92);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
    backdrop-filter: blur(18px);
    padding: 0.2rem;
    margin-left: 1rem !important;
    margin-right: 0.75rem;
  }

  .agent-setup-title-mode-switch {
    margin-left: 1.5rem;
  }

  .title-row.rtl-row .agent-setup-title-mode-switch {
    margin-left: 2.5rem;
    margin-right: 0;
  }

  .dark .agent-setup-mode-switch {
    border-color: rgba(255, 255, 255, 0.1);
    background: rgba(28, 28, 30, 0.92);
  }

  .agent-setup-mode-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 2rem;
    border: 0;
    border-radius: 9999px;
    padding: 0 0.95rem;
    background: transparent;
    color: #86868b;
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    transition: background-color 160ms ease, color 160ms ease, box-shadow 160ms ease;
  }

  .agent-setup-mode-button:hover {
    color: #1d1d1f;
  }

  .dark .agent-setup-mode-button:hover {
    color: #f5f5f7;
  }

  .agent-setup-mode-button-active {
    background: rgba(0, 0, 0, 0.06);
    color: #1d1d1f;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.04);
  }

  .dark .agent-setup-mode-button-active {
    background: rgba(255, 255, 255, 0.12);
    color: #f5f5f7;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
  }

  .agent-setup-mode-button-active:hover {
    color: #1d1d1f;
  }

  .dark .agent-setup-mode-button-active:hover {
    color: #f5f5f7;
  }

  .ai-agent-setup[dir='ltr'] .agent-setup-mode-button + .agent-setup-mode-button {
    margin-left: 0.35rem;
  }

  .ai-agent-setup[dir='rtl'] .agent-setup-mode-button + .agent-setup-mode-button {
    margin-right: 0.35rem;
  }

  .agent-setup-mode-icon {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  .ai-agent-setup[dir='ltr'] .agent-setup-mode-button .agent-setup-mode-icon {
    margin-right: 0.4rem;
  }

  .ai-agent-setup[dir='rtl'] .agent-setup-mode-button .agent-setup-mode-icon {
    margin-left: 0.4rem;
  }

  .agent-setup-chat-toolbar .agent-setup-new-chat-btn {
    margin-inline-end: 0.65rem;
  }

  .agent-setup-new-chat-icon {
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
  }

  .ai-agent-setup[dir='ltr'] .agent-setup-new-chat-btn .agent-setup-new-chat-icon {
    margin-right: 0.35rem;
  }

  .ai-agent-setup[dir='rtl'] .agent-setup-new-chat-btn .agent-setup-new-chat-icon {
    margin-left: 0.35rem;
  }
  
  
  .ai-agent-setup[dir='rtl'] .space-x-2 > :not([hidden]) ~ :not([hidden]){margin-right:0.5rem;margin-left:0}
  .ai-agent-setup[dir='rtl'] .space-x-3 > :not([hidden]) ~ :not([hidden]){margin-right:0.75rem;margin-left:0}
  .ai-agent-setup[dir='rtl'] .space-x-4 > :not([hidden]) ~ :not([hidden]){margin-right:1rem;margin-left:0}
  .ai-agent-setup[dir='rtl'] .ml-1{margin-left:0;margin-right:0.25rem}
  .ai-agent-setup[dir='rtl'] .ml-2{margin-left:0;margin-right:0.5rem}
  .ai-agent-setup[dir='rtl'] .ml-3{margin-left:0;margin-right:0.75rem}
  .ai-agent-setup[dir='rtl'] .ml-4{margin-left:0;margin-right:1rem}
  .ai-agent-setup[dir='rtl'] .mr-1{margin-right:0;margin-left:0.25rem}
  .ai-agent-setup[dir='rtl'] .mr-2{margin-right:0;margin-left:0.5rem}
  .ai-agent-setup[dir='rtl'] .mr-3{margin-right:0;margin-left:0.75rem}
  .ai-agent-setup[dir='rtl'] .mr-4{margin-right:0;margin-left:1rem}

  .voice-settings-tab {
    width: 100%;
  }

  .voice-settings-header {
    width: 100%;
    margin-bottom: 1.25rem;
  }

  .voice-settings-title,
  .voice-settings-subtitle {
    text-align: start;
    width: 100%;
  }

  .voice-settings-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    align-items: stretch;
    width: 100%;
    margin-top: 0;
    margin-inline: -0.5rem;
  }

  .voice-settings-card {
    min-width: 0;
    margin-inline: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid oklch(var(--bc) / 0.15);
    border-radius: 0.75rem;
    background: oklch(var(--b1));
    padding: 1rem;
    box-shadow: 0 1px 2px oklch(var(--bc) / 0.06);
  }

  .voice-settings-card--wide {
    grid-column: 1 / -1;
  }

  .voice-settings-bottom-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-column: 1 / -1;
    align-items: stretch;
    margin-inline: -0.5rem;
  }

  .voice-settings-bottom-row .voice-settings-idle-control {
    margin-top: 0.75rem;
    padding-top: 0;
    border-top: 0;
  }

  .voice-settings-idle-toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .voice-settings-card-head {
    display: flex;
    align-items: flex-start;
    flex-direction: row;
  }

  .voice-settings-card-icon {
    display: flex;
    height: 2.5rem;
    width: 2.5rem;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 0.75rem;
    background: oklch(var(--b2));
  }

  .voice-settings-card-copy {
    min-width: 0;
    flex: 1;
    margin-inline-start: 0.75rem;
    text-align: start;
  }

  .voice-settings-call-tools {
    border: 1px solid oklch(var(--bc) / 0.12);
    border-radius: 0.75rem;
    background: oklch(var(--b2));
    padding: 0.75rem;
  }

  .voice-settings-call-tools-title {
    display: flex;
    align-items: center;
    color: oklch(var(--bc) / 0.8);
  }

  .voice-settings-call-tools-title > span {
    margin-inline-start: 0.5rem;
  }

  .voice-settings-call-tool {
    display: flex;
    align-items: center;
    padding-top: 0.75rem;
  }

  .voice-settings-call-tool + .voice-settings-call-tool {
    margin-top: 0.75rem;
    border-top: 1px solid oklch(var(--bc) / 0.1);
  }

  .voice-settings-call-tool-copy {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    margin-inline-end: 0.75rem;
    text-align: start;
  }

  .voice-settings-idle-control {
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid oklch(var(--bc) / 0.1);
    text-align: start;
  }

  .voice-settings-engine-options {
    display: flex;
    flex-direction: column;
  }

  .voice-settings-engine-option {
    display: block;
    cursor: pointer;
    border: 1px solid oklch(var(--bc) / 0.12);
    border-radius: 0.75rem;
    background: oklch(var(--b2));
    padding: 0.75rem;
    transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .voice-settings-engine-option + .voice-settings-engine-option {
    margin-top: 0.5rem;
  }

  .voice-settings-engine-option--active {
    border-color: oklch(var(--p));
    background: oklch(var(--b1));
    box-shadow: 0 1px 3px oklch(var(--bc) / 0.08);
  }

  .voice-settings-engine-option-body {
    display: flex;
    align-items: flex-start;
    flex-direction: row;
  }

  .voice-settings-engine-option-icon {
    display: flex;
    height: 2.25rem;
    width: 2.25rem;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    background: oklch(var(--b3));
  }

  .voice-settings-engine-option-icon--active {
    background: oklch(var(--p));
    color: oklch(var(--pc));
  }

  .voice-settings-engine-option-copy {
    min-width: 0;
    flex: 1;
    margin-inline-start: 0.75rem;
    text-align: start;
  }

  .voice-settings-reasoning-head {
    display: flex;
    align-items: center;
    flex-direction: row;
    text-align: start;
  }

  .voice-settings-fillers-toolbar {
    display: flex;
    align-items: flex-end;
    flex-wrap: wrap;
    margin-top: 1rem;
    margin-inline: -0.375rem;
  }

  .voice-settings-fillers-language {
    width: 240px;
    flex-shrink: 0;
    padding-inline: 0.375rem;
  }

  .voice-settings-fillers-language .custom-selector-container button {
    height: var(--size-field, 3rem);
    min-height: var(--size-field, 3rem);
    padding-top: 0;
    padding-bottom: 0;
  }

  .voice-settings-fillers-language .custom-selector-container .truncate {
    overflow: visible;
    text-overflow: unset;
    white-space: nowrap;
  }

  .voice-settings-fillers-add {
    margin-inline: 0.375rem;
    height: var(--size-field, 3rem);
    min-height: var(--size-field, 3rem);
  }

  .voice-settings-fillers-add-icon {
    margin-inline-end: 0.5rem;
  }

  .voice-settings-fillers-list {
    margin-top: 0.75rem;
  }

  .voice-settings-filler-row {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
  }

  .voice-settings-filler-input {
    min-width: 0;
    flex: 1;
  }

  .voice-settings-filler-delete {
    flex-shrink: 0;
    margin-inline-start: 0.75rem;
  }

  .voice-settings-filler-delete.btn-error {
    background-color: #dc2626;
    border-color: #dc2626;
    color: #ffffff;
  }

  .voice-settings-filler-delete.btn-error:hover {
    background-color: #b91c1c;
    border-color: #b91c1c;
    color: #ffffff;
  }

  .voice-settings-footer {
    width: 100%;
  }

  @media (max-width: 900px) {
    .voice-settings-grid {
      grid-template-columns: minmax(0, 1fr);
      margin-inline: 0;
    }

    .voice-settings-card {
      margin-inline: 0;
    }

    .voice-settings-bottom-row {
      grid-template-columns: minmax(0, 1fr);
      margin-inline: 0;
    }

    .voice-settings-fillers-language {
      width: 100%;
    }

    .voice-settings-fillers-add {
      width: calc(100% - 0.75rem);
      margin-top: 0.5rem;
    }
  }

  .custom-tab-button {
    border: none;
    background-color: transparent;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    margin-bottom: -1px; 
  }
  
  .custom-tab-button.inactive-tab {
    color: var(--fallback-bc, oklch(var(--bc)/0.6)); 
  }
  
  .custom-tab-button.inactive-tab:hover {
    color: var(--fallback-bc, oklch(var(--bc)/0.8));
    background-color: var(--fallback-b2, oklch(var(--b2)/0.5)); 
  }
  
  .custom-tab-button.active-tab {
    color: var(--fallback-p, oklch(var(--p)/1)); 
    border-bottom-color: var(--fallback-p, oklch(var(--p)/1)); 
    font-weight: 600;
  }
  
  .tab-pane {
    display: block;
  }
  
  .ai-agent-setup[dir='rtl'] .title-row{margin-right:1rem}

  .prompt-editor-modal {
    padding: 1.5rem;
    background: rgb(71 85 105 / 0.6);
    backdrop-filter: blur(7px);
  }

  .prompt-editor-shell {
    position: relative;
    z-index: 1;
    display: flex;
    width: calc(100% - 3rem);
    height: calc(100% - 3rem);
    margin: auto;
    overflow: hidden;
    border: 1px solid oklch(var(--bc) / 0.12);
    border-radius: 1.25rem;
    background: oklch(var(--b1));
    box-shadow: 0 24px 80px oklch(var(--bc) / 0.3);
  }

  .prompt-editor-shell--rtl {
    flex-direction: row-reverse;
  }

  .prompt-editor-main.modal-box {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    width: auto;
    max-width: none;
    min-width: 0;
    height: 100%;
    margin: 0;
    padding: 1.5rem;
    border-radius: 0;
    background: oklch(var(--b1));
    box-shadow: none;
    overflow: hidden;
  }

  .prompt-editor-header {
    flex-shrink: 0;
  }

  .prompt-editor-workspace {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    margin-top: 1rem;
  }

  .prompt-editor-textarea {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
    resize: none;
    padding: 1rem;
    line-height: 1.65;
  }

  .prompt-editor-workspace > .text-xs {
    position: absolute;
    inset-inline-end: 0.5rem;
    bottom: 0.5rem;
    padding: 0.2rem 0.45rem;
    border-radius: 0.35rem;
    background: oklch(var(--b1) / 0.86);
  }

  .prompt-editor-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-shrink: 0;
    margin-top: 1rem;
  }

  .prompt-editor-footer > .btn + .btn {
    margin-inline-start: 0.5rem;
  }

  .prompt-editor-assistant {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    width: min(34vw, 32rem);
    min-width: 21rem;
    height: 100%;
    border-inline-start: 1px solid oklch(var(--bc) / 0.12);
    background: oklch(var(--b2));
    opacity: 1;
    overflow: hidden;
    transition: width 240ms ease, min-width 240ms ease, opacity 180ms ease, border-color 180ms ease;
  }

  .prompt-editor-assistant--closed {
    width: 0;
    min-width: 0;
    border-color: transparent;
    opacity: 0;
    pointer-events: none;
  }

  .prompt-editor-assistant-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    min-width: 21rem;
    padding: 0.9rem 1rem;
    border-bottom: 1px solid oklch(var(--bc) / 0.1);
    background: oklch(var(--b1));
  }

  .prompt-editor-assistant-heading {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .prompt-editor-assistant-heading > svg {
    flex-shrink: 0;
    margin-inline-end: 0.65rem;
    color: oklch(var(--bc) / 0.72);
  }

  .prompt-editor-assistant-widget.adi-page-panel {
    flex: 1 1 auto;
    min-height: 0;
    height: auto;
  }

  .prompt-editor-assistant .adi-page-alert {
    display: none;
  }

  .prompt-editor-assistant .adi-chat-page-inner,
  .prompt-editor-assistant .adi-compose-page-shell,
  .prompt-editor-assistant .adi-prompt-grid {
    max-width: none;
  }

  .prompt-editor-assistant .adi-welcome-page {
    padding-top: 1rem;
  }

  .prompt-editor-assistant .adi-welcome-page-title {
    font-size: 1.35rem;
  }

  .prompt-editor-assistant .adi-prompt-grid {
    display: block;
  }

  .prompt-editor-assistant .adi-prompt-card {
    min-height: auto;
    margin: 0.35rem 0;
  }

  .prompt-editor-assistant-open {
    position: absolute;
    inset-inline-end: 1.25rem;
    top: 1.25rem;
    z-index: 2;
    animation: prompt-assistant-open 200ms ease-out;
  }

  .prompt-editor-backdrop {
    background: transparent;
  }

  .prompt-editor-modal-enter-active,
  .prompt-editor-modal-leave-active {
    transition: opacity 180ms ease;
  }

  .prompt-editor-modal-enter-active .prompt-editor-shell,
  .prompt-editor-modal-leave-active .prompt-editor-shell {
    transition: transform 180ms ease, opacity 180ms ease;
  }

  .prompt-editor-modal-enter-from,
  .prompt-editor-modal-leave-to {
    opacity: 0;
  }

  .prompt-editor-modal-enter-from .prompt-editor-shell,
  .prompt-editor-modal-leave-to .prompt-editor-shell {
    transform: scale(0.98);
    opacity: 0;
  }

  @keyframes prompt-assistant-open {
    0% {
      transform: translateX(0.5rem);
      opacity: 0;
    }
    100% {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @media (max-width: 800px) {
    .prompt-editor-modal {
      padding: 0.75rem;
    }

    .prompt-editor-shell {
      width: calc(100% - 1.5rem);
      height: calc(100% - 1.5rem);
      flex-direction: column;
    }

    .prompt-editor-shell--rtl {
      flex-direction: column;
    }

    .prompt-editor-main.modal-box {
      height: auto;
      min-height: 0;
      padding: 1rem;
    }

    .prompt-editor-assistant {
      width: 100%;
      min-width: 0;
      height: 42%;
      border-top: 1px solid oklch(var(--bc) / 0.12);
      border-inline-start: 0;
    }

    .prompt-editor-assistant--closed {
      width: 100%;
      height: 0;
      min-height: 0;
      border-color: transparent;
    }

    .prompt-editor-assistant-header {
      min-width: 0;
    }
  }
  
  @keyframes scaleIn {
    0% { transform: scale(0.96); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
  
  </style>
  <style>
  
  
  
  
  .ai-agent-setup .btn{margin-inline:0.25rem}

  .ai-agent-setup[dir='rtl'] table th,
  .ai-agent-setup[dir='rtl'] table td{ text-align:right !important }
  .ai-agent-setup[dir='rtl'] table td .flex{ justify-content:flex-end }
  .ai-agent-setup[dir='rtl'] table td.kb-actions-cell{direction:rtl;justify-content:flex-start !important}

  .markdown-content p{margin-bottom:0.75em}
  .markdown-content p:last-child{margin-bottom:0}
  .markdown-content a{color:#10a37f;text-decoration:underline;font-weight:500}
  .markdown-content strong{font-weight:600}
  .markdown-content ul, .markdown-content ol{margin-top:0.5em;margin-bottom:0.9em;padding-left:1.25em}
  .markdown-content li{margin-bottom:0.25em}
  .markdown-content ul{list-style-type:disc}
  .markdown-content ol{list-style-type:decimal}
  .markdown-content[dir='rtl'] ul, .markdown-content[dir='rtl'] ol{padding-right:1.25em;padding-left:0}
  .markdown-content h1{font-size:1.25em;font-weight:700;margin:0.75em 0 0.5em}
  .markdown-content h2{font-size:1.1em;font-weight:600;margin:0.75em 0 0.5em}
  .markdown-content blockquote{border-left:3px solid rgba(0,0,0,0.2);padding-left:0.75em;margin:0.75em 0;opacity:0.8;font-style:italic}
  .markdown-content code{background-color:rgba(0,0,0,0.05);padding:0.2em 0.4em;border-radius:4px;font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,Consolas,monospace;font-size:0.9em}
  .markdown-content pre{background-color:#000;color:#fff;padding:1rem;border-radius:0.5rem;overflow-x:auto;margin:0.75em 0}
  .markdown-content pre code{background-color:transparent;padding:0;color:inherit}
  .markdown-content table{width:100%;border-collapse:separate;border-spacing:0;margin:0.9em 0;border:1px solid rgba(148,163,184,0.35);border-radius:12px;overflow:hidden;background-color:#ffffff;box-shadow:0 8px 20px rgba(15,23,42,0.06)}
  .markdown-content th{background-color:rgba(15,23,42,0.06);color:#0f172a;font-weight:600;font-size:0.86rem;text-align:left;padding:10px 12px;border-bottom:1px solid rgba(148,163,184,0.35)}
  .markdown-content td{color:#1f2937;font-size:0.86rem;padding:10px 12px;border-bottom:1px solid rgba(148,163,184,0.2)}
  .markdown-content tr:last-child td{border-bottom:none}
  .markdown-content[dir='rtl'] th,
  .markdown-content[dir='rtl'] td{text-align:right}
  .dark .markdown-content table{background-color:#0b1220}
  .dark .markdown-content th{background-color:rgba(148,163,184,0.1);color:#e5e7eb}
  .dark .markdown-content td{color:#e5e7eb;border-bottom:1px solid rgba(148,163,184,0.15)}
  </style>
